import { STANDING, BARE_IN_WILD, wetPalette, SEA_WATER, hullSheet, HULL_CX, HULL_CY } from './art.ts';
import { foliageCadence, SCREEN_PREP_STEPS } from './frame-pacing.ts';
import { openingWorkshop, openingResource } from './opening-workshop.ts';
import { FROST_LINE, SCORCH_LINE, tempWord, BIOME_WARMTH } from './sim/fields.ts';
import { REMAINS_FRAMES, REMAINS_BITES, REMAINS_CAP, DIETS, FRIEND_FRAMES, nudgeStanding, BREED_FRAMES, BREED_FED, BREED_REACH, CHUNK_CAP } from './sim/food.ts';
import { CASTS, CAST_INSET, SHADOW_ON } from './hud.ts';
import { SimWorld, emit, resetIds } from './sim/things.ts';
import { bearingWords, WORDS } from './deepmark.ts';   // a read stone names the beast it says (round 44)
import { matterOf, traitsOf, DAY_PERIOD, FRAMES_PER_MINUTE, moodOfSeed, moodOf } from './matter.ts';
import { ITEMS, torchGlow, wieldedWeapon, wearWeapon, whet, mend, WHET_COST, wear, takeOff, wearing, wearGear, gearOf, GEAR_SLOTS, addItem, removeItem, count as bagCount, selectSlot, cycleSelected, selected as bagSelected, selectedTool, liftOrDrop, moveCursor, takeOut, takeOutHeld, syncFlags, migrate, bagOf, newBag, HOTBAR, BAG_SLOTS, FORGE_TAKES } from './sim/inventory.ts';
import { setThornCutInto } from './sim/things.ts';
import { matchingForgeRecipe, missingForgeIngredient, forgeTakes } from './sim/inventory.ts';
setThornCutInto(T.DEBRIS);
// Game loop, screen transitions, HUD, saves and audio.
import { Systems, sealsNothing } from './systems.ts';
import { RNG, mix, hashString, randomSeed } from './rng.ts';
import { cure, afflicted, activeNames, afflict } from './sim/afflictions.ts';
import { LIT_FRAMES } from './systems.ts';
import { seedMods, baseSeed } from './seed.ts';
import {
  T, TS, tile, isSolid, isBombable, isThorn, BIOMES,
  VARIED, VARIANTS, EDGED, animOf,
  drawText, textWidth as glyphWidth,
  drawLeaf, drawGlim, drawSigil, drawPod, drawSeed, drawSeedBig, sprite, particle,
  validateSprites,
  UI, standAway, cliffTones } from './art.ts';
import { VW, VH, HUD_H, VIGOR_PER_ROW, clock, BAG_BOX_Y, bagPitch, bagButtons, loadoutBoxes, HOTBAR_X, HOTBAR_Y, HOTBAR_PITCH } from './view.ts';
import { bakeSoil } from './soil.ts';
import { coloursOf, creatureSprite } from './creatures.ts';
import { skyAt, NOON_DAY, NIGHT_DAY } from './sky.ts';
import { maskNormals, RIG_OX, RIG_W } from './rig.ts';
import { SWING, SWING_BUFFER } from './swing.ts';
import {
  EMBER_COOL, MAX_HP_CAP, MAX_PODS_CAP, MAX_HULLS, TOOLS, BEAST_GROWTH, BEAST_VIGOR, BEAST_HOME } from './rules.ts';
import {
  generateWorld, generateScreen, linked, CHARM_MARK,
  COLS, ROWS, WORLD_W, WORLD_H,
  spawnTile,
} from './world.ts';
import { BARRIERS, isWet, OPEN, OPENED_BY, walkableTile } from './passable.ts';
import {
  growDecals, growCritters, stirDecals, stirCritters, drawDecal, drawCritter,
} from './decals.ts';
import {
  growTurf, stirTurf, drawTurf, drawBlades, bakeTurf, bakeTurfSteps, pushTurf, cutTurf, mixHex, TURF, pixelSink, setWindSign } from './grass.ts';
import { buildWaterMask } from './watermask.ts';
import { bakeTileNormals, crownNormals, bakeHeightCanvas, bakeFieldHeights, bakeFieldLevels } from './light.ts';
import { bakeCanopySteps, canopyPlan, drawCanopy } from './canopy.ts';
import { toolVerb, actionLabel } from './labels.ts';
import * as A from './audio.ts';
import * as IU from './inkui.ts';
import * as H from './hud.ts';
import { fieldKinds, fieldKindsSteps, bakeGroundFieldSteps, smoothLabelsSteps } from './groundfield.ts';
import * as S from './save.ts';
import { Scene3D } from './scene3d.ts';
import * as RIG from './rig.ts';
(globalThis as any).__rigModule = RIG;   // the 3D view poses the body through the rig without importing it (scene3d.ts)
import type { RunRecord, Totals } from './save.ts';

/** How far a lungful of water reaches, in pixels. Three tiles. */
export const SPRAY_REACH = 3 * 16;
import { swingWeaponOf,
  Player, Enemy, Shot, Item, Pod, Puff, Flame, overlaps, bodyPoints, clampBox, PW, PH,
  bodyNormals, ENEMY_DEFS } from './entities.ts';
import { Music , prewarmRooms } from './music.ts';

/** How many tiles an arrival has to be able to stand on before it counts as a
 *  place rather than a pocket. A gated mouth is four. */
// A PLACE, NOT A CELL. 14 standable tiles is smaller than the dead ends the
// generator actually makes: measured over 200 seeds, 6 of 1,935 gated launches
// landed in a pocket of 6, 11, 15, 17, 20 or 22 tiles with no mouth, no stairs
// and nothing to die to — and the position was saved. A screen is 192 tiles;
// anything under a quarter of it is somewhere you are stuck, not somewhere you
// are. 40 also covers the 22-tile case with margin.
const ROOMY = 40;
/** The flag that says you carry each tool, and what each is called. `TOOLS`
 *  itself lives in `rules.ts`: the loader validates against the same list. */
const TOOL_FLAG = { pods: 'hasPods', tide: 'hasTide', ember: 'hasEmber' };
const TOOL_NAME = { pods: 'the rattle pod', tide: 'the silt lung', ember: 'the emberwake' };   // the charms' names, the same words the bag uses (LS-232)
/** What the use button reads when each is in the slot. */
/**
 * The rows of the FRAME one screen's water mask may occupy while it is `dy`
 * pixels into a scroll: its own sliding window, intersected with the
 * playfield.
 *
 * Pure and exported because the bug it encodes was invisible to every suite
 * in the project. `scrollWater` clipped to `HUD_H + dy` for a band `ROWS * TS`
 * tall, and the OUTGOING screen's `dy` is negative — so the clip began above
 * `HUD_H`, mask pixels landed in the HUD, and the presenter reflected the top
 * of the playfield across the readouts for the whole transition. It needed
 * both screens to carry water, because only the outgoing mask has a negative
 * offset. `water.mjs` passed with the fault present and passes now; a
 * rendered-picture check could not see a mask, and a headless one could not
 * build one. A number can be checked by either.
 */
export function maskBand(dy, hudH = HUD_H, playH = ROWS * TS) {
  // CLAMPED AT BOTH ENDS, INCLUDING WHEN THE BAND IS EMPTY. Past `dy = playH`
  // the screen has left the frame entirely and the band is nothing — but
  // `max(hudH, hudH + dy)` still put that nothing at 241 on a playfield that
  // ends at 240, i.e. outside the frame it is meant to be an interval within.
  // Real scroll offsets never get there; an interval that can name a row that
  // does not exist is still wrong, and the check that says so caught it.
  const top = Math.min(hudH + playH, Math.max(hudH, hudH + dy));
  const bot = Math.min(hudH + playH, Math.max(hudH, hudH + dy + playH));
  return [top, Math.max(top, bot)];
}

export const TOOL_LABEL = { pods: 'pod', tide: 'dive', ember: 'burn' };
/** The Emberwake's sweep is a decision, so it costs time. */
/** How many of each stackable a run may carry. Defined in `rules.ts`, where
 *  the loader's clamps can read the same numbers without importing this file
 *  back; re-exported because `main.ts` and the suite ask this module for them. */
export { MAX_HP_CAP, MAX_PODS_CAP } from './rules.ts';

/** Tiles that a save can record as broken, and what a break leaves behind.
 *  Anything the player can destroy belongs here or the destruction does not
 *  survive a reload. */
/** The grammar of a saved area key, and the one place that knows it. Both
 *  live in `src/save.ts` now, beside the loader that checks against them;
 *  re-exported because the suite asks this module for them. */
export { AREA_KEY_RE, QUIET_KEY_RE } from './save.ts';

/** Things somebody MADE, which the wood may not grow over. See `drawWorld`. */
const MADE_TILES = new Set([T.STONE, T.CAIRN, T.BRAZIER, T.BRAZIER_LIT]);

/** ONE ANSWER TO "IS THIS A SCREEN COORDINATE, AND WHICH ONE" — in
 *  `src/save.ts`, beside the only thing that calls it. Re-exported for the
 *  suite, which imports it from here. */
export { coordKey } from './save.ts';

/** What one seed's best run and the lifetime totals look like. Declared in
 *  `src/save.ts`, with the store that holds them. */
export type { RunRecord, Totals } from './save.ts';

const BREAKABLE = new Set([T.RUBBLE, T.CRACK, T.THORN, T.NEST, T.TREE,   // a felled tree stays felled across a reload (2026-09-19): it stood again and paid its logs again on every death
  // A WAYSHRINE IS NOT BROKEN, IT IS LIT — but "a tile the player changed and
  // the save has to remember" is exactly what this table is, and inventing a
  // second parallel collection for one more tile is how `visited`, `broken`
  // and `quiet` ended up with three different degrees of validation.
  T.BRAZIER, T.CAIRN]);

/** Frames a track request must hold before the music actually changes. */
export { TRACK_HOLD } from './audio.ts';

// ONE DEFINITION, IN ONE PLACE — and that place is `view.js` now, because
// `hud.js` needs the same numbers and importing them back out of here would
// make the two files a cycle. Re-exported so `main.js` and the playground,
// which import them from this module, did not have to change.
export { VW, VH, HUD_H, VIGOR_PER_ROW } from './view.ts';
// Roman numerals stay upper case: they are numbers, not words, and 'iii'
// in a lowercase face reads as a stutter.
/* THE SAVE FORMAT, THE KEY GRAMMAR, THE RECORDS STORE AND THE LOADER all
 * live in `src/save.ts` now. Nothing else in the game reads them, and a
 * module that owns the disk can be reviewed as one thing. `Game` keeps a
 * one-line delegator for each entry point, below, so no call site moved. */

/* HEIGHT YOU CAN SEE WITHOUT WALKING TO THE EDGE.
 *
 * Cliff faces alone do not read as depth on a top-down map: you see them only
 * where you are already standing at a drop, so the world looks flat everywhere
 * else. What sells height in Link to the Past and Minish Cap is that each
 * level is a different VALUE — a plateau is lighter than the shelf below it,
 * so you read "that ground is above me" from across the screen and the drawn
 * face merely confirms it.
 *
 * Each terrace lifts the ground toward its own highlight. The key carries the
 * level, so `drawOneTile`'s baked-art cache still hits — six biomes times four
 * levels is twenty-four entries, not one per tile. */
function pal2(parts: any) { return parts.pal || BIOMES.heath; }
const CANOPY_PAD = 6;              // tiles of margin on the canopy bake: past the depth map's four-tile reach plus a crown's two, so a crown at a seam is the same crown from both sides
/* LOWER GROUND IS IN SHADE — that is the direction that reads.
 *
 * The first version lifted the UPPER terraces toward `stoneFace`, which is
 * nearly white, so the top of the island bleached out and lost its colour
 * while the bottom stayed saturated. Depth in a top-down picture comes from
 * the low ground being darker, not the high ground being paler: the sun is
 * over the shoulder and the bottom of a valley is where the light does not
 * reach. Same separation, colour kept. */
/* ...AND THE TOP OF THE ISLAND CATCHES THE SUN.
 *
 * Shading only downward gave the highest ground the palette's own base colour
 * and nothing above it, so the frame had no top end: measured on the game's
 * own buffer, p5 = 46 and p95 = 159, with the brightest one per cent barely
 * reaching 183. A picture with no lights has to be read by hue alone, which is
 * how a world of green ground and green trees becomes mush.
 *
 * A negative entry lifts instead of darkens — the highest terrace toward a
 * warm highlight rather than toward white, so it gains value without losing
 * its colour. Four rungs, spanning about a third of the range, which is the
 * separation this idiom needs between one floor and the next. */
/* THE SPREAD IS WHAT MAKES HEIGHT READ, and it was too narrow to survive the
 * countries drawn over it. Positive darkens, negative lifts, so the lowest
 * terrace sits in shadow and the highest catches the light -- the direction was
 * always right. The SIZE was not: measured over 265 real drops, the ground
 * above one was a median of 9 luma lighter than the ground below, and in 80 of
 * them it was DARKER, because one step of terrace moved the ground less than
 * crossing a border between two countries does. A third of the cliffs in the
 * game read backwards, which is why an art critic scored depth 2 of 10 and
 * called the walls "floating bars bounding nothing". */
/* HOW FAR EACH TERRACE IS PUSHED FROM THE COUNTRY'S OWN GROUND.
 *
 * Widened earlier this round to 0.46/0.22/0/-0.22 so the terraces would read at
 * map scale, and at screen scale that is a 69-luma spread across four levels: a
 * bloom hillside stepping down three times came out as horizontal STRIPES of
 * light and dark green with ruled edges, corduroy rather than a slope. The
 * height cue was legible and the ground was not.
 *
 * Half of it still separates the levels -- and the floor-line rule that keeps
 * every wall under every floor gets easier as this narrows, not harder. */
/* NARROWED TO HALF THIS ONCE AND PUT BACK.
 *
 * A bloom hillside reads as horizontal bands of light and dark, and halving
 * this was the obvious cause -- but the bands turned out to be rows of nine and
 * eleven CLIFF tiles: a genuinely stepped hillside, not a shading artefact.
 * Narrowing changed a third of the island's pixels, did not touch the bands,
 * and cost the floor-line rule a luma or two of its margin. */
const LEDGE_HOLD = 16;   // frames of pushing into an edge before the hop
/* Halved from [0.46, 0.22, 0, -0.22]: a level-0 inlet of fen beside level-1
 * heath was mixed 46% toward the shade tone and read as a shadow lying on the
 * grass, 43% darker than the ground a tile away (read-0, screen 5,1). The
 * floor-line rule is measured on wall faces now (LS-161), not on ground. */
const TERRACE_SHADE = [0.32, 0.16, 0.0, -0.16];   // a third under the 0.46 it was (the dark patches), and still 18 luma between the lowest and highest turf, which the suite asks
const SHADE_TONE = UI.shadeTone;

/** Scale a hex colour's channels, which darkens or lightens it without moving
 *  its hue -- see the note on `cliffTones`, which learned the same lesson. */
function darkenHex(hex, k) {
  const c = hex.replace('#', '');
  const h = (v) => Math.max(0, Math.min(255, Math.round(v * k)))
    .toString(16).padStart(2, '0');
  return '#' + h(parseInt(c.slice(0, 2), 16)) + h(parseInt(c.slice(2, 4), 16))
    + h(parseInt(c.slice(4, 6), 16));
}

/* Tiles per cell of the mottle, and how far it may push the floor. Six tiles is
 * a third of a screen -- large enough to read as terrain rather than as noise,
 * small enough that a screenful holds two or three of them. The alpha is bounded
 * well under the drift the turf takes (0.32) because this one lands on the floor
 * the player reads walls against. */
export const WASH_CELL = 6, WASH_MAX = 0.15;
export function washAt(seed, tx, ty) {
  const fx = tx / WASH_CELL, fy = ty / WASH_CELL;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const ax = fx - x0, ay = fy - y0;
  const sx2 = ax * ax * (3 - 2 * ax), sy2 = ay * ay * (3 - 2 * ay);
  const at = (x, y) => ((mix(seed + 4451, x, y) >>> 8) / 16777216);
  const a = at(x0, y0), b = at(x0 + 1, y0);
  const c = at(x0, y0 + 1), d = at(x0 + 1, y0 + 1);
  return ((a + (b - a) * sx2) * (1 - sy2) + (c + (d - c) * sx2) * sy2) * 2 - 1;
}

/** THE MOTTLE OF ONE SCREEN, as raw RGBA, one pixel per tile plus a border ring.
 *
 * Split out of the draw so a check can read it. `groundWash` used to build this
 * straight into an ImageData, which means the only way to ask whether the sea
 * is masked or whether the floor varies at all is to stand up a canvas -- and
 * the fault this pass exists to fix was FOUND on a rendered image, so what has
 * to be defended is the numbers that reach the paint.
 *
 * The ring is sampled one tile beyond the screen on every side so the bilinear
 * blow-up has real neighbours at the window's edge instead of clamping; its
 * MASK is taken from the nearest tile inside, because a screen does not hold
 * its neighbours' tiles.
 */
export function washImage(seed, ox, oy, tiles) {
  const w = COLS + 2, h = ROWS + 2;
  const out = new Uint8ClampedArray(w * h * 4);
  for (let iy = 0; iy < h; iy++) {
    for (let ix = 0; ix < w; ix++) {
      const d = washAt(seed, ox + ix - 1, oy + iy - 1);
      const o = (iy * w + ix) * 4;
      const warm = d >= 0;
      out[o] = warm ? 203 : 79;
      out[o + 1] = warm ? 176 : 143;
      out[o + 2] = warm ? 106 : 134;
      /* NOT OVER THE SEA. The first smooth cut washed every tile, and on open
       * water -- the one surface in the picture with no texture of its own to
       * hide it -- the mottle read as soft horizontal BANDS out to the horizon,
       * plainly a rendering artefact rather than weather. The sea also travels:
       * the water pass displaces those pixels every frame, so a tint painted
       * into them swims.
       *
       * Alpha carries the mask, so the wash still fades out smoothly as it
       * reaches the shore instead of stopping at a tile edge. */
      const cx = Math.min(COLS - 1, Math.max(0, ix - 1));
      const cy = Math.min(ROWS - 1, Math.max(0, iy - 1));
      const k = tiles[cy][cx];
      /* THE WASH IS FOR FLOORS. It was laid on every dry tile, and a tree is
       * a dry tile: warm cells pushed its canopy to lime and cool cells to
       * teal, so one copse held trees of two colours and read as two
       * countries. An object with its own outline keeps its own colour --
       * only ground you can walk on takes the tint. */
      const floor = OPEN.has(k) && !isWet(k);
      out[o + 3] = floor ? Math.round(Math.abs(d) * WASH_MAX * 255) : 0;
    }
  }
  return out;
}
const LIFT_TONE = UI.liftTone;
const terracePalCache = new Map();
/* EXPORTED so a check can ask the real thing.
 *
 * The rule a wall has to keep -- never as light as the lowest FLOOR in its
 * country -- spans two files: this builds the shaded ground per terrace and
 * `cliffTones` reads `darkFloor` off it. A check that rebuilt either half
 * would be a second model of the rule and would agree with a broken one. */
export function terracePal(name, lv, other = null, mix = 0) {
  const k = name + '|' + (other || '') + '|' + mix + '|' + lv;
  let p = terracePalCache.get(k);
  if (p) return p;
  let base = BIOMES[name];
  /* THE NEIGHBOUR'S GROUND RIDES ALONG, it does not get mixed in.
   *
   * Mixing the two palettes was tried and it muddies: the average of two
   * authored palettes is a colour belonging to neither, and a border screen
   * came out grey. The tile painter stipples this instead, so both countries
   * keep their own colours and the eye does the blending. */
  void other; void mix;   // the countries meet in `drawBiomeEdges`, not here

  if (!base) { terracePalCache.set(k, base); return base; }
  const t0 = TERRACE_SHADE[Math.max(0, Math.min(3, lv))];
  const t = Math.abs(t0);
  const tone = t0 < 0 ? LIFT_TONE : SHADE_TONE;
  p = { ...base };
  // the ground and what grows on it darken; rock, water and ink do not,
  // because a cliff face is the same stone whatever it holds up
  for (const key of ['ground', 'shade', 'tuft', 'sand', 'bed', 'bed2']) {
    if (base[key]) p[key] = mixHex(base[key], tone, t);
  }
  /* THE DARKEST FLOOR THIS COUNTRY HAS, carried on every level's palette.
   *
   * The terraces are shaded across a wide range and a cliff face is held a
   * fixed distance under the ground BESIDE it -- and those two rules together
   * do not compose. Measured: ash's walkable ground runs 97 to 166 luma across
   * its four levels while its rock runs 55 to 107, so a level-0 FLOOR is
   * darker than a level-3 WALL. Brightness therefore cannot tell you where you
   * can walk, and on a screen carrying all four levels -- which is most of ash
   * -- it does not.
   *
   * `cliffTones` needs to know the bottom of the country's floor range to keep
   * every wall under it, and it only ever sees one level's palette. So the
   * level-0 ground rides along on all of them. */
  p.darkFloor = mixHex(base.ground || base.floor || UI.groundFallback,
    SHADE_TONE, Math.abs(TERRACE_SHADE[0]));
  // the level above, for a rock tile drawn as the plateau's own floor -- see T.CLIFF
  p.name = name; p.lv = lv;
  p.up = () => terracePal(name, Math.min(3, lv + 1), other, mix);
  terracePalCache.set(k, p);
  return p;
}

/** What each Great Beast is made of, left where it falls and worn on the body (LS-239). */
export const GEAR_OF: Record<string, string> = { sow: 'hide', oldshell: 'plate', bramblemother: 'mantle' };
/* WHAT THE WORLD PLACES, AND HOW FAR OUT (LS-335). `[kind, nearest, farthest]`
 * in screens from the start. Exported so a check can ask that everything this
 * table intends to place IS placed, exactly once each, without keeping its own
 * copy of the list -- a hand-kept copy is what turned five checks red the last
 * time a kind was added. Append, never insert: the placement of everything
 * before an entry depends on its position here. */
/** THE DEATH FALL, in frames: how long the forager is seen to go down before
 *  the screen that names what it cost. Named because three places need it --
 *  the timer, the ragdoll's own length, and the grace in main.ts in which a
 *  press cannot cut it short (2026-09-10). */
/** SIX COUNTRIES, SIX BESTIARIES: which small kin live where, by tier. This
 *  lived inside `fillArea`; the night's prowlers (LS-379) draw from the same
 *  table, so it is one table. */
export const BESTIARY: Record<string, string[][]> = {
  heath: [['sporeling', 'sporeling', 'wisp', 'gloam', 'tidecrab', 'stormgull', 'duskling'], ['charger', 'eel', 'stormgull'], ['charger', 'bramble']],
  bloom: [['sporeling', 'sporeling', 'bramble', 'gloam', 'mossback', 'duskling'], ['bramble', 'mossback', 'duskling'], ['bramble', 'shellback']],
  fen: [['sporeling', 'wisp', 'wisp', 'gloam', 'eel', 'mossback', 'tidecrab', 'stormgull', 'duskling'], ['bramble', 'eel', 'mossback', 'stormgull'], ['bramble', 'shellback']],
  scree: [['sporeling', 'shellback', 'cinder', 'screehopper'], ['shellback', 'charger', 'sunbasker', 'screehopper'], ['shellback', 'charger']],
  dust: [['sporeling', 'sporeling', 'charger', 'sunbasker'], ['charger', 'charger', 'sunbasker'], ['charger', 'shellback']],
  ash: [['sporeling', 'wisp', 'cinder', 'sunbasker'], ['wisp', 'charger', 'cinder', 'screehopper'], ['wisp', 'shellback', 'charger']],
};

/** Leaving by a stone: frames the action must be held beside one (LS-379). */
export const LEAVE_HOLD = 90;   // a second and a half, not three quarters (LS-397, 2026-09-12): a press at a stone to 'interact' was ending the run; the stone's sparks grow through the hold, so the intent is visible before it lands
/** Marks a living beast may leave on one screen before that screen is done changing (LS-379). */
export const MARK_CAP = 24;

export const DEATH_FRAMES = 96;
/** ...and how much faster it runs once a press has asked for the screen.
 *
 *  TWO TRUE THINGS HAD TO BE RECONCILED (2026-09-10). A press during the fall
 *  used to be DROPPED -- four input sites listed title, gameover and win and
 *  none listed `dying` -- so a player who pressed enter as they fell pressed
 *  into nothing for a second and a half. The fix then was to skip straight to
 *  the screen, and that is why Vijay never saw a death: "why is there no death
 *  animation why do i immediately see the death screen its very jarring". You
 *  die with a thumb already on the button, so the press that killed you ends
 *  the fall you were meant to watch.
 *
 *  Neither "drop the press" nor "skip the fall" is right. A press makes the
 *  fall RUN, three frames a frame: it is answered inside a fifth of a second,
 *  the whole animation still plays, and nothing is discarded. */
export const DEATH_RUSH = 3;

/** THE STEAM'S THREE TONES, thin to thick. No alpha: a translucent wash over a
 *  pixel-art meadow reads as fog on the GLASS rather than as steam in the
 *  world, so the density is carried by a dither and the colour only warms. */
export const STEAM = ['#c9b7a0', '#e4d4b8', '#f8eed8'];   // warm, so it reads as heat off the ground and not as fog on it

export const LOOT_BANDS: [string, number, number][] = [
  /* UPKEEP FIRST, AND NEAR (2026-09-16). The placer walks this table in order and each cache takes a screen the
   * next cannot, so a kind late in the table lands where the near screens are already spoken for. The whetstone sat
   * eighth, in a band of two to six, and every change to the pool -- a new kind anywhere in the table -- shuffled it
   * within that band: at two screens out the quest bot felled all three beasts, at six it died nineteen times at the
   * old shell with a dull spear, because a blade with no blows left strikes as bare hands. Moving them to the FRONT
   * of the table fixed that seed and broke another (q0 met the sow unarmed: the axe had moved), so they keep their
   * place and their bands end where the first fights begin -- one to three, one to four. */
  ['spear', 1, 3], ['axe', 2, 5], ['torch', 3, 7], ['pod', 1, 4], ['tide', 2, 6], ['ember', 3, 7],
  ['treads', 2, 4], ['bell', 4, 8], ['whetstone', 1, 3], ['rivet', 1, 4], ['rod', 3, 8],
  ['ward', 2, 6], ['hone', 3, 7], ['clasp', 4, 8],
  ['fins', 2, 5], ['soles', 3, 6], ['oilskin', 2, 6],
  ['quill', 3, 7], ['hare', 2, 5], ['spoon', 2, 6], ['moth', 3, 7],
  /* THE CONSUMABLES LIE IN CACHES TOO (LS-335, fourth batch), appended so no
   * existing cache moves. Each is banded where it is worth having: the salve
   * and the pitch anywhere out, the brine near the water bands, the draught
   * far out, because burning ground is what the far country is. */
  ['salve', 2, 6], ['pitch', 2, 6], ['brine', 3, 7], ['draught', 4, 8],
  /* THE SECOND RANK OF WEAPONS (LS-335, 2026-09-15), appended so no existing cache moves. Banded by what each is
   * worth carrying where: the knife, the shard and the club lie near home, because they are what you fight with
   * before you have an answer; the billhook and the harpoon in the middle country; the maul, the pike and the
   * firebrand far out, where the things they are the answer to live. */
  ['knife', 1, 3], ['shard', 1, 4], ['club', 1, 4],
  ['billhook', 2, 6], ['harpoon', 3, 7],
  ['maul', 4, 8], ['pike', 4, 8], ['firebrand', 3, 7],
  /* ...AND THE THREE MATERIALS THE FIRE WANTS, banded where their weapons belong: flint near home, since the shard
   * and the knife are what you make before you have an answer; scrap and sinew further out. */
  ['flint', 1, 4], ['scrap', 3, 7], ['sinew', 2, 6],
  /* ...AND FOUR CONSUMABLES (LS-335, 2026-09-16): the quickroot and the stonemilk near, where the first fights are;
   * the nightcap in the middle country; the windfall by the far water, since it is a raft's thing. */
  ['quickroot', 1, 4], ['stonemilk', 2, 5], ['nightcap', 3, 7], ['windfall', 3, 7],
  ['honeycomb', 1, 4], ['saltcake', 2, 6], ['bitterleaf', 2, 6], ['emberseed', 2, 5],
  /* ...AND FIVE CHARMS (LS-335, 2026-09-16): the stoneheart near the axe's country, the crow in the middle, the ember
   * and the keel and the anchor by the far water and the night. */
  ['stoneheart', 2, 5], ['crow', 3, 7], ['embercharm', 3, 7], ['keel', 3, 7], ['anchor', 4, 8],
  ['lodestone', 3, 7], ['miser', 2, 6], ['frost', 2, 6], ['salt', 3, 7],
];
/** Every kind `worldLoot` means to place: the banded caches, and the pearl on
 *  the bed of a basin, which is placed by its own rule because the bands look
 *  for ground you can stand on and the whole point of the pearl is that you
 *  cannot. A check asks THIS list, so a kind going missing is red and a kind
 *  being added is not. */
/** A band read as a fraction of a world this deep: band 1 is the glade's doorstep, band 8 the far edge, wherever
 *  that edge is. The bands were written for an eight-deep world (one island) and the archipelago is twenty to
 *  thirty-nine deep, so unscaled they all fell inside the first fifth of the map -- see `worldLoot`. */
export const BAND_SCALE = 8;
/* ...AND ONLY THE FAR BANDS STRETCH, which the bots measured for me (2026-09-16). Scaling EVERY band spread the
 * caches handsomely -- fourteen near, ten middle, eight far on route-1, against thirty-one/nought/one before -- and
 * starved the early game doing it: quest.ts's bot went from winning two seeds in 3.1 and 4.2 minutes with three
 * deaths and one, to 4.8 minutes with seven and NOT WINNING the second at all, eighteen deaths, arriving at a beast
 * unarmed. The near caches were arming the player before the first fight, which is work nobody had written down.
 * So a band that starts at the doorstep stays where it is, and a band that starts three screens out or further --
 * the far country's rewards -- is read as a fraction of the world's depth. */
export const bandAt = (v: number, depth: number, lo = v) => (lo < 3 ? v : Math.round(1 + (v - 1) * (Math.max(depth, BAND_SCALE) - 1) / (BAND_SCALE - 1)));

export const LOOT_KINDS: string[] = [...LOOT_BANDS.map(([k]) => k as string), 'pearl', 'coal', 'tusk'];
/* A WORLD WITH NO ANSWERS IN IT, for a check that needs one (2026-09-10). The
 * three weapons each Great Beast answers to are what an unfinishable world is
 * missing, and "the quest can be won by playing it" wants a control that
 * cannot be won -- a hand-picked losing SEED is a treadmill, because every
 * time the bot gets better the control starts winning (it was q1, then q2).
 * `__noAnswers` is the diagnostic knob, in the style of `__sharedChain`. */
export const ANSWER_KINDS = ['spear', 'axe', 'torch'];
/** The hull's way on the water (LS-383): thrust a frame from the stick, the water's drag a frame, the top speed, the wind's push on a raft, and which way a body faces at each of eight headings (0 east, clockwise on screen). */
export const HULL_THRUST = 0.13, HULL_DRAG = 0.95, HULL_TOP = 2.2, RAFT_WIND = 0.03;
/** A RAFT HAS A PADDLE (LS-389, decided 2026-09-13): half the boat's thrust, on top of the wind, so a crossing does not wait on the
 *  day's veer. A third (LS-462, 2026-09-26) made 14 px a second into route-1's east wind and nothing at all across it, and the
 *  newcomer probe spent four minutes in sight of the sow's shore; at half, into the wind is about 40 px a second and with it
 *  about 110, so the fair wind still nearly triples the way. */
export const BRAND_FUEL = 6;   // a brand on bare ground burns a few seconds where it lies (systems ticks)
export const RAFT_PADDLE = HULL_THRUST / 2;
export const HEADING_DIR = ['right', 'right', 'down', 'left', 'left', 'left', 'up', 'right'];
/** The kinds a world may leave out (round 52's design lens, 2026-09-11): every seed placed the same twenty-seven caches of
 *  twenty-seven kinds, so a second run was the first run elsewhere. The answers, the tools (whetstone, rivet) and the set pieces
 *  are always placed; of these sixteen a world places a seeded dozen, so what is out there differs by seed. */
/* THE CLUTTER CUT (2026-09-26; Vijay: "take it upon yourself to make this game actually good", "feel free to make radical
 * changes"). A world placed a seeded 33 of 44 optional kinds -- the second rank of weapons, 23 charms, a dozen things to
 * eat -- and the newcomer probe's bag filled in its first minutes with frost, emberseed, quickroot, bitterleaf, ward,
 * honeycomb and stoneheart, none of which a first-time player can read or use. A world now places three optional finds,
 * from four anyone understands. The kinds stay defined, so a save that holds them still loads; the old list is kept below
 * as OPTIONAL_KINDS_WAS for anyone bringing some back, one at a time and for a reason. */
export const OPTIONAL_KINDS = ['pitch'];   // ...and of the four, the one a hunt uses: pitch lights a blade and makes a torch (2026-09-27; the knife, club and salve went with the second rank)
export const OPTIONAL_KINDS_WAS = ['treads', 'bell', 'ward', 'hone', 'clasp', 'fins', 'soles', 'oilskin', 'quill', 'hare', 'spoon', 'moth', 'salve', 'pitch', 'brine', 'draught',
  /* ...AND THE SECOND RANK OF WEAPONS IS OPTIONAL TOO (2026-09-15). A world that placed all twenty-four of these
   * would be a world where the armoury is never a question, and this card's whole argument is that a thing earns
   * its place by being a DECISION. The three answers (spear, axe, torch) are still always placed, so no world is
   * unfinishable for want of a verb; which of the SECOND rank you find is the seed's business. Eighteen of
   * twenty-four keeps the proportion the twelve of sixteen had. */
  'knife', 'shard', 'club', 'billhook', 'harpoon', 'maul', 'pike', 'firebrand',
  'flint', 'scrap', 'sinew', 'quickroot', 'stonemilk', 'nightcap', 'windfall', 'honeycomb', 'saltcake', 'bitterleaf', 'emberseed',
  'stoneheart', 'crow', 'embercharm', 'keel', 'anchor', 'lodestone', 'miser', 'frost', 'salt'];
export const OPTIONAL_PLACED = 1;   // three of the four, so worlds still differ   // thirty-three of forty-four, the same three-quarters   // twenty-three of thirty-one, the same three-quarters the twelve of sixteen was
export function optionalKindsFor(seedNum: number): Set<string> {
  const rng = new RNG(mix(seedNum, 335, 12)); const pool = [...OPTIONAL_KINDS]; const out = new Set<string>();
  while (out.size < OPTIONAL_PLACED && pool.length) out.add(pool.splice(rng.int(pool.length), 1)[0]);
  return out;
}
export const BEAST_NAMES: Record<string, string> = { sow: 'the sow', oldshell: 'the old shell', bramblemother: 'the bramble mother' };

/* WHAT A RUN LEAVES BEHIND IN THE ONE GAME THE BROWSER REUSES (the robustness lens, rounds 42 and 46): every per-run
 * scratch field on the Game and what a fresh game holds in it. reset() walks this table; test/checks/save.ts plays a
 * run, resets, and asks that every field here reads as a fresh game's. */
export const RUN_FIELDS = {
  pickupUntil: () => new WeakMap<object, number>(),
    handAt: () => -1, handWas: () => null, lastWeapon: () => null,   // the hand's memory is the run's (LS-454)
  _restHp: () => undefined, _restAt: () => 0, _outMemo: () => null, heldShot: () => null,   // vigor at rest and the hull's way out are the run's (2026-09-26)
  _landings: () => null,   // where a hull can put you ashore, per island (LS-461)
  _fw: () => null,
  bagOpen: () => false,   // the bag screen is the run's (round 52's robustness lens): E, then R, then Enter began a fresh world with the bag up
  filled: () => new Set(), closedKey: () => null, bandFrames: () => 0, remains: () => [], sleptAt: () => new Map(), _loot: () => null, _lairs: () => null, _approach: () => null, _forges: () => null, _forgesSeeded: () => false, _shelterSeeded: () => false, _raftSeeded: () => false, treeChops: () => new Map(), leaveHold: () => 0, left: () => false, markedMinute: () => -1, gearSave: () => null, slowmo: () => 0, turfJob: () => null, fieldJob: () => null, beastBodies: () => new Map(), spawnedHere: () => 0,
  /* THE AFTERMATH: the frame the third beast fell, or 0. See `onEnemyKilled`. */
  aftermath: () => 0,
  lastPool: () => [],   // the last roster's pool is the run's too (save.ts found it left behind a reset)
  /* THE MIXER'S MEMORY IS A RUN FIELD, and it was not in this table (Vijay,
   * from play: "a lot of sfx break after i die and respawn like sword swings").
   *
   * Every gate in `cue` is "how long since", measured against `game.frame`:
   * SAME_GAP on the name, VOICE_GAP on the family, `quietUntil` after a loud
   * one, IMPACT_SHADOW on impacts. `frame` IS in this table, so a new run
   * restarts it -- and the mixer's memory did not, so every one of those
   * subtractions went NEGATIVE on the first frame back. `0 - 5080` is less
   * than three, so the swing had "just played" and was dropped.
   *
   * MEASURED: a death after five thousand frames left the mixer believing it
   * had last swung at 5080, and it would go on believing that for eighty-five
   * seconds of play. Which is exactly the failure the comment in `reset` warns
   * about -- a field added to the class and not to the table. */
  mix: () => null,
  lightArt: () => null, sunWash: () => null, layerCache: () => null,   // ...and the layer canvases (the robustness lens, round 49): 48 pictures of the OLD world outlived a new one
  /* ...AND THE OTHER TWO OF THE SAME SHAPE (the robustness lens, round 50): maps of full-screen canvases keyed by
   * screen coordinates, which every world has. Measured, 36 of 36 keys survived a reset into a new world, and the
   * blade signature carries `frame`, which the reset puts back to 0 -- so the guard against drawing one world's
   * grass over another's was resting on a counter the reset zeroes. */
  canopyLayers: () => new Map(), bladeLayers: () => new Map(),
  bakeJob: () => null,
  nextGust: () => 0, swingId: () => 0, struckThisSwing: () => null,
  nestTimer: () => 0,   // the beat of warning before a brood hatches: the screen's, and the run's (2026-09-17 -- it was the process's, so a new world's first nest fired with no warning at all)
  /* FOUR MORE THE TABLE HAD MISSED (2026-09-17, a twelve-agent sweep with a probe for each). `aboard` survived a reset,
   * and a body that died on a hull woke on the restored hull afloat -- steerBoat pins it there every frame, so the new
   * run began unable to walk. `bossUntil` survived too: a new run asked for the boss track on 185 of its first 210
   * frames with no beast on the glass. And two screen-keyed canvas caches outlived the world they were painted for:
   * `washBig` handed a new world the old one's ground wash (the same canvas object, byte for byte), `cliffTints` the
   * old one's rock tint (with `cliffTintFrame`, its clock, beside it). */
  aboard: () => false, bossUntil: () => 0, washBig: () => null, cliffTints: () => new Map(), cliffTintFrame: () => new Map(),
  frame: () => 0, dirtyParts: () => new Set(), rebakedAt: () => null, kitPulse: () => undefined,   // the round-47 key-set guard found these (test/checks/save.ts)
};

/* THE SUN'S WASH OVER THE ISLAND, AS PURE ARITHMETIC (2026-09-06).
 *
 * Extracted from the bake so it can be MEASURED. It was a nested loop inside a draw method reading a stubbed canvas,
 * which meant nothing in the suite could say how fast the light changes as you walk -- and that was the defect Vijay
 * reported from play: "why is the sun lighting suddenly changing when i moving around". A field you cannot sample is
 * a field you cannot hold to a bar. Now `test/checks/world.ts` walks it and the bar is a number.
 *
 * Four sine bands over WORLD coordinates, so the light is one continuous sky and a screen's edges agree with its
 * neighbours' by construction -- no seam, and nothing restarts when you cross.
 *
 * EVERY WAVELENGTH IS 2.6x WHAT IT FIRST WAS. Measured along rows of world pixels, with the per-pixel grain smoothed
 * out so only what the eye reads as LIGHT is left, the wash used to change by a median of 14.9 luma -- worst 20.4 --
 * over each 87 px, which is one second at a walking pace. The sun brightened and dimmed about once a second as you
 * crossed a screen. (A flat wash measures 0.0 on that metric, so the number is honest.)
 *
 * The fix is a zoom, not a redesign: all four bands keep their weights, their phases and their relative scales, so
 * the layering reads exactly as it did -- the field is simply drawn 2.6x larger. That halves the walk-rate (14.9 ->
 * 7.0 median, 20.4 -> 8.8 worst) while keeping 88% of the tonal variety across the island (36.5 -> 32.2 luma). Going
 * further flattens it: at 3.6x the variety collapses to 16.3.
 *
 * The other route -- dropping the shortest band and giving its weight to the rest -- was measured and is WORSE
 * (11.4), because concentrating the same alpha into fewer bands makes each one steeper. Amplitude matters as much as
 * wavelength, which is why this is a zoom and not a deletion.
 *
 * The sun's DIRECTION is not this field's job and never was -- the rim lights, the cast shadows and the lit facets on
 * every solid say where the light comes from. This is the weather over the ground. */
export function sunWashAt(wx: number, wy: number, night: boolean) {
  const TAU = Math.PI * 2;
  const warmC = night ? [120, 150, 220] : [255, 198, 104];
  const coolC = night ? [24, 20, 54] : [58, 42, 102];
  const warmA = night ? 0.10 : 0.20, coolA = night ? 0.30 : 0.22;
  const w1 = 0.5 + 0.5 * Math.sin(TAU * (wx / (VW * 9.1) - wy / (PH * 11.7)));
  const w2 = 0.5 + 0.5 * Math.sin(TAU * (wx / (VW * 4.42) + wy / (PH * 5.98)) + 0.9);
  const c1 = 0.5 + 0.5 * Math.sin(TAU * (wx / (VW * 13.0) + wy / (PH * 7.8)) + 1.7);
  const m1 = 0.5 + 0.5 * Math.sin(TAU * (wx / (VW * 1.612) - wy / (PH * 2.028)) + 2.4);
  const warm = warmA * (0.42 * w1 + 0.33 * w2 + 0.25 * m1);
  const cool = coolA * (0.7 * c1 + 0.3 * (1 - m1));
  /* One pass, two tints, composited here rather than by two fills: `a` is what the whole wash is worth at this pixel
   * and the colour is the two tints in their own proportion, so nothing is drawn twice. */
  const a = warm + cool;
  const k = a > 0 ? warm / a : 0;
  return {
    r: Math.round(warmC[0] * k + coolC[0] * (1 - k)),
    g: Math.round(warmC[1] * k + coolC[1] * (1 - k)),
    b: Math.round(warmC[2] * k + coolC[2] * (1 - k)),
    a: Math.round(Math.max(0, Math.min(1, a)) * 255),
  };
}

/* DRIVE A STEPPED FIELD, AND CHARGE THE CLOCK ONLY WHILE IT WORKS (2026-09-06).
 *
 * The bake's costly phases are all `smoothLabels` over a screen, and each was one uninterruptible call -- so the
 * pre-bake could be paced as finely as you liked and one step still blew the frame. They are generators now, and
 * this drives one a chunk at a time. Timing must not span the `yield`: wall clock across a yield measures the
 * frames in between and would report a bake that takes a second. */
function* timedField(name: string, it: any) {
  let r: any;
  do {
    const t0 = performance.now();
    r = it.next();
    ((globalThis as any).__bakeTimes ||= {})[name] = ((globalThis as any).__bakeTimes[name] || 0) + performance.now() - t0;
    if (!r.done) yield;
  } while (!r.done);
  return r.value;
}

/** How far a blade may overhang the top of its own screen. See the note on `pixelSink` in `grass.ts`.
 *  `__noLip` takes it away again, which is the control for the check that measures the seam. */
const BLADE_LIP = 8;

export class Game {
  screenSavePending = false;
  systems: any;
  push: any;
  debugUnlock = false;

  onWater: any;
  onHeat: any;
  onWorldEffects: ((visible: boolean) => void) | undefined;
  heatShown: any;
  screen: any;

  /* ...AND THE LAST TWENTY, FOUND BY RUNNING `tsc` (2026-09-06). The block below fixed 4,646 of about 5,300 and
   * these were what remained: caches and flags assigned in a method, plus three the PAGE sets on the game from
   * outside -- `cssScale`, `quality` and `fps` all come from `main.ts`, and declaring them is the only place that
   * contract is written down. `npm test` runs `tsc` first, so none of this was being read while the check was red. */
  washCache: any; washBig: any; layerCache: any; tileLayerCache: any; soilArt: any; warnedNaN = false;
  stoneLikeness: { kind: string; x: number; y: number; from: number; until: number } | null = null;   // a felled beast's likeness over a read stone (LS-294)
  /* THE GAME IS IN 3D (Vijay, 2026-09-14: "wtf why do we still have 2d get rid of it make 3d default ... we do not
   * need to support both please fully index on 3d"). scene3d.ts paints the world's rows from real geometry and this is
   * no longer a choice: there is no view toggle and nothing remembers a preference. The 2D painters are NOT gone and
   * cannot be -- the scene's floor is their bake, and their sprites are its bodies and props -- but the FLAT
   * PRESENTATION is now only the rescue for a device that cannot give us a GL context (Game.render falls through to it
   * when scene3d.draw returns false or throws, which is also what every headless check without a GL flag gets). */
  view3d = true; scene3d: Scene3D | null = null;
  showFps = false;   // ?fps=1: draw the corner readout (draws a second and the rung) so a device can be photographed rather than tapped (2026-09-14)
  noWorld = false;   // ?world=0: draw the readout and no world at all, for the checks that hold a RULE (2026-09-14)
  artPicture = false;   // ?art=1: compose the painters' bake flat, for the art checks and the flat instruments only (2026-09-14)
  paintBake: ((game: any, g: any, seen: any[], here: number, hereY: number) => void) | null = null;   // src/bake-frame.ts, fetched by main.ts only under ?art=1
  cliffTints: any; cliffTintFrame: any; _terraceInk: any; palA: any; palIds: any;
  bagOpen: any; bandShown: any; fall: any; struckThisSwing: any; barsAfterCanopy: any;
  cssScale: any; quality: any; fps: any;

  /* FIELDS, DECLARED.
   *
   * The migration's single biggest source of type errors — 4,646 of about
   * 5,300 — was this: properties assigned in methods rather than in the
   * constructor are invisible to the checker, so every read of one was an
   * error. Declaring them is what turns a class from a bag into a shape, and
   * a shape is what catches the defect that started this: `drawWorld`'s parts
   * object was missing `tiles` and nothing said so until it was reported from
   * play.
   *
   * They start as `any` because 44,000 lines cannot be typed in one step and
   * a wrong type is worse than a missing one. Narrowing them is the work that
   * follows, and it is worth doing in the order the bugs came from.
   */
  _litKey: any;
  _normalFrame: any;
  _camMask: any;
  _camNormal: any;
  _wetKey: any;
  _wetMask: any;
  amb: any;
  area: any;
  audio: any;
  beatRecord: any;
  broken: any;
  placed: any;   // what the player set down, per screen (LS-379)
  leaveHold: any; left: any; markedMinute: any;   // the way out, and the beasts' marks (LS-379)
  /** WHAT A PIECE OF GEAR JUST DID (LS-298). Four pieces with real effects and no moment where any of them
   *  visibly saved you: when a worn piece turns a blow or keeps an affliction off, the vigor plate says its name
   *  in gold for a second and a half, the way the leaves go red when something eats one. */
  gearSave: { kind: string; until: number } | null = null;
  musicVol = 1; sfxVol = 1;   // the menu's two sliders (LS-300), 0..1, applied to the music bus and the effects bus
  stormFlash = 0;   // frames of the sky's flash: a beat before a strike and on it (LS-381)
  aboard = false;   // the player rides the hull in state.boat (LS-383)
  fairCue: any = null;
  lastPool: string[] = [];   // the island the wind would carry a raft to now, or null (LS-397 h): the hull's glint and its motes both read it
  gearSaved(kind: string) { this.gearSave = { kind, until: this.runFrames + 90 }; }
  fieldJob: any = null;   // a neighbour's ground field being painted a few steps a frame (2026-09-11)
  turfJob: any = null;   // a turf repaint in flight, two steps a frame
  warmedLayers: any = null; scratchCtx: any = null;   // which of a neighbour's draw layers are primed, and the context they are painted into
  bus: any;
  canopy: any;
  canvas: any;
  charms: any;
  charted: any;
  critters: any;
  deathTimer: any;
  deathRush: any;   // a press during the fall: it runs faster, it is not skipped (2026-09-10)
  decals: any;
  enemies: any;
  flames: any;
  frame: any;
  frames: any;
  fx: any;
  g: any;
  gestured: any;
  untouched = false;   // a resumed tab nobody has touched yet: it writes no save until a gesture says it is the one being played (2026-09-19)
  input: any;
  items: any;
  pickupUntil = new WeakMap<object, number>();
  jukebox: any;
  lastRun: any;
  layers: any;
  message: any;
  brief: string[] = [];   // the toasts still to come after the one showing (the run's opening brief, a beast's growth)
  mode: any;
  musicBus: any;
  musicGuard: any;
  muted: any;
  /** False in headless test games: skip the per-screen cosmetic bakes (decals,
   *  grass, canopy, water mask, normals) that no rule of play reads. Never set
   *  in the real game, where the whole point is to look at it. */
  cosmetics?: boolean;
  nestTimer: any;
  night: any;
  rosters: any;
  itemStash: any;
  keyBinding: ((code: string, action: string) => boolean) | null = null;   // main.ts hands over its BINDS lookup, so the bag screen answers the player's own keys (2026-09-17)
  offscreenSim: any;
  musicOff: any;
  sfxOff: any;
  /** The seed's suffixes, parsed once: `{ night, deep, warren }`. */
  mods: any;
  normalMap: any;
  onBrazierLit: any;
  onLight: any;
  onZoom: any;
  zoom: any;
  _camHeight: any;
  shadows: any;
  rt: any;
  otherTab: any;
  owned: any;
  pal: any;
  palKey: any;
  paused: any;
  menuHold = false;   // the menu is up over a world held still (main.ts openRuns): paused, but not the chart
  player: any;
  pods: any;
  previousBest: any;
  pushing: any;
  quiet: any;
  respawnAt: any;
  resume: any;
  resumed: any;
  runFrames: any;
  saveNow: any;
  saveQuarantined: any;
  saveSeq: any;
  screens: any;
  partsCache: any;
  terrain: any;
  cam: any;
  seed: any;
  seedNum: any;
  shake: any;
  nextGust: any;   // the frame the next gust crosses the window (LS-209)
  shots: any;
  spawnedHere: any;
  aftermath: number = 0;   // the frame the third beast fell: the run is won and still walkable (LS-304)
  state: any;
  stop: any;
  storageFailed: any;
  swingId: any;
  tiles: any;
  touch: any;
  turf: any;
  turfArt: any;
  turfOx: any;
  turfOy: any;
  visited: any;
  wantFor: any;
  wantTrack: any;
  waterMask: any;
  world: any;


  constructor(canvas) {
    validateSprites();
    this.canvas = canvas;
    canvas.width = VW; canvas.height = VH;
    this.g = canvas.getContext('2d');
    this.g.imageSmoothingEnabled = false;
    // `podHeld`, not only `podPressed`. Two of the three charms were
    // PERMISSIONS rather than verbs — the Silt Lung made `solid()` return
    // false for water and the Emberwake added a rider to a swing you already
    // had, so collecting either changed where you may stand and nothing you
    // DO. The second button was bound to one thing; holding it is the other.
    this.input = {
      up: false, down: false, left: false, right: false,
      attackPressed: false, attackHeld: false, podPressed: false, podHeld: false,
      dodgePressed: false,
    };
    this.muted = false;
    /* THE LIGHT CASTS AND OCCLUDES (LS-201). Vijay: "turn both on". `shadows`
     * marches the height field toward the sun so standing things throw
     * shadows; `rt` adds a shadow ray from the two nearest lamps and the
     * contact shade around everything. Both were built off by default and are
     * on now by his call. Either can be turned down to 0 at runtime -- a
     * phone that struggles has one number to change, not a code path. */
    this.shadows = 0.85;
    this.rt = 1;
    this.frame = 0;
    this.shake = null;
    this.stop = 0;
    this.message = null;
    this.paused = false;
    this.touch = false;
    this.reset(randomSeed());
  }

  // ---------------------------------------------------------------- setup
  reset(seed, forgeLayout = 2) {
    resetIds();   // the things' id counter is per process; a reload must not inherit the last run's count
    // ...AND THE RUN'S OWN LEFTOVERS (the robustness lens, round 42): the browser reuses one Game, and a load after the first diverged from a fresh game through these
    for (const k in RUN_FIELDS) this[k] = RUN_FIELDS[k]();
    if (this.input) { this.input.podHeld = false; this.input.podPressed = false; this.input.attackHeld = false; this.input.attackPressed = false; this.input.dodgePressed = false; }   // a hold that rode across a reset (round 52)   // the table is the list (the robustness lens, round 46): a field added to the class and not to the table is the round-42 bug again
    // One place. `Alpha`, `ALPHA` and `" alpha "` are otherwise three separate
    // worlds with three separate save slots, and the normalisation used to
    // live in two main.js call sites — both correct today, which is exactly
    // how the round-4 seed bug got in.
    this.seed = Game.normaliseSeed(seed);
    // THE NIGHT IS PART OF THE SEED, NOT PART OF THE SAVE.
    //
    // A reviewer scored replayability 3 and then 4: nothing unlocks, nothing
    // new appears on run two, and GOAL.md's escalation rule asks for reasons
    // to START AGAIN rather than more first-run content. This is the smallest
    // one that cannot break anything: a night world.
    //
    // It is a suffix on the seed rather than a flag in `state`, so a given
    // (seed, night) pair generates exactly one world and determinism per seed
    // is untouched — `alpha` and `alpha!night` are two worlds, each fixed, each
    // with its own save slot. And it changes nothing the gating proof reads:
    // the tiles, the gates, the charm order and the hollow layouts of
    // `alpha!night` ARE `alpha`'s. What changes is that every room is dark and
    // everything in it is harder.
    /* ...AND `!night` IS NO LONGER THE ONLY ONE.
     *
     * It was, and it was the same world: measured over 24 seed pairs it moved
     * deaths 5.6 to 9.3 and damage 101 to 140 and left **wins at 12/24 either
     * way**. Harder to survive, and it changed nothing about what you did or
     * where you went — which is why `replayability` sat at 5 for two rounds.
     *
     * `!deep` and `!warren` pin the delve axis at its ends. Measured over 60
     * seeds and 3 hollows against the same seed unmodified: `!deep` changes
     * the rooms in 141 of 180 and runs 9.21 deep against 6.82, `!warren`
     * changes 125 of 180 and carries 2.66 loop edges against 1.33. Room count
     * is identical in 180 of 180, so the spoil heap outside each mouth stays
     * honest about the dig behind it.
     *
     * `seedMods`/`baseSeed` rather than a regex: the suffixes are canonically
     * ordered, so `a!deep!warren` and `a!warren!deep` are one world and one
     * save slot, and every unmodified seed hashes exactly as it did. */
    this.mods = seedMods(this.seed);
    this.night = this.mods.night;
    this.seedNum = hashString(baseSeed(this.seed));
    this.systems = new Systems(this.seedNum);   // the world clock and the reactions of matter.ts
    // a fault report belongs to the slot it came from, not to the next run
    this.saveQuarantined = false;
    this.world = generateWorld(this.seedNum);
    /* THE LAND'S SHAPE, BUILT ONCE FOR THE WHOLE MAP.
     *
     * `src/terrain.ts` is elevation, moisture, rivers and terraces over all
     * 128x96 tiles at once — a river that restarts at a screen boundary is not
     * a river, and a hillside that restarts at one is not a hillside. Nothing
     * about which TILE goes where reads it yet; the relief pass does, so the
     * world is lit by its own slope instead of by what each tile happens to
     * be. */
    // the world owns its ground; this is the same field the generator saw
    this.terrain = this.world.terrain;
    this.screens = new Map();
    this.chunkTiles = [];
    /* ...AND THE PIXELS GROWN ON THEM, which are derived and therefore
     * throwable. Cleared with the screens because a new world's screen (3,4)
     * has nothing to do with the old one's. */
    this.partsCache = new Map();
    (globalThis as any).__game = this;   // the one game the body's struck() may ask about its gear (the shell crown, LS-335)
    this.rosters = new Map();   // LS-195: the creatures of every wild screen you are not on, keyed 'x,y'
    this.itemStash = new Map();   // LS-211: what a screen dropped while you were not on it (its bake's glims, a death next door), delivered when you arrive
    // one small mottle image per screen -- see the groundWash pass
    this.washCache = new Map();
    this.quiet = new Map();
    this.broken = new Map();
    this.placed = new Map();
    this._quickMasks = new Map();   // the stand-in water masks of another world are no use (LS-387)
    this.visited = new Set();
    // Screens a chart has marked but the player has not walked. The map shows
    // them as rumour, not as memory.
    this.charted = new Set();
    this.state = {
      hp: 10, maxHp: 10, glim: 0, pods: 0, maxPods: 8, seeds: 0, beam: 1,   // `seeds` is dead and kept: an old save carries it and the records read it
      hasPods: false, hasTide: false, hasEmber: false, rattle: false, kills: 0,   // `rattle`: the Rattle has been FOUND (2026-09-18) -- `hasPods` is the bag's own count, so the two rules that meant the capability died with the last pod
      // the lung holds one lungful; going under is how you take one
      lungFull: false,
      /* THE CONSUMABLES' THREE NUMBERS, DECLARED HERE (LS-335, fourth batch).
       * They belong in the fresh state and not just in the sanitiser, because
       * a field that only appears once it has been used makes a LOADED run a
       * different shape from a fresh one -- which is exactly the asymmetry the
       * save fuzzer names as "the save changed by being loaded", and it caught
       * this within one gate. `pitch` is blows left on the coating, `draught`
       * the frame the twenty seconds run out on, `pitchSwing` the last swing
       * charged. */
      pitch: 0, draught: 0, pitchSwing: 0,
      setDown: 0,   // what your own hand has put in the world (LS-379)
      forgeLayout: forgeLayout === 2 ? 2 : forgeLayout === 1 ? 1 : 0,
      tool: 'pods', emberCool: 0,
      bag: (() => { const b = newBag(); b.slots[0] = { kind: 'sickle', n: 1, dur: ITEMS.sickle.dur }; return b; })(),   // the inventory (LS-225): a hotbar and a bag of slots, the sickle in the first
      beastsSlain: [],           // the three great beasts (LS-225): which have fallen
      lootTaken: [],             // world caches picked up, 'x,y:kind', so a weapon is found once (LS-225)
      hoard: [],                 // what the Great Beasts left, so a reload cannot delete it (the robustness lens, round 51)
      gear: { body: null, feet: null, charm: null },   // what is worn (LS-239)
      beasts: null,              // the Great Beasts' roaming records, made on first need (LS-225 step 3)
    };
    // ON THE SEED'S OWN OPENING TILE, not the middle of the screen. These
    // were the same point while the spawn anchor was a constant; it is drawn
    // per seed now, and the player has to stand where the glade was composed.
    const [spx0, spy0] = spawnTile(this.world.seedNum);
    this.area = { kind: 'wild', x: this.world.start.x, y: this.world.start.y, level: 0 };
    // ...and the opening tile is a tile of the START SCREEN, which sits some
    // way into a map that is 128x96 tiles across
    this.player = new Player(
      (this.world.start.x * COLS + spx0) * TS + (TS - 13) / 2,
      (this.world.start.y * ROWS + spy0) * TS + (TS - 13) / 2);
    this.enemies = []; this.shots = []; this.items = []; this.pods = []; this.fx = [];
    this.flames = [];
    this.cam = null;
    // the ledge drop in progress, or null — see `checkLedge`
    this.fall = null;
    /* THE CLOCK IS SET BACK BEFORE THE WORLD IS ASKED WHAT IT LOOKS LIKE (2026-09-18). `loadArea` wakes the screen --
     * the tide's stand, the scorch the clock has grown back, the tufts, the nests -- and every one of those reads
     * `runFrames`, which was still the PREVIOUS run's: a seed begun again entered its opening screen on the old
     * run's hour, with the tide snapped to it, and only caught up when the clock ticked round. */
    this.runFrames = 0;
    this.loadArea();
    this.mode = 'title';
    this.paused = false;
    this.message = null;
    this.deathTimer = 0;
    this.deathRush = false;
    this.resumed = false;
    // NOT nulled: dropping it orphaned a live GainNode on the audio graph
    // — forty deaths, forty orphans, nothing left holding a reference to
    // disconnect them — and restarted the score from bar 1 with no crossfade
    // every time you died. The player outlives the world; the eight-frame
    // debounce handles the track change.
    if (this.jukebox === undefined) this.jukebox = null;
    this.wantTrack = undefined;
    this.wantFor = 0;
    // Never left undefined. The concurrent-write guard compares
    // `disk.writeSeq > this.saveSeq`, and `N > undefined` is false for every
    // N — so a tab that started a world without loading its save (which is
    // every tab opened on a #seed URL) sailed straight past the guard and
    // overwrote. Measured: 200 of 200 runs destroyed, 0 warnings shown.
    // ...AND A SESSION REMEMBERS WHAT IT HAS WRITTEN. Zero here says "this
    // session knows nothing about that slot", which is right for a tab that
    // has just opened — and wrong for a session returning to a seed it played
    // ten minutes ago. Measured, in ONE tab with no other window open: play
    // seed X and save (writeSeq 3), switch to another seed, come back to X —
    // `saveSeq` is 0, the disk says 3, the guard concludes another tab owns
    // the run, and every autosave for the rest of the session is refused
    // while the HUD blames a tab that does not exist. `docs/GOAL.md` asks
    // that a run never lose progress; this loses all of it, silently.
    //
    // A real second tab is a different session with a different `OWNED`, so
    // it still latches. This only stops a session mistaking itself for one.
    // ON THE INSTANCE, AND NOT CLEARED BY `reset`. A module-level map would
    // be shared by every Game in the page, which is not what a second tab is
    // — a second tab is a second JS context — and it made the suite's model
    // of one ("a new Game") unable to express it at all: four checks that
    // guard a live run against being clobbered went green when they should
    // not have. Created only when missing, so switching seeds keeps it.
    if (!this.owned) this.owned = new Map();
    this.saveSeq = this.owned.get(seed) || 0;
    this.otherTab = false;
    this.pushing = null;
    this.stop = 0;
    this.shake = null;
  }

  startGame() {
    this.mode = 'play';
    this.spawnEnemies();
    this.openingRemains();
    /* NOR ANY OPENING LINE (2026-09-10, LS-373). `slay the three beasts` was
     * the first thing a run said, and it was in the golden frames -- a
     * baseline picture of the game with an instruction printed across it. */
    this.message = null;
    // ...AND THE BRIEF GOES ON (the design lens: onboarding was a five-second toast and a dim skull row): their names, then where to look
    // one line each, so every line fits its box (the art lens: the three names in one ran edge to edge), and each says where its beast roams
    // ...each line naming the country its lair is actually in (the lore lens, round 40: 'the dust' was true on four seeds in eight)
    const where = (k: string) => { const l = this.beastLairs().find((q) => q.kind === k); return l && this.world.biome[l.y] ? this.world.biome[l.y][l.x] : 'wilds'; };
    /* THE RUN OPENS IN SILENCE (2026-09-10, LS-373). Three toasts stood here
     * -- the sow, the old shell and the bramble mother, each with the country
     * it lies in -- and they were the first words a player read. The stones
     * say it in the marks, the map says it in geometry, and neither is in
     * English. Vijay: "get rid of the the 3 beasts prompt when the game
     * starts and all in game prompts". */
    /* ?unlock IN THE URL: start with every charm in hand, for looking at
     * the world rather than earning it. Only a fresh start reads
     * it; a save that already exists is left as it is. */
    if (this.debugUnlock) {
      const st: any = this.state;
      addItem(st, 'pod', Math.max(0, 5 - bagCount(st, 'pod'))); addItem(st, 'tide', 1 - bagCount(st, 'tide')); addItem(st, 'ember', 1 - bagCount(st, 'ember'));   // the bag holds them now (LS-225)
      st.tool = st.tool || 'pods';
      this.message = null;   // not announced either (LS-373)
    }
    this.save();
  }


  areaKey(a = this.area) {
    return 'W|' + a.x + ',' + a.y;
  }

  /* THE COSMETIC BAKE FOR ANY SCREEN, not merely the one under the player.
   *
   * Step 2 of the continuous camera (LS-142). A camera that spans a seam has
   * two to four screens on the glass at once and needs every one of them
   * grown, lit and masked — `drawWorld` already draws a parts object at an
   * arbitrary translate, so the only thing missing was somewhere for the
   * neighbours' parts to live.
   *
   * Memoised, because the bake is most of what a screen costs: a 300-seed
   * sweep that scans all 64 screens calls `loadArea` 19,200 times, and
   * profiled, world generation is 7.1ms a seed against the bake's much larger
   * share. Twelve screens is a 3x3 neighbourhood plus slack for the roll.
   *
   * INVALIDATED BY `markBroken`, which is the only place a wild screen's tiles
   * ever change. `applyBroken` mutates the tile array in place and the array
   * is shared with `this.screens`, so the grid stays right on its own; it is
   * the turf, the canopy, the water mask and the normal buffer grown FROM that
   * grid which go stale. A wall you blast has to stop being a wall in the
   * picture as well as in the rule. */
  /* WHERE THE WINDOW IS, in world pixels.
   *
   * The overworld was a slideshow: one screen filled the glass, you walked
   * into the edge, and the whole picture slid across and stopped. The world
   * has been one continuous 128x96 tile map all along — seam rows agree
   * across every join, 100% measured — so the only thing making it a
   * slideshow was that nothing ever looked at two screens at once outside a
   * transition.
   *
   * The viewport stays exactly one screen in size and moves continuously,
   * centred on the player and clamped at the world's outer edge, so the edge
   * of the map still reads as an edge rather than as a void. Between two and
   * four screens are on the glass at any moment, which is what `screenParts`
   * was built for. */
  camera() {
    if (!this.cam) this.snapCamera();
    return { x: Math.round(this.cam.x), y: Math.round(this.cam.y) };
  }

  /** Where the window would sit if it were locked to the player. */
  cameraTarget() {
    // the player's own coordinates ARE world coordinates in the wilds now
    const wx = this.player.cx;
    const wy = this.player.cy;
    return {
      x: Math.max(0, Math.min(WORLD_W * PW - PW, wx - PW / 2)),
      y: Math.max(0, Math.min(WORLD_H * PH - PH, wy - PH / 2)),
    };
  }

  /** Put the window exactly where it belongs, with no travel. */
  snapCamera() {
    const t = this.cameraTarget();
    this.cam = { x: t.x, y: t.y };
  }

  /* THE WINDOW FOLLOWS, IT DOES NOT TRACK.
   *
   * `camera()` used to recompute the whole thing every frame from the player's
   * position and ROUND it. Two faults came out of that. The rounding sits on a
   * value that moves a fraction of a pixel a frame, so the window flicked back
   * and forth by one pixel while the player walked in a straight line — every
   * tile in the world shifting a pixel and back, sixty times a second. And a
   * window locked to the player has to move for every step the player takes,
   * including the ones they take back, so a player nudging left and right
   * dragged the whole world with them.
   *
   * A DEAD ZONE fixes the second: the window ignores the player until they are
   * more than `DEAD` pixels from its centre, so small movements move nothing
   * and the frame stays still while you fight or read a stone.
   *
   * A LERP fixes the first, and it fixes the jumps too — the window eases
   * toward where it should be rather than arriving there, so a gated crossing
   * that puts the player three tiles inside a screen, or a rescue that moves
   * them, becomes a short travel rather than a cut. State is kept as FLOATS
   * and rounded only when it is handed to the renderer; rounding the state
   * itself is what made a lerp jitter in the first place.
   */
  /* WHICH SCREEN THE PLAYER IS STANDING IN, noticed rather than decided.
   *
   * `area` used to be the thing that moved and the player was rebased to
   * match. Now the player moves and `area` follows, which is the whole of what
   * a seam is in an open world: bookkeeping. Nothing about the player's
   * position changes here — no inset, no rescue, no invulnerability, no
   * cooldown, none of the four things that were each fixed separately when
   * the rebase was still real.
   *
   * The per-screen work still happens on the frame the screen changes, because
   * spawning, the anti-farm latches, `visited` and the save are all keyed per
   * screen and have no reason not to be. */
  updateArea() {
    if (this.mode !== 'play') return;
    const sx = Math.floor(this.player.cx / (COLS * TS));
    const sy = Math.floor(this.player.cy / (ROWS * TS));
    if (sx === this.area.x && sy === this.area.y) return;
    /* A POSITION THAT IS NOT A NUMBER (2026-09-13): a probe's teleport left the body at NaN for a moment, and NaN slips
     * past every range test below -- `generateScreen(world, NaN, NaN)` failed every frame until the body moved. Not a
     * screen; nothing to do, said once. */
    if (!Number.isFinite(sx) || !Number.isFinite(sy)) { if (!this.warnedNaN) { this.warnedNaN = true; console.warn('the body stands nowhere: ' + this.player.x + ',' + this.player.y); } return; }
    if (sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) return;
    /* AND ONLY ONCE THE PLAYER IS PROPERLY IN IT.
     *
     * Nothing stops the player standing exactly on a boundary now — that is
     * the point — so without hysteresis `area` flips on every frame they spend
     * there. Traced over a wander: frames 4293 and 4294 flipping 4,7 / 5,7,
     * and each flip runs `loadArea`, `spawnEnemies` and `save`. A player
     * loitering on a seam would respawn the screen's creatures sixty times a
     * second and write a save each time.
     *
     * A tile of overlap costs nothing and makes the switch happen once. */
    /* ...UNLESS IT STAYS THERE (LS-511). A body that stops inside the band -- a pocket ten pixels past a seam, the quest
     * bot's q4 for 86,308 frames -- belongs to the chunk it stands in; half a second of standing makes the switch. */
    const HYST = TS;
    const inX = this.player.cx - sx * COLS * TS, inY = this.player.cy - sy * ROWS * TS;
    const inBand = (sx !== this.area.x && (inX < HYST || inX > COLS * TS - HYST)) || (sy !== this.area.y && (inY < HYST || inY > ROWS * TS - HYST));
    if (inBand) { this.bandFrames = (this.bandFrames || 0) + 1; if (this.bandFrames < 30) return; }
    this.bandFrames = 0;
    /* NOTHING CHANGES HANDS AT A SEAM (LS-511 stage 3). The creatures and things round the body are one list in world
     * space, woken and put to sleep by distance (`streamWorld`); crossing into a chunk only says which chunk is underfoot,
     * and a chunk whose own creatures are all gone grows a new roster where nobody is looking (`enterChunk`). */
    this.area = { kind: 'wild', x: sx, y: sy, level: 0 };
    this.loadArea();
    this.enterChunk(sx, sy);
    this.save();
  }

  updateCamera() {
    if (!this.cam) { this.snapCamera(); return; }
    const DEAD = 12;          // pixels of slack before the window moves at all
    const EASE = 0.16;        // how much of the remaining gap it closes a frame
    const t = this.cameraTarget();
    for (const k of ['x', 'y']) {
      const d = t[k] - this.cam[k];
      const over = Math.abs(d) - DEAD;
      if (over <= 0) continue;
      this.cam[k] += Math.sign(d) * over * EASE;
      // ...and never leaves the world, whatever the easing is mid-travel
      const lim = k === 'x' ? WORLD_W * PW - PW : WORLD_H * PH - PH;
      this.cam[k] = Math.max(0, Math.min(lim, this.cam[k]));
    }
  }

  /** Which screens the window is standing on, as [sx, sy, offsetX, offsetY]. */
  visibleScreens(cam) {
    const out: any[] = [];
    const x0 = Math.floor(cam.x / PW), x1 = Math.floor((cam.x + PW - 1) / PW);
    const y0 = Math.floor(cam.y / PH), y1 = Math.floor((cam.y + PH - 1) / PH);
    for (let sy = y0; sy <= y1; sy++) {
      for (let sx = x0; sx <= x1; sx++) {
        if (sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) continue;
        out.push([sx, sy, sx * PW - cam.x, sy * PH - cam.y]);
      }
    }
    return out;
  }

  screenPreparationTargets() {
    const cam = this.cam ? this.camera() : this.cameraTarget();
    const face = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[this.player.dir] || [0, 0];
    const targets = new Map<string, number[]>();
    // The camera reveals neighbours before the player's area changes.
    for (const view of [cam, { x: cam.x + face[0] * PW, y: cam.y + face[1] * PH }]) {
      for (const [x, y] of this.visibleScreens(view)) targets.set(x + ',' + y, [x, y]);
    }
    /* THE 3D FRUSTUM'S RING (2026-09-16): the oblique camera sees a row north of the window and the corners beside it, and
     * the scene wants the ring around those standing before the window reaches them -- so every neighbour of a window
     * screen is a target too, after the window's own. The draw never bakes (scene3d.readyParts); this is where a screen
     * gets baked, one paced step a frame, and prebuild() stands its terrain up. */
    /* THE RING IS ROUND THE WINDOW, NOT ROUND THE FRUSTUM (2026-09-18). The ring of eight was taken around EVERY
     * target gathered so far -- including the frustum's own screens, which are already a ring -- so the list ran to
     * 25 or 29 screens. `trimPartsCache` caps the cache at sixteen and refuses to evict anything the preparer still
     * wants, so with 29 wanted nothing was ever evictable: measured over a minute's walking the cache settled at 29
     * screens, 190 canvases and 41 MB, and the cap was dead. The window's neighbours are what a step can reveal; the
     * frustum's screens are targets in their own right and `prebuild` still stands them up a frame ahead. */
    const windowScreens = [...targets.values()];
    if (this.view3d && this.scene3d) for (const [x, y] of this.scene3d.seenScreens(cam)) if (!targets.has(x + ',' + y)) targets.set(x + ',' + y, [x, y]);   // the frustum's own screens, before the ring
    if (this.view3d) { for (const [x, y] of windowScreens) for (const [dx, dy] of [[0, -1], [-1, -1], [1, -1], [-1, 0], [1, 0], [0, 1], [-1, 1], [1, 1]]) { const nx = x + dx, ny = y + dy; if (nx < 0 || ny < 0 || nx >= WORLD_W || ny >= WORLD_H) continue; if (!targets.has(nx + ',' + ny)) targets.set(nx + ',' + ny, [nx, ny]); } }
    return [...targets.values()];
  }

  /** THE OPENING BAKE TAKES THE NEIGHBOURS TOO (LS-352 / LS-246, 2026-09-11).
   *
   *  Measured on a phone viewport at 4x CPU throttle, walking north off the
   *  glade: two frames of 290 and 340 ms were whole-screen bakes forced by
   *  the draw the moment the window revealed a neighbour the paced pre-bake
   *  had not reached, one frame of 490 ms was the loot pass (`worldLoot`,
   *  LS-379's caches) computed inside the first neighbour's roster, and the
   *  first draw of each new screen paid 250 ms building its ground field.
   *  LS-291 moved the start screen's own bake behind the loading overlay,
   *  where a wait is honest; this puts the four neighbours, the loot pass
   *  and the ground fields there too. Fixed work, deterministic: the same
   *  steps in the same order on any machine. */
  warmStart() {
    if (!this.world) return;
    this.worldLoot();
    const here = [this.area.x, this.area.y];
    const around = [here, [here[0] + 1, here[1]], [here[0] - 1, here[1]], [here[0], here[1] + 1], [here[0], here[1] - 1]]
      .filter(([x, y]) => x >= 0 && y >= 0 && x < WORLD_W && y < WORLD_H);
    for (const [x, y] of around) {
      this.screenPartsStep(x, y, 1e9);
      const parts = this.partsCache.get(x + ',' + y);
      if (parts && parts.groundField === undefined) this.groundFieldFor(parts, x, y, this.wildPalAt(parts, x, y));
    }
    // ...and every room's reverb impulse, which used to be made in the frame of the first switch into it (LS-188)
    try { const ac = this.ac && this.ac(); if (ac) prewarmRooms(ac); } catch (err) { /* no audio on this device */ }
    // ...and their draw layers, so the first frame after "begin" draws from caches too (LS-188)
    this.player.dir = this.player.dir || 'down';
    for (let n = 0; n < 64 && this.warmLayers(); n++) { /* one layer a call, until every visible neighbour is primed */ }
  }
  /** One visible neighbour's ground field, a few steps a frame, once its parts are made: the first draw of a screen used to build it whole (2026-09-11). */
  warmGroundField() {
    for (const [x, y] of this.screenPreparationTargets()) {
      const parts = this.partsCache.get(x + ',' + y);
      if (!parts || parts.groundField !== undefined) continue;
      const k = x + ',' + y;
      if (!this.fieldJob || this.fieldJob.k !== k) this.fieldJob = { k, it: this.groundFieldSteps(parts, x, y, this.wildPalAt(parts, x, y)) };
      const r = this.fieldJob.it.next();   // one step a frame: four together were 42 ms on a 4x phone (2026-09-11)
      if (r.done) this.fieldJob = null;
      return true;
    }
    return false;
  }
  /** A NEIGHBOUR'S DRAW LAYERS, ONE A FRAME (LS-188's second pass, 2026-09-11).
   *
   *  The first draw of a screen built every cached layer at once -- the sun
   *  wash, the ground wash, the tile layer, the cliffs, decals, lip and
   *  ledges, the blades and the canopy -- which the 4x phone profile put at
   *  100-170 ms in one frame at each arrival, after the bakes themselves had
   *  been moved off the frame. Once a visible neighbour has its parts and its
   *  ground field, one layer a frame is painted here into a scratch context
   *  so its cache is primed before the window reveals it. The same painters
   *  the draw uses, in a fixed order; nothing about the world changes. */
  warmLayers() {
    if (typeof document === 'undefined') return false;
    for (const [x, y] of this.screenPreparationTargets()) {
      if (x === this.area.x && y === this.area.y) continue;
      const k = x + ',' + y;
      const parts = this.partsCache.get(k);
      if (!parts || parts.groundField === undefined) continue;
      const had = (this.warmedLayers ||= new Map()).get(k);
      const rec = had && had.tiles === parts.tiles ? had : { tiles: parts.tiles, names: new Set<string>() };
      this.warmedLayers.set(k, rec);
      const order = this.view3d ? ['wash', 'tiles', 'decals', 'ledges'] : ['sun', 'wash', 'tiles', 'cliffs', 'decals', 'lip', 'ledges', 'blades-back', 'blades-front', 'canopy'];   // in 3D the floor (scene3d.bareFloor) paints through these four alone (2026-09-16)   // one painter a frame; the blades' two passes and the canopy were a lump of 34 ms at 4x together
      const name = order.find((n) => !rec.names.has(n));
      if (!name) continue;
      if (!this.scratchCtx) { const c = document.createElement('canvas'); c.width = PW; c.height = PH; this.scratchCtx = c.getContext('2d'); }
      const cg = this.scratchCtx;
      if (!cg || !cg.getImageData || !cg.createImageData) return false;   // a stub context (the sims): nothing to prime, the draw will do what it does
      const pal = parts.pal || this.pal;
      const was = this.g; this.g = cg;
      const hadLayers = this.layers; this.layers = [];   // the terrace painters note their layer on the draw's list, which exists only in a draw
      /* BEST EFFORT. A warm that throws must not break a start: the first cut
       * threw in every headless sim (a stub canvas with no image data) and in
       * the browser, and the loading overlay never came down. A layer that
       * cannot be primed is left to the draw. */
      try {
        switch (name) {
          case 'sun': this.sunWashFor(x, y); break;
          case 'wash': this.groundWash(cg, parts, x, y); break;
          case 'tiles': this.cachedTiles(parts, x, y, this.wildPalAt(parts, x, y)); break;
          case 'cliffs': this.cachedLayer(cg, parts, 'cliffs', (c) => this.drawCliffs(c, parts)); break;
          case 'decals': this.cachedLayer(cg, parts, 'decals', (c) => { for (const d of parts.decals || []) drawDecal(c, d, pal); }); break;
          case 'lip': this.cachedLayer(cg, parts, 'lip', (c) => this.drawCliffLip(c, parts)); break;
          case 'ledges': this.cachedLayer(cg, parts, 'ledges', (c) => this.drawLedges(c, parts)); break;
          case 'blades-back': if (parts.turf) this.drawBladesCached(cg, parts, pal, -1e9, false); break;
          case 'blades-front': if (parts.turf) this.drawBladesCached(cg, parts, pal, -1e9, true); break;
          case 'canopy': this.drawCanopyCached(cg, parts); break;
        }
      } catch (err) { if ((globalThis as any).__warmErrors) (globalThis as any).__warmErrors.push(name + ': ' + (err && (err as any).message)); }
      finally { this.g = was; this.layers = hadLayers; }
      rec.names.add(name);
      return true;
    }
    return false;
  }
  prepareScreens(order) {
    const targets = this.screenPreparationTargets();
    const missing = targets.filter(([x, y]) => !this.partsCache.has(x + ',' + y));
    if (missing.length) {
      const visible = new Set(this.visibleScreens(this.camera()).map(([x, y]) => x + ',' + y));
      if (this.view3d && this.scene3d) for (const [x, y] of this.scene3d.seenScreens(this.camera())) visible.add(x + ',' + y);   // the 3D frustum sees a row north of the window (2026-09-16)
      const urgent = missing.find(([x, y]) => visible.has(x + ',' + y));
      const active = missing.find(([x, y]) => this.bakeJob?.k === x + ',' + y);
      /* THE GROUND IS NOT STARVED BY THE BAKE (LS-480): with any target unbaked this returned before the 3D ground was
       * ever stood up ahead, so walking left the draw to build a seen screen's ground on the spot -- 70 to 160 ms at a
       * phone's pace, the worst of the walking hitches. Unless a seen screen is waiting on its bake, every other drawn
       * frame goes to the ground. */
      if (!urgent && (this.renderCount & 1) && this.view3d && this.scene3d && this.scene3d.prebuild(this)) return;
      const [x, y] = urgent || active || missing[0];
      /* A SCREEN THE WINDOW SEES IS FINISHED WITHIN A BOUNDED HITCH (2026-09-16). Two steps a frame is the pace for a
       * screen the window is walking toward; for one it already sees, that pace leaves a hole in the ground for as long
       * as the bake takes (the 3D draw no longer bakes on the spot), so the urgent bake gets a time budget instead:
       * up to twenty-four milliseconds of steps this frame, which is a bounded hitch rather than a freeze or a hole. */
      this.screenPartsStep(x, y, this.touch && !urgent ? 1 : SCREEN_PREP_STEPS, false, urgent ? 24 : 0);
      return;
    }
    // A stale picture can wait; a missing screen would block rendering entirely.
    const dirty = [...this.dirtyParts].find(k => this.bakeJob?.k === k)
      || [...this.dirtyParts].find(k => this.frame - ((this.rebakedAt ||= new Map()).get(k) ?? -Infinity) >= 120);
    if (dirty) {
      const [x, y] = dirty.split(',').map(Number);
      if (this.screenPartsStep(x, y, 1, true)) {
        this.dirtyParts.delete(dirty);
        (this.rebakedAt ||= new Map()).set(dirty, this.frame);
        this.lightArt?.delete(dirty);
      }
      return;
    }
    if (this.warmGroundField()) return;
    /* THE 3D GROUND BEFORE THE PAINTERS' LAYERS (2026-09-16). warmLayers primes ten 2D painter layers a screen, one a
     * frame, and prebuild waited behind all of them -- eighty-odd frames for a ring of eight, while a body crosses a
     * screen in a hundred and twenty: the walker outran the ring and the draw fell back to building on the spot. The
     * terrain first; and in 3D only the four layers its floor paints through are warmed at all. */
    if (this.view3d && this.scene3d && this.scene3d.prebuild(this)) return;   // the 3D ground of a target, one a frame, ahead of the window
    if (this.warmLayers()) return;
    for (const [dx, dy] of order) {
      const x = this.area.x + dx, y = this.area.y + dy;
      if (x < 0 || y < 0 || x >= WORLD_W || y >= WORLD_H || this.partsCache.has(x + ',' + y)) continue;
      this.screenPartsStep(x, y, this.touch ? 1 : SCREEN_PREP_STEPS);   // a phone takes one step a frame (LS-480)
      break;
    }
  }

  /** Advance a fixed number of screen-preparation steps: true when done.
   *  One job at a time; a job for another key is dropped for this one. */
  bakeJob: any = null;
  /* A SCREEN WHOSE TILES CHANGED IS DIRTY, NOT GONE. The simulation used to
   * delete the current screen's parts on every tile change -- a fire
   * spreading, grass growing back, the tide, a path wearing -- and the next
   * frame's render baked the whole screen again: forty-four full bakes in
   * sixty frames beside a burning hedge. Now the change marks the screen
   * dirty, the stale parts keep drawing (the tile layer itself is
   * incremental and shows the change at once), and the paced pre-bake
   * re-bakes it a few milliseconds a frame and swaps the new parts in. */
  dirtyParts: Set<string> = new Set();
  rebakedAt: Map<string, number> | null = null;
  markPartsDirty(x, y) { if (this.partsCache.has(x + ',' + y)) this.dirtyParts.add(x + ',' + y); else this.partsCache.delete(x + ',' + y); }
  *preparePartsSteps(x, y, screen) {
    const parts = yield* this.bakePartsSteps({ kind: 'wild', x, y, level: 0 }, screen.tiles, screen.biome, BIOMES[screen.biome]);
    parts.biomeTiles = screen.biomeTiles;
    parts.wetQ = this.wetQuantumOf(x, y);   // the wet step this bake was painted at, for the tile layer's palettes
    if (this.cosmetics !== false && typeof document !== 'undefined') {
      // The first visible frame must not also build the ground-field image.
      yield* this.groundFieldSteps(parts, x, y, this.wildPalAt(parts, x, y));
    }
    return parts;
  }
  screenPartsStep(x, y, steps = 1, force = false, budgetMs = 0) {
    const k = x + ',' + y;
    if (this.partsCache.has(k) && !force) return true;
    if (!this.bakeJob || this.bakeJob.k !== k) {
      const s = this.screenAt(x, y);
      this.bakeJob = { k, s, it: this.preparePartsSteps(x, y, s) };
    }
    // Fixed work, not elapsed time. Rosters advance independently in update().
    // The perf gate measures both individual steps and the shipping batch size.
    let r;
    try {
      const t0 = budgetMs > 0 ? performance.now() : 0;
      r = this.bakeJob.it.next();
      for (let n = 1; !r.done && (n < Math.max(1, steps) || (budgetMs > 0 && performance.now() - t0 < budgetMs)); n++) r = this.bakeJob.it.next();
    } catch (err) { this.bakeJob = null; throw err; }   // a bake that throws is not re-entered for ever (LS-230)
    if (!r.done) return false;
    const p = r.value; p.biomeTiles = this.bakeJob.s.biomeTiles;
    this.bakeJob = null;
    /* THE LIVE TURF STAYS LIVE (Vijay: "grass doesn't cut until a few seconds
     * later"). A background re-bake of the screen underfoot made a fresh turf
     * array, the drawing took the fresh one, and the blade went on cutting
     * the old one -- a cut showed only when the next re-bake read it off the
     * wear field. The new turf takes the old cells' cut states, and the game
     * points at the new arrays from here on. */
    const cur = k === this.area.x + ',' + this.area.y;
    let cutsCarried = 0;
    if (cur && this.turf && p.turf && this.turf !== p.turf && this.turf.length === p.turf.length) {
      for (let y = 0; y < p.turf.length; y++) { const o = this.turf[y], n = p.turf[y]; if (!o || !n || o.length !== n.length) continue;
        for (let x = 0; x < n.length; x++) if (o[x] && n[x]) { if ((o[x].cut | 0) > 0) cutsCarried++; n[x].cut = o[x].cut; n[x].cut0 = o[x].cut0; n[x].cutCx = o[x].cutCx; n[x].cutCy = o[x].cutCy; n[x].cutR = o[x].cutR; if (o[x].tuft !== undefined) n[x].tuft = o[x].tuft; } }   // every field of a cut (LS-236)
    }
    // ...and the crowns' lit pictures survive the re-bake: painted afresh each time, every tree repainted every two seconds beside a fire
    { const old = this.partsCache.get(k); if (old && old.canopy && p.canopy && old.canopy.litCache && old.canopy.seed === p.canopy.seed) p.canopy.litCache = old.canopy.litCache; }
    this.partsCache.set(k, p);
    /* THE PICTURE CARRIES THE CUTS ACROSS THE SWAP (2026-09-11; Vijay: "chopping grass doesn't work at all and
     * then later the chopped grass pops in"). The re-bake painted its turf picture from cells that had no cuts
     * yet -- the cuts are copied in just above, after the paint -- and the swap took that picture, so a cut made
     * while a background re-bake was finishing vanished for twenty-one frames (measured, the swap two frames
     * after the swing) until the next repaint beat; on a phone, where a trampled meadow re-bakes without pause,
     * that is a cut that never shows until the beat happens to land. The old picture, which has the cuts, stays
     * on the glass and a repaint from the merged cells starts at once; the gate that spaces repaints is opened
     * for it, and a job in flight for the old cells is dropped, since it was painting the wrong turf. */
    if (cur) {
      this.turf = p.turf;
      if (cutsCarried > 0) { this.turfJob = null; this.turfRepaintAt = -1e9; this.refreshTurfArt(true); }
      else this.turfArt = p.turfArt;
    }
    this.trimPartsCache();
    return true;
  }

  /* EVICT BY DISTANCE, NEVER THE GROUND UNDERFOOT (Vijay: "the game is
   * lagging a ton on mobile"). The cache held twelve screens and let the
   * OLDEST go; the screen you stand on is the oldest of the nine around
   * you, so once three leftovers from the last place filled the cache, every
   * neighbour the pre-bake finished evicted the current screen, and the
   * water pass baked it again next frame: forty-four full bakes in sixty
   * frames, measured, on every screen with a past. Sixteen screens now, and
   * the one that goes is the farthest from where you are. */
  trimPartsCache() {
    const CAP = 16;
    if (this.partsCache.size <= CAP) return;
    const needed = new Set(this.screenPreparationTargets().map(([x, y]) => x + ',' + y));
    while (this.partsCache.size > CAP) {
      let worst: string | null = null, worstD = -1;
      for (const key of this.partsCache.keys()) {
        if (needed.has(key)) continue;
        const [x, y] = key.split(',').map(Number);
        const d = Math.max(Math.abs(x - this.area.x), Math.abs(y - this.area.y));
        if (d > worstD) { worstD = d; worst = key; }
      }
      if (!worst || worstD <= 1) break;   // nothing but the 3x3 around you: keep it all
      this.partsCache.delete(worst);
    }
  }

  /* ONE SCREEN'S WASH, BAKED AND KEPT (2026-09-06). Baked once a screen rather than eight gradients a frame: the
   * gradients cost 1.4 ms of a 6 ms budget and the wash depends only on which screen it is and whether it is night,
   * so it is a picture, blitted. Keyed on the SCREEN, not on the player's screen, because the window stands on four
   * of them at once and every one of them needs its own. */
  sunWashFor(sx, sy) {
    const key = sx + ',' + sy + (this.night ? '!n' : '');
    const had = (this.sunWash ||= new Map()).get(key);
    if (had) return had;
    const c = document.createElement('canvas'); c.width = VW; c.height = PH;
    const cg = c.getContext('2d');
    const img = cg.createImageData(VW, PH);
    const d = img.data;
    const ox = sx * VW, oy = sy * PH;
    for (let y = 0; y < PH; y++) {
      for (let x = 0; x < VW; x++) {
        const p = sunWashAt(ox + x, oy + y, this.night);
        const i2 = (y * VW + x) * 4;
        d[i2] = p.r; d[i2 + 1] = p.g; d[i2 + 2] = p.b; d[i2 + 3] = p.a;
      }
    }
    cg.putImageData(img, 0, 0);
    this.sunWash.set(key, c);
    /* Sixteen, not forty: four screens are in the window and the ones around them are what you walk into next. */
    if (this.sunWash.size > 16) this.sunWash.delete(this.sunWash.keys().next().value);
    return c;
  }

  /* THE PARTS ONLY IF THEY ARE ALREADY MADE (2026-09-06; Vijay: "mobile is still lagging significantly").
   *
   * `screenParts` BAKES A WHOLE SCREEN SYNCHRONOUSLY when it is cold -- about 72 ms on the desk, and near 290 ms
   * on a phone -- and the presenter's two buffers called it once per visible screen EVERY FRAME. So the paced
   * pre-bake, which exists precisely so that a screen is made a few milliseconds at a time, was bypassed by the
   * water mask and the light before it could finish: profiled at 4x CPU throttle, `cameraWater` had a mean of 6.2
   * ms and a MAX OF 853, and the parts calls under it peaked at 314.
   *
   * The world pass must have its parts or it would draw nothing. These two need not: they are overlays, and a
   * screen whose mask is missing for one frame is invisible, where a third of a second of stall is not. */
  bakedParts(x, y) { return this.partsCache.get(x + ',' + y) || null; }

  screenParts(x, y) {
    const k = x + ',' + y;
    let p = this.partsCache.get(k);
    if (!p && this.bakeJob && this.bakeJob.k === k) { this.screenPartsStep(x, y, 1e9); p = this.partsCache.get(k); }   // a job half done for this very screen: finish it, do not start over
    if (!p) {
      const s = this.screenAt(x, y);
      p = this.bakeParts({ kind: 'wild', x, y, level: 0 }, s.tiles, s.biome, BIOMES[s.biome]);
      p.biomeTiles = s.biomeTiles;
      this.partsCache.set(k, p);
      this.trimPartsCache();
    }
    return p;
  }

  screenAt(x, y) {
    const k = x + ',' + y;
    let s = this.screens.get(k);
    if (s) { this.chunkTiles[y * WORLD_W + x] = s.tiles; return s; }
    if (!s) {
      s = generateScreen(this.world, x, y);
      this.screens.set(k, s);
      /* A WALL YOU BLASTED IS DOWN EVERYWHERE, not only where you are standing.
       *
       * `applyBroken` ran in `loadArea`, on the screen being entered, and that
       * was enough while the only screen that could be touched was the current
       * one. Collision reads the whole map now, so a screen the player has
       * never entered can still be walked into — and a wall they cleared on a
       * previous visit has to be down before anything asks about it. The break
       * is applied at GENERATION, once, where the tiles first exist. */
      this.applyBrokenTo(s.tiles, 'W|' + x + ',' + y, s);
      this.seedFoundForges();   // the two forges the world lays out, into the same overlay the player's own set-downs use (2026-09-12)
      this.seedApproachShelter();   // and a thicket on every beast's approach that would otherwise carry too little (LS-395)
      this.seedFoundRaft();   // and a raft on the first shore (LS-397)
      this.applyPlacedTo(s.tiles, 'W|' + x + ',' + y);   // ...and what the player set down, after (LS-379)
      if (this.systems) this.systems.applyToChunk(this, x, y, s.tiles);   // and what the world's clocks left there: scorch, cut grass, pushed stones, nests (LS-511)
    }
    this.chunkTiles[y * WORLD_W + x] = s.tiles;
    return s;
  }

  /** How many lesser creatures a screen near the glade may hold before next door's wait at the seam (LS-455): two on the
   *  first ring round the start, three on the second, no cap elsewhere or off the home island. */
  /** VIGOR COMES BACK AT REST (2026-09-26). Five seconds with nothing hurting you and no roused great beast within ten tiles,
   *  and a point returns every three seconds, with a green mote off the body. The newcomer probe sat at three of ten for
   *  twenty seconds on the sow's beach with no way it could see to mend, and died of the next brush; food and sap still
   *  mend in the middle of a fight, which is what they are for now. */
  mendAtRest() {
    const st = this.state, now = this.runFrames || 0;
    if (this._restHp === undefined || (this._restAt || 0) > now) { this._restHp = st.hp; this._restAt = now; }
    if (st.hp < this._restHp) this._restAt = now;
    this._restHp = st.hp;
    if (st.hp <= 0 || st.hp >= st.maxHp || now - this._restAt < 300 || (now - this._restAt) % 180 !== 0) return;
    if ((this.enemies || []).some((e: any) => e && !e.dead && e.def && e.def.beast && this.player && Math.hypot(e.cx - this.player.cx, e.cy - this.player.cy) < 160 && (!this.beastRoused || this.beastRoused(e)))) return;
    st.hp = Math.min(st.maxHp, st.hp + 1); this._restHp = st.hp;
    const p = this.player; if (p) for (let i = 0; i < 3; i++) this.puff(p.cx, p.cy - 6, '#8fd46a', 2, 'spark');
  }
  _restHp: number | undefined = undefined; _restAt = 0;
  gladeCrowdCap(ax: number, ay: number): number {
    const w = this.world; if (!w || !w.start || (globalThis as any).__noCrowdCap) return Infinity;
    const W0 = w.biome[0].length, isle = (x: number, y: number) => w.island ? w.island[y * W0 + x] : 0;
    /* ...AND EVERYWHERE ELSE, FOUR (2026-09-26): the ring's convergence happened on every island -- on the sow's beach the
     * newcomer probe was pinned between cliff and water by a shellback, a bramble and a sporeling from three screens */
    if (isle(ax, ay) !== isle(w.start.x, w.start.y)) return 4;
    const d = Math.max(Math.abs(ax - w.start.x), Math.abs(ay - w.start.y));
    return d <= 1 ? 2 : d <= 2 ? 3 : 4;
  }
  /** The identity a respawn budget is held against: a wild screen. */
  quietKey(a = this.area) {
    return a.x + ',' + a.y;
  }

  /* WHAT THE CANOPY IS GROWN FROM — one answer, for everyone who asks.
   *
   * There were three callers with the arguments spelled out three times, and
   * two of them were wrong in the same two ways:
   *
   *   THE SEED WAS PER-SCREEN. `areaSeed()` is `mix(seedNum, x, y, 77)` — a
   *   different number on every screen — so the world-space hash `crownsFor`
   *   uses to make a crown "the same crown from either side of a seam" was
   *   being fed a different key on each side. Measured: areaSeed(3,1) and
   *   areaSeed(4,1) share nothing.
   *
   *   THE ORIGIN WAS IN THE WRONG UNIT. `wox / TS` is `area.x * COLS`, i.e.
   *   48 for screen 3, and `crownsFor` then computes `wx = gx + ox * 256`.
   *   Adjacent screens therefore hashed world positions 4,096 px apart for the
   *   same physical tree instead of 256.
   *
   * Round 36 "fixed the seams" and shipped a change that was 0.0% effective on
   * 16,487 straddling crowns. The check written beside it reported 84%,
   * because it called `canopyPlan` with `world.seedNum` and `sx` — the
   * arguments the code SHOULD pass, not the ones it did. A check that spells
   * out a call site rather than asking for it is a second model of the call,
   * and this is what that costs.
   */
  /* Returns a TUPLE, not an array — `bakeCanopy(...args)` has a fixed arity
   * and a `T[]` spread into it says nothing about how many arguments arrive.
   * Writing the shape down is what makes the spread checkable. */
  canopyArgs(tiles = this.tiles, palKey = this.palKey, area = this.area):
    [any, any, number, number, number, number, number, number, any] {
    /* THE WOOD IS BAKED WITH A MARGIN OF REAL NEIGHBOURING TILES.
     *
     * A crown within its radius of a screen edge used to be shrunk to fit the
     * bake canvas, and the screen next door — which could only see its own
     * tiles — shrank the same crown differently or refused to draw it at all,
     * because the test for "is there a tree there" clamped to the edge tile.
     * That was survivable while a border wall made the edge tile a tree 87.5%
     * of the time. With the wall gone it is a band of stunted crowns along
     * every seam, and measured by turning the pass off it was ALL of the
     * remaining lattice.
     *
     * Two tiles of margin is more than the largest crown radius, so nothing
     * inside the playfield is ever clipped or resized by the canvas again. */
    const pad = CANOPY_PAD;
    let grid = tiles;
    if (pad) {
      grid = [];
      for (let y = -pad; y < ROWS + pad; y++) {
        const row: any[] = [];
        for (let x = -pad; x < COLS + pad; x++) {
          const v = this.tileAt(area.x * COLS + x, area.y * ROWS + y);
          row.push(v === undefined ? T.GROUND : v);
        }
        grid.push(row);
      }
    }
    // and the lookup a crown asks which country it is standing in
    const biomeAt = this.world.biomeAtTile
      ? (tx, ty) => this.world.biomeAtTile(tx, ty) : null;
    return [grid, palKey, COLS, ROWS, this.world.seedNum, area.x, area.y, pad, biomeAt];
  }

  /* THE COSMETIC BAKE, AS A FUNCTION OF A SCREEN RATHER THAN OF `this`.
   *
   * Everything here produces pixels and nothing else, and none of it is read
   * by any rule of play — walkability comes from `tiles`, and the crown
   * GEOMETRY the seam checks assert on is `canopyPlan`, which is pure. It sat
   * inside `loadArea` writing to eight fields of the game object, which is
   * fine while exactly one screen is ever on the glass and impossible the
   * moment a camera spans a seam.
   *
   * Returning a parts object instead is the first step of LS-142: the same
   * bake, the same order, the same arguments, with its inputs named. Nothing
   * about the picture moves — the world digest and the golden frames are the
   * check on that — and `pushWater`/`pushLight` deliberately stay behind in
   * `loadArea`, because they hand buffers to the PRESENTER and only the
   * screen actually being drawn may do that. */
  /* THE BAKE, WHOLE: what every synchronous caller wants. */
  bakeParts(area, tiles, palKey, pal) {
    const it = this.bakePartsSteps(area, tiles, palKey, pal);
    let r = it.next(); while (!r.done) r = it.next();
    return r.value;
  }

  /* THE BAKE, IN STEPS (Vijay: "the game is lagging a ton on mobile keep
   * performance in mind"). Baking a neighbour's parts was one hundred
   * milliseconds on the desk and several hundred on a phone, and the paced
   * pre-bake did one whole screen every twelfth frame -- so arriving anywhere
   * new was eight hitches in a row. The bake is a generator now: it yields
   * after every stage, and the pre-bake steps it a few milliseconds a frame,
   * so the same work is spread over as many frames as it needs. */
  /** How wet a screen is, in three steps, from its rain field: 0 dry, 1 damp, 2 soaked. The bake folds it into the
   *  palette and the key, so a soaked screen is painted in its wet state and re-baked when the step changes. */
  wetQuantumOf(sx, sy) {
    const sys: any = this.systems; if (!sys) return 0;
    /* one world field (LS-511): the chunk's own tiles of it. Rain ticks round the body, so a chunk out of reach keeps
     * the wet it had when it was last in it. */
    const f = sys.fieldsFor(this);
    if (!f || !f.rainWet) return 0;
    let sum = 0; for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) sum += f.rainWetAt(sx * COLS + x, sy * ROWS + y);
    const m = sum / (COLS * ROWS);
    return m >= 0.7 ? 2 : m >= 0.3 ? 1 : 0;
  }
  *bakePartsSteps(area, tiles, palKey, pal) {
    const wetQ = this.wetQuantumOf(area.x, area.y);
    if (wetQ) { pal = wetPalette(pal, wetQ / 2); palKey = palKey + '|w' + wetQ; }   // the screen's wet state, in its palette and its key (2026-09-11)
    const flora = this.floraKey(area, palKey);
    const wox = area.x * COLS * TS;
    const woy = area.y * ROWS * TS;
    const seed = this.areaSeed(area);
    const paint = this.cosmetics !== false;
    const cArgs = this.canopyArgs(tiles, palKey, area);
    const p: any = {
      tiles, pal, palKey,
      coords: { x: area.x, y: area.y },
      turfOx: wox / TS, turfOy: woy / TS,
    };
    /* AND THE SCATTER ASKS PER TILE TOO -- see the note on `growDecals`. The
     * blend's dominant country at that tile, which is the same thing the ground
     * palette is drawn from, so a toadstool cannot land on a country that grows
     * none. */
    const blendFor = this.world && this.world.biomeBlendAt;
    p.decals = !paint ? [] : growDecals(tiles, flora, seed, COLS, ROWS, wox, woy,
      blendFor ? (wx, wy) => blendFor(wx, wy).a : null);
    /* THE GRASS TAKES ITS COVER PER TILE, from the same blend the ground
     * palette uses — see the note on `growTurf`. Read from the screen's one key
     * it put a ruled line down the map wherever two screens disagreed. */
    const blendAt = this.world && this.world.biomeBlendAt;
    const coverAt = blendAt
      ? (wx, wy) => {
        const m = blendAt(wx, wy);
        const a = (TURF[m.a] || TURF.heath).cover;
        const b = (TURF[m.b] || TURF.heath).cover;
        return a + (b - a) * m.t;
      }
      : null;
    /* THE RIM WEARS TURF TOO. A side or back rock tile is painted as the
     * plateau's floor (`drawCliffs`), but the turf is grown from the tile
     * kinds and left every rock tile bare: measured on the composite, a
     * darker one-tile kerb ran round every plateau between two turfs. For
     * the turf's purposes a rock tile that is not a front is ground -- and
     * it stands at the plateau's level, not its own. */
    const terr0 = this.terrain;
    const wt = (tx, ty) => terr0.terrace[terr0.at(
      Math.max(0, Math.min(terr0.TW - 1, wox / TS + tx)), Math.max(0, Math.min(terr0.TH - 1, woy / TS + ty)))];
    const isFront = (tx, ty) => terr0 && terr0.face[terr0.at(
      Math.max(0, Math.min(terr0.TW - 1, wox / TS + tx)), Math.max(0, Math.min(terr0.TH - 1, woy / TS + ty)))]
      && wt(tx, ty - 1) > wt(tx, ty);
    /* THE GROUND RUNS UNDER THE PROPS (LS-218). A tree, a standing stone, a
     * cairn, a nest, a brazier, a boulder stands ON the ground; the turf, the
     * soil and the ground field used to stop dead at its cell, which drew a
     * sixteen-pixel square of bare tile around every one of them -- the
     * loudest grid on a wooded screen. For the ground layers those cells are
     * ground, and the prop is drawn over whatever grows there. */
    const UNDER = new Set([T.TREE, T.STONE, T.CAIRN, T.NEST, T.BRAZIER, T.BRAZIER_LIT, T.PUSH, T.RUBBLE, T.ROCK]);   // tall things, and rubble (a boulder on the turf since LS-218): cracks, debris and scorch ARE the ground
    /* A TUFT IS MOSS TO THE FIELD (LS-236): cut grass used to become a tile of its own -- a sixteen-pixel square of dark soil and straw in the middle of a field, the grid back in the one place the player looks. The tile is still TUFT to the matter (dry, it burns, it regrows to moss); to the turf and the soft ground it is the moss it was, and the cut is painted INTO the turf as a round swath that regrows from its edge. */
    const groundTiles = tiles.map((row, ty) => row.map((v, tx) => UNDER.has(v) ? T.GROUND : v === T.TUFT ? T.MOSS : ((terr0 && v === T.CLIFF && !isFront(tx, ty)) ? T.GROUND : v)));
    /* THE GROUND UNDER THE THINGS THAT STAND. The tile layer is drawn from
     * this map, with ground where a boulder, a stone or a tree stands, so the
     * field can meander that ground into its neighbours like any other; the
     * standing things themselves are drawn over it afterwards. A ground tile
     * drawn under each prop on its own was a square again -- "that adds back
     * the grid where the props are". */
    /* ...AND A THICKET STANDS ON GROUND TOO (LS-375, 2026-09-10). `T.THORN`
     * was in neither this set nor `BARE_IN_WILD`, so the tile layer painted
     * the thicket's own opaque base and the field's meander stopped dead at
     * it: a hard-edged square of bare floor with stems on it, in the middle of
     * a meadow, which is what "reads as a wooden crate" was. It gets GROUND like
     * every other prop, which is the whole point of this map: the field
     * meanders that ground into its neighbours, and a kind the field cannot
     * meander leaves the grid showing. A thicket at the waterline stands on
     * SHALLOW, which is where these actually grow: plain GROUND put a green
     * square against the lake, and full WATER -- tried first -- dropped a
     * deep-water square into a pale shore. */
    /* AT THE WATERLINE MEANS DEEP WATER BESIDE IT, or shallow on two sides.
     * One shallow neighbour is not a waterline -- it is a puddle nearby, and
     * putting a wet tile under a thicket standing on dry ground is the same
     * fault in the other direction. */
    const wetBeside = (x: number, y: number) => {
      let deep = 0, wet = 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const u = tiles[y + dy] && tiles[y + dy][x + dx];
        if (u === T.WATER) deep++;
        if (u === T.WATER || u === T.SHALLOW) wet++;
      }
      return deep > 0 || wet >= 2;
    };
    /* THE TILE BEYOND THE EDGE, RAW (2026-09-12): the neighbour screen's cell, for the level rim and the fields. */
    const rawBeyond = (tx, ty) => {
      const nx = area.x + Math.floor(tx / COLS), ny = area.y + Math.floor(ty / ROWS);
      if (nx < 0 || ny < 0 || nx >= WORLD_W || ny >= WORLD_H) return null;
      const q = this.partsCache && this.partsCache.get(nx + ',' + ny);
      const t = q && q.tiles ? q.tiles : (this.screenAt ? this.screenAt(nx, ny).tiles : null);
      if (!t) return null;
      return t[((ty % ROWS) + ROWS) % ROWS][((tx % COLS) + COLS) % COLS];
    };
    const tileAt = (tx, ty) => (tx >= 0 && ty >= 0 && tx < COLS && ty < ROWS) ? tiles[ty][tx] : rawBeyond(tx, ty);
    /* A WALL THAT FACES AWAY IS THE PLATEAU'S EDGE (2026-09-12; Vijay's coast shot, "that seam at the top"). Route-1
     * screen 3,10 has a row of cliff along its top with the higher ground south of it: the camera looks at that wall
     * from behind, so there is no face to see, only the upper terrace ending and the lower ground beyond. It was drawn
     * as a rock box all the same, and the box's ruled outline lay on the screen's edge. Such a tile is the upper
     * terrace's ground to every painter (its rim level already lifts it); the terrace's tone, the slope's shade and
     * the drop's ink say where the edge is, and the cell still blocks. */
    const northFace = (tx, ty) => tiles[ty][tx] === T.CLIFF && terr0 && !isFront(tx, ty)
      && tileAt(tx, ty + 1) !== T.CLIFF && tileAt(tx, ty - 1) !== T.CLIFF && wt(tx, ty + 1) > wt(tx, ty) && wt(tx, ty - 1) <= wt(tx, ty);
    /* THE GROUND UNDER A BUSH IS THE GROUND ROUND IT (LS-394): a bramble on the beach stood on a square of soil in the
     * sand, because the tile under every dry bramble was ground. It is sand where sand is beside it. */
    const sandBeside = (x, y) => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => tiles[y + dy] && tiles[y + dy][x + dx] === T.SAND);
    p.propGround = tiles.map((row, y) => row.map((v, x) =>
      v === T.THORN ? (wetBeside(x, y) ? T.SHALLOW : sandBeside(x, y) ? T.SAND : T.GROUND) : UNDER.has(v) ? T.GROUND : (v === T.CLIFF && northFace(x, y)) ? T.GROUND : v));
    /* A BRAMBLE IS THE GROUND UNDER IT TO THE FIELD (LS-395, 2026-09-12): pixels labelled thorn kept the cell's own
     * art while the field blended everything round them, so a planted thicket sat on a square of unblended ground.
     * The field never labels thorn; the bush is drawn bare over whatever the field paints. */
    for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) if (tiles[ty][tx] === T.THORN) groundTiles[ty][tx] = p.propGround[ty][tx];
    const turfTiles = groundTiles;
    p.groundTiles = groundTiles;
    const rimLevel = (tx, ty) => {
      /* THE SEA IS SEA LEVEL (LS-383, the merge): a tile the seal drowned keeps the terrace the terrain gave it, and the
       * field then painted a plateau of ash flats over open water (play-1 screen 10,4). Sea is the floor, whatever the
       * terrain under it says; a lake on a shelf keeps its shelf. */
      const v = tileAt(tx, ty);
      if (v === T.SEA) return 0;
      let top = wt(tx, ty);
      /* ...AND A CLIFF BEYOND THE EDGE RIMS LIKE ONE INSIDE IT (2026-09-12; Vijay's coast shot, "that seam at the top").
       * Route-1 screen 3,10 has a wall of cliff along its top row with the plateau behind it; the screen above asked
       * the rim of that row and was told the terrain's floor, so its height field stayed flat to the seam while the
       * plateau's began there as a vertical step. The shader then laid the plateau's shade on the lower ground with a
       * ruled edge along the seam, at every hour. Read through the edge, the level climbs into the neighbour's foot
       * and the height bake's blur makes the same slope it makes inside a screen. */
      if (v === T.CLIFF) {
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) top = Math.max(top, wt(tx + dx, ty + dy));
      }
      return top;
    };
    // the per-pixel kinds of the soft ground (LS-218): the turf, the soil and the water mask follow the field, not the cell;
    // the neighbouring screens' tiles answer for the border, so the field has no seam
    const beyondGround = (tx, ty) => {
      const v = rawBeyond(tx, ty);
      if (v === null) return null;
      return UNDER.has(v) ? T.GROUND : v;
    };
    p.groundKinds = paint && !(globalThis as any).__noField
      ? yield* timedField('kinds', fieldKindsSteps(this.world, groundTiles, COLS, ROWS, area.x, area.y, beyondGround)) : null;
    // `timedField` above keeps this phase's clock
    yield;
    // ...and the rock field: the true cliff cells (a side cliff is ground to the turf but rock to the cliff art)
    // (a prop beside a cliff -- rubble, a tree, a stone -- is ground to the rock field too, or the cliff's outline would stop dead on that cell's edge)
    p.rockKinds = p.groundKinds
      ? yield* timedField('rock', fieldKindsSteps(this.world, tiles.map((row, ty) => row.map((v, tx) => UNDER.has(v) ? T.GROUND : (v === T.CLIFF && northFace(tx, ty)) ? T.GROUND : v)), COLS, ROWS, area.x, area.y, (tx, ty) => { const v = beyondGround(tx, ty); return v; }, 1)) : null;
    // `timedField` above keeps this phase's clock   // no cliff bias: the outline meanders both ways
    yield;
    { const __t0 = performance.now();
    p.turf = !paint ? null : growTurf(turfTiles, flora, COLS, ROWS, this.seedNum,
      wox / TS, woy / TS, coverAt);
    ((globalThis as any).__bakeTimes ||= {})['turf'] = ((globalThis as any).__bakeTimes['turf'] || 0) + performance.now() - __t0; }
    yield;
    // the terrace level per pixel (LS-218): the tile levels smoothed off the grid, for the turf's tone and the ground's
    p.levelOf = terr0 ? rimLevel : null;   // the tile's own level, for the field bake's palettes (LS-218)
    /* THE FOLD FIELD. Where a plateau's top turns into its face is a line the
     * rock field cannot see (rock on both sides) and the level field cannot
     * see (a face tile carries its top's level), so the tops of the faces
     * and the lips above them stayed ruled. This field is 'front' for the
     * face tiles and nothing for the rest, smoothed with the same warp. */
    p.frontField = paint && terr0 && !(globalThis as any).__noField
      ? yield* timedField('front', smoothLabelsSteps((tx, ty) => (tiles[ty] && tiles[ty][tx] === T.CLIFF && isFront(tx, ty)) ? 1 : 0, COLS, ROWS, this.seedNum + 13, area.x * COLS, area.y * ROWS)) : null;
    yield;
    /* THE PALETTE IS BIOME AND LEVEL. Two ground tiles on the same terrace
     * still meet on a ruled line where the biome changes under them (fen
     * green against heath grey), so the bake's palette label is the biome's
     * index and the level together, smoothed like the level was (LS-218). */
    const palIds = this.palIds || (this.palIds = new Map());
    const palA = this.palA || (this.palA = new Map());
    const blendFn = this.world ? this.world.biomeBlendAt : null;
    const palId = (tx, ty) => {
      // the same key `wildPalAt` paints the tile with: the blend of two biomes and its mix, then the level
      const wx = area.x * COLS + tx, wy = area.y * ROWS + ty;
      const m = blendFn ? blendFn(wx, wy) : null;
      const b = m ? m.a + '|' + m.b + '|' + m.t : (this.world && this.world.biomeAtTile ? this.world.biomeAtTile(wx, wy) : '');
      let i = palIds.get(b); if (i === undefined) { i = palIds.size; palIds.set(b, i); }
      const id = i * 8 + Math.min(7, Math.max(0, (terr0 ? rimLevel(tx, ty) : 0) | 0));
      if (m && !palA.has(id)) palA.set(id, m.a);   // the primary biome of the label, for the soil's inks
      return id;
    };
    p.palOf = palId;
    p.groundPals = paint && !(globalThis as any).__noField && this.world && this.world.biomeAtTile
      ? yield* timedField('pals', smoothLabelsSteps(palId, COLS, ROWS, this.seedNum + 11, area.x * COLS, area.y * ROWS)) : null;
    yield;
    p.groundLevels = paint && terr0 && !(globalThis as any).__noField
      ? yield* timedField('levels', smoothLabelsSteps((tx, ty) => rimLevel(tx, ty), COLS, ROWS, this.seedNum + 7, area.x * COLS, area.y * ROWS))
      : null;
    yield;
    p.turfArt = !paint ? null : yield* timedField('turf', bakeTurfSteps(p.turf, COLS, ROWS, palKey, pal, flora,
      this.seedNum, wox / TS, woy / TS,
      (lv) => terracePal(flora, lv),
      terr0 ? rimLevel : null, p.groundLevels, coverAt, p.groundKinds));
    yield;
    /* THE TURF'S PICTURE CAN BE PAINTED AGAIN ALONE (Vijay: "grass cut takes
     * a few seconds to disappear ... on desktop too"). The roots image holds
     * the tufts, and a cut changed the cell but not the picture until the
     * whole screen re-baked -- immediately once, and after the two-second
     * dirty throttle now. The bake keeps its own closure, so a cut or a
     * regrowth repaints just this picture, at once. */
    p.rebakeTurf = !paint ? null : () => { p.turfArt = bakeTurf(p.turf, COLS, ROWS, palKey, pal, flora, this.seedNum, wox / TS, woy / TS, (lv) => terracePal(flora, lv), terr0 ? rimLevel : null, p.groundLevels, coverAt, p.groundKinds); return p.turfArt; };
    // ...and the same repaint as steps, for a refresh that takes a few frames instead of one (LS-188, 2026-09-11)
    p.rebakeTurfSteps = !paint ? null : () => bakeTurfSteps(p.turf, COLS, ROWS, palKey, pal, flora, this.seedNum, wox / TS, woy / TS, (lv) => terracePal(flora, lv), terr0 ? rimLevel : null, p.groundLevels, coverAt, p.groundKinds);
    { const __t0 = performance.now();
    p.soilArt = paint ? bakeSoil(groundTiles, COLS, ROWS, pal, flora, this.seedNum,
      wox / TS, woy / TS,
      blendFor ? (tx, ty) => terracePal(blendFor(wox / TS + tx, woy / TS + ty).a, 0) : null,
      blendFor ? (tx, ty) => blendFor(wox / TS + tx, woy / TS + ty).a : null, p.groundKinds,
      p.groundPals || null, p.groundPals ? (lb) => (palA.has(lb) ? terracePal(palA.get(lb), 0) : null) : null) : null;
    ((globalThis as any).__bakeTimes ||= {})['soil'] = ((globalThis as any).__bakeTimes['soil'] || 0) + performance.now() - __t0; }
    yield;
    /* THE CANOPY IS THE ONE STEP THAT DID NOT FIT (2026-09-06). It was 19.4 ms of a screen's 72 -- and 15.3 ms of
     * that was a single `smoothLabels` call inside `paintMass`, over the PADDED grid, which is three and a half
     * times a screen's pixels. So the pre-bake could be paced as finely as you liked and this step still blew the
     * frame. It is driven a chunk at a time here, and the clock is only running while the work is: timing across a
     * `yield` would measure the frames in between and report a bake that takes a second. */
    if (!paint) p.canopy = null;
    else {
      const it = bakeCanopySteps(...cArgs);
      let r: any;
      do {
        const __t0 = performance.now();
        r = it.next();
        ((globalThis as any).__bakeTimes ||= {})['canopy'] = ((globalThis as any).__bakeTimes['canopy'] || 0) + performance.now() - __t0;
        if (!r.done) yield;
      } while (!r.done);
      p.canopy = r.value;
    }
    yield;
    /* How deep the water already is at the top of this screen, per pixel
     * column — walked up through the WORLD's tiles, so the shader's depth
     * banding does not restart at every screen boundary. Capped at the mask's
     * own byte range; past that the band is saturated anyway. */
    let depthIn = null;
    if (paint) {
      depthIn = new Int32Array(COLS * TS);
      for (let x = 0; x < COLS * TS; x++) {
        const tx = area.x * COLS + Math.floor(x / TS);
        let n = 0;
        for (let up = 1; up <= 16; up++) {
          const v = this.tileAt(tx, area.y * ROWS - up);
          if (v === undefined || !isWet(v)) break;
          n += TS;
        }
        depthIn[x] = Math.min(255, n);
      }
    }
    { const __t0 = performance.now();
    p.waterMask = paint ? buildWaterMask(tiles, COLS, ROWS, HUD_H, depthIn, p.groundKinds) : null;
    ((globalThis as any).__bakeTimes ||= {})['mask'] = ((globalThis as any).__bakeTimes['mask'] || 0) + performance.now() - __t0; }
    yield;
    /* A WINDOW ON THE LAND, with a one-tile border so the gradient at the edge
     * of a screen is computed from the tile next door rather than from a
     * clamp — which is what makes two screens agree about a hillside that
     * crosses between them. Only the wilds: a hollow room is a room, and it
     * has no landscape. */
    const land = this.terrain ? {
      get: (tx, ty) => {
        const t = this.terrain;
        const wx = Math.max(0, Math.min(t.TW - 1, area.x * COLS + tx));
        const wy = Math.max(0, Math.min(t.TH - 1, area.y * ROWS + ty));
        return t.elev[t.at(wx, wy)];
      },
      step: (tx, ty) => {
        if (tiles[ty] && tiles[ty][tx] === T.SEA) return 0;   // sea is sea level (see rimLevel)
        const t = this.terrain;
        const wx = Math.max(0, Math.min(t.TW - 1, area.x * COLS + tx));
        const wy = Math.max(0, Math.min(t.TH - 1, area.y * ROWS + ty));
        return t.terrace[t.at(wx, wy)];
      },
      /** How many tiles tall the rock is here — 0 where there is none. */
      face: (tx, ty) => {
        const t = this.terrain;
        if (!t.face) return 0;
        const wx = Math.max(0, Math.min(t.TW - 1, area.x * COLS + tx));
        const wy = Math.max(0, Math.min(t.TH - 1, area.y * ROWS + ty));
        return t.face[t.at(wx, wy)];
      },
      /** Is the rock here a lone outcrop rather than part of a wall? See the
       *  note in `terrain.ts`: a formation under four tiles is drawn as a
       *  boulder, because a one-tile wall is a plank. */
      crag: (tx, ty) => {
        const t = this.terrain;
        if (!t.crag) return false;
        const wx = Math.max(0, Math.min(t.TW - 1, area.x * COLS + tx));
        const wy = Math.max(0, Math.min(t.TH - 1, area.y * ROWS + ty));
        return !!t.crag[t.at(wx, wy)];
      },
      // a STUB: one, two or three tiles of visible wall, drawn as rock by the
      // tile art (LS-199) and therefore not a wall this pass should hang a
      // face under. Asked of the terrain so both painters get one answer.
      stub: (tx, ty) => {
        const t = this.terrain;
        if (!t.stubAt) return false;
        return !!t.stubAt(area.x * COLS + tx, area.y * ROWS + ty);
      },
    } : null;
    /* Which tiles on this screen are a ramp, in local coordinates. The draw
     * needs it because a ramp is a hole cut in a cliff and the cliff has to be
     * drawn AROUND it — tapering into the gap — rather than either straight
     * over it or not at all. */
    p.ramps = [];
    if (this.world.ramps) {
      for (const r of this.world.ramps) {
        for (const [wx, wy] of r.wide ? [[r.x, r.y], r.wide] : [[r.x, r.y]]) {
          const lx = wx - area.x * COLS, ly = wy - area.y * ROWS;
          if (lx >= -1 && ly >= -1 && lx <= COLS && ly <= ROWS) p.ramps.push([lx, ly]);
        }
      }
    }
    p.land = land;
    { const __t0 = performance.now();
    p.fieldHeights = paint && p.groundLevels ? bakeFieldHeights(p.groundLevels, p.rockKinds || null, COLS, ROWS, HUD_H, 8, 4, T.CLIFF, { tiles, kind: T.RAMP, levelOf: p.levelOf },
      (px, py) => rimLevel(Math.floor(px / TS), Math.floor(py / TS))) : null;
    ((globalThis as any).__bakeTimes ||= {})['heights'] = ((globalThis as any).__bakeTimes['heights'] || 0) + performance.now() - __t0; }   // the ground's own height from the fields (LS-221)
    yield;
    { const __t0 = performance.now();
    p.normalMap = !paint ? null : bakeTileNormals(tiles, COLS, ROWS, HUD_H, land, p.fieldHeights);
    ((globalThis as any).__bakeTimes ||= {})['normals'] = ((globalThis as any).__bakeTimes['normals'] || 0) + performance.now() - __t0; }
    yield;
    if (p.normalMap) {
      // the plan's crowns are in the PADDED bake's coordinates; the relief is
      // in the playfield's, so they come back by the margin
      const cp = CANOPY_PAD * TS;
      crownNormals(p.normalMap,
        canopyPlan(...cArgs).filter((layer) => layer.spec.kind !== 'boulder')
          .map((layer: any) => ({ ...layer,
            crowns: layer.crowns.map((c: any) => ({ ...c, x: c.x - cp, y: c.y - cp })) })),
        HUD_H);
    }
    /* THE SCREEN AS A HEIGHT FIELD (LS-201), beside its normals: what the
     * shadow march walks through. Same table as the relief, so the shape that
     * casts is the shape that is lit. */
    { const __t0 = performance.now();
    p.fieldLevels = p.groundLevels ? bakeFieldLevels(p.groundLevels, COLS, ROWS, HUD_H, 8, { tiles, kind: T.RAMP, levelOf: p.levelOf }) : null;   // the terraces unblurred, for the perspective camera (2026-09-13)
    p.heightMap = bakeHeightCanvas(tiles, COLS, ROWS, HUD_H, p.fieldHeights || null, p.fieldLevels);
    ((globalThis as any).__bakeTimes ||= {})['heightmap'] = ((globalThis as any).__bakeTimes['heightmap'] || 0) + performance.now() - __t0; }
    yield;
    p.critters = true
      ? growCritters(tiles, flora, seed, COLS, ROWS) : [];
    return p;
  }

  loadArea() {
    const s = this.screenAt(this.area.x, this.area.y);
    this.tiles = s.tiles;
    this.screen = s;
    this.palKey = s.biome;
    this.pal = BIOMES[s.biome];
    this.visited.add(this.area.x + ',' + this.area.y);
    this.respawnAt = { x: this.area.x, y: this.area.y };
    this.applyBroken();
    /* The flames are the fires' pictures and the fires are the world's (LS-511): nothing about them changes on a crossing. */
    /* The world's lists -- creatures, things, shots, pods, effects -- are the world's and are not emptied here (LS-511). */
    this.pushing = null;
    this.stop = 0;
    this.shake = null;
    // GROW THE GROUND. Deterministic, from the screen's own seed, and after
    // `applyBroken` so a wall you opened is soil the next time you come back.
    // None of it is solid and none of it is known to `passable.js`: this layer
    // must never be able to decide where a player may walk.
    /* ONE BAKE, LIVING IN `bakeParts`.
     *
     * This was eighty lines of scatter, turf, canopy, water mask and normals
     * writing straight into eight fields of the game object — correct while
     * exactly one screen is ever on the glass, and impossible the moment a
     * camera spans a seam. It is a function of a screen now (LS-142 step 1);
     * the same calls in the same order with their inputs named.
     *
     * `pushWater` and `pushLight` stay HERE and did not move into it. They
     * hand buffers to the presenter, and only the screen actually being drawn
     * may do that — a neighbourhood bake that pushed its own water would put
     * the wrong lake under the shader. */
    const paint = this.cosmetics !== false;
    // through the cache, because a camera needs the neighbours' parts too
    const parts = this.screenParts(this.area.x, this.area.y);
    this.turfOx = parts.turfOx; this.turfOy = parts.turfOy;
    this.decals = parts.decals;
    this.turf = parts.turf;
    this.turfArt = parts.turfArt;
    this.soilArt = parts.soilArt;
    this.canopy = parts.canopy;
    this.waterMask = parts.waterMask;
    if (paint) this.pushWater();
    this.normalMap = parts.normalMap;
    if (paint) this.pushLight();
    this.critters = parts.critters;
  }

  /** Hand the presenter the water for whatever is on the glass RIGHT NOW.
   *
   *  THE TITLE SCREEN WAS FULL OF REFLECTIONS. The mask is built by
   *  `loadArea`, which runs at boot so a run is ready the instant you press a
   *  key — and the presenter then applied it to every frame, including the
   *  title, the map and the death screen, which are drawn into the same
   *  buffer. The result was a smeared rectangle of starfield lying in the
   *  middle of the title, in the shape of a lake on a screen nobody had seen
   *  yet. The mask belongs to the WORLD; anything that is not the world is
   *  dry.
   *
   *  `wx`/`wy` are where the frame's top-left corner sits in the world, in
   *  game pixels, so the surface pattern travels with the lake. */
  _quickMasks: Map<string, any> = new Map();
  /** A water mask from the bare tiles, for a screen the window shows before its bake: no field, no shore foam. */
  quickWaterMask(sx, sy) {
    if (typeof document === 'undefined' || !this.world) return null;
    const k = sx + ',' + sy; let m = this._quickMasks.get(k);
    if (m === undefined) {
      const s = this.screenAt(sx, sy);
      m = s && s.tiles ? buildWaterMask(s.tiles, COLS, ROWS, HUD_H, null, null) : null;
      if (this._quickMasks.size > 64) this._quickMasks.clear();
      this._quickMasks.set(k, m);
    }
    return m;
  }
  pushWater(mask = this.waterMask, wx = null, wy = null, seam = -1) {
    if (!this.onWater) return;
    const world = (this.mode === 'play' || this.mode === 'dying') && !this.paused && !this.bagOpen;
    let m = null, ax = 0, ay = 0;
    if (world) {
      m = mask;
      ax = wx !== null ? wx : this.area.x * COLS * TS;
      ay = (wy !== null ? wy : this.area.y * ROWS * TS)
        - HUD_H;
    }
    // ...and only when something actually changed. This is called every frame
    // so that leaving the title, opening the map or dying takes effect without
    // anything having to remember to say so — and `setWater` re-uploads a
    // texture, which is not free 60 times a second for a mask that has not
    // moved.
    const key = (m ? 'm' : '-') + ax + ',' + ay + ',' + seam;
    if (key === this._wetKey && m === this._wetMask) return;
    this._wetKey = key; this._wetMask = m;
    this.onWater(m, HUD_H / (ROWS * TS + HUD_H), ax, ay, seam);
  }

  /* WHAT IS SHINING ON THE WORLD THIS FRAME.
   *
   * The same contract as `pushWater`: anything that is not the world — the
   * title, the map, the death screen — is FLAT, because they are drawn into
   * the same buffer and a relief pass over a menu is a menu with a dent in
   * it. That fault is not hypothetical; the water mask shipped it, and the
   * title screen had a lake-shaped smear of starfield in the middle of it
   * until somebody noticed.
   *
   * Lights are in the frame's own pixels, HUD included, because that is the
   * space the shader works in and it does not know the top 48 rows are a
   * readout.
   */
  /** The sky of this moment: the world clock's hour, or the dead of night on a `!night` seed. */
  skyNow() {
    const clk = this.systems ? this.systems.clock(this) : null;
    const sky: any = skyAt(this.night ? NIGHT_DAY : (clk ? clk.day : NOON_DAY));
    sky.hud = HUD_H;
    return sky;
  }
  pushLight() {
    if (!this.onLight) return;
    /* A SCROLL HAS ITS OWN COMPOSER, AND THIS IS A SAVING, NOT A FIX.
     *
     * `render` calls this near the top of the frame and `scrollLight` runs
     * later, inside the scroll branch — so the composite wins on ordering
     * whether or not this line exists, and deleting it leaves every check
     * green. What it actually buys is one wasted composite and one wasted
     * 256x240 texture upload per transition frame.
     *
     * Kept, and labelled, rather than removed: the ordering is not obvious
     * from either function, and a guard whose value is performance should not
     * be mistaken for one holding correctness. */
    const world = (this.mode === 'play' || this.mode === 'dying') && !this.paused && !this.bagOpen;
    const litIn3d = this.view3d && !(typeof location !== 'undefined' && /[?&]pixelfloor=1/.test(location.search));   /* B (2026-09-27): the scene lights itself; the painters' normal map, relit over a full-resolution world at the game's own pixel, was a checker over all of it */
    if (!world || !this.normalMap || litIn3d) {
      if (this._litKey === null) return;
      this._litKey = null;
      this.onLight(null, [], null);
      return;
    }
    /* EVERYTHING ON THE GLASS HAS A SHAPE, not just the ground.
     *
     * The tiles and the canopy bake once per screen; the ACTORS move, so
     * their relief is stamped onto a copy every frame. Without this the world
     * had form and every body walking on it was a flat cut-out — the shape of
     * mistake the whole normal buffer exists to avoid, made one layer up.
     *
     * `bodyNormals` reads the sprite's own mask, so a body's relief is the
     * distance transform of exactly the pixels that are drawn: the picture and
     * its shape cannot disagree, because they are the same pixels. And the
     * sprite NAME comes from the actor's own `bodyName`, which `draw` uses
     * too, so they cannot name two different frames either.
     */
    let buf = this.normalMap;
    const bodies = [this.player, ...(this.enemies || [])]
      .filter((e) => e && !e.dead && !e.submerged && typeof e.bodyName === 'function');
    if (bodies.length && typeof document !== 'undefined') {
      if (!this._normalFrame) {
        this._normalFrame = document.createElement('canvas');
        this._normalFrame.width = this.normalMap.width;
        this._normalFrame.height = this.normalMap.height;
      }
      const nc = this._normalFrame.getContext('2d');
      if (nc) {
        nc.clearRect(0, 0, this._normalFrame.width, this._normalFrame.height);
        nc.drawImage(this.normalMap, 0, 0);
        for (const e of bodies) {
          const r = e.lastRig;
          if (r && r.mask) {
            // the rig's own mask (LS-184), so the light sees the shape on the glass
            const img = maskNormals(r.key, r.mask, r.w, r.h, r.normals);
            if (img) if (img.sx !== undefined) nc.drawImage(img.canvas, img.sx, img.sy, img.w, img.h, Math.round(e.x - this.originX()) + r.ox, Math.round(e.y - this.originY()) + r.oy + HUD_H, img.w, img.h); else nc.drawImage(img, Math.round(e.x - this.originX()) + r.ox, Math.round(e.y - this.originY()) + r.oy + HUD_H);
            continue;
          }
          const frame = Math.floor((e.anim || 0) / 7) % 2;
          const bn = e.bodyName(frame);
          const img = bodyNormals(bn.name, bn.flip);
          // ...at the same offset `draw` uses for the body itself
          // bodies live in world px; the buffer is the screen
          if (img) if (img.sx !== undefined) nc.drawImage(img.canvas, img.sx, img.sy, img.w, img.h, Math.round(e.x - this.originX() - 1), Math.round(e.y - this.originY() - 2) + HUD_H, img.w, img.h); else nc.drawImage(img, Math.round(e.x - this.originX() - 1), Math.round(e.y - this.originY() - 2) + HUD_H);
        }
        buf = this._normalFrame;
      }
    }
    /* THE HOUR LIGHTS THE WILD. The brazier used to be the only source, by
     * design: the one thing the player turns on. The sky now comes from the
     * world clock (a `!night` seed holds it at the dead of night), and a lit
     * thing -- a brazier, a fire the ember or a blast started, the torch you
     * carry, a wisp -- counts for more as the day goes, so at noon they are
     * warmth and at night they are the way. Sixteen nearest to the player. */
    const sky = this.skyNow();
    const glow = 1 + 0.8 * sky.dark;
    const lights = [];
    const pcx = this.player.cx - this.originX(), pcy = this.player.cy - this.originY();
    const add = (x, y, r, i, col) => lights.push({ x, y: y + HUD_H, r, i: i * glow, col, d: Math.hypot(x - pcx, y - pcy) });
    for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
      if (this.tiles[ty][tx] !== T.BRAZIER_LIT) continue;
      const f = 0.92 + 0.08 * Math.sin((this.frame + tx * 37 + ty * 61) / 22);
      add(tx * TS + 8, ty * TS + 8, 52, 0.55 * f, [1.0, 0.72, 0.38]); lights[lights.length - 1].fire = true;
    }
    /* THE ANSWER LIES IN A WARM LIGHT (LS-464): the ground under a beast's answer glows like a brazier's, and a few sparks rise */
    for (const bcn of this.answerBeacons()) {
      const x = bcn.wx - this.originX(), y = bcn.wy - this.originY(); if (x < -20 || y < -20 || x > COLS * TS + 20 || y > ROWS * TS + 20) continue;
      add(x, y, 40, 0.5 + 0.08 * Math.sin(this.frame / 30), [1.0, 0.8, 0.45]);
      if (this.frame % 14 === 0 && this.fx.length < 80) this.puff(bcn.wx, bcn.wy - 2, UI.gold, 1, 'spark');
    }
    /* ...BUT NOT ON A STANDING STONE (2026-09-20; Vijay: "the gold shimmer thing on stones looks terrible get rid of
     * it"). The pulse and its spark stay on the things a press BUILDS with -- a forge's rock, a piece about to be set
     * down -- where a warm light reads as the fire it belongs to. On a stone it was a permanent gold twinkle on a grey
     * thing, and the stone already says what it is: the aftermath's cool call and the sparks that grow under a held
     * key are the cues that stayed. */
    { const t = this.actPulse(); if (t && t.kind !== 'stone') add(t.tx * TS + 8 - this.originX(), t.ty * TS + 8 - this.originY(), 22, 0.35 + 0.25 * Math.sin(this.frame / 6), [1.0, 0.86, 0.45]); }   // the key's target pulses (LS-397)
    /* ...AND SO DOES A RAFT WITH A FAIR WIND (LS-397 h, 2026-09-15). Two cheaper cues were built and measured
     * first: gold motes off the hull, which a wind ribbon draws spline-thin and faded so they put not one pixel of
     * their own colour on the glass, and four gold pixels on the lashing, which landed on the hull sprite's own pale
     * pegs and were invisible in an A/B. This is the language that already works on this screen -- a warm pulsing
     * LIGHT on the world and a spark rising off it, the same treatment as the key's target -- and the meaning is
     * the same kind of thing: the game marking what to do next. Gold on the raft says this wind carries you toward
     * a beast that lives; the dark says wait for it to come round. */
    { const b = this.state.boat; if (b && b.kind === 'raft' && this.fairCue) add(b.x - this.originX(), b.y - this.originY(), 30, 0.4 + 0.28 * Math.sin(this.frame / 7), [1.0, 0.84, 0.42]); }
    { const c = this.cureTarget(); if (c) add(c.tx * TS + 8 - this.originX(), c.ty * TS + 8 - this.originY(), 26, 0.3 + 0.25 * Math.sin(this.frame / 9), c.cool ? [0.55, 0.85, 1.0] : [1.0, 0.7, 0.35]); }   // ...and the cure for what ails the body (LS-397)
    if (this.systems) for (const fr of this.systems.fires) {
      const f = 0.85 + 0.15 * Math.sin((this.frame * 1.7 + fr.x * 53 + fr.y * 29) / 9);
      add(fr.x * TS + 8 - this.originX(), fr.y * TS + 8 - this.originY(), 40, 0.5 * f, [1.0, 0.62, 0.28]); lights[lights.length - 1].fire = true;   // fires are in world tiles (LS-511)
    }
    if (this.player.lit > 0) add(pcx, pcy, 46, 0.5 + 0.05 * Math.sin(this.frame / 5), [1.0, 0.76, 0.45]);
    else if ((this.state.sight || 0) > this.runFrames) add(pcx, pcy, 40, 0.42 + 0.04 * Math.sin(this.frame / 7), [0.72, 0.82, 1.0]);   // a nightcap's sight: cooler and a little smaller than a torch, and no fire in it (LS-335)
    /* IN THE AFTERMATH THE STONES CALL YOU, AND THEY DO IT WITHOUT WORDS (2026-09-08; Vijay: "i really
     * don't like the text prompts across the game, i want the entire game to be playable without any
     * instructions"). The run is won and it ends when you touch a standing stone -- which was a toast
     * saying so, which is exactly the kind of line that should not have to exist. A stone lights
     * instead, slowly, on the breath the braziers use, so what you have to do is a thing you can see
     * from across a screen. */
    if (this.stonesCalling() && this.world && this.world.stones) {
      const breath = 0.55 + 0.2 * Math.sin(this.frame / 26);
      for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
        if (!this.world.stones.has(this.area.x + ',' + this.area.y + '|' + tx + ',' + ty)) continue;
        add(tx * TS + 8, ty * TS + 4, 44, breath, [0.82, 0.90, 1.0]);
      }
    }
    for (const e of this.enemies || []) {
      if (e.dead || e.type !== 'wisp') continue;
      add(e.x + 6 - this.originX(), e.y + 6 - this.originY(), 30, 0.38, [0.7, 0.9, 1.0]);
    }
    lights.sort((a, b) => a.d - b.d);
    if (lights.length > 16) lights.length = 16;
    const flick = lights.length ? this.frame % 22 : 0;
    const key = lights.length + ':' + this.areaKey() + ':' + flick + ':' + Math.floor(sky.day * 600)
      + ':' + lights.map((l) => Math.round(l.x) + ',' + Math.round(l.y)).join('|')
      + ':' + bodies.map((e) => Math.round(e.x) + ',' + Math.round(e.y)).join('|');
    if (key === this._litKey) return;
    this._litKey = key;
    this.onLight(buf, lights, sky);
  }

  /** During a scroll, two screens are on the glass at once and each has its
   *  own water. Compose one mask per frame from both, each at the offset it is
   *  actually being drawn at, clipped to the half of the playfield it occupies
   *  — they tile it exactly, so neither can paint over the other's lake.
   *
   *  Headless has no `createImageData`, and `buildWaterMask` returns null
   *  there; everything below is a no-op in that case rather than a throw. */
/* THE PRESENTER'S BUFFERS FOR THE WINDOW, not for one screen of it.
   *
   * `scrollWater` and `scrollLight` have composed TWO screens for the length of
   * a transition since the round the reflections were found sitting on dry
   * land, and `scrollLight`'s comment records what happens without it: "the
   * presenter got the incoming screen's normal buffer, unshifted, with the
   * lights at unshifted tile positions. Half the picture was lit by the shape
   * of the screen replacing it."
   *
   * A camera has exactly that problem permanently rather than for twelve
   * frames, so these are the same two functions with the offsets coming from
   * the window instead of from an easing curve, over the up-to-four screens it
   * stands on. Same scratch canvases, same `maskBand` clip that keeps mask
   * pixels out of the HUD band — that clip was written because reflections
   * smeared across "wilds / fen" for a whole transition.
   */
  cameraWater(cam) {
    if (!this.onWater) return;
    const FW = COLS * TS, FH = ROWS * TS + HUD_H;
    let c = this._camMask;
    if (!c) {
      if (typeof document === 'undefined') return;
      c = this._camMask = document.createElement('canvas');
      c.width = FW; c.height = FH;
    }
    const g = c.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, FW, FH);          // R = 0 is "not water": an empty mask is dry
    let any = false;
    for (const [sx, sy, ox, oy] of this.visibleScreens(cam)) {
      const ready = this.bakedParts(sx, sy);          // never force a bake for a mask: see `bakedParts`
      /* AN UNBAKED NEIGHBOUR IS STILL WATER (LS-387, 2026-09-12). A screen the window showed before its bake had no
       * mask, so its region of the window was dry to the shader and its sea drew raw -- paler and unbanded beside the
       * screen the body stood on: the two looks of the open sea, and the screen-shaped patches on every map. A quick
       * mask straight from the tiles stands in until the bake's own arrives. */
      const mask = (ready && ready.waterMask) || this.quickWaterMask(sx, sy);
      if (!mask) continue;
      const [top, bot] = maskBand(Math.round(oy));
      if (bot <= top) continue;
      any = true;
      g.save();
      g.beginPath();
      g.rect(Math.round(ox), top, FW, bot - top);
      g.clip();
      g.drawImage(mask, Math.round(ox), Math.round(oy));
      g.restore();
    }
    /* NO WATER IN THE WINDOW IS STILL A MESSAGE, AND IT HAS FIVE PARTS.
     *
     * This called `this.onWater(null, 0, 0, -1)` — four arguments to a
     * five-argument handler. `main.ts` takes `(mask, topV, wx, wy, seam)`, so
     * the HUD fraction arrived as 0, the world y as -1 and the seam as
     * `undefined`, and `Display.draw` threw on every frame of every screen
     * with no water in it. The frame loop catches and "recovers", so it
     * showed up not as a crash but as the water going wrong — reported from
     * play as the shader not respecting the camera.
     *
     * `pushWater` builds the whole message, which is why it exists. */
    if (!any) { this.pushWater(null, cam.x, cam.y, -1); return; }
    /* NO SEAM. THE MIRROR MAY READ ACROSS A JOIN, BECAUSE THE JOIN IS REAL.
     *
     * `scrollWater` passes the shader a seam so the shoreline mirror stops at
     * the boundary between the two screens on the glass, and I copied that
     * here. It is wrong for a camera and the reason is worth stating: during a
     * TRANSITION the two screens are sliding, so neither is at its true world
     * position and the pixels above a lake are not the world above it. The
     * mirror had to be stopped. A window shows both screens AT their world
     * positions — the pixels above a lake are the bank above it — so stopping
     * the mirror cuts a continuous lake in half.
     *
     * Reported from play, and the symptom is exactly this shape: it happened
     * on vertical scrolling and never on horizontal, because the mirror
     * samples straight up and a horizontal boundary is the only one it can
     * cross. Measured with the camera at y=273 the seam came out at 0.6625 —
     * a cut two thirds of the way down the frame. */
    this.pushWater(c, cam.x, cam.y, -1);
  }

  cameraLight(cam) {
    if (!this.onLight) return;
    if (this.paused || this.bagOpen) { this.pushLight(); return; }
    /* ONLY WHEN SOMETHING MOVED (LS-188). This rebuilt the normal buffer and
     * re-uploaded it every frame; standing still, that was the whole frame's
     * light work for nothing. The key holds the camera, the hour, the lights'
     * flicker phase and every body's position to the pixel. */
    const sky0 = this.skyNow();
    const camKey = 'cam:' + Math.round(cam.x) + ',' + Math.round(cam.y) + ':' + Math.floor(sky0.day * 600) + ':' + (this.frame % 22)
      + ':' + (this.systems ? this.systems.fires.length : 0) + ':' + (this.player.lit > 0 ? 1 : 0)
      + ':' + [this.player, ...(this.enemies || [])].map((e) => e && !e.dead ? Math.round(e.x) + ',' + Math.round(e.y) + (e.anim ? Math.floor(e.anim / 7) % 2 : 0)
        + ':' + (e.lastRig ? e.lastRig.key + ',' + e.lastRig.ox + ',' + e.lastRig.oy : '') : '').join('|');
    if (camKey === this._litKey) return;
    const FW = COLS * TS, FH = ROWS * TS + HUD_H;
    let c = this._camNormal;
    if (!c) {
      if (typeof document === 'undefined') return;
      c = this._camNormal = document.createElement('canvas');
      c.width = FW; c.height = FH;
    }
    const g = c.getContext('2d');
    if (!g) return;
    g.fillStyle = 'rgb(128,128,255)';   // flat: facing the viewer
    g.fillRect(0, 0, FW, FH);
    /* ...AND THE SAME FRAME AS A HEIGHT FIELD, for the shadow march. Composed
     * exactly like the normals -- the screens' baked fields, then the bodies
     * stamped where they stand -- so the two always agree about what is
     * standing where. */
    let hc = this._camHeight;
    if (!hc) { hc = this._camHeight = document.createElement('canvas'); hc.width = FW; hc.height = FH; }
    const hg = hc.getContext('2d');
    // cleared, not painted: this is a data buffer (red is height) and a
    // transparent pixel reads as zero height, which is the ground
    if (hg) hg.clearRect(0, 0, FW, FH);
    const lights: any[] = [];
    const curOx = this.originX() - Math.round(cam.x), curOy = this.originY() - Math.round(cam.y);
    const sky = this.skyNow();
    const glow = 1 + 0.8 * sky.dark;
    for (const [sx, sy, ox, oy] of this.visibleScreens(cam)) {
      /* Never force a bake for the light either (see `bakedParts`) -- and SKIP THE WHOLE SCREEN when its parts are
       * not made, not merely the normals: the lit-brazier pass below reads `parts.tiles`, and guarding only the
       * first block left it to throw on null. A screen with no parts yet contributes no normals, no heights and no
       * braziers this frame, which is one frame of a lamp not glowing against a third of a second of stall. */
      const parts = this.bakedParts(sx, sy);
      if (!parts) continue;
      if (parts.normalMap) {
        const [top, bot] = maskBand(Math.round(oy));
        if (bot > top) {
          g.save();
          g.beginPath();
          g.rect(Math.round(ox), top, FW, bot - top);
          g.clip();
          g.drawImage(parts.normalMap, Math.round(ox), Math.round(oy));
          g.restore();
          if (hg && parts.heightMap) {
            hg.save();
            hg.beginPath();
            hg.rect(Math.round(ox), top, FW, bot - top);
            hg.clip();
            hg.drawImage(parts.heightMap, Math.round(ox), Math.round(oy));
            hg.restore();
          }
        }
      }
      // ...AND THE BRAZIERS OF EVERY SCREEN ON THE GLASS. A lit brazier two
      // tiles the other side of a seam still lights the rock in front of you.
      const shift = (x, y) => [x + Math.round(ox), y + HUD_H + Math.round(oy)];
      for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
        if (parts.tiles[ty][tx] !== T.BRAZIER_LIT) continue;
        const f = 0.92 + 0.08 * Math.sin((this.frame + tx * 37 + ty * 61) / 22);
        const [x, y] = shift(tx * TS + 8, ty * TS + 8);
        lights.push({ x, y, r: 74 * f, i: 1.15 * f * glow, col: [1.0, 0.72, 0.38], fire: true });   // a brazier is a fire to the display's fire pass (2026-09-11)
      }
    }
    // Actors cross screen boundaries; stamp them after every terrain normal map.
    {
      const shift = (x, y) => [x + curOx, y + HUD_H + curOy];
      /* THE HOUR LIGHTS THE WILD -- see `pushLight`. Fires, the torch and the
       * wisps belong to the screen the player is on. */
      if (this.systems) for (const fr of this.systems.fires) {
        const f = 0.85 + 0.15 * Math.sin((this.frame * 1.7 + fr.x * 53 + fr.y * 29) / 9);
        const x = fr.x * TS + 8 - Math.round(cam.x), y = fr.y * TS + 8 - Math.round(cam.y) + HUD_H;   // fires are in world tiles (LS-511)
        lights.push({ x, y, r: 56, i: 0.9 * f * glow, col: [1.0, 0.62, 0.28], fire: true });   // ...and so is a flame on the ground
      }
      const pcx = this.player.cx - this.originX(), pcy = this.player.cy - this.originY();
      if (this.player.lit > 0) {
        const [x, y] = shift(pcx, pcy);
        lights.push({ x, y, r: torchGlow(this.state), i: (0.9 + 0.08 * Math.sin(this.frame / 5)) * glow, col: [1.0, 0.76, 0.45] });   // the moth charm (LS-335) widens what a carried flame reaches
      }
      for (const e of this.enemies || []) {
        if (e.dead || e.type !== 'wisp') continue;
        const [x, y] = shift(e.x + 6 - this.originX(), e.y + 6 - this.originY());
        lights.push({ x, y, r: 40, i: 0.7 * glow, col: [0.7, 0.9, 1.0] });
      }
      // the actors' own relief, stamped where they stand (see `pushLight`)
      for (const e of [this.player, ...(this.enemies || [])]) {
        if (!e || e.dead || e.submerged) continue;
        const r = e.lastRig;
        if (r && r.mask) {
          // the rig's own mask (LS-184), so the light sees the shape on the glass -- refreshed every third frame per body (a canvas a body a frame on a phone)
          let img = e.reliefImg;
          /* THE STAGGER IS SEEDED, AND IT STICKS (2026-09-06). Rebuilding every body's relief on the same frame
           * costs a canvas a body a frame on a phone, so each body rebuilds on one frame in three -- but the slot
           * that decides WHICH was `Math.floor(Math.random() * 3)`, so with the frame pinned the picture still came
           * out differently run to run. The golden baseline caught it as ONE PIXEL of the ash frame flickering
           * between two runs, which is exactly the size of thing a hash is for.
           *
           * Worse, it was `||=`: a body that rolled slot 0 never cached it, because 0 is falsy -- so it re-rolled
           * every single frame and rebuilt whenever the dice said so. Derived from where the body first stood, it
           * still spreads the work across three frames and is the same every run; `??=` makes zero stick. */
          e.reliefSlot ??= (Math.round(e.x) * 7 + Math.round(e.y) * 13) % 3;
          if (!img || (e.reliefKey !== r.key && (e === this.player || (this.frame + e.reliefSlot) % 3 === 0))) { img = maskNormals(r.key, r.mask, r.w, r.h, r.normals); e.reliefImg = img; e.reliefKey = r.key; }
          if (!img) continue;
          const [x, y] = shift(Math.round(e.x - this.originX()) + r.ox, Math.round(e.y - this.originY()) + r.oy);
          if (img.sx !== undefined) g.drawImage(img.canvas, img.sx, img.sy, img.w, img.h, Math.round(x), Math.round(y), img.w, img.h); else g.drawImage(img, Math.round(x), Math.round(y));
          continue;
        }
        if (typeof e.bodyName !== 'function') continue;
        const bn = e.bodyName(Math.floor((e.anim || 0) / 7) % 2);
        const img = bodyNormals(bn.name, bn.flip);
        if (!img) continue;
        const [x, y] = shift(e.x - this.originX() - 1, e.y - this.originY() - 2);
        if (img.sx !== undefined) g.drawImage(img.canvas, img.sx, img.sy, img.w, img.h, Math.round(x), Math.round(y), img.w, img.h); else g.drawImage(img, Math.round(x), Math.round(y));
      }
    }
    /* THE BODIES STAND IN THE HEIGHT FIELD TOO, or they take the sun's
     * shadow without throwing one. A body is a dome about its own width: at
     * thirteen pixels there is nothing finer to say, and the shadow it casts
     * is a shadow the size of a person. */
    if (hg) {
      for (const e of [this.player, ...(this.enemies || [])]) {
        if (!e || e.dead || e.submerged) continue;
        const bx = Math.round(e.cx - this.originX()) + curOx, by = Math.round(e.cy - this.originY()) + curOy + HUD_H;
        const r = Math.max(4, (e.w || 13) * 0.5);
        const grd = hg.createRadialGradient(bx, by, 0, bx, by, r);
        const tall = Math.round(255 * (e.def && e.def.boss ? 20 : 11) / 24);
        grd.addColorStop(0, 'rgb(' + tall + ',0,0)');
        grd.addColorStop(1, 'rgb(0,0,0)');
        hg.fillStyle = grd;
        hg.beginPath(); hg.arc(bx, by, r, 0, Math.PI * 2); hg.fill();
      }
    }
    const pc = [this.player.cx - this.originX(), this.player.cy - this.originY() + HUD_H];
    lights.sort((a, b) => Math.hypot(a.x - pc[0], a.y - pc[1]) - Math.hypot(b.x - pc[0], b.y - pc[1]));
    if (lights.length > 16) lights.length = 16;
    this._litKey = camKey;
    /* THE SHADOW STRENGTH RIDES WITH THE HOUR'S RELIEF, and it is OFF unless
     * the game asks for it: `game.shadows` in 0..1. The march is sixteen
     * texture samples a pixel, which is nothing on a desktop and not nothing
     * on a phone, so it is a decision and not a default. */
    /* `shadows` casts from the sun; `rt` adds a shadow ray per point light and
     * the contact shade. Either one needs the height field, so the buffer is
     * only built and uploaded when one of them is asked for. */
    const wantsHeight = this.shadows || this.rt;
    const relief = wantsHeight ? { ...sky, shadow: this.shadows || 0, rt: this.rt || 0 } : sky;
    this.onLight(c, lights, relief, wantsHeight ? hc : null);
  }


  applyBroken() { this.applyBrokenTo(this.tiles, this.areaKey(), this.screen); this.applyPlacedTo(this.tiles, this.areaKey()); }

  applyBrokenTo(tiles, key, screen = null) {
    // `screenAt` calls this while a world is still being built, before `broken`
    // exists — and a screen generated then has nothing broken on it yet anyway
    if (!this.broken) return;
    const set = this.broken.get(key);
    if (!set) return;
    for (const k of set) {
      const [tx, ty] = k.split(',').map(Number);
      const cur = tiles[ty][tx];
      // A TABLE, NOT A LIST OF ORS. `T.NEST` was added to the game and this
      // whitelist did not learn about it, so `breakNest` recorded the break
      // and `applyBroken` ignored it: 14 of 14 cut nests were standing again
      // after a reload, and the one enemy source the game says you can switch
      // off switched itself back on. A new tile kind cannot forget to join a
      // table the way it can forget to join a chain of `||`.
      if (BREAKABLE.has(cur)) {
        tiles[ty][tx] = cur === T.BRAZIER ? T.BRAZIER_LIT
          : cur === T.TREE ? T.DEBRIS
          : cur === T.THORN || cur === T.NEST
            ? T.GROUND
            : T.GROUND;
      }
    }
  }

  /** Whether the next press of each action would actually do anything.
   *
   *  The touch buttons used to light and buzz on every press: ten SICKLE taps
   *  33ms apart lit ten times and produced two swings, because the 18-frame
   *  attack refractory silently ate the rest, and the same lie held while
   *  submerged, where the swing is blocked outright. A control that confirms
   *  an input the game threw away is worse than one that does nothing. These
   *  live here, not in the input layer, so the predicate and the rule cannot
   *  drift apart — and so they can be tested without a browser. */
  canSwing() {
    if (this.bagOpen) return false;
    // both hands are busy under the water
    if (this.player.submerged) return false;
    // A HITSTOP EATS PRESSES, and neither predicate looked at it. `update()`
    // does `if (this.stop > 0) { this.stop--; attackPressed = false; ... }` —
    // so ten presses across a ten-frame hitstop lit the button ten times and
    // produced zero swings. Hitstops fire on a boss kill (10 frames), on the
    // Warden's and the Broodmother's second movement (6), and on every blast
    // (3): i.e. exactly when a player mashes.
    //
    // `> 0`, NOT `> 1`. The analogy with `attack <= 1` is wrong: `attack`
    // decrements and the update carries on to the swing, so the last frame of
    // a swing does accept. `stop` decrements and RETURNS, so the last frame of
    // a hitstop accepts nothing — and `> 1` promised one press in thirty-two
    // that the game then threw away.
    //
    // `attackPressed` too: it is a boolean with no queue, so four presses
    // inside one frame produced one swing and lit four times.
    if (this.paused || this.mode !== 'play') return false;
    if (this.stop > 0 || this.input.attackPressed) return false;
    /* A REELING BODY CANNOT SWING, and the button has to say so. The update
     * returns out of the knockback before it reaches the sickle, so a press
     * inside one lights the button and does nothing -- which is the exact
     * lie this predicate exists to prevent. It never showed because nothing
     * in the press soak reached the player in six hundred frames; once
     * creatures chased on the diagonal (LS-194) a bramble did, and four
     * presses in thirty-three were promised and thrown away. */
    if (this.player.kb) return false;
    // Recovery and the attack clock tick before the press is consumed.
    return this.player.swingWait() <= 1 && !this.player.swimming;
  }
  /** One short follow-up, shared by live swings and the end of heavy recovery. */
  canBufferSwing() {
    if (this.paused || this.mode !== 'play' || this.stop > 0 || this.bagOpen || this.input.attackPressed) return false;
    const p = this.player;
    if (p.kb) return false;   // a reeling body keeps no press either
    const wait = p.swingWait();
    return wait > 1 && wait <= SWING_BUFFER && !p.buffered && p.dodge <= 0 && !p.swimming && !p.submerged;
  }
  /** Kept as the name half the file and the input layer already use. */
  canPod() { return this.canUseTool(); }
  /** The dodge (LS-178): a dash on a cooldown, not from the water. */
  canDodge() {
    if (this.paused || this.bagOpen || this.mode !== 'play' || this.stop > 0) return false;
    const p = this.player;
    return !!p && !p.kb && !(p.ragdoll > 0) && !(p.dodge > 0) && !(p.dodgeCool > 0) && !p.swimming && !p.submerged;   // ...and not while it is reeling or down (2026-09-17): canSwing has carried the same guard for a year, and without it a press during a knockdown was latched and the roll fired half a second later, unbidden
  }

  /** Whether the d-pad's direction is going anywhere. The one control a
   *  player holds continuously was the one that never learned to say no: with
   *  the map open, holding UP lit the arm, set `input.up`, and moved the
   *  player zero pixels for as long as they held it. */
  /** Can the player steer RIGHT NOW? Two different reasons it might be no,
   *  and the controls need to tell them apart:
   *
   *    - the game is not reading input at all (paused, a fade, the map). An
   *      expected transition; the stick is asleep, not refusing you.
   *    - the player is mid-launch. The input is read and thrown away, which
   *      is the one case that used to look identical to steering: measured
   *      with LEFT held through a real launch, the arms stayed lit, the
   *      flags stayed true, and the player moved 0.0px on that axis.
   */
  canSteer() {
    if (this.paused) return false;
    if (this.mode !== 'play') return false;
    // A LAUNCH IS NOT STEERING. `padState.steering` stayed true for the whole
    // 26-frame flight, so the stick reported it was taking input the game had
    // already decided to discard — and then the input you were holding when
    // it started fired at full speed the instant it ended.
    if (this.player && this.player.kb && this.player.kb.launched) return false;
    return true;
  }

  /** ...and WHY not, for the controls to draw. `'flying'` is the launch,
   *  `'held'` is a transition the player is expected to wait through, and
   *  `null` means they are steering. */
  steerBlockedBy() {
    if (this.player && this.player.kb && this.player.kb.launched) return 'flying';
    if (this.canSteer()) return null;
    return 'held';
  }



  /** WHAT THE PLAYER HAS SET DOWN, per screen (LS-379, slice three). Not
   *  `broken`: that is a set of coordinates whose tile is RECOMPUTED from
   *  what the generator made there, so it can only ever say "taken", never
   *  "put". A placed tile is a kind at a coordinate, applied after the broken
   *  ones whenever a screen's tiles are made or loaded, and saved beside them. */
  applyPlacedTo(tiles, key) {
    if (!this.placed) return;
    const set = this.placed.get(key);
    if (!set) return;
    for (const [k, kind] of set) {
      const [tx, ty] = k.split(',').map(Number);
      if (tiles[ty] && tiles[ty][tx] !== undefined) tiles[ty][tx] = kind;
    }
  }

  /** SET IT DOWN (LS-379, slice three): the one placement verb. With a
   *  material picked, the action button puts it back as the tile it came from
   *  on the tile in front of you -- open ground for either, and the shallows
   *  as well for stone, which is a dam. Refused where a body stands, where the
   *  ground is not open, and on the seam. Everything the world already knows
   *  about a thorn or a rock applies to the placed one unasked: a planted
   *  hedge beds a sleeper, burns, and stops the night's pack; a set stone is
   *  crushed like any other. */
  /** WHETHER A HEDGE, A STONE OR A ROD MAY BE SET ON A TILE (LS-458, 2026-09-26): one rule for the key's pulse and the
   *  press. The pulse asked only whether the ground was open, the press also refused a set-down that would seal the way
   *  through or land on a body -- so the key glowed 'set thorn' where pressing it did nothing, and the newcomer probe
   *  pressed it for seven minutes beside the home forge. */
  canSetDown(kind: number, tx: number, ty: number): boolean {   // (tx, ty): a WORLD tile (LS-511: and no longer refused on a screen's edge)
    const here = this.tileAt(tx, ty);
    if (here === undefined) return false;
    const open = here === T.GROUND || here === T.MOSS || here === T.TUFT || here === T.SAND || (kind === T.ROCK && here === T.SHALLOW);
    if (!open) return false;
    if ((kind === T.ROCK || kind === T.THORN) && here !== T.SHALLOW && !sealsNothing(this.systems.grid(this), tx, ty)) return false;   // ...except a dam, a stone laid in the shallows
    const rx = tx * TS, ry = ty * TS;
    const inTile = (b) => b && !b.dead && b.x < rx + TS && b.x + b.w > rx && b.y < ry + TS && b.y + b.h > ry;
    return !(inTile(this.player) || (this.enemies || []).some(inTile));
  }
  placeMaterial(sel) {
    const def = ITEMS[sel.kind];
    // A hull is a persistent world object, placed in front on water that fits its footprint.
    if (def && (def.place === 'RAFT' || def.place === 'BOAT')) {
      const p = this.player;
      const [ddx, ddy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir] || [0, 1];
      const wx = p.cx + ddx * 20, wy = p.cy + ddy * 18;
      const tt = this.tileKindAt(wx, wy);
      if (!(tt === T.WATER || tt === T.SEA || tt === T.SHALLOW) || !this.canPlaceHull(wx, wy)) { this.sfx('refuse'); return false; }
      // Keep previous hulls at their actual shores. Only boarding changes which hull is active.
      if (this.state.boat) (this.state.otherBoats ||= []).push(this.state.boat);
      this.state.boat = { kind: sel.kind, x: wx, y: wy, vx: 0, vy: 0, heading: { up: 6, down: 2, left: 4, right: 0 }[p.dir] ?? 0 };
      /* SET DOWN AT THE SHORE IT WAITS FOR YOU (2026-09-29, the playthrough: the first raft a player ever made rode the wind
       * four hundred pixels out to sea in a second and a half, before the body could step into it). A hull laid beside
       * land is drawn up and moored, as one stepped off beside land already is; boarding casts it off. */
      this.state.boat.moored = this.hullBesideLand(this.state.boat);
      removeItem(this.state, sel.kind, 1);
      this.state.setDown = (this.state.setDown || 0) + 1;
      for (let i = 0; i < 8; i++) this.puff(wx, wy, UI.foamLit, 3, 'splash');
      this.sfx('splash', wx); this.save();
      return true;
    }
    const kind = def && def.place === 'THORN' ? T.THORN : def && def.place === 'ROCK' ? T.ROCK : def && def.place === 'ROD' ? T.ROD : null;   // ...or a lightning rod (LS-381)
    if (kind === null) { this.sfx('refuse'); return false; }
    const p = this.player;
    const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir] || [0, 1];
    const tx = Math.floor((p.cx + dx * (p.w / 2 + 6)) / TS), ty = Math.floor((p.cy + dy * (p.h / 2 + 6)) / TS);
    if (!this.canSetDown(kind, tx, ty)) { this.sfx('refuse'); return false; }
    const here = this.tileAt(tx, ty);
    /* ...AND IT MAY NOT SEAL THE WAY THROUGH (2026-09-18). `pushBoulder` has asked this since LS-222 -- a boulder
     * cannot be pushed into the one gap in a wall -- and a hedge or a rock SET DOWN by hand skipped the question:
     * a stone laid in a one-tile corridor closed it, and nothing in the game can take a placed rock away again, so
     * a screen could be cut in two for the rest of the run by one press. The same question, asked once. */
    const rx = tx * TS, ry = ty * TS;
    this.setTileAt(tx, ty, kind);
    this.markPlacedAt(tx, ty, kind);
    /* AND THE TIDE LEAVES A DAM ALONE (2026-09-17). A stone is the one thing the placement rule lets you put on
     * water -- "and the shallows as well for stone, which is a dam" -- and it was the one placement the tide ate:
     * four seconds later the tile was shallow again, the stone was spent, and the picture still had the rock in it.
     * The tide skips the tiles a player has changed, and that record is `systems.marks`, so a dam goes in it. */
    if (this.systems && this.systems.mark) this.systems.mark(this, tx, ty, kind);
    this.state.setDown = (this.state.setDown || 0) + 1;   // the record counts what your own hand put in the world (LS-379)
    removeItem(this.state, sel.kind, 1);
    this.puff(rx + 8, ry + 8, kind === T.THORN ? UI.leaf : UI.linen, 6, kind === T.THORN ? 'leaf' : 'dust');
    this.sfx(kind === T.THORN ? 'crash_brush' : 'grind', rx + 8);
    this.save();
    return true;
  }

  /** A break, filed under the chunk that holds world tile (wtx, wty); see markBroken. */
  markBrokenAt(wtx: number, wty: number) {
    const c = this.chunkOf(wtx, wty); if (!c) return;
    const k = c.key, lk = c.lx + ',' + c.ly;
    if (!this.broken.has(k)) this.broken.set(k, new Set());
    this.broken.get(k).add(lk);
    const put = this.placed && this.placed.get(k);
    if (put) { put.delete(lk); if (!put.size) this.placed.delete(k); }
    if (this.systems) this.systems.unmark(wtx, wty);
    this.partsCache.delete(c.sx + ',' + c.sy);
  }
  /** What the player set down at world tile (wtx, wty), filed under its chunk; see markPlaced. */
  markPlacedAt(wtx: number, wty: number, kind: number) {
    const c = this.chunkOf(wtx, wty); if (!c) return;
    if (!this.placed.has(c.key)) this.placed.set(c.key, new Map());
    this.placed.get(c.key).set(c.lx + ',' + c.ly, kind);
    this.partsCache.delete(c.sx + ',' + c.sy);
  }
  markBroken(tx, ty) {
    const k = this.areaKey();
    if (!this.broken.has(k)) this.broken.set(k, new Set());
    this.broken.get(k).add(tx + ',' + ty);
    /* AND WHAT WAS PLACED THERE IS NO LONGER PLACED (2026-09-17). Both overlays are applied on every screen read,
     * the placed one second, and the save round-trips both: so a stone you set down and then crushed stood again on
     * every reload and gave its stone back, for ever. The world's own seeded approach thickets are `placed` THORN
     * too, which made them an endless supply of cuttings. A break is the last word on a tile. */
    const put = this.placed && this.placed.get(k);
    if (put) { put.delete(tx + ',' + ty); if (!put.size) this.placed.delete(k); }
    /* ...AND THE MARK GOES WITH IT (2026-09-18). `systems.marks` is the OTHER overlay -- the record that keeps the
     * tide off a dam and the regrowth off a tile a player changed -- and it is applied on every screen read too. So
     * the same farm this comment describes was still open through the other door: plant a hedge, cut it, walk off the
     * screen and back, and the hedge is standing again with another cutting in it, for as long as you care to walk.
     * A break is the last word on a tile, in both records. */
    const marked = this.systems && this.systems.marks && this.systems.marks.get(k);
    if (marked) { marked.delete(tx + ',' + ty); if (!marked.size) this.systems.marks.delete(k); }
    // ...and the pixels grown on that grid are now a picture of a wall that
    // is not there any more. `screenParts` explains why this is the only
    // place that has to say so.
    this.partsCache.delete(this.area.x + ',' + this.area.y);
  }

  areaSeed(a = this.area) {
    return mix(this.seedNum, a.x, a.y, 77);
  }

  /** Fill the current area, and REMEMBER WHICH SCREEN EACH ONE BELONGS TO.
   *
   * The overworld is one continuous map now, so "which screen is this creature
   * from" stopped being answerable from `this.area` the moment the window
   * could show two of them. `spawnEnemiesKeeping` needs it to tell a survivor
   * of the screen you just left from a fresh spawn of the screen you just
   * entered, and the tag is written here so that no call site can forget. */
  spawnEnemies() {
    this.enemies = this.fillChunk(this.area.x, this.area.y, false);
    return this.enemies;
  }

  /** CROSSING A SEAM IS NOT A REASON TO EMPTY THE SCREEN.
   *
   * `spawnEnemies` opens by throwing `this.enemies` away, which was right when
   * a screen was the whole world and you left it in a wipe. With a window that
   * scrolls, two screens are on the glass at once and stepping over the
   * boundary between them deleted every creature in sight -- including the one
   * you were fighting, standing in the middle of the frame. Reported from play:
   * "monsters are randomly despawning when they're clearly not off screen".
   *
   * So the ones still in the window come across with you. The `home` tag stops
   * that turning into the opposite bug: walk back over the seam and the screen
   * you left would otherwise be handed a second set of creatures on top of the
   * survivors you brought. A screen that still has any of its own alive is
   * already populated and is left alone. */

  /** Is this body inside the window the player is actually looking at? The pad
   *  is a tile: a creature half off the edge is still on screen. */
  inWindow(e, pad = TS) {
    if (!this.cam) return false;
    const c = this.camera();
    return e.x + e.w > c.x - pad && e.x < c.x + PW + pad
      && e.y + e.h > c.y - pad && e.y < c.y + PH + pad;
  }

  fillArea(a = this.area, out: any[] = this.enemies) {   /* a chunk's roster, into the list it is handed (LS-511: no screen is swapped in to make it) */
    if (a) this.seedForgeOfferings(a);   // the found forges' half-laid recipes, before any early return below (2026-09-12)
    this.spawnedHere = 0;
    const rng = new RNG(this.areaSeed(a));
    /* A BEAST'S HOARD COMES BACK WHEREVER IT FELL, INCLUDING HERE (2026-09-06). This lived further down, beside
     * the world's caches -- and the start screen RETURNS EARLY a few lines below, so a beast felled on or near
     * the spawn left a hoard that was recorded, saved, loaded and never put back -- `fillArea` never reached the
     * loop at all.
     *
     * HONESTLY: the bash hit this through its OWN drift, not through play. The probe let the area wander back to
     * the spawn during its settle frames, so the kill was recorded there. A beast cannot actually reach the first
     * three screens since batch ninety-five, and no lair sits inside them (42 lairs over 14 seeds, nearest 3), so
     * this is a latent gap rather than a bug anyone has hit. Fixed anyway: a hoard coming back should not depend
     * on WHICH screen it fell on, and the next change to the spawn or the leash would make it reachable without
     * anybody remembering this line was here. */
    for (const h of this.state.hoard || []) {
      if (!h || h.x !== a.x || h.y !== a.y) continue;
      if ((this.state.lootTaken || []).includes(h.key)) continue;
      const hk = h.x + ',' + h.y;
      if (this.items.some((i) => i.loot === h.key) || (this.itemStash.get(hk) || []).some((i) => i.loot === h.key)) continue;
      /* THE ORIGIN COMES OFF FIRST (2026-09-18). `dropItem` adds the screen's origin to what it is given -- that is
       * what `fitDrop` is for -- and the hoard record holds WORLD pixels, so a beast's piece was re-dropped a
       * whole screen away from where it fell: on the sow's lair at 13,3 the hide came back 2,080 px east. The loop
       * above has already proved this record belongs to the screen being filled, so its own origin is the one to
       * take off. */
      this.dropAt(h.kind, h.px, h.py, { loot: h.key });
    }
    const start = this.world.start;
    if (a.x === start.x && a.y === start.y) {
      // In `state`, not on the instance: it used to live only in memory
      // and never be serialised, so a reload re-granted the opening glade's
      // two glim. Six reloads measured twelve free glim against a whole
      // run's honest supply of about seventy-seven.
      if (!this.state.openingLoot) {
        this.state.openingLoot = true;
        // RELATIVE TO THE GLADE, which moves per seed now. At fixed
        // (5,7) and (10,4) the first of these landed in open water the
        // moment the anchor moved off [7, 5]. Both offsets sit inside the
        // glade's inner bands, which only ever write GROUND or MOSS, so
        // they are walkable whatever the noise does.
        const [gx, gy] = spawnTile(this.world.seedNum);
        // ...offset into the world, like everything else placed by tile in
        // the wilds. `spawnTile` names a tile of the START SCREEN, and the
        // start screen is some way into a map 128x96 tiles across.
        // through dropItem, so each glim is fitted to open ground (a fixed
        // offset from the spawn tile could sit inside a boulder)
        this.dropAt('glim', (a.x * PW) + (gx - 2) * TS + 2, (a.y * PH) + (gy + 2) * TS + 2);
        this.dropAt('glim', (a.x * PW) + (gx + 2) * TS + 2, (a.y * PH) + (gy + 1) * TS + 2);
      }
      return out;
    }
    /* WEAPONS LIE IN THE WORLD (LS-225 step 2). Each cache is on a screen
     * chosen by the seed and lies there until it is taken, quiet screen or
     * not, and never comes back: the key goes into `lootTaken`. */
    for (const l of this.worldLoot()) {
      if (l.x !== a.x || l.y !== a.y) continue;
      const key = l.x + ',' + l.y + ':' + l.kind;
      if ((this.state.lootTaken || []).includes(key)) continue;
      // once: a quiet screen's pre-bake dropped it into the stash and the arrival dropped it again (LS-230)
      const k2 = l.x + ',' + l.y;
      if (this.items.some((i) => i.loot === key) || (this.itemStash.get(k2) || []).some((i) => i.loot === key)) continue;
      /* `bed` IS THE PEARL'S FLAG AND IT MEANS UNDER WATER. The tusk needs
       * the same exact drop -- a spit is one tile wide and `fitDrop` would
       * carry the cache off it -- but it lies on DRY ground, and asking for
       * `bed` told test/checks/world.ts the tusk was on the seabed. Two
       * flags, and the check for each says what it means. */
      this.dropAt(l.kind, l.x * PW + l.px, l.y * PH + l.py, { loot: key, bulk: l.kind === 'pod' ? 8 : 1, exact: !!((l as any).bed || (l as any).exact) });   // a cache's place is its screen's own pixels
    }
    const qk = this.quietKey(a);
    const left = this.quiet.get(qk) || 0;
    /* THE GUARD IS NOT PART OF THE POPULATION, so it stands whether or not
     * this screen is a quiet one (LS-302). A cache guarded on some visits
     * and open on others is not a situation, it is a coin toss. */
    if (left > 0) { if (this.resumed) this.resumed = false; else this.quiet.set(qk, left - 1); this.guardTheSpit(a, out); return out; }   // a reload's own spawn is not a visit (2026-09-19)
    if (this.resumed) this.resumed = false;
    const tier = this.world.tier[a.y][a.x];
    const d = this.world.dist.get(a.x + ',' + a.y) ?? 3;
    /* THE FIRST ISLAND IS NOT A GAUNTLET (Vijay, 2026-09-13: "too many monsters initially / on the first island, it's
     * overwhelming"). `d` is the road's length from the glade, and on the island world the road winds: a screen two
     * steps from the glade read d 10-12 and rostered three or four, chargers among them (route-1's 10,10 stood
     * four chargers). Round the glade the count is by the crow's distance -- one within a screen, two within two,
     * three anywhere on the home island -- and nothing that charges stands within two screens of it. */
    const W0 = this.world.biome[0].length, isleOf = (x: number, y: number) => this.world.island ? this.world.island[y * W0 + x] : 0;
    const nearGlade = isleOf(a.x, a.y) === isleOf(this.world.start.x, this.world.start.y) ? Math.max(Math.abs(a.x - this.world.start.x), Math.abs(a.y - this.world.start.y)) : 99;
    /* A BEAST'S LAIR IS THE BEAST'S (2026-09-13). Measured on the old shell's lair with the fight probe: over one fight
     * the body took 2,046 touches from the shell and 824 from the lesser kin standing around it -- shellbacks and a
     * charger rostered as ordinary fauna -- while the shell's own slam landed seven. A one-life run fells the sow with
     * no deaths and then dies here, which is the attrition of a crowd rather than the fight the beast was designed as.
     * The lair screen keeps one lesser creature, not a pack; the approach screens are untouched, so the walk in is
     * still dangerous. */
    /* A BEAST'S LAIR IS THE BEAST'S, AND ONLY THE BEAST'S (2026-09-13). Cut to one lesser creature first, and the fight
     * probe then read 236 contacts from a single BRAMBLE against 175 from the sow -- a thing that does not move, sitting
     * where a body must circle. The approach screens keep their fauna, so the walk in is as dangerous as it was; the
     * lair itself holds the beast and nothing else while it lives. */
    const onLair = this.beastLairs().some((l) => l.x === a.x && l.y === a.y && !(this.state.beastsSlain || []).includes(l.kind));
    const count = onLair ? 0 : Math.min(nearGlade <= 1 ? 1 : nearGlade <= 2 ? 2 : nearGlade < 99 ? 3 : 4, 1 + rng.int(2) + Math.floor(d / 4));
    /* SIX COUNTRIES, SIX BESTIARIES.
     *
     * The pool depended on TIER alone: `sporeling, sporeling, wisp` at tier
     * 0 in every world, so the design lens measured 1,428 tier-0 spawns
     * across exactly TWO species — 68% sporeling, 32% wisp — and named it
     * the only difficulty axis that is free content. Round 24 gave the six
     * biomes a real obstacle grammar and round 27 gave the five bodies five
     * silhouettes; the monster table never heard about either. Six distinct
     * countries drawn differently, holding their rocks differently, and
     * spawning the same two animals.
     *
     * The bodies already exist and each one suits a ground. A bramble is a
     * thorn thing and belongs where thorns grow; a shellback takes the
     * floor and belongs on stone; a wisp drifts and belongs over water and
     * ash. So each biome names its own weighting, and TIER still decides
     * how much of the roster is unlocked — the curve is unchanged and only
     * its flavour is local.
     *
     * `sporeling` stays in every pool: it is the body a player learns the
     * game against, and a country with nothing familiar in it reads as a
     * different game rather than a different place.
     */
    const home = BESTIARY[this.world.biome[a.y][a.x]] || BESTIARY.heath;
    const pool = home[0].slice();
    if (tier >= 1) pool.push(...home[1]);
    if (tier >= 2) pool.push(...home[2]);
    /* WHAT ONLY COMES OUT AT NIGHT (LS-293): a kind marked `night` is in the country's pool only after dusk, so a
     * screen rostered by day and the same screen rostered by night are populated differently. */
    /* ...AND THE WHOLE QUESTION IS ASKED IN ONE PLACE (2026-09-18). This block and the night pack's `prowlPool`
     * had two copies of it, and the pack's copy was empty: it sent chargers onto the opening glade, day creatures
     * at night, and an eel onto dry land, where its own collision makes every tile a wall. `rosterKinds` is the
     * one rule now, and the pack asks it too. */
    const here = this.rosterKinds(pool, a);
    this.lastPool = here;   // the kinds this roster could have drawn, for a check to ask (the dice are the dice)
    for (let i = 0; i < count; i++) this.placeEnemy(rng.pick(here), rng, { hpBonus: tier + (this.night ? 1 : 0) }, a, out);
    this.guardTheSpit(a, out);   // something stands over the tusk on the spit (LS-302)
    this.bedThicket(a, out);   // and one of them may be asleep in the thicket (LS-302)
    return out;
  }

  /** The outdoor nest: one burst, then spent. A budget on a counter is a farm; a tile spent in the same frame is not. */
  updateWildNest() {
    if (this.mode !== 'play') return;
    /* NOT ON A LIVING BEAST'S LAIR (2026-09-14). The roster gives that screen no lesser creature and the seam holds the
     * neighbours back, but a NEST on it went on hatching three at a time into the middle of the fight: the bramble
     * mother's leg died with 109 points of SPORELING touch against eight of hers, after 2,232 swings. The lair is the
     * beast's; the brood waits until it is felled. */
    if (this.beastLairs && (this.beastLairs() || []).some((l: any) => l.x === this.area.x && l.y === this.area.y && !(this.state.beastsSlain || []).includes(l.kind))) return;
    /* THE NEST NEAREST THE BODY, within ten tiles (LS-511: it was the first nest on the screen underfoot, so one a step
     * across a seam brooded nothing and one at the far side of a screen brooded for a body that could not see it) */
    let nx = -1, ny = -1, nd = 11;
    const [ptx, pty] = [Math.floor(this.player.cx / TS), Math.floor(this.player.cy / TS)];
    for (const [k] of this.systems.nests) { const [x, y] = k.split(',').map(Number); const d = Math.max(Math.abs(x - ptx), Math.abs(y - pty)); if (d < nd && this.tileAt(x, y) === T.NEST) { nd = d; nx = x; ny = y; } }
    if (nx < 0) { this.nestTimer = 0; return; }   // ...and it is dropped on leaving the screen: a counter carried away primed the next nest you walked onto (2026-09-17)
    // a beat of warning: the puff and the sound land before the brood does
    this.nestTimer = (this.nestTimer || 0) + 1;
    if (this.nestTimer < 45) return;
    const rng = new RNG(mix(this.areaSeed(), 7717));
    const before = this.enemies.length;
    for (let i = 0; i < 3; i++) {
      this.placeEnemyNear(rng.chance(0.5) ? 'sporeling' : 'wisp', rng, nx, ny,
        // scaled the way every other wild spawn is: by the tier of the chunk it stands on
        { hpBonus: this.world.tier[Math.floor(ny / ROWS)][Math.floor(nx / COLS)] });
    }
    for (let i = before; i < this.enemies.length; i++) this.enemies[i].fromNest = true;
    // spent in the same frame it fires: there is no state to farm
    this.markBrokenAt(nx, ny);
    this.setTileAt(nx, ny, T.SCORCH);
    this.puff(nx * TS + 8, ny * TS + 8, UI.spore, 10);
    this.sfx('growl', nx * TS + 8);   // world pixels, as every other effect in this file is (2026-09-20): a screen-local one put the brood's warning on the world's origin screen
    this.nestTimer = 0;
    this.save();
  }

  /** Cutting a nest down. Not `markBroken` alone: the room has to stop being
   *  a nest room for the door bars to lift. */
  breakNestAt(tx, ty) {   // a world tile; markBrokenAt takes it out of the nests' ledger too (2026-09-19: a cut nest was re-planted on every reload)
    this.markBrokenAt(tx, ty);
    this.setTileAt(tx, ty, T.SCORCH);
    this.puff(tx * TS + 8, ty * TS + 8, UI.spore, 12, 'spark');
    this.sfx('grind', tx * TS + 8);
    this.shakeFor(4, 10);
    this.save();
  }

  /* ------------------------------------------------------------- tools
   *
   * THEY WERE NOT TOOLS, THEY WERE PERMISSIONS. Of the three things the whole
   * game is gated around, exactly one was a verb: the Rattle had a button, a
   * pouch and three uses. The Silt Lung made `solid()` return false for water
   * and the Emberwake added a rider to a swing you already had — collect
   * either and nothing you DO changes, only where you may stand. You could
   * finish the game having never made a decision with two of your three
   * tools.
   *
   * So: one slot, one button. The slot holds a tool, the button uses it, and
   * what the button does is the tool's business. */
  ownedTools() {
    return TOOLS.filter((t) => this.state[TOOL_FLAG[t]]);
  }

  /** The tool in the slot, or null before the first hollow. Kept honest on
   *  read: a save from before this existed, or one naming a tool you have not
   *  found, must not leave the slot pointing at nothing. */
  equipped() {
    const owned = this.ownedTools();
    if (!owned.length) return null;
    /* THE PICKED SLOT WINS (LS-374, 2026-09-10). With one action button the
     * thing in your hand decides what a press does, so the silt lung in the
     * picked slot IS the tool -- `state.tool` was a second, separate selector
     * with its own control, and the button read `pod` with the lung in hand.
     * Read here rather than synced on selection: the slot changes by the
     * hotbar keys, by the next control, by the bag and by a pickup, and a
     * value kept in step at four call sites is a value that falls out of
     * step. `state.tool` is still what the cycle sets and what a save keeps,
     * for when what you hold is not a charm at all. */
    const sel = bagSelected(this.state);
    const held = sel && ITEMS[sel.kind] ? ITEMS[sel.kind].tool : null;
    if (held && owned.includes(held)) return held;
    return owned.includes(this.state.tool) ? this.state.tool : owned[0];
  }

  /** Next tool in the slot. Only ever cycles what you actually carry, so the
   *  button never shows you something you cannot use. */
  cycleTool(dir = 1) {
    const owned = this.ownedTools();
    if (owned.length < 2) return false;
    const at = owned.indexOf(this.equipped());
    this.state.tool = owned[(at + dir + owned.length) % owned.length];
    this.sfx('drop');
    
    this.save();
    return true;
  }

  /** Can the use button do anything right now? The control says so rather
   *  than lying and then eating the press. */
  /** Is this kind a weapon? Asked by the input layer, which must not import the item table (2026-09-18). */
  itemIsWeapon(kind: string) { return !!(ITEMS[kind] && ITEMS[kind].weapon); }

  canUseTool() {
    if (this.paused || this.bagOpen || this.mode !== 'play') return false;
    if (this.stop > 0 || this.input.podPressed) return false;
    if (this.heldShot) return true;   // a thing in hand: the key throws it
    { const t = this.actTarget && this.actTarget(); if (t && t.kind === 'lift') return true; }
    /* ...OR LAYS IT ON THE FORGE'S ROCK (2026-09-18). The plate over the button already reads "lay" when a material
     * is held in front of a forge -- it is drawn from `actTarget()` -- and this function, one call away, knew nothing
     * about the forge: a plain material (a log, a sinew, a flint, an iron scrap: nineteen of the twenty-five the
     * fire takes) has no `eat`, no `gear` and no `place`, so it fell through to the tool slot and was refused. Both
     * roads into `useTool` gate on this, so on a fresh run nineteen of the recipes could not be begun at all unless
     * the player happened to be carrying a charm whose own verb answered here. One question, one answer. */
    { const t = this.actTarget && this.actTarget(); if (t && t.kind === 'forge') {
      if (t.action !== 'take') return true;
      const slots = bagOf(this.state).slots;
      return this.items.some(it => this.takeableAtForge(it, t.tx, t.ty) && ITEMS[it.kind]?.cap > 0
        && slots.some(s => !s || (s.kind === it.kind && s.n < ITEMS[it.kind].cap)));
    } }
    const sel = bagSelected(this.state);
    if (sel && (sel.kind === 'glim' || (ITEMS[sel.kind] && ITEMS[sel.kind].eat))) return true;   // the button eats or trades what is picked (LS-229): it answered for the charm and refused sap
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].weapon && ITEMS[sel.kind].dur && sel.dur !== undefined && sel.dur < ITEMS[sel.kind].dur) return true;   // ...or whets it (LS-238)
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].gear) return true;   // ...or wears it (LS-239)
    /* ...OR SETS IT DOWN (LS-379, found 2026-09-11 by the real-input builder).
     * A material in hand fell through to the tool slot's rules, so the one
     * action refused cuttings and stone from a key or the phone's button, and
     * nothing could be set down by a player at all; the placement check had
     * set the input flag by hand. `placeMaterial` still refuses on ground
     * that will not take it. */
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].place) return true;   // a material that can be set down; a whetstone is a material with no place, and is used on the blade, not pressed
    const tool = this.equipped();
    if (tool === 'pods') return this.state.pods > 0 && this.pods.length < 2;
    if (tool === 'tide') {
      // surfacing is always allowed; going under needs deep water; and on dry
      // land the lung can still do something if it is holding water
      return !!this.player.submerged || this.isSwimAt(this.player.cx, this.player.cy)
        || !!this.state.lungFull || !!(this.systems && this.systems.canEbb(this));
    }
    // ...and the ember always has something to say, even in water: hissing out
    // IS the answer to "what happens if I try", and a button that refuses
    // silently teaches nothing.
    if (tool === 'ember') return (this.state.emberCool || 0) <= 0 || this.inWater();
    return false;
  }

  /** Use it. */
  /** Standing in water, or under it. */
  inWater() {
    const p = this.player;
    return !!p.submerged || p.swimming || p.wading
      || this.isSwimAt(p.cx, p.cy) || this.tileKindAt(p.cx, p.cy) === T.SHALLOW;
  }

  /* ONE BUTTON, MORE THAN ONE VERB.
   *
   * Each charm did exactly one thing, everywhere, which made it a permission
   * rather than a tool: the question "what does this do" had one answer and
   * you never asked it again. Each now reads the situation, and the second
   * answer is the interesting one:
   *
   *   the Rattle    places a pod — and, with one already out, sets it off.
   *                 That is what makes the launch aimable and lets you leave
   *                 a pod behind you and choose the moment.
   *   the Silt Lung dives, in water. On land it sprays what it last breathed:
   *                 it puts out a fire and shoves what it hits. The lung holds
   *                 one lungful, and going under is how you fill it.
   *   the Emberwake sets an ember down — unless you are standing in water, in
   *                 which case it hisses out and tells you so, or unless you
   *                 are facing an unlit brazier, which it lights directly
   *                 rather than waiting for the fire to find its way there.
   */
  /** The world facts that decide which verb a tool runs. Read by `useTool`
   *  and by the button's label, so the two cannot disagree about what a press
   *  is going to do — which they did, in five of eight situations. */
  toolContext() {
    const p = this.player;
    return {
      livePod: this.pods.some((q) => !q.dead && q.fuse > 2),
      brazier: !!this.besideLitBrazier(),   // five glim here is pitch, and the label must say so (round 53's lore lens)
      submerged: !!(p && p.submerged),
      inDeep: !!(p && this.isSwimAt(p.cx, p.cy)),
      lungFull: !!this.state.lungFull,
      canEbb: !!(this.systems && this.systems.canEbb(this)),
      inWater: this.inWater(),
      brazierAhead: !!this.lightBrazierAhead(true),
      emberCool: this.state.emberCool || 0,
    };
  }

  /** ONE ACTION BUTTON, AND IT DOES WHAT YOU ARE HOLDING (LS-374, 2026-09-10;
   *  Vijay: "we don't need a secondary action button the primary one should
   *  just use whatever you have equipped line in minecraft").
   *
   *  There were two: one swung the weapon and one used the picked thing, and
   *  on a keyboard that was Z and X. Now the picked slot decides. A weapon --
   *  or an empty hand, which is a weapon in this game -- swings. Anything else
   *  is used: a charm, a sap, a piece of gear, a purse of glim.
   *
   *  WHAT THIS COST, and where it went: with a weapon picked, `useTool` meant
   *  WHET. One button cannot both swing a blade and sharpen it, and a blade
   *  that is worn must still swing -- that is what holding it is for. So
   *  whetting and mending moved to the bag's HAND box, which is the picture of
   *  the thing in your hand, in the screen where you look after your kit. */
  /** LOOKING AFTER WHAT IS IN YOUR HAND (LS-374): the whet and the mend. It was
   *  the one thing the second action button did with a weapon picked, and one
   *  button cannot both swing a blade and sharpen it. It has two homes now,
   *  and they are the same call: the bag's HAND box, which is the picture of
   *  the thing you are holding, and X on a keyboard -- not a second action,
   *  but the maintenance the box does, for hands that are not on the glass. */
  lookAfterHeld() {
    const held = bagSelected(this.state);
    const d = held && ITEMS[held.kind];
    if (!d || !d.dur) { this.sfx('refuse'); return false; }
    const r = held.dur !== undefined && held.dur <= 0 ? mend(this.state) : whet(this.state);
    if (r === 'ok') { this.sfx('grind', this.player.x + 8); this.save(); return true; }
    this.sfx('refuse');
    return false;
  }

  /** A lit brazier underfoot or on any side (round 52's economy): where glim buys pitch. */
  besideLitBrazier() {
    if (!this.player) return false;
    const tx = Math.floor(this.player.cx / TS), ty = Math.floor(this.player.cy / TS);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (this.tileAt(tx + dx, ty + dy) === T.BRAZIER_LIT) return true;
    return false;
  }
  pressIsUse() {
    const sel = bagSelected(this.state);
    if (!sel) return false;                       // bare hands swing
    const d = ITEMS[sel.kind];
    return !!d && !d.weapon;
  }

  /** THE PITCH POT'S ONE COUNTER (LS-335, fourth batch).
   *
   *  Two things a blow can set alight and they are reached down different
   *  paths: a TILE answers `emit(w, 'burn', ...)` from the struck-terrain
   *  branch, and a hedge-CREATURE is lit by the torch's own line further up.
   *  The coating was charged in the first only, so a pitched axe lit a bramble
   *  for 239 frames and the pot still said three blows left -- measured. One
   *  method now, asked by both, and it answers whether the coating is on as
   *  well as spending it, so the two readers cannot disagree about that
   *  either. A blow is charged once per swing id: a swing that touches two
   *  things costs one, and a swing through the air costs nothing. */
  spendPitch(sid: any): boolean {
    if (!((this.state.pitch || 0) > 0)) return false;
    if (this.state.pitchSwing !== sid) {
      this.state.pitchSwing = sid;
      this.state.pitch = Math.max(0, (this.state.pitch || 0) - 1);
    }
    return true;
  }
  /** The stonemilk's mirror of the pitch (LS-335, 2026-09-16): three blows that CRUSH whatever is in hand, charged once a swing. */
  spendStone(sid: any): boolean {
    if (!((this.state.stone || 0) > 0)) return false;
    if (this.state.stoneSwing !== sid) {
      this.state.stoneSwing = sid;
      this.state.stone = Math.max(0, (this.state.stone || 0) - 1);
    }
    return true;
  }
  /** The bearing from the hull to the nearest island still holding a living beast, wind ignored -- what a windfall blows toward. */
  wantedBearing(): number[] | null {
    const b = this.state.boat; if (!b || !this.world || !this.world.island) return null;
    const W = WORLD_W, H = WORLD_H, isle = (sx: number, sy: number) => this.world.island[sy * W + sx];
    const hx = Math.floor(b.x / (COLS * TS)), hy = Math.floor(b.y / (ROWS * TS));
    const want = new Set<number>();
    for (const r of this.state.beasts || []) { if (!r || r.alive === false || !r.home) continue; const id = isle(r.home[0], r.home[1]); if (id >= 0) want.add(id); }
    const hereIsle = hx >= 0 && hy >= 0 && hx < W && hy < H ? isle(hx, hy) : -1;
    let best: any = null;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const id = isle(x, y); if (id < 0 || !want.has(id) || (hereIsle >= 0 && id === hereIsle)) continue;
      if (Math.abs(x - hx) <= 1 && Math.abs(y - hy) <= 1) continue;
      const gx = (x * COLS + COLS / 2) * TS, gy = (y * ROWS + ROWS / 2) * TS;
      const dx = gx - b.x, dy = gy - b.y, len = Math.hypot(dx, dy) || 1;
      if (!best || len < best.len) best = { len, dir: [dx / len, dy / len] };
    }
    return best ? best.dir : null;
  }

  /* ===== LIFT AND THROW (2026-09-27, docs/DIRECTION.md: verbs over items) =====
   * Facing a loose rock, a fire or open water with nothing else for the key to do, the key lifts it -- the rock off
   * its bed, a brand out of the fire, a double handful of water -- and it rides over the head. Either press throws it
   * the way you face. What it meets decides what it does: a rock strikes and lies where it lands (in water a stepping
   * stone), a brand sets alight what it strikes or comes down on, water douses and soaks and leaves the ground wet
   * enough to bog a charging beast. The sow's other answers are built from these. */
  heldShot: any = null;
  /** The key's target AS A PULSE: lift and throw pulse until the hand has done each twice, then only the press knows
   *  them -- a light that teaches a verb and then nags for ever made the newcomer probe lift and throw water all day. */
  actPulse(): { tx: number; ty: number; kind: string; action?: 'take' } | null {
    const t = this.actTarget(); if (!t) return null;
    if (t.kind === 'lift' && (this.state.lifted || 0) >= 2) return null;
    if (t.kind === 'throw' && (this.state.thrown || 0) >= 2) return null;
    return t;
  }
  liftAhead(): { tx: number; ty: number; what: string } | null {
    if (!this.area || this.aboard || this.heldShot || !this.player) return null;
    const p = this.player; if (p.wading || p.swimming || p.submerged) return null;
    const [dx, dy] = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[p.dir] || [0, 1];
    const tx = Math.floor(p.cx / TS) + dx, ty = Math.floor(p.cy / TS) + dy;   // the next tile over from the one the body stands in (a world tile, LS-511)
    const t = this.tileAt(tx, ty); if (t === undefined) return null;
    const besideFire = (x: number, y: number) => [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]].some(([ax, ay]) => { const v = this.tileAt(x + ax, y + ay); return v === T.BRAZIER || v === T.BRAZIER_LIT; });
    if (t === T.ROCK && !besideFire(tx, ty)) return { tx, ty, what: 'rock' };   // a forge's rock stays where the forge needs it
    if (t === T.BRAZIER_LIT || (this.systems && this.systems.burning && this.systems.burning(tx, ty))) return { tx, ty, what: 'brand' };
    if (t === T.WATER || t === T.SHALLOW || t === T.SEA) return { tx, ty, what: 'water' };
    return null;
  }
  lift(t: { tx: number; ty: number; what: string }): boolean {
    const p = this.player, wx = t.tx * TS + 8, wy = t.ty * TS + 8;
    if (t.what === 'rock') { const n = this.items.length; this.breakTileAt(t.tx, t.ty); this.items.splice(n); this.puff(wx, wy, UI.linen, 5, 'dust'); this.sfx('grind', wx); }
    else if (t.what === 'water') { this.puff(wx, wy, UI.foamLit, 4, 'splash'); this.sfx('splash', wx); }
    else { this.puff(wx, wy, UI.flame, 4, 'ember'); this.sfx('flame', wx); }
    const sh: any = new Shot(t.what, p.cx - 4, p.y - 12, 0, 0, true); sh.carried = true; sh.life = t.what === 'brand' ? 1200 : 1e9;
    clampBox(sh); this.shots.push(sh); this.heldShot = sh;
    this.state.lifted = (this.state.lifted || 0) + 1;
    return true;
  }
  /** Each frame: the thing in hand rides over the head; a brand burns down, and water or rain puts it out. */
  carryHeld() {
    const sh = this.heldShot; if (!sh) return;
    const p = this.player;
    if (sh.dead) { this.heldShot = null; return; }
    if (!this.shots.includes(sh)) this.shots.push(sh);   // a screen change clears the shots; what is in hand comes along
    sh.x = p.cx - 4; sh.y = p.y - 13 + ((this.frame >> 3) & 1); sh.anim = (sh.anim || 0) + 1; clampBox(sh);
    if (sh.kind === 'brand') {
      const rain = this.systems && this.systems.rain ? this.systems.rain(this) : 0;
      if (--sh.life <= 0 || p.wading || p.swimming || p.submerged || rain > 0.5) { sh.dead = true; this.heldShot = null; for (let i = 0; i < 4; i++) this.puff(sh.cx, sh.cy, UI.linen, 3, 'smoke'); this.sfx('flame', sh.cx); return; }
      if (sh.life % 5 === 0) this.puff(sh.cx + ((this.frame >> 2) % 3) - 1, sh.cy - 4, UI.flame, 1, 'ember');
    }
    if ((p.swimming || p.submerged) && sh.kind === 'rock') { this.throwHeld(); }   // nobody swims with a boulder: it goes
  }
  throwHeld(): boolean {
    const sh = this.heldShot; if (!sh) return false;
    const p = this.player, [dx, dy] = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[p.dir] || [0, 1];
    this.heldShot = null;
    const sp = sh.kind === 'water' ? 3 : 4;
    sh.carried = false; sh.vx = dx * sp; sh.vy = dy * sp; sh.life = sh.kind === 'water' ? 14 : 22;
    sh.x = p.cx - 4 + dx * 8; sh.y = p.cy - 6 + dy * 8; clampBox(sh); sh.throwId = 'thrown-' + this.runFrames;
    if (!this.shots.includes(sh)) this.shots.push(sh);
    this.sfx('swing', p.cx);
    this.state.thrown = (this.state.thrown || 0) + 1;
    return true;
  }
  /** A thrown thing strikes a body. */
  thrownHits(sh: any, e: any) {
    const beast = !!(e.def && e.def.beast), burns = !!traitsOf(e.type).burns;
    if (sh.kind === 'rock') {
      e.hurt(this, 2, sh.cx - sh.vx * 3, sh.cy - sh.vy * 3, sh.throwId);
      if (!beast && !e.dead) e.stun = Math.max(e.stun || 0, 40);
      this.shakeFor(2, 6); this.sfx('crash_stone', sh.cx); this.puff(sh.cx, sh.cy, UI.linen, 5, 'dust');
    } else if (sh.kind === 'brand') {
      if (beast || burns) { e.alight = Math.max(e.alight || 0, beast ? 240 : 180); e.lastCause = 'fire'; }
      else e.hurt(this, 1, sh.cx, sh.cy, sh.throwId);
      for (let i = 0; i < 6; i++) this.puff(e.cx, e.cy, UI.flame, 3, 'ember'); this.sfx('flame', sh.cx);
    } else {
      if (e.alight > 0) { e.alight = 0; for (let i = 0; i < 4; i++) this.puff(e.cx, e.cy - 4, UI.linen, 3, 'smoke'); }
      afflict(e, 'Soaked', 600);
      if (traitsOf(e.type).eatsFire) e.hurt(this, 2, sh.cx, sh.cy, sh.throwId);   // water is what a fire-eater is afraid of
      this.puff(sh.cx, sh.cy, UI.foamLit, 6, 'splash'); this.sfx('splash', sh.cx);
    }
  }
  /** ...and comes down: `hitBody` when it struck one, where that body stands. */
  thrownLands(sh: any, hitBody: any) {
    const tx = Math.floor(sh.cx / TS), ty = Math.floor(sh.cy / TS);   // world tiles (LS-511)
    const t = this.tileAt(tx, ty);
    if (t === undefined) return;
    const wx = tx * TS + 8, wy = ty * TS + 8;
    if (sh.kind === 'rock') {
      if (t === T.WATER || t === T.SEA) {   // a stone thrown in the deep is a stepping stone, as a boulder pushed there is
        this.setTileAt(tx, ty, T.SHALLOW); this.markPlacedAt(tx, ty, T.SHALLOW); this.systems.mark(this, tx, ty, T.SHALLOW);
        for (let i = 0; i < 6; i++) this.puff(wx, wy, UI.foamLit, 3, 'splash'); this.sfx('splash', wx);
      } else if (!hitBody && this.canSetDown(T.ROCK, tx, ty) && sealsNothing(this.systems.grid(this), tx, ty)) {
        this.setTileAt(tx, ty, T.ROCK); this.markPlacedAt(tx, ty, T.ROCK); this.systems.mark(this, tx, ty, T.ROCK);
        this.puff(wx, wy, UI.linen, 6, 'dust'); this.sfx('grind', wx); this.shakeFor(1, 4);
      } else this.dropAt('stone', sh.x, sh.y, { dropped: true });   // on a body, or where a rock may not stand: a stone to pick up (it was dropped a screen's origin away from where it fell)
    } else if (sh.kind === 'brand') {
      if (isWet(t)) { for (let i = 0; i < 4; i++) this.puff(wx, wy, UI.linen, 3, 'smoke'); this.sfx('splash', wx); return; }
      { const ntx = tx + Math.sign(sh.vx || 0), nty = ty + Math.sign(sh.vy || 0);   // flung against a hedge, the hedge takes it, not the ground at its foot
        if ((ntx !== tx || nty !== ty) && this.systems && this.systems.ignite) this.systems.ignite(this, ntx, nty, true); }
      if (this.systems && this.systems.ignite && !this.systems.ignite(this, tx, ty, true) && this.systems.flare && !(this.systems.burning && this.systems.burning(tx, ty))) this.systems.flare(this, tx, ty, BRAND_FUEL);   // what will burn catches; bare ground keeps the brand burning where it lies a moment
      this.puff(wx, wy, UI.flame, 5, 'ember');
    } else {   // water: the tile and its eight neighbours take it, as a shower of rain would; a fire there goes out
      const f = this.systems && this.systems.fieldsFor ? this.systems.fieldsFor(this) : null;
      for (let y = ty - 1; y <= ty + 1; y++) for (let x = tx - 1; x <= tx + 1; x++) {
        if (f && f.inside(x, y)) f.rainWet[f.idx(x, y)] = Math.min(1, f.rainWet[f.idx(x, y)] + (x === tx && y === ty ? 0.95 : 0.7));
        if (this.systems && this.systems.burning && this.systems.burning(x, y) && this.systems.smother) this.systems.smother(this, x, y);
      }
      for (let i = 0; i < 8; i++) this.puff(wx + (i % 3 - 1) * 6, wy + ((i >> 1) % 3 - 1) * 6, UI.foamLit, 2, 'splash'); this.sfx('splash', wx);
      if (!hitBody && this.player && Math.hypot(this.player.cx - wx, this.player.cy - wy) < 20 && this.player.lit > 0) this.player.lit = 0;
    }
  }
  useTool() {
    /* EATING (LS-224/225): with sap picked on the hotbar the tool button eats
     * one -- two vigor back, and a wound or a poison cleared. Sap used to heal
     * on pickup; in the bag it is a thing you choose to use. */
    if (this.heldShot) return this.throwHeld();
    { const l = this.actTarget(); if (l && l.kind === 'lift') { const a = this.liftAhead(); if (a) return this.lift(a); } }
    const sel = bagSelected(this.state);
    if (sel && this.offerToForge(sel)) return true;   // facing a forge's rock, a material is laid on it (LS-384)
    if (sel && (sel.kind === 'meat' || sel.kind === 'cookedmeat') && this.feedAhead(sel)) return true;   // facing a creature, meat is offered, not eaten (LS-521)
    /* ...AND THE PIECE IT WANTS, WHATEVER IS IN THE HAND (2026-09-29, the playthrough: with the raft's last cutting in the
     * bag and the sickle in the hand, the button at the rock did nothing, and nothing said the cutting had to be picked
     * first). Facing a rock whose pile is one piece short, the button lays that piece from the bag and the hand keeps
     * what it held. Only the piece the pile names: a rock is not a bin for whatever you carry. */
    { const a = this.forgeAhead(); const w = a && this.forgeWants().find((q) => q.tx === a.tx && q.ty === a.ty);
      if (w) { const bag = bagOf(this.state), i = bag.slots.findIndex((q: any) => q && q.kind === w.kind && q.n > 0);
        if (i >= 0 && bag.slots[i] !== sel) { const was = bag.selected; bag.selected = i; const ok = this.offerToForge(bag.slots[i]); bag.selected = was; syncFlags(this.state); if (ok) return true; } } }
    const target = this.actTarget();
    if (target?.action === 'take') return this.takeForgeOfferings(target.tx, target.ty);
    /* A DENTED PIECE IS MENDED BEFORE IT IS WORN (2026-09-09, LS-335). Armour wears out -- a shell
     * plate has thirty turns -- and nothing put them back: this branch simply wore it again, so a
     * plate dented through was dead for the rest of the run. A rivet mends it, or three glim, the
     * same two ways a blade has. Sound gear still goes on, which is what this branch was for. */
    /* DENTED THROUGH, NOT MERELY USED -- AND IT NEVER SWALLOWS THE PRESS (2026-09-09, found by codex
     * the hour after the rivet shipped).
     *
     * This read `dur < max`, so a perfectly good 29-of-30 plate went to the repair branch, and with
     * nothing to pay with the press was REFUSED before `wear` ever ran: a plate you had used once
     * could not be put on at all. Measured at 29/30: the press returned false and nothing was worn.
     * That is a worse fault than the hole the rivet was added to fill.
     *
     * A piece is mended only when it is dented THROUGH, and a mend that cannot be paid for falls
     * through to the wear branch below rather than returning -- so the press always does something,
     * and the something it does is what it did before the rivet existed. */
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].gear && ITEMS[sel.kind].dur && sel.dur !== undefined && sel.dur <= 0) {
      const r = mend(this.state);
      if (r === 'ok') { this.sfx('grind', this.player.x + 8); this.save(); return true; }
    }
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].gear) {   // X with a piece of gear picked wears it (LS-239); what was worn there comes into the picked slot
      const d = ITEMS[sel.kind]; const was = wear(this.state);
      this.sfx('bag'); this.puff(this.player.cx, this.player.cy - 4, UI.gold, 6, 'spark'); this.player.wieldShow = 18;
      this.save(); return true;
    }
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].weapon && ITEMS[sel.kind].dur && sel.dur !== undefined && sel.dur < ITEMS[sel.kind].dur) {   // X with a worn weapon picked whets it (LS-238): three glim, the edge back
      const r = whet(this.state);
      if (r === 'glim') { this.sfx('refuse'); return false; }
      if (r === 'ok') { this.sfx('grind', this.player.x + 8); this.save(); return true; }   // a stone on an edge, not a chip (the music lens, round 49)
    }
    const glimCost = (n: number) => Math.max(1, Math.round(n * (wearing(this.state, 'miser') ? 0.8 : 1)));   // the miser charm (LS-335): a fifth off everything glim buys
    if (sel && sel.kind === 'glim' && this.besideLitBrazier() && sel.n >= glimCost(5)) {   // ...or five of it, beside a lit brazier, is a pot of pitch (round 52's design lens): the first thing glim buys before ten
      /* THE POT IS HANDED OVER BEFORE IT IS PAID FOR (2026-09-18). `addItem` returns what did not fit, and that was
       * thrown away: with a full bag the press took five glim and gave nothing at all. Every other hand-over in the
       * game checks that return. */
      if (addItem(this.state, 'pitch', 1) !== 0) { this.sfx('refuse'); return false; }
      removeItem(this.state, 'glim', glimCost(5)); this.sfx('buy'); this.save(); return true;
    }
    if (sel && sel.kind === 'glim') {   // glim's one use: ten of it, pressed, is one more vigor (the design lens, 2026-09-05: sap already drops every few kills, so a bought sap was worth forty-five of them; what the ground never drops is life)
      if (sel.n < glimCost(10)) { this.sfx('refuse'); return false; }   // the register of the other toasts (LS-232): a thing seen, not a receipt
      if (this.state.maxHp >= MAX_HP_CAP) { this.sfx('refuse'); return false; }
      removeItem(this.state, 'glim', glimCost(10)); this.state.maxHp += 1; this.state.hp = Math.min(this.state.maxHp, this.state.hp + 1); this.sfx('sap'); return true;
    }
    /* WHAT A CONSUMABLE DOES, AND WHEN IT REFUSES (LS-335, fourth batch).
     * `eat` used to be one effect -- sap's -- so the kind was never read and
     * the "would this do anything" rule was hardcoded to hp and two
     * afflictions. Four more kinds each want their own answer to both
     * questions, and a consumable that spends itself for nothing is worse
     * than one that says no. */
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].place) return this.placeMaterial(sel);   // set it down (LS-379)
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].eat && ITEMS[sel.kind].eat !== 'heal') {
      const st = this.state;
      const on = activeNames(this.player);
      const effect = ITEMS[sel.kind].eat;
      const gustTo = effect === 'gust' ? this.wantedBearing() : null;
      const idle = this.eatIdle(sel);
      if (idle) { this.sfx('refuse'); return false; }
      removeItem(st, sel.kind, 1);
      if (effect === 'cure') for (const n of on) cure(this.player, n);
      if (effect === 'pitch') st.pitch = 3;                       // three blows, counted where a blow touches something
      if (effect === 'brine') st.lungFull = true;
      if (effect === 'draught') st.draught = this.runFrames + 1200;   // twenty seconds at sixty
      if (effect === 'haste') st.haste = this.runFrames + 1200;       // twenty seconds a sixth faster on land (LS-335)
      if (effect === 'stone') st.stone = 3;                           // three crushing blows, spent as the pitch's are
      if (effect === 'sight') st.sight = this.runFrames + 1200;       // twenty seconds of a light that is not a fire
      if (effect === 'mend') { cure(this.player, 'Soaked'); cure(this.player, 'Chilled'); st.hp = Math.min(st.maxHp, st.hp + 1); }   // the moss poultice (LS-335)
      if (effect === 'feast') st.hp = Math.min(st.maxHp, st.hp + 2);                       // honeycomb (LS-335)
      if (effect === 'feast3') st.hp = Math.min(st.maxHp, st.hp + 3);                      // cooked meat (LS-519)
      if (effect === 'raw') { st.hp = Math.min(st.maxHp, st.hp + 1); if ((mix(this.seedNum, this.runFrames, 5190) >>> 0) % 3 === 0) afflict(this.player, 'Poisoned', 600); }   // raw meat: one time in three it turns (LS-519)
      if (effect === 'warm') { cure(this.player, 'Chilled'); st.warm = this.runFrames + 1200; }   // salt cake: twenty seconds the cold cannot chill
      if (effect === 'dry') { cure(this.player, 'Soaked'); st.dry = this.runFrames + 1200; }      // bitterleaf: twenty seconds the rain cannot soak
      if (effect === 'kindle') this.player.lit = Math.max(this.player.lit || 0, LIT_FRAMES);     // ember seed: the torch lit with no fire near
      if (effect === 'grow') { st.maxHp += 1; st.hp = Math.min(st.maxHp, st.hp + 1); }           // heart draught (LS-335): one vigor for good
      if (effect === 'stone10') st.stone = 10;                                                    // tusk broth: ten crushing blows
      if (effect === 'mendall') st.hp = st.maxHp;                                                 // shell soup
      if (effect === 'gust' && gustTo) st.gust = { dir: gustTo, until: this.runFrames + 1200 };   // the wind held toward the beast
      this.puff(this.player.cx, this.player.cy - 6, effect === 'pitch' ? UI.ember : UI.foamLit, 5, 'spark');
      this.sfx('eat'); this.save(); return true;
    }
    if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].eat) {
      const st = this.state;
      /* THE REFUSAL READS THE SAME LIST THE CURE DOES (2026-09-18). Sap cures four things -- Bleeding, Poisoned,
       * Chilled and Scorched, as the docs' own table says of each -- and the refusal knew two of them: at full vigor
       * a Scorched body, which is losing a point every six seconds, was told no by the one item that stops it. */
      const AILS = ['Bleeding', 'Poisoned', 'Chilled', 'Scorched'];
      if (st.hp >= st.maxHp && !AILS.some((n) => afflicted(this.player, n))) { this.sfx('refuse'); return false; }
      removeItem(st, sel.kind, 1);
      st.hp = Math.min(st.maxHp, st.hp + (wearing(st, 'spoon') ? 3 : 2)); /* the horn spoon (LS-335) */ cure(this.player, 'Bleeding'); cure(this.player, 'Poisoned'); cure(this.player, 'Chilled'); cure(this.player, 'Scorched');   // sap answers the cold and the heat too (LS-240)
      this.sfx('eat'); return true;
    }
    const tool = this.equipped();
    // ONE DECISION, TWO READERS. `src/labels.ts` names the verb; this runs it.
    const verb = toolVerb(tool, this.toolContext());
    if (tool === 'pods') {
      if (verb === 'setoff') {
        return this.setOffPod(this.pods.find((q) => !q.dead && q.fuse > 2));
      }
      return this.placePod();
    }
    if (tool === 'tide') {
      if (verb === 'rise' || verb === 'dive') return this.toggleSubmerge();
      if (verb === 'ebb') { this.systems.callEbb(this); this.sfx('ebb', this.player.x + 8); return true; }   // the tide's own cue (LS-233)
      if (verb === 'idle') return undefined;
      return this.spray();
    }
    // ...AND THE MODEL ENFORCES ITS OWN COOLDOWN. `dropEmber` set `emberCool`
    // and never read it; only `main.js`'s `canPod()` refused, which is the
    // input layer standing in for the rule — the exact drift `canSwing`/
    // `canPod` were moved into this file to prevent. Measured: 51 embers in
    // 120 frames against a 150-frame cooldown.
    if (tool === 'ember') {
      // water first: it costs nothing and it is the answer to the question
      if (verb === 'douse') {
        /* `UI.foamLit`, NOT `UI.foam` — which does not exist and never did.
         * `foam` is a BIOME key; `UI` has `foamLit`. This passed `undefined`
         * as a fill colour, and a canvas given one keeps whatever was set
         * last, so the steam came out in the colour of whatever drew before
         * it. Found by the type checker on the first clean pass, which is the
         * fifth time this project has shipped a colour that is not there. */
        this.puff(this.player.cx, this.player.cy - 4, UI.foamLit, 8, 'smoke');
        /* was: 'it hisses out' — the puff of steam above the water IS the message */
        this.sfx('drop');
        return undefined;
      }
      if (verb === 'cooling') return undefined;
      if (verb === 'light') return this.lightBrazierAhead();
      return this.dropEmber();
    }
    return undefined;
  }

  /** The flora key for this area: its biome. */
  floraKey(area = this.area, palKey = this.palKey) {
    return palKey;
  }

  /** The tile kind at a pixel position, or undefined off the map. */
  tileKindAt(px, py) {
    // by world position, like isWadeAt/isSwimAt: the screen-local index was
    // off the grid on every screen but the first
    return this.tileAtPx(px, py);
  }

  /** The tile the player is facing, one step ahead. */
  aheadTile() {
    const p = this.player;
    const at = {
      up: [p.cx, p.y - 4], down: [p.cx, p.y + p.h + 4],
      left: [p.x - 4, p.cy], right: [p.x + p.w + 4, p.cy],
    }[p.dir];
    // a WORLD tile (LS-511): the screen-local one was null a step across a seam, and nothing there could be acted on
    const tx = Math.floor(at[0] / TS), ty = Math.floor(at[1] / TS);
    const kind = this.tileAt(tx, ty);
    if (kind === undefined) return null;
    return { tx, ty, kind };
  }

  /** An ember held to a brazier lights it, rather than being put on the floor
   *  and left to find its own way there. */
  /** @param {boolean} ask  look only — the label has to know whether there is
   *  a brazier in front of you without lighting it to find out. */
  lightBrazierAhead(ask = false) {
    const a = this.aheadTile();
    if (!a || a.kind !== T.BRAZIER) return null;
    if (ask) return true;
    this.setTileAt(a.tx, a.ty, T.BRAZIER_LIT);
    this.puff(a.tx * TS + 8, a.ty * TS + 8, UI.flame, 8, 'ember');
    this.state.emberCool = EMBER_COOL;
    this.sfx('flame', a.tx * TS + 8);
    /* was: 'the brazier takes it' — the brazier lights */
    this.onBrazierLit?.(a.tx, a.ty);
    return true;
  }

  /** Blow a pod you have already placed, instead of placing another. */
  setOffPod(pod) {
    pod.fuse = Math.min(pod.fuse, 2);
    this.sfx('drop');
    return true;
  }

  /** THE LUNG HOLDS WHAT IT LAST BREATHED. On dry land it sprays that lungful
   *  in the direction you are facing: it puts out fire — which is the only way
   *  to undo a fire you set badly — and shoves what it hits. One lungful, and
   *  going under is how you fill it. */
  spray() {
    if (!this.state.lungFull) return undefined;
    const p = this.player;
    const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir];
    /** THREE TILES, AND THE NUMBER IS LOAD-BEARING. A mutation taking this to
     *  nine tiles — a jet that crosses the whole room, douses every fire in a
     *  lane and shoves everything standing in it — left all 576 checks green.
     *  The lung holds one breath; it is a tool for the thing in front of you,
     *  not a weapon that clears a room. */
    const REACH = SPRAY_REACH;
    let doused = 0;
    for (let d = 6; d <= REACH; d += 6) {
      const x = p.cx + dx * d, y = p.cy + dy * d;
      this.puff(x, y, UI.foamLit, 2, 'splash');   // see the note on the douse puff
      // a fire in the jet goes out
      for (const f of this.flames || []) {
        /* THROUGH THE SYSTEMS, THE WAY A ROCK DOUSES ONE (2026-09-19): this killed the FLAME -- the picture -- and left
         * the fire in `systems.fires`, so the tile went on burning and spreading with nothing drawn on it, and the
         * body that had just sprayed it stood in an invisible fire. `smother` takes the entry and its Flame together. */
        if (!f.dead && Math.hypot(f.cx - x, f.cy - y) < TS && this.systems && this.systems.smother(this, f.tx, f.ty)) doused++;
      }
      // ...and anything standing in it is shoved back
      for (const e of this.enemies) {
        if (e.dead || Math.hypot(e.cx - x, e.cy - y) > TS) continue;
        e.kb = { dx, dy, t: 9 };
        if (e.state === 'charge' || e.state === 'wind') e.state = 'idle';
      }
      /* THE WORLD'S OWN TILE, NOT THE SCREEN'S GRID (2026-09-18). `x` and `y` are WORLD pixels and `this.tiles` is the
       * current screen's sixteen-by-twelve, so on every screen but 0,0 the row index was off the end of the array and
       * the optional chain made it `undefined` -- which is not solid. The spray went through cliffs, rock and trees
       * on every screen a player can actually be standing on. */
      if (isSolid(this.tileKindAt(x, y))) break;
    }
    this.state.lungFull = false;
    this.sfx('splash', this.player.x + 8);
    
    return true;
  }

  /** THE SILT LUNG. One rule: it takes a breath in, or lets one out.
   *
   *  In deep water, use it and you take the water in and go under — slow,
   *  unseen, shots passing over you, and a hedge rooted at the waterline has
   *  nothing to root into so you pass beneath it. Use it again and you blow
   *  out: the breath goes one way and you go the other, hard, and everything
   *  floating near you goes with the water.
   *
   *  What that is FOR is not written down. It is a way across a lake. It is a
   *  way to put a seed pod somewhere you cannot stand. It is a way to shove
   *  the thing chasing you into a thicket. It is a way to break a shot that
   *  was going to hit you. It is one rule about water and the things in it.
   */
  toggleSubmerge() {
    const p = this.player;
    if (p.submerged) return this.surge();
    if (!this.isSwimAt(p.cx, p.cy)) return;
    p.submerged = true;
    this.puff(p.cx, p.cy, UI.underPale, 9, 'splash');
    this.sfx('enter');
  }

  /** The breath out. */
  surge() {
    const p = this.player;
    // NB: `DIRS` is keyed n/s/w/e — the hollow's door directions — and the
    // player's `dir` is up/down/left/right. Indexing one with the other
    // returns undefined and the fallback sent every surge downward.
    const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir];
    p.submerged = false;
    // you go one way...
    p.kb = { dx, dy, t: 16 };
    // ...and the water goes the other, taking what is floating in it
    for (const e of this.enemies) {
      if (e.dead) continue;
      const d = Math.hypot(e.cx - p.cx, e.cy - p.cy);
      if (d > 44) continue;
      const a = Math.atan2(e.cy - p.cy, e.cx - p.cx);
      e.kb = { dx: Math.cos(a), dy: Math.sin(a), t: 14 };
    }
    for (const pod of this.pods) {
      const d = Math.hypot(pod.cx - p.cx, pod.cy - p.cy);
      if (d > 44) continue;
      const a = Math.atan2(pod.cy - p.cy, pod.cx - p.cx);
      pod.x += Math.cos(a) * 18; pod.y += Math.sin(a) * 18;
      clampBox(pod);
    }
    this.shots = this.shots.filter((sh) => sh.friendly
      || Math.hypot(sh.cx - p.cx, sh.cy - p.cy) > 34);
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      this.puff(p.cx + Math.cos(a) * 12, p.cy + Math.sin(a) * 12, UI.underPale, 4, 'splash');
    }
    this.sfx('blast', p.cx);
    this.shakeFor(3, 8);
  }

  /** THE EMBERWAKE. One rule: an ember creeps through thorn and stops at
   *  anything that is not a thicket. What that rule is FOR is not written
   *  down anywhere, and that is the point — a thicket lights from a corner, a
   *  line of thorn is a fuse to somewhere you cannot walk, water is a
   *  firebreak, a brazier across the room can be lit from outside it, and a
   *  seed pod the fire reaches goes off. None of those are features; they are
   *  the same rule seen from different rooms. */
  dropEmber() {
    const p = this.player;
    const ahead = {
      up: [p.cx, p.y - 4], down: [p.cx, p.y + p.h + 4],
      left: [p.x - 4, p.cy], right: [p.x + p.w + 4, p.cy],
    }[p.dir];
    // world px to the screen's own tile (LS-164: read as world tiles the
    // ember always fell outside the screen box and lit the player's own tile)
    let tx = Math.floor(ahead[0] / TS), ty = Math.floor(ahead[1] / TS);   // world tiles: the tile ahead, over a seam or not (LS-511)
    if (this.tileAt(tx, ty) === undefined) { tx = Math.floor(p.cx / TS); ty = Math.floor(p.cy / TS); }
    {
      // the matter table decides what the ember does (LS-174): the thorn in
      // front of you opens under it like a swing, anything else that can burn
      // catches, and what cannot burn still shows a flame -- the tool always
      // does something visible, or a player cannot tell it fired
      if (this.tileAt(tx, ty) === T.THORN) this.burnThornAt(tx, ty);
      /* A REFUSED EMBER IS A SPARK, NOT A FIRE (2026-09-19). "The tool always does something visible" was kept with
       * a three-tick FLARE -- a real fire in the list -- on any tile the ignite refused, and a fire on a tree's tile
       * is a burning tree: a refused ember turned a tree to scorch in fifteen frames, on ground the matter table says
       * will not take a flame. The something visible is a puff of embers now, and only the things that burn, burn. */
      else if (!this.systems.ignite(this, tx, ty)) this.puff(tx * TS + 8, ty * TS + 8, UI.flame, 8, 'ember');
    }
    this.state.emberCool = EMBER_COOL;
    this.sfx('flame');
    this.shakeFor(2, 6);
  }

  /** A thorn a spread fire has burnt through is broken the way a struck one
   *  is (LS-174): the hollow a hedge was sealing opens, the break is saved,
   *  and what is left is scorch that the clock grows back. */
  /** A hedge cut by a blade: litter where it stood (LS-225 step 2). */
  cutThornAt(tx, ty) {   // a world tile (LS-511)
    if (this.tileAt(tx, ty) !== T.THORN) return;
    this.markBrokenAt(tx, ty);
    this.setTileAt(tx, ty, T.DEBRIS);
    this.dropAt('cuttings', tx * TS + 2, ty * TS + 2, { dropped: true });   // a cut thorn leaves cuttings (LS-379): the world is made of what you use   // ...AND THE SPOIL IS SAVED (2026-09-18): only `dropped` items are written, and a BREAK is written, so a reload or a death deleted the cuttings and left the hedge gone for ever -- the one thing the world will not make again
    this.puff(tx * TS + 8, ty * TS + 8, UI.leafDim || UI.gold, 6, 'ember');
    this.sfx('break');
    this.save();
  }
  markBurntAt(tx, ty) {   // a world tile: what the fire took is broken the way a struck one is, and lies scorched
    this.markBrokenAt(tx, ty);
    this.setTileAt(tx, ty, T.SCORCH);
  }



  /** Put one down as close to a given tile as there is room for. */
  /** `opts` carries `hpBonus` and friends; most spawns want the defaults. */
  /* `tx`/`ty` ARE TILES OF THE CURRENT SPACE — world tiles in the wilds.
   *
   * `summon` already passes world tiles, because it divides a world pixel by
   * TS. The nest spawns pass a tile found by scanning `this.tiles`, which is
   * one screen's grid, so they add the origin at the call. Mixing the two
   * silently put the brood on the wrong screen. */
  placeEnemyNear(type, rng, tx, ty, opts: any = {}) {
    const ring = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, -1], [1, 1], [-1, 1], [-1, -1],
      [0, -2], [2, 0], [0, 2], [-2, 0]];
    for (const [dx, dy] of ring) {
      const x = tx + dx, y = ty + dy;
      if (x < 1 || y < 1 || x >= this.gridW() - 1 || y >= this.gridH() - 1) continue;
      if (this.solid(x * TS + 8, y * TS + 8, false, null)) continue;
      const e = new Enemy(type, x * TS + 2, y * TS + 2, rng, opts || {});
      this.enemies.push(e);
      return e;
    }
    return this.placeEnemy(type, rng, opts);
  }

  /* WHERE THIS AREA'S GRID SITS IN THE WORLD, in pixels.
   *
   * A hollow room and a cave are one grid and start at the origin. A wild
   * screen is a window onto a map that is 128x96 tiles across, so everything
   * placed by TILE has to be offset into it — enemies, drops, rewards. This is
   * the one place that arithmetic lives. */
  originX() { return this.area.x * COLS * TS; }
  originY() { return this.area.y * ROWS * TS; }

  /** SOMETHING SLEEPS IN THE THICKET (LS-302). The verbs were all built: a
   *  creature that fears fire hides in shelter beside it, a hidden body takes
   *  nothing but a fire-tagged blow, and a hidden body is not drawn. Nothing
   *  ever STARTED hidden, so a thicket was scenery. One screen in three that
   *  has a thicket of three tiles or more has a sleeper in it, chosen by the
   *  screen's own seed -- burn the thicket and it comes out. */
  /** SOMETHING STANDS OVER THE TUSK (LS-302, the sixth situation).
   *
   *  The tusk is placed on an islet or a spit, and this is the half that makes
   *  the water matter: a body that cannot float drowns in three seconds of
   *  deep water, so a guard standing on ground with water round it can be
   *  fought, or walked into the deep and left there. The guard is one of the
   *  screen's own creatures where the screen has a floatless one, so the
   *  population is unchanged; a quiet screen gets a shellback, which is the
   *  creature systems.ts names for exactly this ("a shellback is stone"). */
  guardTheSpit(a = this.area, out: any[] = this.enemies) {
    const tiles = this.screenAt(a.x, a.y).tiles;
    if ((globalThis as any).__noGuard) return;
    const l: any = (this.worldLoot() as any[]).find((q: any) => q.x === a.x && q.y === a.y && q.guard);
    if (!l) return;
    if ((this.state.lootTaken || []).includes(l.x + ',' + l.y + ':' + l.kind)) return;   // taken, and there is nothing left to stand over
    const tx = Math.floor(l.px / TS), ty = Math.floor(l.py / TS);
    let guard: any = (out || []).find((e: any) =>
      e && !e.dead && !e.hiding && !e.def.boss && !e.def.beast && !traitsOf(e.type || e.kind).floats);
    if (!guard) {
      /* THE GUARD IS PUT WHERE THE SITUATION WANTS IT (2026-09-18). This asked `placeEnemy`, which throws sixty
       * random darts at the screen and refuses anything solid, shallow or within 56 px of the body -- and a spit is
       * a thread of dry land in the water, so the darts miss and the sixth situation was silently absent: measured,
       * one world in thirty-two had a tusk with nothing standing over it. The tile is chosen four lines below in any
       * case, so the body is made there. */
      const before = out.length;
      this.placeEnemy('shellback', new RNG(mix(this.areaSeed(a), 5150)), {}, a, out);
      if (out.length === before) {
        const made: any = new Enemy('shellback', (a.x * PW) + tx * TS + 2, (a.y * PH) + ty * TS + 2, new RNG(mix(this.areaSeed(a), 5151)), {});
        made.spawnFx = 0; made.home = a.x + ',' + a.y;
        out.push(made);
        this.spawnedHere++;
      }
      guard = out[out.length - 1];
    }
    /* ON THE SPIT, BESIDE THE TUSK RATHER THAN ON IT: a body standing on a
     * cache is a cache you cannot see. Nearest standable neighbour first, the
     * tusk's own tile only if the spit is one tile wide. */
    const spots: [number, number][] = [];
    for (let r = 1; r <= 2; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const x = tx + dx, y = ty + dy, v = tiles[y]?.[x];
      if (v === undefined || !walkableTile(v) || isWet(v)) continue;
      spots.push([x, y]);
    }
    const [gx, gy] = spots[0] || [tx, ty];
    const ox = (a.x * PW), oy = (a.y * PH);
    /* SET THE BOX, DO NOT NUDGE IT. `recentre` is not attached to `Enemy` --
     * entities.ts:1413 says it comes from the prototype "elsewhere" and for a
     * creature it is undefined -- so `cx` is whatever `clampBox` last wrote,
     * and moving by `target - cx` moved it by a stale delta: measured 8 tiles
     * off the tusk it was supposed to be standing over. */
    guard.x = ox + gx * TS + TS / 2 - guard.w / 2;
    guard.y = oy + gy * TS + TS / 2 - guard.h / 2;
    clampBox(guard);
    guard.guarding = true;
    if (typeof guard.recentre === 'function') guard.recentre();
  }

  bedThicket(a = this.area, out: any[] = this.enemies) {
    const tiles = this.screenAt(a.x, a.y).tiles;
    if ((globalThis as any).__noSleeper) return;   // a diagnostic knob, like `__sharedChain`: it is how the q0 failure was attributed to this feature in one run
    const seed = this.areaSeed(a);
    /* ...AND ALWAYS ON A HUNT'S APPROACH (LS-302's last line). One screen in
     * two was a fine density for the island at large and left NINE of
     * twenty-four approaches carrying fewer than two of the six situations,
     * measured. A thicket on the way to a lair always has something in it:
     * the sleeper is the cheapest of the six to guarantee, because it asks
     * only for shelter tiles and the screen's own creature. */
    const onApproach = this.approachScreens().has(a.x + ',' + a.y);
    if (!onApproach && (mix(seed, 4242) >>> 0) % 3) return;   // off the approaches one screen in three, not two (2026-09-12): the coast's clumps put a sleeper on 36% of screens against a bar of 35
    const shelter: [number, number][] = [];
    for (let ty = 1; ty < ROWS - 1; ty++) for (let tx = 1; tx < COLS - 1; tx++) {
      const v = tiles[ty][tx];
      if (v === T.NEST || !matterOf(v).shelter) continue;
      let n = 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const w = tiles[ty + dy]?.[tx + dx];
        if (w !== undefined && w !== T.NEST && matterOf(w).shelter) n++;
      }
      if (n >= 2) shelter.push([tx, ty]);
    }
    if (shelter.length < 3) return;
    /* ANYTHING CAN BED DOWN IN A THICKET. This asked for `fearsFire`, which is
     * the trait that makes a creature RUN into shelter -- a different thing
     * from being asleep in it, and only 63 screens in 400 spawn one, so the
     * situation appeared on 2.2% of screens and was not a situation at all.
     * What flushes a sleeper is being set alight, and `struck` takes a
     * fire-tagged blow from a hidden body whatever the species. */
    const sleeper = (out || []).find((e: any) => e && !e.dead && !e.hiding && !e.guarding && !e.def.boss);
    if (!sleeper) return;
    const [tx, ty] = shelter[(mix(seed, 4343) >>> 0) % shelter.length];
    const ox = (a.x * PW), oy = (a.y * PH);
    sleeper.hiding = { tx: ox / TS + tx, ty: oy / TS + ty, t: 0, from: [sleeper.x, sleeper.y], sleep: true, bedding: true };   // and it is lying on two glim (LS-302); a world tile, as a body's own hiding is (LS-511)
    sleeper.x += ox + tx * TS + TS / 2 - sleeper.cx;
    sleeper.y += oy + ty * TS + TS / 2 - sleeper.cy;
    if (typeof sleeper.recentre === 'function') sleeper.recentre();
  }

  placeEnemy(type, rng, opts, a = this.area, out: any[] = this.enemies) {
    const tiles = this.screenAt(a.x, a.y).tiles;
    /* (2026-09-12: a rule that emptied a living beast's lair of lesser creatures was tried here and taken out the same
     * day -- the kin are the design (round 44: they wait at the seam while the beast is awake and come in when it
     * sleeps) and the tusk's guard stands on such screens; the fight the bot lost was the bot's, see beasts.ts.) */
    for (let tries = 0; tries < 60; tries++) {
      const tx = rng.irange(1, COLS - 2), ty = rng.irange(1, ROWS - 2);
      const t = tiles[ty][tx];
      const beach = (tx2: number, ty2: number) => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => { const r = tiles[ty2 + dy]; const k = r && r[tx2 + dx]; return k === T.SEA || k === T.SHALLOW || k === T.WATER; });
      if (ENEMY_DEFS[type] && ENEMY_DEFS[type].aquatic ? t !== T.WATER : ENEMY_DEFS[type] && ENEMY_DEFS[type].lowTide ? (isSolid(t) || t === T.SHALLOW || !beach(tx, ty)) : (isSolid(t) || t === T.SHALLOW)) continue;   // an eel is placed in deep water (LS-293);
      /* AND A WET-KEYED CREATURE IS PUT ON WET GROUND (2026-09-17). The mossback drinks from the ground's wetness and
       * the dry kills it: two hit points, one every two seconds of dryness. Placed anywhere on a bloom screen, every
       * one of them died within four seconds of the screen being made -- creatures spawned to die, and their drops
       * and their deaths counted. A screen with no water is not mossback ground: if sixty tries find no shore, none
       * is placed there at all. */
      if (ENEMY_DEFS[type] && ENEMY_DEFS[type].wet && !beach(tx, ty)) continue;   // ...and a tidecrab on the sand beside it, by the lowTide gate above
      const x = tx * TS + 1 + (a.x * PW), y = ty * TS + 1 + (a.y * PH);
      if (Math.hypot(x - this.player.x, y - this.player.y) < 56) continue;
      out.push(new Enemy(type, x, y, new RNG(mix(this.areaSeed(a), tries, out.length)), opts));
      this.spawnedHere++;
      return;
    }
  }

  /** Hollow rewards belong to their room until actually picked up. */
  /** Put a reward down at (x, y), or at the nearest tile to it that a player
   *  can actually stand on.
   *
   *  A boss room's thorn seal is a full column, and the seed and the vigor
   *  spawn on a fixed row — so in 17 of 180 rooms the seed of a hollow sat
   *  inside the hedge. An item you cannot reach is a hollow you cannot
   *  finish, and nothing was looking. */
  /* WHERE A DROPPED THING CAN ACTUALLY BE PICKED UP.
   *
   * Split out of `dropItem` because `dropItem` is not the only way an item
   * reaches the floor and was the only one that checked. Measured over twelve
   * bot runs: 12 of 1,342 items landed somewhere no standable position
   * overlaps them — 10 from the ordinary-room glim row below, which is a
   * hardcoded 4x2 grid near the bottom of the room and lands inside the
   * blocks of a `still` shrine or a puzzle, and 2 from enemies dying against
   * a wall. Small, and it is still a reward the game showed the player and
   * then would not let them have.
   *
   * Returns the nearest [x, y] a player could reach, or the original if it
   * was already fine. Callers keep their own `permanent` flag: a boss's sap
   * and an ordinary room's glim have different lifetimes and this must not
   * quietly give them the same one.
   */
  /* TAKES A PLACE ON THIS AREA'S GRID, RETURNS ONE IN THE WORLD.
   *
   * Every caller thinks in the grid it is placing into — the middle of a stash
   * room, a tile beside a mouth — and that is the right way to think about it.
   * The world does not work in those coordinates any more, and `solid` least
   * of all: asked about a screen-local pixel in the wilds it answers for the
   * top-left screen of the map. So the offset is applied HERE, once, at the
   * boundary between "which tile" and "where in the world". */
  fitDrop(x, y) {   // world pixels in and out: the nearest tile a body could stand on, within six tiles
    const p = this.player;
    const fits = (ix, iy) => { const tx = Math.floor((ix + 7) / TS), ty = Math.floor((iy + 7) / TS); return this.tileAt(tx, ty) !== undefined && !this.solid(tx * TS + TS / 2, ty * TS + TS / 2, false, p); };
    if (fits(x, y)) return [x, y];
    const cx = Math.floor((x + 7) / TS), cy = Math.floor((y + 7) / TS);
    let best = null, bestD = Infinity;
    for (let ty = cy - 6; ty <= cy + 6; ty++) for (let tx = cx - 6; tx <= cx + 6; tx++) {
      const ix = tx * TS + 1, iy = ty * TS + 1;
      if (!fits(ix, iy)) continue;
      const d = Math.hypot(ix - x, iy - y);
      if (d < bestD) { bestD = d; best = [ix, iy]; }
    }
    return best || [x, y];
  }

  /* THE GREAT BEASTS (LS-225 steps 3 and 4). Vijay: "the objective will be to
   * defeat 3 very large and powerful monsters that roam the overworld".
   *
   * A beast is not a screen's creature. Its RECORD lives in the save --
   * where it is in world pixels, how much life it has, whether it is alive
   * -- and roams there whether or not you can see it: every few frames it
   * walks toward a spot in its territory (its lair screen and the screens
   * around it), and picks a new spot when it gets there. When its screen is
   * yours or next to yours a BODY is made from the record and put among the
   * enemies, where the ordinary machinery moves, draws and hurts it; while
   * the body stands, the record is written from it each frame, and when the
   * body is out of reach it is dropped and the record walks on alone. The
   * rosters never hold a beast: `stashRoster` leaves it out, and the record
   * makes a new body the moment you are near again. Death goes the other
   * way -- `onEnemyKilled` marks the record and the bar. */
  _lairs: { kind: string; x: number; y: number; px: number; py: number }[] | null = null;
  _approach: Set<string> | null = null;
  sunWash: Map<string, any> | null = null;
  dullSaidAt = -999;   // when the dull blade last said so (round 49): a toast on its own clock, not on the frame counter's
  lightArt: Map<string, any> | null = null;   // the shadows and the lines of light, one picture a screen   // the sun's wash, one picture a screen (2026-09-05)
  hudTop = HUD_H;   // the picture starts here: entities read it for the toast's rows without importing the view (a cycle)
  beastBodies: Map<any, any> = new Map();   // a beast's body, keyed by its record (the robustness lens, round 46: an index into a list a load may reorder)
  /** What may come for the player on this screen tonight: the country's
   *  bestiary up to the screen's tier (LS-379). */
  prowlPool(): string[] {
    const home = BESTIARY[this.world.biome[this.area.y][this.area.x]] || BESTIARY.heath;
    const tier = this.world.tier[this.area.y][this.area.x];
    const pool = home[0].slice();
    if (tier >= 1) pool.push(...home[1]);
    if (tier >= 2) pool.push(...home[2]);
    /* AND THE SAME RULES THE ROSTER KEEPS (2026-09-18). A screen's roster filters its country's bestiary by the hour,
     * the rain, the tide and the water on the screen, and keeps anything that CHARGES two screens clear of the glade.
     * The night's pack was handed the raw list, so it sent chargers onto the opening glade, day creatures at night,
     * and an eel onto dry land -- where its own collision makes every tile a wall, so it stood there, alive and
     * frozen, for the rest of the run. One question, asked once, answered the same way for both. */
    return this.rosterKinds(pool);
  }
  /** The kinds this screen may hold RIGHT NOW: the hour, the weather, the tide, the water on it, and the glade's
   *  own peace. Shared by the roster and by the night's pack, which used to disagree. */
  rosterKinds(pool: string[], a = this.area): string[] {
    const tiles = this.screenAt(a.x, a.y).tiles;
    const clock = this.systems ? this.systems.clock(this) : { day: 0.3, night: false, tide: 0 };
    const dark = !!this.night || !!clock.night, duskNow = clock.day >= 0.45 && clock.day < 0.6;   // `this.night` is the night WORLD; the clock's is the night of the day
    const raining = !!(this.systems && this.systems.rain(this) > 0.3);
    const ebb = !!(this.systems && this.systems.tideNow(this) < -0.3);   // the roster's own question, asked the same way (2026-09-18): the clock's phase is not the water's height
    let sea = false, shallow = false, deep = false;
    if (tiles) for (const row of tiles) for (const v of row) { if (v === T.SEA) sea = true; else if (v === T.SHALLOW) shallow = true; else if (v === T.WATER) deep = true; }
    if (sea) deep = true;   // the open sea is deep water for anything that swims in it
    const W0 = this.world.biome[0].length, isleOf = (x: number, y: number) => (this.world.island ? this.world.island[y * W0 + x] : 0);
    const nearGlade = isleOf(a.x, a.y) === isleOf(this.world.start.x, this.world.start.y)
      ? Math.max(Math.abs(a.x - this.world.start.x), Math.abs(a.y - this.world.start.y)) : 99;
    const here0 = pool.filter((k) => ENEMY_DEFS[k] && (!ENEMY_DEFS[k].night || dark) && (!ENEMY_DEFS[k].day || !dark) && (!ENEMY_DEFS[k].rain || raining) && (!ENEMY_DEFS[k].dusk || duskNow) && (!ENEMY_DEFS[k].aquatic || deep) && (!ENEMY_DEFS[k].lowTide || (ebb && (sea || shallow))) && (!ENEMY_DEFS[k].wet || deep || shallow || sea));   // ...and a kind marked wet needs wet ground somewhere on the screen
    /* nothing that charges within two screens of the glade -- and nothing armoured either (LS-460, 2026-09-26): the newcomer probe
     * died one screen from the start swinging into a shellback's plate; the guard is learned where you can walk away */
    const here1 = nearGlade <= 2 ? here0.filter((k) => !ENEMY_DEFS[k].charges && !ENEMY_DEFS[k].armoured) : here0;
    return here1.length ? here1 : here0.length ? here0 : ['sporeling'];   // ...and never back to the unfiltered list (2026-09-18)
  }

  /** HOW MUCH THE AIR OVER THIS COUNTRY BENDS THE PICTURE (2026-09-10; Vijay:
   *  "can we properly simulate it with a displacement shader effect").
   *
   *  Air over hot ground has a different refractive index, so what is seen
   *  THROUGH it is displaced. The present pass does the work -- it samples the
   *  frame a whole pixel or two to the side, wobbling -- and all it needs from
   *  the game is how hot the ground on the glass is. Worked out the same way
   *  the ribbons and the scorching are: the country's base warmth, the sun's
   *  height, the night and the rain.
   *
   *  It starts a little UNDER the scorch line, like everything else about the
   *  heat, so a player reads the country before it costs them: dust at noon
   *  shimmers and never burns, which is where the sign is learned. */
  pushHeat() {
    if (!this.onHeat) return;
    let amount = 0;
    if (this.mode === 'play' && this.systems) {
      const clock = this.systems.clock(this);
      const warm = clock.day < 0.5 ? Math.sin(clock.day * Math.PI) : 0;
      const biome = this.world && this.world.biome && this.world.biome[this.area.y] ? this.world.biome[this.area.y][this.area.x] : 'heath';
      const target = (BIOME_WARMTH[biome] ?? 0.5) + moodOfSeed(this.seedNum).warm + 0.12 * (warm - 0.5) - (clock.night ? 0.12 : 0) - 0.12 * this.systems.rain(this);   // the mood's warmth, as the field has it
      amount = Math.max(0, Math.min(1, (target - (SCORCH_LINE - 0.10)) / 0.16));
    }
    /* EASED, because a country's edge is a screen boundary and the shimmer
     * must not switch on like a light when you cross one. A twentieth a frame
     * is about a second to come up. */
    this.heatShown = (this.heatShown || 0) + (amount - (this.heatShown || 0)) * 0.05;
    if (this.heatShown < 0.002) this.heatShown = amount === 0 ? 0 : this.heatShown;
    this.onHeat(this.heatShown);
  }

  /** THE TORCH SHOWS WHAT WANTS TO BURN (LS-379, legibility). A player could
   *  not tell that thorn is kindling and moss is not; the numbers are in the
   *  matter table and nowhere on the glass. Within a lit torch's own light,
   *  anything with a flammability of half or more -- thorn, dry tuft --
   *  twinkles amber: the fire tells you what it wants, before it has it. No
   *  text; the light teaches. Drawn in the wind's layer, in world pixels, and
   *  the count is returned so a check can ask. */
  /** THE STONES REMEMBER (LS-294): the likeness of a beast felled on an earlier run rises over the stone that names it,
   *  for three seconds, and fades. Drawn in world space, over the front layer, at the stone's own scale. */
  drawStoneLikeness(g) {
    const s = this.stoneLikeness; if (!s) return;
    if (this.runFrames > s.until) { this.stoneLikeness = null; return; }
    const spr: any = creatureSprite({ type: s.kind, dir: 'down', anim: 0, moving: false, state: 'idle', stun: 0, flipped: 0, reel: 0, alight: 0, launched: false } as any);
    if (!spr || !spr.canvas) return;
    const t = (this.runFrames - s.from) / Math.max(1, s.until - s.from);
    const rise = Math.min(1, t * 3) * 12, fade = t < 0.75 ? 1 : (1 - t) / 0.25;
    const cam = this.camera();
    g.save(); g.globalAlpha = 0.9 * fade;
    g.drawImage(spr.canvas, spr.sx, spr.sy, spr.w, spr.h, Math.round(s.x - spr.w / 2 - cam.x), Math.round(s.y - spr.h - rise - cam.y), spr.w, spr.h);
    g.restore();
  }
  drawKindling(g) {
    const p = this.player;
    if (!p || !(p.lit > 0)) return 0;
    const r = torchGlow(this.state);
    const cx = Math.floor(p.cx / TS), cy = Math.floor(p.cy / TS), rt = Math.ceil(r / TS);   // world tiles round the torch (LS-511)
    let drawn = 0;
    for (let ty = cy - rt; ty <= cy + rt; ty++) for (let tx = cx - rt; tx <= cx + rt; tx++) {
      const v = this.tileAt(tx, ty); if (v === undefined) continue;
      const m = matterOf(v);
      if (!(m.flammable >= 0.5) || v === T.BRAZIER) continue;   // a bowl is not kindling: without the ember it will not take the torch, and the twinkle promised it would (2026-09-19)
      const wx = tx * TS + 8, wy = ty * TS + 8;
      if (Math.hypot(wx - p.cx, wy - p.cy) > r) continue;
      const h = mix(this.seedNum, tx, ty, 313) >>> 0;
      const n = m.flammable >= 1 ? 3 : 2;
      for (let i = 0; i < n; i++) {
        const q = (h >> (i * 5)) & 31;
        if (((this.frame >> 3) + q) % 3 === 0) continue;   // a twinkle: each fleck is out one beat in three
        g.fillStyle = (q & 1) ? '#ffb85c' : '#ffe08a';
        g.fillRect(tx * TS + 2 + (q % 12), ty * TS + 2 + ((q * 7) % 12), 1, 1);
        drawn++;
      }
    }
    return drawn;
  }

  /** The standing stone within a tile of the player, if there is one. */
  /** THE THING THE KEY WOULD ACT ON NOW (LS-397, 2026-09-12; Vijay: "the game is quite confusing to play right now"):
   *  a stone beside the body, or a forge's rock ahead with a material in hand. Drawn as a warm pulse and a spark on
   *  it, so the moment the one key will do something is visible without a word. */
  actTarget(): { tx: number; ty: number; kind: string; action?: 'take' } | null {
    if (!this.area || this.aboard) return null;
    if (this.heldShot && this.player) { const p = this.player, [dx, dy] = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[p.dir] || [0, 1]; return { tx: Math.floor((p.cx + dx * 40) / TS), ty: Math.floor((p.cy + dy * 40) / TS), kind: 'throw' }; }   // what is in hand goes first: the light falls where it will land
    const st = this.stoneBeside(); if (st) return { tx: st[0], ty: st[1], kind: 'stone' };
    const sel = this.state && this.state.bag ? this.state.bag.slots[this.state.bag.selected] : null;
    if (sel && forgeTakes(sel.kind)) { const a = this.forgeAhead(); if (a) return { tx: a.tx, ty: a.ty, kind: 'forge' }; }
    if (!sel || ITEMS[sel.kind]?.weapon) {
      const a = this.forgeAhead();
      if (a && this.items.some(it => this.takeableAtForge(it, a.tx, a.ty))) {
        return { tx: a.tx, ty: a.ty, kind: 'forge', action: 'take' };
      }
    }
    // a thing that can be set down, where it would go
    const def = sel && ITEMS[sel.kind];
    if (def && def.place) {
      const p = this.player, [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir] || [0, 1];
      if (def.place === 'RAFT' || def.place === 'BOAT') {
        const wx = p.cx + dx * 20, wy = p.cy + dy * 18, tt = this.tileKindAt(wx, wy);
        if ((tt === T.WATER || tt === T.SEA || tt === T.SHALLOW) && this.canPlaceHull(wx, wy)) return { tx: Math.floor(wx / TS), ty: Math.floor(wy / TS), kind: 'set' };
      } else {
        const tx = Math.floor((p.cx + dx * (p.w / 2 + 6)) / TS), ty = Math.floor((p.cy + dy * (p.h / 2 + 6)) / TS);
        const kind = def.place === 'ROCK' ? T.ROCK : def.place === 'ROD' ? T.ROD : T.THORN;
        /* ...and not for the piece a forge on this screen is showing as its ghost (LS-457): the cutting that finishes the raft
         * is also a hedge's seed, and the pulse would have the newcomer plant it. The press still sets it; the light points at the rock. */
        if (this.canSetDown(kind, tx, ty) && !this.forgeWants().some((w) => w.kind === sel.kind)) return { tx, ty, kind: 'set' };   // the same rule the press obeys (LS-458)
      }
    }
    /* nothing else for the key: a loose rock, a fire or open water ahead can be lifted, with an empty hand or a blade */
    /* THE FOOD WEB'S TWO VERBS, SHOWN WHERE THEY APPLY (LS-435, no words): meat in hand before a lesser creature pulses
       the creature -- the tool offers it; a blade before remains pulses them -- a swing cuts meat. */
    { const p = this.player, [dx, dy] = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[p.dir] || [0, 1];
      const ax = p.cx + dx * 14, ay = p.cy + dy * 14;
      if (sel && (sel.kind === 'meat' || sel.kind === 'cookedmeat')) {
        const e = this.enemies.find((q: any) => q && !q.dead && !q.def.boss && !q.prowler && !q.hiding && q.spawnFx <= 0 && Math.hypot(q.cx - ax, q.cy - ay) < 26);
        if (e) return { tx: Math.floor(e.cx / TS), ty: Math.floor(e.cy / TS), kind: 'feed' };
      }
      const verb = (wieldedWeapon(this.state).weapon || ({} as any)).verb;   // what is in hand (swingWeaponOf is the swing's own, bare between swings)
      if (verb === 'cut' || verb === 'pierce') {
        const r = this.remains.find((q: any) => q.bites > 0 && Math.hypot(q.x - ax, q.y - ay) < 22);
        if (r) return { tx: Math.floor(r.x / TS), ty: Math.floor(r.y / TS), kind: 'carve' };
      } }
    if (!sel || ITEMS[sel.kind]?.weapon) { const l = this.liftAhead(); if (l) return { tx: l.tx, ty: l.ty, kind: 'lift' }; }
    return null;
  }
  /** WOULD THE KEY DO SOMETHING WITH WHAT IS PICKED, right now (LS-397)? The bag's verbs -- eat, wear, whet, mend, glim's
   *  two buys -- with none of their side effects; the HUD lets the picked slot breathe while one of them is true. */
  /** Would eating the picked consumable do nothing right now? Each kind's own rule (2026-09-19): the HUD's breath asked
   *  sap's question of all twenty. */
  eatIdle(sel): boolean {
    const st = this.state;
    const on = activeNames(this.player);
    const effect = ITEMS[sel.kind].eat;
    const gustTo = effect === 'gust' ? this.wantedBearing() : null;
    return effect === 'cure' ? on.length === 0
        : effect === 'pitch' ? (st.pitch || 0) > 0
        : effect === 'brine' ? !!st.lungFull
        : effect === 'draught' ? (st.draught || 0) > this.runFrames
        : effect === 'haste' ? (st.haste || 0) > this.runFrames
        : effect === 'stone' ? (st.stone || 0) > 0
        : effect === 'sight' ? (st.sight || 0) > this.runFrames || this.player.lit > 0
        : effect === 'gust' ? !(this.aboard && st.boat && st.boat.kind === 'raft' && gustTo) || (st.gust && st.gust.until > this.runFrames)
        : effect === 'mend' ? !on.includes('Soaked') && !on.includes('Chilled') && st.hp >= st.maxHp
        : effect === 'feast' ? st.hp >= st.maxHp
        : effect === 'feast3' || effect === 'raw' ? st.hp >= st.maxHp
        : effect === 'warm' ? (st.warm || 0) > this.runFrames
        : effect === 'dry' ? (st.dry || 0) > this.runFrames
        : effect === 'kindle' ? this.player.lit > 0 || !!this.player.wading || !!this.player.swimming || !!this.player.submerged
        : effect === 'grow' ? st.maxHp >= MAX_HP_CAP
        : effect === 'stone10' ? (st.stone || 0) > 0
        : effect === 'mendall' ? st.hp >= st.maxHp
        : true;
  }
  /** THE HAND GOES BACK TO THE BLADE (LS-454, 2026-09-26). Eating the last sap, laying the last log on the forge, wearing
   *  the one mantle or setting down the only hull left the selection on an empty slot, and the hand read "nothing": the
   *  newcomer probe ate its only sap mid-fight and fought on with its fists until it died. When the slot the hand was
   *  on empties by use -- the same slot, a stack last frame, nothing now -- the hand takes up the weapon it last held,
   *  or the first in the hotbar. Choosing an empty slot on purpose moves the selection, so it is left alone; the bag
   *  screen resets the memory, since what it moves is the player's own doing. */
  handAt = -1; handWas: string | null = null; lastWeapon: string | null = null;
  handFallsBack() {
    const st = this.state; if (!st || !st.bag || this.mode !== 'play' || (globalThis as any).__noHandFallback) return;
    const b = st.bag, cur = b.slots[b.selected];
    if (cur && ITEMS[cur.kind] && ITEMS[cur.kind].weapon) this.lastWeapon = cur.kind;
    if (!cur && this.handWas && this.handAt === b.selected) {
      let to = this.lastWeapon ? b.slots.findIndex((x: any, i: number) => i < HOTBAR && x && x.kind === this.lastWeapon) : -1;
      if (to < 0) to = b.slots.findIndex((x: any, i: number) => i < HOTBAR && x && ITEMS[x.kind] && ITEMS[x.kind].weapon);
      if (to >= 0) selectSlot(st, to);
    }
    const now = b.slots[b.selected];
    this.handAt = b.selected; this.handWas = now ? now.kind : null;
  }
  useWould(): string | null {
    if (!this.state || !this.state.bag || this.mode !== 'play') return null;
    const sel = this.state.bag.slots[this.state.bag.selected]; if (!sel) return null;
    const def = ITEMS[sel.kind]; if (!def) return null;
    if (def.gear && def.dur && sel.dur !== undefined && sel.dur <= 0) return 'mend';
    if (def.gear) return 'wear';
    if (def.weapon && def.dur && sel.dur !== undefined && sel.dur < def.dur && (this.state.glim || 0) >= 3) return 'whet';
    if (sel.kind === 'glim' && this.besideLitBrazier && this.besideLitBrazier() && sel.n >= 5) return 'pitch';
    if (sel.kind === 'glim' && sel.n >= 10 && this.state.maxHp < MAX_HP_CAP) return 'vigor';
    if (def.eat === 'heal') { const hurt = this.state.hp < this.state.maxHp; const ailing = !!(this.player && this.player.afflictions && [...this.player.afflictions.values()].some((a: any) => a.left > 0)); if (hurt || ailing) return 'eat'; }
    else if (def.eat) { if (!this.eatIdle(sel)) return 'eat'; }   // the kind's own rule, not sap's (2026-09-19)
    return null;
  }
  /** WHAT WOULD CURE WHAT AILS THE BODY, on this screen (LS-397): a fire for the chilled and the soaked, water for the
   *  scorched -- as a tile to light; the sap for a bite or a poison is the bag's, and the HUD flares it. */
  cureTarget(): { tx: number; ty: number; cool: boolean } | null {
    const p = this.player; if (!p || !p.afflictions) return null;
    const has = (n: string) => { const a = p.afflictions.get(n); return !!(a && a.left > 0); };
    const px = Math.floor(p.cx / TS), py = Math.floor(p.cy / TS);   // world tiles, nine round the body (LS-511: the screen underfoot's)
    let best: any = null;
    const consider = (tx: number, ty: number, cool: boolean) => { const d = Math.hypot(tx - px, ty - py); if (d <= 9 && (!best || d < best.d)) best = { tx, ty, cool, d }; };
    const chill = has('Chilled') || has('Soaked'), hot = has('Scorched');
    if (chill || hot) for (let ty = py - 9; ty <= py + 9; ty++) for (let tx = px - 9; tx <= px + 9; tx++) { const v = this.tileAt(tx, ty); if (chill && v === T.BRAZIER_LIT) consider(tx, ty, false); if (hot && (v === T.WATER || v === T.SHALLOW || v === T.SEA)) consider(tx, ty, true); }
    if (chill) for (const fr of this.systems.fires) consider(fr.x, fr.y, false);
    return best;
  }
  /** The stone at a WORLD tile, whichever screen that tile belongs to (2026-09-18). */
  stoneAtWorld(wx: number, wy: number) {
    if (!this.world || !this.world.stones) return undefined;
    const sx = Math.floor(wx / COLS), sy = Math.floor(wy / ROWS);
    if (sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) return undefined;
    return this.world.stones.get(sx + ',' + sy + '|' + (wx - sx * COLS) + ',' + (wy - sy * ROWS));
  }
  /** THE STONE BESIDE THE BODY, ASKED IN WORLD TILES (2026-09-18).
   *
   *  This asked `area` for the screen and the body for its tile INSIDE that screen -- and `updateArea` keeps a tile
   *  of hysteresis, so a body up to sixteen pixels into the next screen still belongs to the old one. Standing in
   *  that band, one tile from a stone on the far side of the seam, the nine tiles this looked at were rows 11 to 13
   *  of the OLD screen and the stone was invisible: the way out of the wilds did not exist from that side. Found by
   *  the whole-run bot, which stood at world tile 148,108 with the stone at 148,109 -- "d0 beside false" -- and
   *  wandered for five hundred ticks. The world is one coordinate space; the lookup is too. */
  stoneBeside(): [number, number] | null {
    if (!this.world || !this.world.stones) return null;
    const wx = Math.floor(this.player.cx / TS), wy = Math.floor(this.player.cy / TS);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (this.stoneAtWorld(wx + dx, wy + dy)) return [wx + dx, wy + dy];   // a world tile (LS-511)
    }
    return null;
  }

  /** Leaving by a stone: the run ends and is recorded as what it was. */
  leaveWilds() {
    this.left = true;
    // a run is written down once: a run left is left in the save, and booting the seed again to hold the stone banks nothing (round 52)
    if (!this.state.left) { this.state.left = true; this.recordWin('run'); }
    /* ...BUT A RUN CONTINUED AND LEFT AGAIN IS STILL THIS SEED'S (2026-09-19). The end screen read the row by hand
     * here (a 2026-09-17 workaround), and nothing it changed after the first leave was ever written: the row, the
     * title and the win screen kept describing the smaller run. `again` updates the row and the most when this
     * leave beats it, counts no run, and sets the same end-screen fields the first leave does. */
    else this.recordWin('again');
    this.save();
    this.mode = 'win';
    this.sfx('stone');   // its own cue (round 52's music lens), not the discovery arpeggio
  }

  /** WHAT THIS RUN CHANGED (LS-379): the record the end screen shows, and
   *  the only verdict there is. Days are the clock's; set down is the player's
   *  own hand; burned is what the fire and the blade have taken. */
  runRecord() {
    const st = this.state;
    const days = this.runFrames / (DAY_PERIOD * FRAMES_PER_MINUTE);
    let burned = 0; for (const set of this.broken.values()) burned += set.size;
    return {
      days: Math.floor(days * 10) / 10,
      felled: (st.beastsSlain || []).length,
      setDown: st.setDown || 0,
      burned,
      caches: (st.lootTaken || []).filter((k) => !String(k).startsWith('hoard:')).length,   // the world's caches only: a beast's hoard writes its pieces into the same list (round 53's design lens: one sow read "16 caches found" for 7 opened)
      kills: st.kills || 0,
    };
  }

  /** EACH LIVING BEAST MARKS ITS COUNTRY (LS-379, slice four). Once a
   *  game-minute, on the screen it is on and the eight round it, a beast
   *  changes one tile: the bramble mother spreads thorn beside thorn and
   *  trees, the sow roots open ground into debris, the old shell floods the
   *  shallows beside his water. Written through `placed`, so it persists and
   *  survives a save, and capped so a country changes rather than fills. A
   *  beast becomes an ecology with growing stakes instead of an item on a
   *  list, and a world left alone for a day is a different world. */
  tickBeastMarks() {
    if (!this.world) return;
    const minute = Math.floor(this.runFrames / FRAMES_PER_MINUTE);
    if (minute === this.markedMinute) return;
    this.markedMinute = minute;
    for (const b of this.beasts()) {
      if (!b || !b.alive) continue;
      const bx = Math.floor(b.x / PW), by = Math.floor(b.y / PH);
      const rng = new RNG(mix(this.seedNum, minute, bx * 7 + by, b.kind === 'sow' ? 1 : b.kind === 'oldshell' ? 2 : 3));
      const sx = Math.max(0, Math.min(WORLD_W - 1, bx + rng.int(3) - 1)), sy = Math.max(0, Math.min(WORLD_H - 1, by + rng.int(3) - 1));
      const key = 'W|' + sx + ',' + sy;
      const here = sx === this.area.x && sy === this.area.y;
      const tiles = here ? this.tiles : this.screenAt(sx, sy).tiles;
      const marks = this.placed.get(key);
      if (marks && [...marks.values()].filter((k) => k !== T.ROCK).length >= MARK_CAP) continue;
      const want = b.kind === 'bramblemother' ? T.THORN : b.kind === 'sow' ? T.DEBRIS : T.WATER;
      const spots: [number, number][] = [];
      for (let ty = 1; ty < ROWS - 1; ty++) for (let tx = 1; tx < COLS - 1; tx++) {
        const v = tiles[ty][tx];
        if (want === T.WATER) { if (v !== T.SHALLOW) continue; }
        else if (v !== T.GROUND && v !== T.MOSS && v !== T.TUFT) continue;
        let beside = false;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
          const n = tiles[ty + dy]?.[tx + dx];
          if (want === T.THORN && (n === T.THORN || n === T.TREE)) beside = true;
          if (want === T.WATER && n === T.WATER) beside = true;
          if (want === T.DEBRIS) beside = true;
        }
        if (beside) spots.push([tx, ty]);
      }
      if (!spots.length) continue;
      const [tx, ty] = spots[rng.int(spots.length)];
      tiles[ty][tx] = want;
      if (!this.placed.has(key)) this.placed.set(key, new Map());
      this.placed.get(key).set(tx + ',' + ty, want);
      if (here) this.partsCache.delete(sx + ',' + sy); else this.partsCache.delete(sx + ',' + sy);
    }
  }

  /** THE HEAT RISES IN RIBBONS (2026-09-10; Vijay, on the fluid sim that was
   *  here first: "didn't lol good at all the heat take inspiration from the
   *  wind").
   *
   *  The problem it answers is the one he reported just before it: "it's also
   *  not even clear visually that that's going to hurt me". Ground over the
   *  scorch line takes a point off whoever stands on it, and the only tell was
   *  one spark on the BODY, after the harm.
   *
   *  A density field was the first answer -- a proper little fluid, scattered
   *  and blurred and dithered -- and however it was tuned it read as fog lying
   *  on the ground. The wind is the thing in this game that already says "the
   *  air is moving", and it says it with RIBBONS: the path a particle has
   *  taken, curved because its velocity is steered by a slow sine, drawn over
   *  everything. Heat is the same statement pointed upward, so it is the same
   *  machinery -- a `Puff` of kind `wind`, warm, short-lived, rising, curling
   *  harder than a gust because heat wobbles.
   *
   *  The vents are the tile's own hash, so a country's plumes keep their
   *  places and can be learned, and the cap is what keeps them a feature of
   *  the ground rather than a weather front. */
  tickHeatRibbons() {
    if (this.mode !== 'play' || this.paused || !this.systems) return;
    if ((globalThis as any).__noSteam) return;
    if (this.frame % 4) return;
    let heat = 0;
    for (const f of this.fx) if (f.kind === 'wind' && (f as any).heat && !f.dead) heat++;
    if (heat >= 34) return;   // a cap, so heat is a feature of the ground and not a weather front
    const clock = this.systems.clock(this);
    const warm = clock.day < 0.5 ? Math.sin(clock.day * Math.PI) : 0;
    const rain = this.systems.rain(this);
    for (const [sx, sy] of this.visibleScreens(this.camera())) {
      const biome = this.world && this.world.biome && this.world.biome[sy] ? this.world.biome[sy][sx] : 'heath';
      const target = (BIOME_WARMTH[biome] ?? 0.5) + 0.12 * (warm - 0.5) - (clock.night ? 0.12 : 0) - 0.12 * rain;
      if (target <= SCORCH_LINE - 0.06) continue;
      const over = Math.min(1, (target - (SCORCH_LINE - 0.06)) / 0.2);
      const tiles = sx === this.area.x && sy === this.area.y ? this.tiles : this.screenAt(sx, sy).tiles;
      /* TWO TILES A BEAT, PICKED OFF THE CLOCK, rather than walking all 192 of
       * them to find the vents every time. */
      for (let n = 0; n < 2; n++) {
        const t = (mix(this.seedNum, this.frame + n * 37, sx * 13 + sy, 5150) >>> 0);
        const tx = t % COLS, ty = (t >> 8) % ROWS;
        if (((mix(this.seedNum, sx * 61 + tx, sy * 71 + ty, 909) >>> 0) % 5) !== 0) continue;   // the vents are the tile's own, and they keep their places
        /* A SCREEN THE WINDOW TOUCHES MAY NOT HAVE TILES YET -- the first
         * frames of a run, or a neighbour off the edge of the map. */
        const row = tiles && tiles[ty];
        const k = row ? row[tx] : undefined;
        if (k === undefined || k === T.WATER || k === T.SEA || isSolid(k)) continue;
        const px = sx * PW + tx * TS + 3 + Math.random() * 10;
        const py = sy * PH + ty * TS + TS - 2;
        const rise = 0.55 + over * 0.5 + Math.random() * 0.25;
        const puff: any = new Puff(px, py, Math.random() < 0.45 ? '#fff0d2' : '#eeddb6',
          (this.systems.wind ? this.systems.wind[0] : 0) * 0.25, -rise + (this.systems.wind ? this.systems.wind[1] : 0) * 0.25, 84 + Math.floor(Math.random() * 46), 'wind',   // the smoke leans with the whole wind, north and south too (LS-389): the tell a raft is planned by
          { span: 14 + Math.floor(Math.random() * 8), curl: (Math.random() < 0.5 ? -1 : 1) * (0.12 + Math.random() * 0.08),
            freq: 0.16 + Math.random() * 0.1, bias: 0.1, drag: 0.995 });
        puff.heat = true;
        this.fx.push(puff);
      }
    }
  }

  /** THE THREE APPROACHES: every screen on the shortest way from the spawn to
   *  a lair (LS-302's last line, "two guaranteed on each hunt's approach").
   *
   *  The card asks for six situations and TWO ON EACH APPROACH, and the second
   *  half is a placement question the code could not even ask before this:
   *  nothing named the route. Measured over 8 worlds and 24 approaches with
   *  the six situations counted honestly -- the pearl's basin, a bedded
   *  sleeper, a brazier ring that holds a cache, the RAKO stone (not any
   *  stone), the coalheart's burning ground and the tusk's spit -- the median
   *  approach carried 3 and NINE OF TWENTY-FOUR carried fewer than two.
   *
   *  Breadth-first over `world.links`, which is the same graph the player
   *  walks; the path is the one a player following the shortest way would
   *  take, not every screen that could be crossed. */
  /** The screens of one beast's approach: the shortest road from the glade to its lair, or, across the water, the
   *  screens of its island within two of the lair (the lair's own too). */
  approachOf(lair) {
    const start = this.world.start, key = (x: number, y: number) => x + ',' + y;
    const out: string[] = [];
    const prev = new Map<string, string>(); const seen = new Set([key(start.x, start.y)]);
    const q: [number, number][] = [[start.x, start.y]];
    for (let i = 0; i < q.length; i++) {
      const [x, y] = q[i];
      if (x === lair.x && y === lair.y) break;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        if (!linked(this.world, x, y, dx, dy)) continue;
        const k = key(x + dx, y + dy);
        if (seen.has(k)) continue;
        seen.add(k); prev.set(k, key(x, y)); q.push([x + dx, y + dy]);
      }
    }
    let cur = key(lair.x, lair.y);
    while (cur && cur !== key(start.x, start.y)) { out.push(cur); cur = prev.get(cur) || ''; }
    const walked = cur === key(start.x, start.y);   // the chain reached the glade: a road exists
    if (!walked && this.world.island) {
      out.length = 0;
      const W = WORLD_W, isle = this.world.island[lair.y * W + lair.x];
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
        const x = lair.x + dx, y = lair.y + dy;
        if (x >= 0 && y >= 0 && x < WORLD_W && y < WORLD_H && this.world.island[y * W + x] === isle) out.push(key(x, y));   // the lair's own screen too: the road version ended on it
      }
    }
    return out;
  }
  approachScreens() {
    if (this._approach) return this._approach;
    const out = new Set<string>();
    for (const lair of this.beastLairs()) for (const k of this.approachOf(lair)) out.add(k);
    return this._approach = out;
  }

  beastLairs() {
    if (this._lairs) return this._lairs;
    const seed = this.world.seedNum, start = this.world.start;
    const all = [...this.world.dist.entries()].map(([k, d]) => { const [x, y] = k.split(',').map(Number); return { x, y, d }; })
      .filter(s => !(s.x === start.x && s.y === start.y)).sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
    const out: { kind: string; x: number; y: number; px: number; py: number }[] = [];
    const used = new Set<string>();
    /* Three lairs, each at least three screens out, on distinct screens, and
     * where the beast belongs when the world allows: the old shell by the fen
     * and the shore, the bramble mother in thorn country. */
    const HOME = BEAST_HOME;   // where its small kin already live (the bestiaries in fillArea); in rules.ts so the manual reads the same table (the lore lens, round 47)
    /* WHICH BEAST TAKES THE NEAR BAND (the design lens, rounds 43 and 52, which disagree, so both are answered).
     *
     * Round 43 made this ROTATE, because the sow was always first and every run opened the same way. Round 52
     * measured what the rotation costs: twelve seeds, identical play, the starting sickle and ten vigor -- SOW
     * FIRST, 5 WON AND 0 LOST; bramble mother first, 1 of 3; OLD SHELL FIRST, 0 WON AND 4 LOST. The old shell wants
     * the spear and a flank, so meeting it first with a sickle is not a hard fight, it is a wall, and which wall
     * you meet was decided by the seed before the player pressed a key.
     *
     * So the sow is always NEAREST -- the first fight is the one that can be won and therefore the one that
     * teaches -- and the other two still rotate between the middle and the far band, which is where round 43's
     * variety actually lives. Repetition in the opening minute costs less than a run decided by a coin. */
    const rot = (mix(seed, 9800) >>> 0) % 2;   // the OTHER two turn with the seed; the sow keeps the near band
    ['sow', 'oldshell', 'bramblemother'].forEach((kind, i) => {
      const unused = all.filter(s => !used.has(s.x + ',' + s.y));
      const far = unused.filter(s => s.d >= 3);
      /* STAGGERED BY DISTANCE (the design lens, round 40: seed 1 had all three at d33-38): the sow in the nearest third of
       * the far country, the old shell in the middle, the bramble mother in the farthest, home biome first within the band. */
      const ds = far.map(s => s.d).sort((p, q) => p - q);
      const q1 = ds.length ? ds[Math.floor(ds.length / 3)] : 0, q2 = ds.length ? ds[Math.floor(ds.length * 2 / 3)] : 0;
      const bi = kind === 'sow' ? 0 : (rot + (kind === 'oldshell' ? 0 : 1)) % 2 + 1;
      const band = bi === 0 ? far.filter(s => s.d <= q1) : bi === 1 ? far.filter(s => s.d > q1 && s.d <= q2) : far.filter(s => s.d > q2);
      /* ON ANOTHER ISLAND (LS-383): a beast lies across the water from the glade, and the three on three different
       * islands when the world has them; the pools below are tried on other islands first, then anywhere. */
      const isle = (s) => this.world.island ? this.world.island[s.y * WORLD_W + s.x] : 0;
      const homeIsle = this.world.island ? this.world.island[start.y * WORLD_W + start.x] : 0;
      const taken = new Set(out.map((l) => isle(l)));
      const away = (s) => isle(s) >= 0 && isle(s) !== homeIsle && !taken.has(isle(s));
      const homeBand = band.filter(s => HOME[kind].includes(this.world.biome[s.y][s.x]));
      const home = far.filter(s => HOME[kind].includes(this.world.biome[s.y][s.x]));
      /* ...AND ON AN ISLAND WORTH THE CROSSING (LS-383, 2026-09-12): the sow's island was one screen on route-0, a rock
       * with a cache and nothing else, because the away pools never asked how big the island was. An island of six
       * screens or more first; the rocks only when nothing else is left. */
      const isleSize = new Map<number, number>();
      if (this.world.island) for (const id of this.world.island) if (id >= 0) isleSize.set(id, (isleSize.get(id) || 0) + 1);
      const big = (s) => (isleSize.get(isle(s)) || 0) >= 6;
      const awayPools = (this.world.islandCount || 1) > 1
        ? [homeBand.filter((s) => away(s) && big(s)), band.filter((s) => away(s) && big(s)), home.filter((s) => away(s) && big(s)), far.filter((s) => away(s) && big(s)), unused.filter((s) => away(s) && big(s)),
           homeBand.filter(away), band.filter(away), home.filter(away), far.filter(away), unused.filter(away)]
        : [];
      /* The pools in order of preference, and within each a five-by-five
       * patch first, then three-by-three, then any open tile: a home pool of
       * two corner screens with no open ground at all (smoke-2's sow) falls
       * through to the rest of the far country rather than to no lair. */
      let placed = false;
      for (const pool of [...awayPools, homeBand, band, home, far, unused, all]) {
        if (!pool.length) continue;
        const first = (mix(seed, 9500 + i * 41) >>> 0) % pool.length;
        for (const pad of [2, 1, 0]) {
          for (let k = 0; k < pool.length && !placed; k++) {
            const pick = pool[(first + k) % pool.length];
            const at = this.openPatch(pick.x, pick.y, COLS / 2, ROWS / 2, pad);
            if (!at) continue;
            used.add(pick.x + ',' + pick.y);
            for (let ny = -1; ny <= 1; ny++) for (let nx = -1; nx <= 1; nx++) used.add((pick.x + nx) + ',' + (pick.y + ny));   // lairs never share a wall (the design lens, round 47: on three seeds of seven the sow joined the shell's fight from the next screen)
            out.push({ kind, x: pick.x, y: pick.y, px: pick.x * PW + at[0] * TS + TS / 2, py: pick.y * PH + at[1] * TS + TS / 2 });
            placed = true;
          }
          if (placed) break;
        }
        if (placed) break;
      }
    });
    /* THE SOW IS NEAREST BY DISTANCE, NOT BY BAND (LS-383, the merge). On islands the bands are per pool and a
     * beast placed across the water can sit nearer than the sow's own band; so the three spots are kept and the
     * kinds dealt over them by distance: the sow on the nearest, the other two turning with the seed. */
    if (out.length === 3) {
      const d = (l) => this.world.dist.get(l.x + ',' + l.y) ?? 99;
      out.sort((a, b) => d(a) - d(b) || a.y - b.y || a.x - b.x);
      const rest = rot ? ['bramblemother', 'oldshell'] : ['oldshell', 'bramblemother'];
      out[0].kind = 'sow'; out[1].kind = rest[0]; out[2].kind = rest[1];
    }
    this.world.lairs = out;   // the standing stones read them (deepmark.ts bearingWords)
    /* EVERY WORLD TEACHES EVERY NOUN (the lore lens, round 43): a charm with no stone left its beast's word unread in
     * that world. A charm no stone carries takes the stone nearest its lair. */
    if (this.world.stones && this.world.stones.size && this.world.order) {
      const marks = new Set(this.world.stones.values());
      this.world.order.forEach((charm, i) => {
        if (marks.has(charm) || !out[i % out.length]) return;
        const l = out[i % out.length]; let best = null, bd = 1e9;
        for (const key of [...this.world.stones.keys()].sort()) { const [sx, sy] = key.split('|')[0].split(',').map(Number); const d = Math.max(Math.abs(sx - l.x), Math.abs(sy - l.y)); if (d < bd) { bd = d; best = key; } }
        if (best) this.world.stones.set(best, charm);
      });
    }
    /* ONE STONE A SEED SAYS RAKO (LS-232, the lore lens): every stone named a lair, so the one word in the
     * script that names nothing -- the withheld word, the reason a decodable script can carry a mystery --
     * was never read anywhere. One stone in each world keeps it. */
    /* ...AND ON PURPOSE (the lore lens, round 41): the stone farthest from every lair, so the one word that names nothing stands where nothing is; ties go to the first sorted key. */
    if (this.world.stones && this.world.stones.size) { const keys = [...this.world.stones.keys()].sort(); const farFrom = (key) => { const [sx, sy] = key.split('|')[0].split(',').map(Number); return Math.min(...out.map((l) => Math.max(Math.abs(l.x - sx), Math.abs(l.y - sy)))); }; let k = keys[0], best = -1; for (const key of keys) { const d = farFrom(key); if (d > best) { best = d; k = key; } }   /* sorted: a Map's order shifts with every screen made, and the rako stone moved with the play path (the robustness lens, round 40) */ this.world.stones.set(k, 'unknown'); this.world.rakoKey = k; }   // rakoKey: generateScreen re-places a screen's stones and would otherwise write over it
    return this._lairs = out;
  }
  /** The tile of screen (sx, sy) nearest (cx, cy) with open ground `pad`
   *  tiles all round it, or null. */
  openPatch(sx, sy, cx, cy, pad = 1): [number, number] | null {
    const tiles = this.screenAt(sx, sy).tiles;
    let best: [number, number] | null = null, bestD = 1e9;
    for (let ty = 1 + pad; ty < ROWS - 1 - pad; ty++) for (let tx = 1 + pad; tx < COLS - 1 - pad; tx++) {
      let open = true;
      for (let dy = -pad; dy <= pad && open; dy++) for (let dx = -pad; dx <= pad; dx++) if (!walkableTile(tiles[ty + dy][tx + dx])) { open = false; break; }
      if (!open) continue;
      const d = (tx - cx) * (tx - cx) + (ty - cy) * (ty - cy);
      if (d < bestD) { bestD = d; best = [tx, ty]; }
    }
    return best;
  }
  beasts() {
    if (this.world) this.world.slain = new Set(this.state.beastsSlain || []);   // the stones read which beasts have fallen (deepmark.ts bearingWords)
    if (!this.state.beasts) this.state.beasts = this.beastLairs().map((l) => ({ kind: l.kind, home: [l.x, l.y], x: l.px, y: l.py, hp: ENEMY_DEFS[l.kind].hp, alive: true, tx: l.px, ty: l.py, seen: false }));
    return this.state.beasts;
  }
  tickBeasts() {
    this.tickBeastMarks();   // what a living beast does to its country (LS-379)
    const list = this.beasts();
    for (let i = 0; i < list.length; i++) {
      const b = list[i]; if (!b || !b.alive) continue;
      try { this.tickBeast(list, i, b); } catch (err) {
        /* A RECORD THAT THROWS IS REBUILT (LS-230): a malformed save record
         * threw every frame, and the frame loop's recovery reloaded the same
         * slot. The record is remade from its lair; the mark, if lit, stays. */
        const lair = this.beastLairs().find((l) => l.kind === b.kind) || this.beastLairs()[i];
        if (lair) list[i] = { kind: lair.kind, home: [lair.x, lair.y], x: lair.px, y: lair.py, hp: ENEMY_DEFS[lair.kind].hp, alive: !this.state.beastsSlain.includes(lair.kind), tx: lair.px, ty: lair.py, seen: false };
        else list.splice(i, 1);
        this.beastBodies.delete(b);
      }
    }
  }
  tickBeast(list, i, b) {
    {
      let body = this.beastBodies.get(b);
      if (!body) { body = this.enemies.find((e) => e && e.def && e.def.beast && e.type === b.kind && !e.dead); if (body) this.beastBodies.set(b, body); }   // a record remade or reloaded adopts the body already standing, and does not raise a second (the robustness lens, round 46)
      if (body) {
        if (body.dead || !this.enemies.includes(body)) { this.beastBodies.delete(b); if (body.dead) b.alive = false; return; }
        b.x = body.cx; b.y = body.cy; b.hp = body.hp;
        // the first SIGHT of it, whenever that comes (LS-233): the roar fired only at the spawn, and a body made on the next screen was never seen
        if (!b.seen && this.inWindow(body)) { b.seen = true; this.sfx('roar', body.x); }
        const bx = Math.floor(b.x / PW), by = Math.floor(b.y / PH);
        if (Math.abs(bx - this.area.x) > 1 || Math.abs(by - this.area.y) > 1) {   // out of reach: the record walks on alone
          this.enemies = this.enemies.filter((e) => e !== body); this.beastBodies.delete(b);
        }
        return;
      }
      // no body: the record heals a point every five seconds toward its full (LS-228: hit-and-leave is not free), and roams
      if (this.runFrames % 300 === 0 && b.hp < (b.max || ENEMY_DEFS[b.kind].hp)) b.hp++;   // toward its GROWN full (the robustness lens, round 46: a beast grown past its kind's life never healed at all)   // the run's clock (the robustness lens, round 39: an odd title idle still moved every beast)
      if (this.runFrames % 2 === 0) {
        const dx = b.tx - b.x, dy = b.ty - b.y, d = Math.hypot(dx, dy);
        if (d < 3) {
          const rng = new RNG(mix(this.seedNum, i, Math.floor(this.runFrames / 4)));   // the run's own clock, not the title's (LS-230: determinism)
          const sx = Math.max(0, Math.min(WORLD_W - 1, b.home[0] + rng.int(5) - 2)), sy = Math.max(0, Math.min(WORLD_H - 1, b.home[1] + rng.int(5) - 2));   // a territory of five screens across (LS-228)
          // only a screen already made: generating one here was a hitch in the middle of a frame (LS-227)
          /* ...AND A SCREEN NOT YET MADE IS MADE (round 53's robustness lens). "Only a screen already made" tied
           * the roam to the screen set, which is a rendering schedule -- screens are made as their pictures bake --
           * so a headless game's beasts wandered differently from a drawn one's on 4 of 6 seeds, and the suite's
           * beasts were not the browser's. Generating the tiles of one screen is a fraction of a millisecond
           * (measured: see the round-53 note); it is the bake that was the hitch, and openPatch does not bake. */
          const at = this.openPatch(sx, sy, 2 + rng.int(COLS - 4), 2 + rng.int(ROWS - 4), 1);
          /* ...AND NEVER INTO THE FIRST THREE SCREENS (the design lens, round 51). A LAIR is placed at least three
           * screens from the start and that rule is checked; the ROAM was clamped only to the world's bounds, so a
           * beast whose lair sat exactly on the line wandered inside it -- measured, 7 of 36 beasts stood within
           * three screens of the start after 2400 idle frames. A player who never presses a key died at spawn
           * inside a minute on 8 of 12 fresh seeds. The opening minute is the one place the game must be gentle,
           * and the roam now obeys the rule its own lair was placed by: too near, and it keeps the target it had. */
          const near = (this.world.dist.get(sx + ',' + sy) ?? 9) < 3;
          // ...and never off its island (2026-09-19): the roam walked a straight line over the sea onto other islands, the glade's among them
          const W0 = this.world.biome[0].length, isle = this.world.island;
          const sameIsle = !isle || isle[sy * W0 + sx] === isle[b.home[1] * W0 + b.home[0]];
          if (at && !near && sameIsle) { b.tx = sx * PW + at[0] * TS + TS / 2; b.ty = sy * PH + at[1] * TS + TS / 2; }
        } else {
          /* ...AND THE WALK OBEYS IT TOO. Clamping only the TARGET took the beasts standing inside the first three
           * screens from 7 of 36 to 4: a straight line between two screens that are both far enough can still pass
           * through one that is not, because the distance is a flood fill and not a radius. A step into the ring is
           * refused and the beast takes a new target on its next tick, which puts it at 0 of 36. */
          const step = Math.min(d, 2.2);
          const nx2 = b.x + dx / d * step, ny2 = b.y + dy / d * step;
          const nd = this.world.dist.get(Math.floor(nx2 / PW) + ',' + Math.floor(ny2 / PH)) ?? 9;
          if (nd >= 3) { b.x = nx2; b.y = ny2; } else { b.tx = b.x; b.ty = b.y; }
        }
      }
      const bx = Math.floor(b.x / PW), by = Math.floor(b.y / PH);
      if (Math.abs(bx - this.area.x) <= 1 && Math.abs(by - this.area.y) <= 1) {
        const size = ENEMY_DEFS[b.kind].size;
        let x = b.x, y = b.y;
        if (!walkableTile(this.tileAtPx(x, y))) { x = b.tx; y = b.ty; }   // between two open spots it may be in rock: stand at the spot it was walking to
        /* ...AND ITS HOME IS THE SCREEN IT STANDS ON (2026-09-19). The home was stamped from the record's screen while
         * the body stood at the target's, one screen over, 20-60% of the time: the idle leash then refused every
         * step (a sow that never walked between charges) and the first blow's `keepToHome` clamped the body 55-103
         * px back over the seam -- the 'bosses teleporting' of play. A target two screens from the player is left
         * for the next tick rather than raised and dropped, which moved the record two screens in one frame. */
        const hx = Math.floor(x / PW), hy = Math.floor(y / PH);
        if (Math.abs(hx - this.area.x) <= 1 && Math.abs(hy - this.area.y) <= 1) {
        const e = new Enemy(b.kind, x - size / 2, y - size / 2, new RNG(mix(this.seedNum, i, Math.floor(this.runFrames / 4), 77)));
        e.hp = b.hp; e.maxHp = Math.max(b.max || e.def.hp, b.hp); e.spawnFx = 0; e.home = hx + ',' + hy; e.territory = this.beastTerritory(b);
        { const frac = e.hp / e.maxHp; e.shed = frac <= 0.33 ? 2 : frac <= 0.66 ? 1 : 0; }   // the sap already shed is shed: a beast reloaded at 25/40 dropped its first band again (the robustness lens, round 41)   // a grown beast's bar is its own
        this.enemies.push(e); this.beastBodies.set(b, e);
        }
      }
    }
  }

  /* WHERE THE WORLD'S WEAPONS LIE. Three caches per world -- the spear
   * near, the axe further, the torch further still -- each on a screen picked
   * by the seed from those the given number of screens from the start, so a
   * run finds them in an order that makes sense and no two runs on one seed
   * disagree. The tile is the middle of the screen, fitted to open ground by
   * `dropItem` when the screen loads. */
  /** A beast's ground (LS-511): its lair's chunk and two round it, less the chunks within three of the glade by road --
   *  the same ground its record roams when nobody is near (`tickBeast`). */
  beastTerritory(b) {
    const [hx, hy] = b.home;
    const cx0 = Math.max(0, hx - 2), cy0 = Math.max(0, hy - 2), cx1 = Math.min(WORLD_W - 1, hx + 2), cy1 = Math.min(WORLD_H - 1, hy + 2);
    const barred = new Set<string>();
    for (let y = cy0; y <= cy1; y++) for (let x = cx0; x <= cx1; x++) if ((this.world.dist.get(x + ',' + y) ?? 9) < 3) barred.add(x + ',' + y);
    return { x0: cx0 * PW, y0: cy0 * PH, x1: (cx1 + 1) * PW, y1: (cy1 + 1) * PH, barred };
  }
  _loot: { kind: string; x: number; y: number; px: number; py: number }[] | null = null;
  /** WHERE A BEAST'S ANSWER LIES, FOR THE WORLD TO SHOW (LS-464, 2026-09-26). Every world puts the spear, the axe and the
   *  torch on their own beast's island near its lair, and nothing said so: the newcomer probe reached the sow's lair screen
   *  and wandered off the other way, the axe one screen east. The answers still lying in the world on the island the body
   *  stands on, in world pixels: the scene warms the ground under them and the HUD points to one out of view. */
  /** THE ANSWER POINTS AT ITS BEAST (2026-09-26). The gold star led the newcomer probe to the axe at 55 seconds, and
   *  then nothing led anywhere: for two and a half minutes it wandered the sow's island holding the sow's answer and
   *  never met the sow. Now, with an answer in the bag, its beast is marked -- a red star at the edge of the view in its
   *  direction, if it roams this island and lives -- so the find says what it is for, without a word. */
  quarryBeacons(): { kind: string; wx: number; wy: number }[] {
    if (!this.world || !this.world.island || !this.area || (globalThis as any).__noAnswerBeacons || !this.state.beasts) return [];
    const W0 = this.world.biome[0].length, isle = (x: number, y: number) => this.world.island[y * W0 + x];
    const here = isle(Math.floor(this.player.cx / (COLS * TS)), Math.floor(this.player.cy / (ROWS * TS)));
    if (here === undefined || here < 0) return [];
    const have = new Set((this.state.bag?.slots || []).filter(Boolean).map((q: any) => q.kind));
    const QUARRY: Record<string, string> = { axe: 'sow', spear: 'oldshell', torch: 'bramblemother' };
    const out: { kind: string; wx: number; wy: number }[] = [];
    for (const r of this.state.beasts) {
      if (!r || r.alive === false || !r.home || isle(r.home[0], r.home[1]) !== here || !Object.keys(QUARRY).some((k) => QUARRY[k] === r.kind && have.has(k))) continue;
      const body = (this.enemies || []).find((e: any) => e && !e.dead && e.type === r.kind);
      out.push({ kind: r.kind, wx: body ? body.cx : r.x, wy: body ? body.cy : r.y });
    }
    return out;
  }
  answerBeacons(): { kind: string; wx: number; wy: number }[] {
    if (!this.world || !this.world.island || !this.area || (globalThis as any).__noAnswerBeacons) return [];
    const W0 = this.world.biome[0].length, isle = (x: number, y: number) => this.world.island[y * W0 + x];
    const here = isle(Math.floor(this.player.cx / (COLS * TS)), Math.floor(this.player.cy / (ROWS * TS)));
    if (here === undefined || here < 0) return [];
    const taken = new Set(this.state.lootTaken || []); const have = new Set((this.state.bag?.slots || []).filter(Boolean).map((q: any) => q.kind));
    const out: { kind: string; wx: number; wy: number }[] = [];
    for (const l of this.worldLoot()) {
      if (!ANSWER_KINDS.includes(l.kind) || have.has(l.kind) || taken.has(l.x + ',' + l.y + ':' + l.kind) || isle(l.x, l.y) !== here) continue;
      /* if the cache is on the glass as a thing lying, beacon the thing (it may have been knocked about); else the placement */
      const lying = (this.items || []).find((it: any) => !it.dead && it.loot === l.x + ',' + l.y + ':' + l.kind);
      out.push({ kind: l.kind, wx: lying ? lying.x + (lying.w || 8) / 2 : l.x * COLS * TS + l.px + 6, wy: lying ? lying.y + (lying.h || 8) / 2 : l.y * ROWS * TS + l.py + 6 });
    }
    return out;
  }
  worldLoot() {
    if (this._loot) return this._loot;
    const out: { kind: string; x: number; y: number; px: number; py: number }[] = [];
    const seed = this.world.seedNum;
    const start = this.world.start;
    const all = [...this.world.dist.entries()].map(([k, d]) => { const [x, y] = k.split(',').map(Number); return { x, y, d }; })
      .filter(s => !(s.x === start.x && s.y === start.y)).sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
    const used = new Set<string>();
    const taken = new Set<string>();   // screen+tile already holding a cache, so a shared screen never stacks two on one tile
    /* A BAND IS A FRACTION OF THE COUNTRY, NOT A COUNT OF SCREENS (2026-09-16, measured).
     *
     * LOOT_BANDS is written on the scale the world had when it was one island: one to eight screens from the glade,
     * where eight was the far edge. The archipelago is twenty to thirty-nine screens deep -- the flood crosses water
     * -- and the bands were never rescaled, so every band's ceiling of eight fell inside the first fifth of the map.
     * Measured on route-1 before this: THIRTY-ONE OF THIRTY-TWO caches lay in the near third of the world, none in
     * the middle third, one in the far third. You sail four screens of open sea to a beast's island and there is
     * nothing on it, which is the same complaint LS-385 made about the situations and the same answer: the content
     * was keyed to a number the world had outgrown.
     *
     * So a band is read as a fraction of THIS world's depth, with the old eight-deep world as the fixed point: band
     * 1 is still the glade's doorstep and band 8 is still the far edge, wherever that edge now is. On a world
     * thirty-nine deep, 1..3 becomes 1..12 and 4..8 becomes 17..39. */
    const depths = [...this.world.dist.values()].filter((v: any) => Number.isFinite(v)) as number[];
    const depth = depths.length ? Math.max(...depths) : BAND_SCALE;
    /* ...AND UPKEEP IS NOT A REWARD (2026-09-16, measured by the bot). The whetstone puts an edge back on a blade and
     * the rivet mends what is dented; a blade with no blows left strikes as BARE HANDS, which cannot pass the old
     * shell's guard at all. Stretching their bands with everything else moved the whetstone from two screens out to
     * six and the rivet from four to ten on seed q4, and quest.ts's bot -- which is deterministic, so this is cause
     * and not variance -- went from felling all three with one death to dying SEVENTEEN times at the shell with a
     * dull spear, six minutes of it spent sailing back to the hull after each. The far country gets the rewards; the
     * doorstep keeps the things that keep you going. */
    const UPKEEP = new Set(['whetstone', 'rivet', 'sap']);
    const banded = (v: number, lo: number, kind: string) => (UPKEEP.has(kind) ? v : bandAt(v, depth, lo));
    // ...and the three charms (LS-228): they were reachable only through ?unlock once the caves went
    /* A WEAPON LIES NEAR THE BEAST IT ANSWERS (the lore lens, 2026-09-05). STORY says 'a spear left in the
     * grass is telling you something wants reach', and the spear lay four, three, nine screens from the old
     * shell. Each weapon's cache is placed within two screens of its beast's lair when a screen there will
     * take it, so the find is the hint. The charms keep their distances from the start. */
    const lairs = this.beastLairs();
    const ANSWERS: Record<string, string> = { spear: 'oldshell', axe: 'sow', torch: 'bramblemother' };
    /* ...ON THE BEAST'S OWN ISLAND (LS-383, the merge): two screens from a lair is open sea on most sides now, and a
     * cache with nowhere to lie fell through to the last stage, which is any screen in the world. */
    const isleOf = (s) => this.world.island ? this.world.island[s.y * WORLD_W + s.x] : 0;
    const nearLair = (s, kind) => { const l = lairs.find(q => q.kind === ANSWERS[kind]); return !!l && !(s.x === l.x && s.y === l.y) && Math.max(Math.abs(s.x - l.x), Math.abs(s.y - l.y)) <= 2 && isleOf(s) === isleOf(l); };
    const optional = (globalThis as any).__allKinds ? new Set(OPTIONAL_KINDS) : optionalKindsFor(seed);   // a seeded dozen of the optional kinds (round 52); __allKinds places every one, for the sheets
    (((globalThis as any).__noAnswers ? LOOT_BANDS.filter(([k]) => !ANSWER_KINDS.includes(k as string)) : LOOT_BANDS).filter(([k]) => !OPTIONAL_KINDS.includes(k as string) || optional.has(k as string)) as [string, number, number][]).forEach(([kind, lo, hi], i) => {   // the treads and the bell lie out on the way (LS-239)
      // ...and on the way in, not past the lair (the design lens, round 44: on seven seeds of fifteen the first beast was met before its weapon)
      const lairD = (k) => { const l = lairs.find(q => q.kind === ANSWERS[k]); return l ? (this.world.dist.get(l.x + ',' + l.y) ?? 99) : 99; };
      /* "on the way in" is a rule of the road (round 44), and a beast across the water has no road: on its island the
       * whole ring is the pool, or the one screen nearer the glade than the lair -- a sliver of coast with no tile to
       * stand on -- was the only candidate and the axe went home (near-3) */
      const homeIsle = this.world.island ? this.world.island[this.world.start.y * WORLD_W + this.world.start.x] : 0;
      const crossing = (k) => { const l = lairs.find(q => q.kind === ANSWERS[k]); return !!l && isleOf(l) !== homeIsle; };
      /* ...as a PREFERENCE, not a filter: the ring screens nearer the glade than the lair come first, the rest of the
       * ring behind them, and the stages walk the pool in that order (`first` is 0 for an answer) */
      const onWay = (s, k) => s.d <= lairD(k);
      let pool = ANSWERS[kind as string]
        ? (() => { const ring = all.filter(s => nearLair(s, kind) && !used.has(s.x + ',' + s.y)); return [...ring.filter(s => onWay(s, kind as string)), ...ring.filter(s => !onWay(s, kind as string))]; })()
        : [];
      if (!pool.length && ANSWERS[kind as string]) pool = all.filter(s => nearLair(s, kind) && !used.has(s.x + ',' + s.y));
      if (!pool.length && ANSWERS[kind as string]) pool = all.filter(s => nearLair(s, kind));   // a small island's ring is soon all taken: an answer shares a screen before it leaves its beast's country (lore-4's spear went home)
      if (!pool.length) pool = all.filter(s => s.d >= banded(lo as number, lo as number, kind as string) && s.d <= banded(hi as number, lo as number, kind as string) && !used.has(s.x + ',' + s.y));
      if (!pool.length) pool = all.filter(s => !used.has(s.x + ',' + s.y));
      if (!pool.length) return;
      const first = ANSWERS[kind as string] ? 0 : (mix(seed, 9100 + i * 37) >>> 0) % pool.length;
      /* On open ground with open ground all round it, nearest the middle of
       * the screen: a cache in a one-tile gap between boulders is one you
       * cannot stand next to, and the middle of some screens is sea. A screen
       * with no such patch is passed over for the next candidate. */
      const cx = COLS / 2 + ((mix(seed, 9200 + i) >>> 0) % 5) - 2, cy = ROWS / 2 + ((mix(seed, 9300 + i) >>> 0) % 5) - 2;
      /* ...AND A CACHE IS NEVER DROPPED FOR WANT OF A PERFECT SPOT (2026-09-10).
       * The eight-of-eight rule was the only rule and a screen was allowed one
       * cache, so kinds late in the table were silently NOT PLACED once the
       * good screens ran out: `soles` went missing on loot-0, and loot-19 lost
       * FIVE of them, which took the reachability sweep's count with it.
       * Three stages, each only reached when the one before found nothing
       * anywhere in the pool: open all round, then three sides of four open,
       * then the same but sharing a screen that already holds one. A second
       * cache never lands on the first one's tile. */
      const stages: [number, boolean][] = ANSWERS[kind as string] ? [[8, true], [3, true], [1, true], [0, true], [3, false]] : [[8, true], [3, true], [3, false]];   // ...and for an answer, any walkable tile of its beast's ring, margins and all, before it leaves the country   // an answer stays in its beast's country while any tile there will hold it; it is never left out of the world (loot-0 lost the axe)
      /* THE ON-WAY SCREENS GET EVERY STAGE FIRST (2026-09-12; world.ts round 44 at 7 of 12 after the world moved): the
       * stages walked the whole pool at eight-open before trying three-open anywhere, so a far screen with a wide
       * clearing beat a near screen with a narrow one, and the preference was only an ordering within a stage. */
      const subPools = ANSWERS[kind as string] ? [pool.filter(s => onWay(s, kind as string)), pool.filter(s => !onWay(s, kind as string))].filter(sp => sp.length) : [pool];
      if (!subPools.length) subPools.push(pool);
      const placed = subPools.some((sp, si) => stages.some(([need, fresh]) => {
        const cands = fresh ? sp : (si === subPools.length - 1 ? all : []);
        if (!cands.length) return false;
        for (let k = 0; k < cands.length; k++) {
          const pick = cands[(first + k) % cands.length];
          const tiles = this.screenAt(pick.x, pick.y).tiles;
          let best: [number, number] | null = null, bestD = 1e9;
          const m = need === 0 ? 1 : 2;   // a coastal sliver has its land at the margins
          for (let ty = m; ty < ROWS - m; ty++) for (let tx = m; tx < COLS - m; tx++) {
            if (!walkableTile(tiles[ty][tx])) continue;
            if (taken.has(pick.x + ',' + pick.y + ':' + tx + ',' + ty)) continue;
            let round = 0;
            for (const [dx, dy] of need === 8 ? [[-1,-1],[0,-1],[1,-1],[-1,0],[1,0],[-1,1],[0,1],[1,1]] : [[0,-1],[-1,0],[1,0],[0,1]])
              if (walkableTile(tiles[ty + dy][tx + dx])) round++;
            if (round < need) continue;
            const d = (tx - cx) * (tx - cx) + (ty - cy) * (ty - cy);
            if (d < bestD) { bestD = d; best = [tx, ty]; }
          }
          if (!best) continue;
          used.add(pick.x + ',' + pick.y);
          taken.add(pick.x + ',' + pick.y + ':' + best[0] + ',' + best[1]);
          out.push({ kind: kind as string, x: pick.x, y: pick.y, px: best[0] * TS + 2, py: best[1] * TS + 2 });
          return true;
        }
        return false;
      }));
      if (!placed) return;
    });
    /* ...AND ONE THING THAT IS NOT ON WALKABLE GROUND AT ALL (LS-302). The ebb
     * exposes a basin for a minute and nothing was ever down there to find, so
     * the silt lung's own verb bought the player nothing. The pearl lies on the
     * bed of a deep basin -- a WATER tile with at least three of its four
     * sides also deep, so it is a basin and not the lip of a puddle -- and the
     * only way to stand on that tile is to call the ebb. */
    {
      /* GENERATED FRESH, NOT `screenAt` (2026-09-10). `loadArea` does
       * `this.tiles = s.tiles`, so the live tile array IS the generator's own
       * cached record for that screen -- and the tide writes into it. So
       * `screenAt` answers "what the world made" for every screen except the
       * ones you have stood on, where it answers what the weather left. On
       * loot-1 that moved the pearl from screen 6,7 to 0,0 depending on
       * whether an area had been loaded first, which is a cache that is not
       * where the game said it was. This pass asks the generator directly, so
       * the placement is a function of the seed and nothing else. (The
       * aliasing itself is a bigger fish: see the board.) */
      const bedOf = (s: any, need: number, kind = T.WATER) => {
        const tiles = generateScreen(this.world, s.x, s.y).tiles;
        let best: [number, number] | null = null, bestN = -1;
        for (let ty = 2; ty < ROWS - 2; ty++) for (let tx = 2; tx < COLS - 2; tx++) {
          if (tiles[ty][tx] !== kind) continue;
          let n = 0;
          for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) if (tiles[ty + dy][tx + dx] === kind) n++;
          if (n > bestN) { bestN = n; best = [tx, ty]; }
        }
        return bestN >= need ? best : null;
      };
      const near = all.filter((s) => s.d >= 2 && s.d <= 7);
      const order = [...near, ...all.filter((s) => !near.includes(s))];
      const first = (mix(seed, 9500) >>> 0) % Math.max(1, order.length);
      /* Three sides deep is a basin; two is a channel; one is the lip of a
       * pool. Every step down is only reached when nothing better exists in
       * the whole world -- loot-5 and loot-8 have deep water but no tile with
       * three deep sides anywhere on the island, and a world without its
       * pearl is a world missing a cache. */
      /* ...AND A WORLD WITH NO DEEP WATER STILL GETS ONE, in the deepest basin
       * it has. loot-5 has 396 shallow tiles and NOT ONE deep: there is
       * nothing there for an ebb to uncover, so the pearl simply lies in the
       * shallows and the situation is the situation that world can offer.
       * Every world holding every kind is worth more than the gate being
       * gated everywhere -- and the check below asks for the gate only where
       * the pearl actually lies on deep water. */
      placePearl: for (const [need, kind] of [[3, T.WATER], [2, T.WATER], [1, T.WATER], [3, T.SHALLOW], [1, T.SHALLOW]] as [number, number][]) {
        for (let k = 0; k < order.length; k++) {
          const s2 = order[(first + k) % order.length];
          const spot = bedOf(s2, need, kind);
          if (!spot) continue;
          out.push({ kind: 'pearl', x: s2.x, y: s2.y, px: spot[0] * TS + 2, py: spot[1] * TS + 2, bed: true } as any);
          break placePearl;
        }
      }
    }
    /* THE COALHEART LIES IN THE COUNTRY THAT BURNS (LS-302, 2026-09-10).
     *
     * The fifth situation the card asks for is "ground that is only survivable
     * once you hold a trophy", and both halves were already built and never
     * met: ash country's base warmth is 0.79 against a scorch line of 0.8, so
     * its ground burns whoever stands on it once the sun is up, and the
     * bramble mother's mantle is what keeps that off. What was missing was a
     * REASON to be there. The charm that makes fire last longer is what the
     * hottest country holds, which is the one cache in the game whose comfort
     * is bought by felling a beast rather than by walking further. Placed on
     * the warmest reachable screen; where a world has no hot country at all it
     * goes to the coldest, which the sow's hide answers instead. */
    {
      /* WHICH GROUND ACTUALLY CROSSES A LINE. The field eases a screen's
       * temperature toward `base + 0.12 * (warm - 0.5) - (night ? 0.12 : 0)`,
       * so the noon sun lifts a country by 0.06 and the night drops it by
       * 0.18. Only ash (0.79 -> 0.85) clears the scorch line of 0.8, and the
       * cold countries (scree 0.38 -> 0.20, fen 0.32 -> 0.14) fall under the
       * frost line of 0.22. Those two are exactly the extremes a trophy
       * answers -- the bramble mother's mantle and the sow's hide -- so the
       * charm goes to whichever line this world's ground actually crosses,
       * and to its plain hottest country in the rare world that crosses
       * neither. */
      const warmth = (x: number, y: number) => BIOME_WARMTH[this.world.biome[y][x]] ?? 0.5;
      const burns = (x: number, y: number) => warmth(x, y) + 0.06 > SCORCH_LINE;
      const bites = (x: number, y: number) => warmth(x, y) - 0.18 < FROST_LINE;
      /* Ground of equal warmth is broken toward a hunt's approach first and
       * the nearer screen second (LS-302's last line). */
      const route = this.approachScreens();
      const onRoute = (s2: any) => (route.has(s2.x + ',' + s2.y) ? 0 : 1);
      const hot = [...all].sort((a, b) => warmth(b.x, b.y) - warmth(a.x, a.y) || onRoute(a) - onRoute(b) || a.d - b.d);
      const cold = [...all].sort((a, b) => warmth(a.x, a.y) - warmth(b.x, b.y) || onRoute(a) - onRoute(b) || a.d - b.d);
      const extreme = burns(hot[0].x, hot[0].y) ? hot : bites(cold[0].x, cold[0].y) ? cold : hot;
      /* AND NOT ON DAMP GROUND (2026-09-17). The tile was chosen for lying near the middle of its screen, and the
       * temperature field cools the ground within two tiles of standing water -- a rule that was dead until the
       * wetness field was ticked outside a fire tick. A whole screen can be damp: on one seed in three the hottest
       * country's coalheart lay on ground that never crossed the scorch line, so the trophy the situation exists to
       * ask for was not needed to stand there. The screens are walked twice: first for one with dry ground, then,
       * if the world has none, for any at all -- the situation is better damp than missing. */
      const wetNearOn = (tiles: any, tx: number, ty: number) => {
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
          const row = tiles[ty + dy]; const k = row && row[tx + dx];
          if (k === T.WATER || k === T.SHALLOW || k === T.SEA) return true;
        }
        return false;
      };
      let laid = false;
      for (const dryOnly of [true, false]) {
        if (laid) break;
        for (const s2 of extreme) {
          const tiles = generateScreen(this.world, s2.x, s2.y).tiles;
          let best: [number, number] | null = null, bestD = 1e9;
          const cx2 = COLS / 2, cy2 = ROWS / 2;
          for (let ty = 2; ty < ROWS - 2; ty++) for (let tx = 2; tx < COLS - 2; tx++) {
            if (!walkableTile(tiles[ty][tx])) continue;
            if (taken.has(s2.x + ',' + s2.y + ':' + tx + ',' + ty)) continue;
            const damp = wetNearOn(tiles, tx, ty);
            if (dryOnly && damp) continue;
            const d = (tx - cx2) * (tx - cx2) + (ty - cy2) * (ty - cy2) + (damp ? 10000 : 0);
            if (d < bestD) { bestD = d; best = [tx, ty]; }
          }
          if (!best) continue;
          used.add(s2.x + ',' + s2.y);
          taken.add(s2.x + ',' + s2.y + ':' + best[0] + ',' + best[1]);
          out.push({ kind: 'coal', x: s2.x, y: s2.y, px: best[0] * TS + 2, py: best[1] * TS + 2 });
          laid = true;
          break;
        }
      }
    }
    /* THE TUSK LIES ON A SPIT, AND SOMETHING STANDS OVER IT (LS-302, the sixth
     * situation, 2026-09-10). systems.ts:202 says it aloud already -- "a
     * shellback is stone, a charger is a boar, and luring either into the deep
     * is a way to fight it" -- and nothing on the island had ever POSED that.
     * Three seconds of deep water drowns a body that cannot float, so a fight
     * on ground with water round it is a fight the terrain can win, which is
     * the one answer in the game that costs no vigor and no weapon.
     *
     * Measured before building it: 1 of 184 cache screens sat on ground the
     * edge cannot reach on foot, so the world does not make this by itself.
     * Graded like the pearl's basin, best first: an islet no dry path reaches,
     * then a spit with three sides deep, then two. */
    {
      const isle = (sx: number, sy: number) => {
        const tiles = generateScreen(this.world, sx, sy).tiles;
        const deep = (x: number, y: number) => tiles[y]?.[x] === T.WATER;   // tidal water only: the tusk's situation is the tide taking the spit, and the sea has no tide
        let best: { tx: number; ty: number; grade: number } | null = null;
        for (let ty = 1; ty < ROWS - 1; ty++) for (let tx = 1; tx < COLS - 1; tx++) {
          const v = tiles[ty][tx];
          if (!walkableTile(v) || isWet(v)) continue;             // dry standable ground, which is what the tusk lies on
          if (taken.has(sx + ',' + sy + ':' + tx + ',' + ty)) continue;
          let deepSides = 0, wetSides = 0, deepNear = 0;
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            if (deep(tx + dx, ty + dy)) deepSides++;
            /* SHALLOW, NOT THE SEA. `isWet` says yes to T.SEA, and `isSwimAt`
             * -- the rule the drowning reads -- says T.WATER and nothing else,
             * so nothing can drown in the ocean. A tile ringed SEA,SAND,SEA,
             * SEA scored like a spit and had no answer on it: tusk-4. */
            const w = tiles[ty + dy]?.[tx + dx];
            if (w === T.WATER || w === T.SHALLOW) wetSides++;
          }
          for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (deep(tx + dx, ty + dy)) deepNear++;
          /* WHAT MAKES THIS THE RIGHT GROUND. Not "the edge cannot walk here",
           * which was the first rule and was measured wrong: `walkableTile`
           * says no to SHALLOW, so a flood fill over it called any tile you
           * have to WADE to an islet -- and wading is free. Four of six worlds
           * put the tusk on a shallow puddle's lip and one on dry ground with
           * no water within two tiles at all. What the situation needs is deep
           * water AT HAND, because deep water is the thing that drowns. */
          /* ...AND A CACHE IS NEVER DROPPED FOR WANT OF A PERFECT SPOT, which
           * is the bands' own rule three stages up. loot-5 is the world with
           * 396 shallow tiles and not one deep -- the same world that has no
           * basin for the pearl -- and with a floor of "three wet sides" it
           * lost the tusk altogether. A shallow lip, then any wet side at
           * all: the terrain cannot drown anything there, so the tusk is
           * merely surrounded, and it is still findable. */
          const grade = deepSides >= 2 ? 4 : deepSides === 1 && wetSides >= 3 ? 3 : deepSides === 1 ? 2
            : deepNear >= 2 && wetSides >= 2 ? 1 : wetSides >= 3 ? 0.5 : wetSides >= 1 ? 0.25 : 0;
          if (grade && (!best || grade > best.grade)) best = { tx, ty, grade };
        }
        return best;
      };
      /* AND IT STAYS A FAR PRIZE. The tusk was a 4-to-8 band before it had a
       * rule of its own, and the first version of this pass walked `all` in
       * distance order and stopped at the first spit it found -- which put it
       * one screen from the spawn on tusk-0, a guarded set-piece before the
       * player has a weapon. The far pool first, then anything. */
      let pick: { s: any; at: { tx: number; ty: number; grade: number } } | null = null;
      /* ...AND ON THE WAY TO A LAIR WHERE ONE WILL DO. A set-piece off the
       * route is a set-piece most runs never see (LS-302's last line). */
      const route = this.approachScreens();
      const score = (s2: any, at: any) => at.grade * 2 + (route.has(s2.x + ',' + s2.y) ? 1 : 0);
      /* THE POOL IS AUTHORITATIVE, and the score only orders WITHIN it. The
       * first cut carried the best score across all three pools, which let a
       * grade-4 spit two screens from the spawn beat a grade-3 one out in the
       * band -- tusk-5 landed at d2 and the far-prize check said so. */
      for (const pool of [all.filter((s2) => s2.d >= 4 && s2.d <= 8), all.filter((s2) => s2.d >= 3), all]) {
        let bestHere: { s: any; at: any } | null = null;
        for (const s2 of pool) {
          const at = isle(s2.x, s2.y);
          if (!at) continue;
          if (!bestHere || score(s2, at) > score(bestHere.s, bestHere.at)) bestHere = { s: s2, at };
          if (bestHere.at.grade === 4 && route.has(bestHere.s.x + ',' + bestHere.s.y)) break;
        }
        if (bestHere && (!pick || score(bestHere.s, bestHere.at) > score(pick.s, pick.at))) pick = bestHere;
        /* A POOL'S PICK IS ONLY GOOD ENOUGH IF DEEP WATER TOUCHES IT (grade 2
         * and up). Taking the far pool's best whatever it was put two of six
         * worlds on a shallow puddle's lip; carrying the best score across all
         * pools instead put tusk-5 two screens from the spawn. Both: the far
         * pool first, and widen only while the water is not there. */
        if (pick && pick.at.grade >= 2) break;
      }
      if (pick) {
        used.add(pick.s.x + ',' + pick.s.y);
        taken.add(pick.s.x + ',' + pick.s.y + ':' + pick.at.tx + ',' + pick.at.ty);
        out.push({ kind: 'tusk', x: pick.s.x, y: pick.s.y, px: pick.at.tx * TS + 2, py: pick.at.ty * TS + 2, exact: true, guard: true } as any);
      }
    }
    return this._loot = out;
  }

  dropAt(kind, x, y, extra: any = {}) {   // (x, y): WORLD pixels (LS-511; `dropItem` took a screen's own pixels and added its origin)
    const [fx, fy] = extra.exact ? [x, y] : this.fitDrop(x, y);
    this.items.push(new Item(kind, fx, fy, true, extra));
    return this.items[this.items.length - 1];
  }

  // ------------------------------------------------------------ collision
  /* THE WORLD IS ADDRESSED IN PIXELS, and in the wilds those pixels run across
   * the whole map rather than across one screen.
   *
   * A seam was never a design decision. Positions were screen-local — x ran 0
   * to 256 inside whichever screen was current — so walking off an edge meant
   * swapping the current screen and REBASING the player by a whole screen.
   * Everything a player felt at a boundary came out of that rebase: a
   * link-graph test that refused crossings the terrain allowed, twenty frames
   * of invulnerability granted on arrival, a forty-eight pixel inset at gated
   * seams, and a window recomputed across the swap. Four fixes for one cause.
   *
   * So the wilds are one coordinate space. `tileAtPx` resolves a world pixel
   * to the screen that owns it and the tile inside it, and everything that
   * asks "what is here" goes through it. A hollow room and a cave stay a
   * single grid, because a room IS one screen and always was. */
  /** The way from a point (the body's centre unless told otherwise) to a target's centre, sampled at the quarter
   * points, crosses no solid tile (LS-230). ONE RULE, READ BY THE SWING AND BY THE DRIVERS (2026-09-21): the old
   * shell's lair is strewn with rock in twos and threes, and a bot that had lined up behind one swung 3,490 times
   * at the shell and landed 41 -- it had no way to ask this, so it stood there until the slams took it (gate 18). */
  bladeClearTo(e, fromX = this.player.cx, fromY = this.player.cy) {
    for (const t of [0.25, 0.5, 0.75]) {
      const tile = this.tileAtPx(fromX + (e.cx - fromX) * t, fromY + (e.cy - fromY) * t);
      if (tile === T.ROCK || tile === T.CLIFF || tile === T.STONE || tile === T.WALL || tile === T.CAIRN || tile === T.TREE) return false;
    }
    return true;
  }
  tileAtPx(px, py) {
    const tx = Math.floor(px / TS), ty = Math.floor(py / TS);
    if (tx < 0 || ty < 0 || tx >= WORLD_W * COLS || ty >= WORLD_H * ROWS) return undefined;
    const sx = (tx / COLS) | 0, sy = (ty / ROWS) | 0;
    const t = this.chunkTiles[sy * WORLD_W + sx] || this.screenAt(sx, sy).tiles;
    return t[ty - sy * ROWS][tx - sx * COLS];
  }

  /** The tile at a TILE coordinate, in whatever space this area uses — world
   *  tiles in the wilds, the room's own grid below ground. */
  /* THE HOTTEST READ IN THE GAME (LS-480): every step, field and creature asks it, and it built a string key and looked
   * the chunk up in a Map each time. The chunks' tile arrays are kept in a flat table as they are made. */
  chunkTiles: (number[][] | undefined)[] = [];
  tileAt(tx, ty) {
    if (!(tx >= 0 && ty >= 0 && tx < WORLD_W * COLS && ty < WORLD_H * ROWS)) return undefined;
    tx = Math.floor(tx); ty = Math.floor(ty);
    const sx = (tx / COLS) | 0, sy = (ty / ROWS) | 0;
    const t = this.chunkTiles[sy * WORLD_W + sx] || this.screenAt(sx, sy).tiles;
    return t[ty - sy * ROWS][tx - sx * COLS];
  }

  /* THE WORLD'S TILES, ADDRESSED AS ONE MAP (LS-511, 2026-09-29). A tile is (wtx, wty) over the whole world, 0 to
   * WORLD_W * COLS across; a screen is only the chunk it is stored in. Everything that reads or changes the ground goes
   * through these, so nothing needs to know which screen it is on -- the screen-local grid and its origin were the
   * flip-screen game's addressing, and a tile a step across a seam was a tile nothing could touch. */
  /** The chunk that holds world tile (wtx, wty), and the tile's place inside it; null off the map. */
  chunkOf(wtx: number, wty: number): { sx: number; sy: number; lx: number; ly: number; key: string; tiles: number[][] } | null {
    if (!(wtx >= 0 && wty >= 0 && wtx < WORLD_W * COLS && wty < WORLD_H * ROWS)) return null;
    const sx = Math.floor(wtx / COLS), sy = Math.floor(wty / ROWS);
    return { sx, sy, lx: wtx - sx * COLS, ly: wty - sy * ROWS, key: 'W|' + sx + ',' + sy, tiles: this.screenAt(sx, sy).tiles };
  }
  /** The grass of the chunk holding world tile (wtx, wty): its turf grid, the tile's place in it, and whether it is the
   *  chunk underfoot (whose picture is the live one). Null where no chunk's grass is baked yet. */
  turfAt(wtx: number, wty: number): { turf: any; lx: number; ly: number; cell: any; here: boolean } | null {
    const c = this.chunkOf(wtx, wty); if (!c) return null;
    const here = !!this.area && c.sx === this.area.x && c.sy === this.area.y;
    const turf = here ? this.turf : (this.partsCache.get(c.sx + ',' + c.sy) || {}).turf;
    if (!turf) return null;
    return { turf, lx: c.lx, ly: c.ly, cell: turf[c.ly] && turf[c.ly][c.lx], here };
  }
  /** Write a world tile: the chunk's tiles, and its bake marked stale. False off the map. */
  setTileAt(wtx: number, wty: number, v: number): boolean {
    const c = this.chunkOf(wtx, wty); if (!c) return false;
    c.tiles[c.ly][c.lx] = v;
    this.markPartsDirty(c.sx, c.sy);
    return true;
  }
  /** The world tile a world pixel lies on. */
  tileOfPx(px: number, py: number): [number, number] { return [Math.floor(px / TS), Math.floor(py / TS)]; }
  /** A world tile's centre in world pixels. */
  pxOfTile(wtx: number, wty: number): [number, number] { return [wtx * TS + TS / 2, wty * TS + TS / 2]; }

  /** How wide the current area's grid is, in tiles. */
  gridW() { return WORLD_W * COLS; }
  gridH() { return WORLD_H * ROWS; }

  /** A tile of the CURRENT space as a body position, with no offset applied —
   *  `bodyAt` is for callers naming a tile of this screen's own grid. */
  bodyAtTile(tx, ty): [number, number] {
    const p = this.player;
    return [tx * TS + (TS - p.w) / 2, ty * TS + (TS - p.h) / 2];
  }

  solid(px, py, ghost, e) {
    if (e && this.closedTo(e, px, py)) return true;   // a closed chunk refuses another chunk's creature (LS-511, see closeTheGround)
    const t = this.tileAtPx(px, py);
    /* THE WORLD'S RIM IS SOLID FOR THE BODY TOO (2026-09-18). It was solid for everything EXCEPT the player, so a walk
     * into the map's edge stepped out of the world 1.45 px a frame until the body's CENTRE left it -- and `checkEdges`
     * then clamped the body's CORNER back, half a body further, in one frame. Held against the rim that is a five-frame
     * sawtooth, 5.8 px each way, for as long as the key is held; 17 seeds in 24 have walkable land on the rim. */
    if (t === undefined) return true;
    // Water. A pool used to be a place nothing could follow you into and no
    // shot could cross — measured, a player standing still in deep water took
    // zero damage in 51 of 60 screens, and the Tide Charm tripled how much of
    // the world was that. Shallows are wadeable by anything with legs, and
    // nothing flying stops at a waterline any more.
    if (e && e.def && e.def.aquatic) return !isWet(t);   // an eel (LS-293): the water is its ground, deep or shallow, and the land its wall
    if (isWet(t)) {
      if (ghost || !e) return false;                 // shots and wisps cross
      /* THE TIDE CHARM OPENS WHAT IT SAYS IT OPENS (2026-09-17). This read the charm against the whole WET set, and WET
       * is `OPENED_BY.tide` PLUS T.SEA -- so the charm made the open sea walkable and handed the player every island on
       * foot, past the boat, the raft, the wind and the whole crossing. Its own list is deep water and shallows. */
      if (e === this.player) return this.aboard ? false : !((this.state.hasTide && OPENED_BY.tide.includes(t)) || (wearing(this.state, 'hide') && (t === T.WATER || t === T.SEA)));   /* ...or the sow's hide worn (2026-09-27): the sea is slow water, not a wall */   /* ...or afloat on a hull (LS-383) */
      if (e && e.panic > 0 && t === T.WATER) return false;   // ...unless fear is driving it: a fright may take it into the deep (LS-379)
      return t === T.WATER || t === T.SEA;           // enemies wade a shallow, but cannot swim deep water -- and the open SEA is not a floor either (2026-09-17: it read as passable, so land creatures walked out over it and nothing drowned them)
    }
    // --- THE SILT LUNG'S SECOND USE ------------------------------------
    // A hedge standing in deep water has nothing to stand on. Hold the Silt
    // Lung and you can go under a thorn tile that has water beside it —
    // no ember, no burning, no permission. It is not every hedge, and finding
    // the ones it works on is the point.
    // THE UNDER-HEDGE ROUTE IS GONE, AND IT WAS A TRAP.
    //
    // `solid()` let the player's BODY into a thorn tile while submerged. The
    // moment the body's centre crossed the tile boundary `isSwimAt` went
    // false, `Player.update` cleared `submerged`, and the thorn turned solid
    // with the body already inside it — every direction blocked, forever.
    // Measured over 40 worlds: 250 of 250 real waterline hedges, zero movement
    // in 400 frames, one case held for 20,000 frames of randomised input with
    // no death and no escape, and the position written to the save. Only
    // reloading the page freed it, and `canLeaveFrom` returned TRUE there, so
    // the project's own softlock detector was blind to it.
    //
    // It could not have been worth keeping even if it worked: 0 of 453
    // waterline hedges in 20 worlds have deep water on BOTH sides, so the
    // traversal it advertised does not exist anywhere the generator can
    // produce. Every instance of the feature was a trap and none was a route.
    // The Silt Lung's verbs are the dive and the surge; this was neither.
    // A launch flies over a hedge and a lake but not into a mountain. This is
    // the one mode that crosses thorns, and only a pod blast produces it.
    //
    // Except the Warden's. Every thorn tile inside a hollow is the seal on a
    // boss room, and 93 of 93 of them were crossed by a single launch — into a
    // room where the player is on the boss's side of a hedge they have not
    // burned, and where in 45% of them the door is not reachable from where
    // the game wakes you. That seal is the last thing the Emberwake is for; a
    // pod is not a second key to it.
    if (ghost === 'launch') {
      return t === T.TREE || t === T.ROCK || t === T.RUBBLE || t === T.CRACK;
    }
    // `T.WATER` used to be in this list and could never be reached: the water
    // branch above returns first for every wet tile, so the entry read as a
    // rule and was dead code. A reviewer's mutation of it was a silent no-op.
    /* ...AND A CLIFF IS NO MORE PASSABLE TO A SHOVE THAN TO A STEP (2026-09-17). The ghost list is what a knockback
     * travels through, and it held only the small obstacles: a body shoved at a cliff face, a standing stone, a
     * cairn, a nest, debris or the open sea went INTO it, and a body inside a wall cannot leave one -- every axis
     * bisects to nothing, and the creature stood there for the rest of the run. Measured: a boar's shove put a
     * bramble in a cliff and it never moved again in 1,800 frames. */
    /* A FLYING KIND CROSSES THORN (2026-09-19): the docs have said so of the wisp and the stormgull since LS-178 and
     * LS-381, and the list below -- which is what a SHOVE travels through as well -- stopped them at every hedge. */
    if (ghost === e && e.def && e.def.ghost && t === T.THORN) return false;
    if (ghost) return t === T.TREE || t === T.ROCK
      || t === T.RUBBLE || t === T.CRACK || t === T.THORN
      || t === T.CLIFF || t === T.STONE || t === T.CAIRN || t === T.NEST || t === T.DEBRIS || t === T.SEA;
    return isSolid(t);
  }

  /* BY WORLD POSITION. Both of these indexed the screen-local tile grid with
   * the body's WORLD pixels, so on any screen but the first they fell off the
   * grid and answered false: the body never waded or swam in the wild, it
   * strode across water at full pace -- "why can i walk on water". */
  isWadeAt(px, py) {
    const t = this.tileAtPx(px, py);
    return t !== undefined && isWet(t);
  }

  /** Out of your depth: slower than the shallows, and both hands are busy. */
  isSwimAt(px, py) {
    const t = this.tileAtPx(px, py);
    return t === T.WATER || (t === T.SEA && !!this.state && wearing(this.state, 'hide'));   // the hide floats you on the open sea (2026-09-27)
  }

  /** THE TILE UNDER A POINT — AND THE POINT IS IN THE WORLD, NOT THE SCREEN.
   *
   * This divided by the tile size and indexed `this.tiles` directly, which was
   * right while a screen WAS the world and the player's coordinates ran 0..255
   * across it. Since the window started scrolling, a body's position is in
   * WORLD pixels -- `updateArea` divides `player.cx` by a whole screen to work
   * out which screen the player is standing on -- so on every screen but the
   * first the result fell outside 0..COLS and this returned null.
   *
   * `checkTileTriggers` opens with `if (!here) return`, so what that looked
   * like from play was that NOTHING could be entered: no hollow mouth, no cave,
   * no staircase back out, anywhere except the top-left screen of the map.
   * Reported as "i cannot enter any cave or dungeon right now". It took the
   * other three callers with it -- a thrown pod could not hit a tile, the tile
   * in front of the player could not be read for a lock or a bramble.
   *
   * The screen's origin comes off first, so `tx`/`ty` stay LOCAL TO
   * `this.tiles` and every caller keeps indexing the grid it already expects.
   * Underground the area is a single screen and the coordinates are already
   * local, which is why the shift is conditional. */
  tileAtPoint(px, py) {   // the WORLD tile at a world pixel, and what stands on it (LS-511: it was null a pixel over a seam)
    const tx = Math.floor(px / TS), ty = Math.floor(py / TS);
    const t = this.tileAt(tx, ty);
    return t === undefined ? null : { t, tx, ty };
  }

  // ---------------------------------------------------------------- update
  update() {
    if (this.bagOpen && this.mode === 'play') { this.handWas = null; this.music(); return; }   // the bag screen holds the world still (LS-225) -- but the score keeps time, or it came back at bar one (LS-233)
    this.frame++;
    this.handFallsBack();
    A.flushCues(this);   // the one impact of the last frame (LS-197)
    this.updateArea();
    this.updateCamera();
    // the seam cooldown, which is what the slide used to be — see `crossWild`
    if (this.mode === 'play' && !this.paused) this.runFrames++;
    this.music();
    if (this.message) { this.message.t--; if (this.message.t <= 0) this.message = null; }
    if (this.shake && --this.shake.t <= 0) this.shake = null;
    // A GUST NOW AND THEN (LS-207): in the wild, every few seconds a handful of
    // streaks cross the window with the world's own wind, so the air is seen
    // to move even when nothing does
    // (LS-209) and each streak is a long ribbon that curls -- a slow, wide
    // steering and thirty points of trail -- not a straight drop of rain
    // and a gust is rare (Vijay: "they're way too frequent"): one every seven to twelve seconds, not every two and a half
    if (this.nextGust === undefined || this.frame > this.nextGust + 100000) this.nextGust = this.frame + 200 + Math.floor(Math.random() * 300);
    const gusting = (globalThis as any).__wind ? this.frame % 24 === 0 : this.frame >= this.nextGust;
    if (this.mode === 'play' && !this.paused && this.systems && gusting && this.fx.length < 80) {
      this.nextGust = this.frame + 420 + Math.floor(Math.random() * 300);
      const [wx, wy] = this.systems.wind || [1, 0];
      const cam = this.cam || { x: this.player.cx - 128, y: this.player.cy - 96 };
      for (let i = 0; i < 3; i++) {
        const sx = cam.x + (wx > 0 ? -12 : 268) + Math.random() * 24, sy = cam.y + 20 + Math.random() * 160;
        const sp = 2.0 + Math.random() * 0.8;
        this.fx.push(new Puff(sx, sy, i === 1 ? UI.linen : UI.paper, wx * sp, wy * sp + (Math.random() - 0.5) * 0.6, 130 + Math.floor(Math.random() * 40), 'wind',
          { span: 36 + i * 4, curl: (Math.random() < 0.5 ? -1 : 1) * (0.06 + Math.random() * 0.03), freq: 0.08 + Math.random() * 0.05, bias: 0.15, drag: 0.999 }));   // a wave of ten to twenty pixels over a ribbon: an S that hooks, not a scratch and not a loop
      }
    }
    /* ...AND THE RAFT SHOWS WHEN THE WIND IS FAIR (LS-397 h). Only while the player can act on it: aboard the hull,
     * or standing within a couple of tiles of it on the shore. Every twenty-fourth frame, so it breathes rather
     * than streams. */
    if (this.mode === 'play' && !this.paused) {
      const b = this.state.boat;
      const near = b && (this.aboard || Math.hypot(b.x - this.player.cx, b.y - this.player.cy) < 40);
      // kept on the game for the draw, so the hull's glint and the motes agree and the island search is walked once
      this.fairCue = near ? this.fairAcross() : null;
      if (this.fairCue && this.frame % 24 === 0 && this.fx.length < 80)
        this.wind(b.x, b.y - 3, this.fairCue.dx, this.fairCue.dy, 3, 0.14, 0.7, { colour: UI.gold, span: 14, curl: 0.008, drag: 0.995, bias: 0, life: 58, bearing: true });
    }
    this.tickHeatRibbons();   // ...and the heat rises in them too (2026-09-10)
    this.pushHeat();

    if (this.mode === 'title' || this.mode === 'win' || this.mode === 'gameover') {
      this.input.attackPressed = false; this.input.podPressed = false; this.input.dodgePressed = false;
      return;
    }
    if (this.paused) { this.player.buffered = false; this.input.attackPressed = false; this.input.podPressed = false; this.input.dodgePressed = false; return; }
    if (this.stop > 0) { this.stop--; this.input.attackPressed = false; this.input.podPressed = false; this.input.dodgePressed = false; return; }
    if (this.mode === 'dying') {
      this.clearInput();
      for (const f of this.fx) f.update();
      this.fx = this.fx.filter(f => !f.dead);
      /* THE FALL RUNS, AND STOPS WHERE IT LIES. The poser's fraction is
       * `1 - ragdoll / ragdollMax`: nought is upright, a third of the way is
       * on its back, and past three quarters the body gets up again -- which
       * is the one thing a death must not do. Held at half. */
      const p = this.player;
      const step = this.deathRush ? DEATH_RUSH : 1;   // a press asked for the screen: the fall runs, it does not vanish
      if (p.ragdoll > 0) p.ragdoll = Math.max(Math.floor((p.ragdollMax || DEATH_FRAMES) / 2), p.ragdoll - step);
      p.anim = (p.anim || 0) + step;
      this.deathTimer -= step;
      if (this.deathTimer <= 0) this.mode = 'gameover';
      return;
    }

    /* THE WAY OUT IS ANY STONE, AT ANY TIME, DELIBERATELY (LS-379, slice
     * four). A stone used to end the run only in the aftermath, after the
     * third beast, which is the kill list wearing a different hat. Hold the
     * action button beside any standing stone for three quarters of a second
     * and you leave the wilds; the stone brightens while it is held, which is
     * the whole prompt. A press does nothing -- the bot reads stones by
     * standing near them and blasts rubble with a press, and neither is a
     * hold -- so nothing leaves by accident. */
    if (this.input.podHeld && this.stoneBeside()) {
      this.leaveHold = (this.leaveHold || 0) + 1;
      /* THE FIRST SPARK ON THE FIRST FRAME (LS-295, round 53's design lens): it fired on frame six of the hold, so a
       * player who taps the button beside a stone -- which is every player, the first time -- saw nothing and never
       * learned that holding does anything. One spark at the first frame is the whole lesson. */
      if (this.leaveHold === 1 || this.leaveHold % 6 === 0) { const [sx, sy] = this.stoneBeside(); this.puff(sx * TS + 8, sy * TS + 4, UI.gold, 2 + Math.floor(this.leaveHold / 12), 'spark'); }
      if (this.leaveHold >= LEAVE_HOLD) { this.leaveHold = 0; this.leaveWilds(); return; }
    } else this.leaveHold = 0;

    /* A DROP IS A MOVE, SO IT OWNS THE FRAME. While one is running the player
     * is on the way down and nothing else may push them about. */
    if (this.fall) { this.clearInput(); this.updateFall(); return; }

    /* ...AND THEY GIVE OFF MOTES, BECAUSE A LIGHT DOES NOT READ AT NOON (2026-09-08). The lit stone above
     * is right at dusk and invisible on the ash country's pale ground in daylight -- measured by diffing the
     * same screen with and against the aftermath. Particles read at any hour, so each standing stone on the
     * screen breathes one upward every half-second. This is the whole instruction, and it is not a sentence. */
    if (this.frame % 20 === 0 && this.mode === 'play') { const t = this.actPulse(); if (t && t.kind !== 'stone') this.puff(t.tx * TS + 8, t.ty * TS + 2, UI.gold, 1, 'spark'); }   // ...and no gold on a standing stone (2026-09-20)
    if (this.frame % 20 === 10 && this.mode === 'play' && this.fairCue) { const b = this.state.boat; if (b) this.puff(b.x, b.y - 4, UI.gold, 1, 'spark'); }   // ...and one off a raft the wind would carry (LS-397 h)   // ...and a spark rises off it (LS-397)
    if (this.stonesCalling() && this.world && this.world.stones && this.frame % 30 === 0) {
      const cam = this.camera();   // every stone on the glass breathes (LS-511: every stone on the screen underfoot)
      for (let ty = Math.floor(cam.y / TS); ty <= Math.floor((cam.y + PH) / TS); ty++) for (let tx = Math.floor(cam.x / TS); tx <= Math.floor((cam.x + PW) / TS); tx++) {
        if (!this.stoneAtWorld(tx, ty)) continue;
        this.puff(tx * TS + 8, ty * TS + 2, UI.glass, 2, 'spark');
      }
    }
    this.readStone();

    // the throw recharges over about two and a half seconds of not being hit
    this.state.beam = Math.min(1, (this.state.beam || 0) + 1 / 150);
    if (this.heldShot && this.input.attackPressed) { this.input.attackPressed = false; this.throwHeld(); }   // with something in hand, the swing is a throw
    this.player.update(this, this.input);
    this.carryHeld();
    this.checkLedge();
    this.mendAtRest();
    // THE LUNG FILLS WHERE IT CAN. It holds one lungful, and going under is
    // how you take one — so the jet is not a free ability, it is a reason to
    // go back to the water.
    if (this.player.submerged || this.isSwimAt(this.player.cx, this.player.cy)) {
      this.state.lungFull = true;
    }
    // ANYTHING THAT WALKS THROUGH IT BENDS IT. The grass is not a backdrop:
    // it is pushed by the player, by everything hunting the player, and by a
    // pod rolling past, and it stands back up on its own.
    if (this.turf) {
      // bodies are in world px since the camera (LS-164); the turf grid is this
      // screen's, so the bodies are handed over in its pixels (the grass had
      // stopped bending and covering since then: Vijay, LS-196)
      const ox0 = this.originX(), oy0 = this.originY();
      const inGrass = [this.player, ...this.enemies].filter((b) => b && !b.dead)
        .map((b) => ({ x: b.x - ox0, y: b.y - oy0, w: b.w, h: b.h, cx: b.cx - ox0, cy: b.cy - oy0, vx: b.vx, vy: b.vy, dir: b.dir, moving: b.moving, kb: b.kb, lastX: b.lastX === undefined ? undefined : b.lastX - ox0, lastY: b.lastY === undefined ? undefined : b.lastY - oy0, body: b }));
      stirTurf(this.turf, inGrass, COLS, ROWS);
      // ...and the blades are pushed DOWN by whatever stands in them, radially,
      // which is the same mechanism the wind uses.
      if (pushTurf(this.turf, inGrass, COLS, ROWS) > 0) this.refreshTurfArt(false);   // grass standing again shows on the next half-second beat
      if (this.turfJob) this.stepTurfJob(); else if (this.turfWantsRepaint && this.frame % 30 === 0) this.refreshTurfArt(true);
    }
    if (this.decals && this.decals.length) {
      stirDecals(this.decals, [this.player, ...this.enemies, ...this.pods]
        .filter((b) => b && !b.dead).map((b) => ({ x: b.x - this.originX(), y: b.y - this.originY(), w: b.w, h: b.h })));   // screen px, as the turf (LS-196)
    }
    if (this.critters && this.critters.length) {
      stirCritters(this.critters, this.player, COLS, ROWS);
    }
    // A NET UNDER THE WHOLE CLASS. `freeThePlayer` was called only where
    // something was known to be able to wedge you — after a knockback lands,
    // on respawn, on load — and each of those is a list someone has to
    // remember to add to. Round 10 added a way in that was on none of them: a
    // submerged body walked into a thorn tile, the tile turned solid around
    // it, and 250 of 250 measured cases had no escape in any direction for
    // 20,000 frames, with the position saved and `canLeaveFrom` reporting
    // true. The rule that allowed it is gone; this is so that the next one
    // costs a frame instead of a run.
    // ...but NOT mid-flight. A launch crosses tiles the body does not fit in —
    // that is what makes it a launch — and it has its own landing rules
    // (`canLeaveFrom`, the revert, `outsideGates`). Running the net every
    // frame yanked the player out of the air and took the Rattle's route past
    // a gate from 24 of 30 worlds down to 5.
    if (this.mode === 'play' && !this.player.kb && !(this.player.ragdoll > 0)   // and no seam is crossed by a body with no say in it: a knockback or a ragdoll stops at the edge (LS-212)
      && !this.bodyFits(this.player.x, this.player.y)) {
      this.freeThePlayer();
    }
    // dropping a pod is never blocked by the swing lock
    if (this.state.emberCool > 0) this.state.emberCool--;
    /* PRE-BAKE THE NEIGHBOURS (LS-188). A screen's parts cost 30-50 ms to bake
     * the first time, which was a hitch the moment a neighbour scrolled into
     * view. Bake one unbaked neighbour every few frames instead, while nothing
     * else is happening, so a seam crossing finds its screen ready. */
    // (the rosters are not cosmetic: a game with no paint still makes them)
    /* THE PRE-BAKE IS PACED (performance, Vijay: "performance is starting to
     * lag on mobile on new build when walking around"). A neighbour's bake is
     * a tenth of a second on the desk and three or four times that on a
     * phone, and it ran every sixth frame until all eight were made: eight
     * hitches in a row after every crossing. Now one every twelfth frame, the
     * four edge neighbours before the diagonals, and the one the player faces
     * first, so the screen you are walking into is the one made. */
    if (!this.stop) {
      /* THE PRE-BAKE, PACED BY TIME: every frame, the nearest neighbour whose
       * parts are not made gets a few milliseconds of its bake (see
       * `bakePartsSteps`); when a screen's parts are done its roster is made
       * too. A whole screen every twelfth frame was a hitch every twelfth
       * frame. */
      const face = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[this.player.dir] || [0, 0];
      const order = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]
        .sort((a, b) => (Math.hypot(a[0], a[1]) - Math.hypot(b[0], b[1])) || ((b[0] * face[0] + b[1] * face[1]) - (a[0] * face[0] + a[1] * face[1])));
      // Population is gameplay: neither an unfinished bake nor a dirty repaint may delay it.
      if (this.mode === 'play') this.streamWorld(order);
      /* ONCE A DRAWN FRAME (LS-480). A late frame runs two updates to catch up, and each baked -- so a phone that could
       * least afford it baked twice as much as a fast one. The pictures are for the draw, so they are paced by the draw;
       * a game nothing has drawn yet (the headless sims, a boot) still bakes every update. */
      if (this.cosmetics !== false && (this.renderCount === 0 || this.prepAt !== this.renderCount)) { this.prepAt = this.renderCount; this.prepareScreens(order); }
    }
    // ...and you surface if the water leaves you, not only if you ask to
    if (this.player.submerged && !this.isSwimAt(this.player.cx, this.player.cy)) {
      this.player.submerged = false;
    }
    this.driftRaft();   // the wind moves a raft nobody rides (LS-383)
    if (this.systems && this.systems.wind) setWindSign(this.systems.wind[0]);   // the meadow's gust tips lean with the world's wind (LS-389)
    if (this.input.podPressed) this.useTool();
    this.input.attackPressed = false;
    this.input.podPressed = false;

    const boxes = this.player.swingBoxes(this);
    /* FLECKS SELL THE SMEAR (LS-209). Vijay: "we don't need wind particles on
     * sword swipes, what we do need is particles to help sell the smears".
     * While the blade is live, specks fly off its tip the way it is moving --
     * along the sweep for a cut, back along the line for a stab -- and die in
     * a dozen frames, so the arc the smear draws has matter coming off it. */
    {
      const pl = this.player;
      if (pl.attack > SWING.follow && pl.attack <= SWING.live) {
        const a = pl.swingAngle(), kind = pl.swingKind || 'fore';
        const last = pl.fleckA === undefined ? a : pl.fleckA; pl.fleckA = a;
        const da = a - last, sgn = da === 0 ? 0 : Math.sign(da);
        const reach = swingWeaponOf(this.player).weapon.reach + (kind === 'thrust' ? 6 : 0);   // the weapon in hand's reach (LS-225)
        const tipX = pl.cx + Math.cos(a) * reach, tipY = pl.cy + Math.sin(a) * reach;
        for (let i = 0; i < 4; i++) {
          let vx, vy;
          if (kind === 'thrust' || sgn === 0) { const side = i & 1 ? 1 : -1; vx = -Math.cos(a) * (0.8 + Math.random() * 1.4) - Math.sin(a) * side * (0.5 + Math.random() * 0.8); vy = -Math.sin(a) * (0.8 + Math.random() * 1.4) + Math.cos(a) * side * (0.5 + Math.random() * 0.8); }
          else { const sp = 1.5 + Math.random() * 1.5, out = 0.3 + Math.random() * 0.8; vx = -Math.sin(a) * sgn * sp + Math.cos(a) * out; vy = Math.cos(a) * sgn * sp + Math.sin(a) * out; }
          this.fx.push(new Puff(tipX + (Math.random() - 0.5) * 3, tipY + (Math.random() - 0.5) * 3, i === 3 ? UI.linen : i === 1 ? UI.gold : UI.paper, vx, vy, 12 + Math.floor(Math.random() * 7), 'fleck'));
        }
      } else pl.fleckA = undefined;
    }
    if (boxes) {
      /* AN EDGE CUTS MEAT FROM REMAINS (LS-519): a cut or a pierce through them takes a bite's worth, once a swing. */
      const verb = swingWeaponOf(this.player).weapon.verb;
      if (verb === 'cut' || verb === 'pierce') for (const r of this.remains) {
        if (r.carved === this.swingId || r.bites <= 0) continue;
        if (!boxes.some((b) => r.x > b.x - 4 && r.x < b.x + b.w + 4 && r.y > b.y - 4 && r.y < b.y + b.h + 4)) continue;
        r.carved = this.swingId; r.bites--;
        this.dropAt('meat', r.x - 4, r.y - 4, { dropped: true });
        this.puff(r.x, r.y, UI.bone, 3, 'fleck');
      }
      for (const e of this.enemies) {
        if (e.dead || e.spawnFx > 0 || (e.friendUntil || 0) > this.runFrames) continue;   // a companion is not struck by your swing (LS-521)
        if (boxes.some(b => overlaps(b, e))) {
          /* A CLEAR LINE FOR THE BLADE (LS-230): a spear across a one-tile
           * channel or through a boulder killed a beast that could not reach
           * you. The way from the body's centre to the target's, sampled at
           * the quarter points, must cross no solid tile. */
          if (!this.bladeClearTo(e)) continue;
          // the riposte (LS-178): the first hit landed in the half-second after a dodge is heavy
          const heavy = this.player.riposte > 0;
          /* ...AND IT IS SPENT ON A BLOW THAT LANDS (2026-09-18). This fired on CONTACT: a swing whose arc touched a
           * shellback's plate rang off for nothing and took the window with it, and so did a fist on a Great Beast,
           * and so did the same arc reaching a sporeling first. The window you earn by rolling is for the blow you
           * choose; the multiplier is read here, where the damage is worked out, and the window is spent below, once
           * the hurt has counted. */
          const kind = this.player.swingKind || 'fore';
          // the torch's verb (LS-228): a hedge-thing the flame touches catches, whether or not the blow lands (a body still reeling from the last blow takes no damage, but it burns)
          if ((swingWeaponOf(this.player).weapon.verb === 'burn' || this.spendPitch(this.swingId)) && (e.type === 'bramble' || e.type === 'bramblemother') && !(e.alight > 0) && e.litBySwing !== this.swingId) { e.litBySwing = this.swingId; e.alight = wearing(this.state, 'coal') ? 360 : 240; /* the coalheart (LS-335) */ this.sfx('flame', e.x + 8); }
          const hp0 = e.hp;
          /* A FIST IS NOT AN ANSWER TO A GREAT BEAST (the design lens, round 48: bare hands felled the sow in 159 swings
           * and a sickle worn dull did it in the same number, so wear cost nothing and the whet had no reason to exist).
           * The blow still lands, still reels and still sounds; it takes nothing off a beast. */
          const bare = swingWeaponOf(this.player).weapon.verb === 'strike';
          if (bare && e.def.beast) {
            /* ...AND THE WALL NAMES THE DOOR (the design lens, round 49: a measured run stood dull for 16% of five
             * minutes holding three times the price of a whet, and the toast said `your hands do nothing to it`
             * with a sickle in the hand). A blade worn dull is named, and the price is named when you can pay it. */
            /* ...ON A CLOCK OF ITS OWN. This was gated on `runFrames % 30`, which is only ever true INSIDE a swing's
             * hit window by luck: a driven fight of ninety frames said nothing at all. A stamp says it at most twice
             * a second and always on the first blow that does nothing. */
            /* IT SAYS THIS IN SPARKS NOW, NOT IN WORDS (2026-09-08; Vijay: "i really don't like the text
             * prompts across the game, i want the entire game to be playable without any instructions").
             * Three lines used to fire here, and one of them named a key -- "3 glim whets it (x)" -- which
             * is the exact shape of thing the game should not have to say. What the player needs to know is
             * that the blow did nothing, and the blow already says so: the chip below, and a bone spark on
             * the body. What is WRONG is legible too, without a word: the HUD's blow counter reads 0 OF 110
             * when the edge is gone, and the hand box is empty when there is nothing in it. */
            if (!e.dead && this.frame - (this.dullSaidAt || -999) > 40) {
              this.dullSaidAt = this.frame;
              this.puff(e.cx, e.cy - 4, UI.bone, 3, 'spark');
            }
            this.sfx('chip', e.x + 8); continue; }
          e.hurt(this, swingWeaponOf(this.player).weapon.damage * (this.state.hasEmber ? 2 : 1) * (heavy ? 2 : 1), this.player.cx, this.player.cy,
            'swing:' + this.swingId);
          if (heavy && e.hp !== hp0) { this.puff(e.cx, e.cy - 4, UI.gold, 5, 'spark'); this.player.riposte = 0; this.sfx('riposte', e.x + 8); this.hitstopFor(9); this.shakeFor(4, 10); }   // the counter, spent where it landed (2026-09-18)
          this.state.blows = (this.state.blows || 0) + 1;   // the first blow landed is when the numbers start to mean something (LS-397)
          // A LANDED BLOW WEARS THE WEAPON (LS-238): one blow off its count; the one that spends the last says so
          /* A BLADE IS SPENT ON WHAT IT IS PRICED FOR (the design lens, round 49). A blow was a blow, so a walked
           * five minutes of cutting sporelings left the sickle at 0 of 110 while a whole bramble mother costs 20 of
           * 55 -- the stock priced the fight and the grass spent it. A great beast or an armoured body takes a blow
           * off the stock; the small kin take one in five. */
          const wearsIt = !!(e.def.beast || e.def.boss || e.def.armoured) || this.swingId % 5 === 0;
          if (e.hp !== hp0 && wearsIt && wearWeapon(this.state, swingWeaponOf(this.player).kind, this.player.swingSlot)) { this.sfx('chip', this.player.x + 8); }
          /* THE PRICE AND THE KEY ARE GONE FROM THIS LINE TOO (2026-09-08). The first pass at "no
           * instructions" missed it, because the scanner that was meant to catch it read only the
           * first quoted fragment of a concatenation and saw `'the '`. Codex named that hole; the
           * hardened scanner found this on its next run. What is left is a NAME and a state -- the
           * blade is worn dull -- which is the same thing the HUD's counter says at 0 OF 110. */
          /* A BLOCKED THRUST STILL TOPPLES IT (2026-09-08, argued by codex against the LS-250 numbers).
           *
           * Measured over twelve driven runs: the shellback costs 31 hp, against 13 for all three Great
           * Beasts together -- one ordinary creature is 2.4x the whole boss roster. Against shellbacks, 27
           * blows landed and 46 were turned by the plate, and there were TWO flips in twelve runs.
           *
           * The counter is real and it was gated behind already beating the defence. Landing on its back
           * opens it for 120 frames, and the thrust is what puts it there -- but the line below skipped the
           * throw whenever the hit took no damage, which is exactly what a blocked hit is. So the answer to
           * the plate was only available to a player who had already got past the plate. The plate re-faces
           * on a 2% roll a frame, a mean wait of 0.83s, so holding an open angle long enough to land the
           * thrust that would teach you the flip is a coin toss; two flips in twelve runs is that number.
           *
           * A thrust into the plate now throws it anyway, once per swing, and STILL DOES NO DAMAGE THROUGH
           * IT. Ordinary experimentation reads clang, clang, topple, and the pale belly and waving legs
           * that already exist say the rest. The wilds are not made safer: every number -- hp 4, touch 2,
           * the cone, the re-face -- is untouched, and a blow through the plate takes nothing off it. What
           * changes is that the fight's own answer is reachable by finding it. */
          const topples = this.player.swingLaunches && e.def.armoured && !e.def.boss && !e.dead
            && e.hp === hp0 && !(e.flipped > 0) && !(e.kb && e.kb.id === this.swingId);
          if (e.dead || (e.hp === hp0 && !topples)) continue;   // blocked, or this swing already landed
          const a = this.player.swingAngle();
          if (this.player.swingLaunches && !e.def.boss) {   // the chain's finisher throws, not every thrust (LS-336)
            /* THE THRUST THROWS. Where the body comes down is the table's
             * business: the lake drowns it, a wall puts it on its back, and
             * whatever it flies through is hit. */
            e.kb = { dx: Math.cos(a), dy: Math.sin(a), t: 12, launched: true, fromX: e.x, fromY: e.y, id: this.swingId }; this.sfx('launch', e.x + 8);
            this.hitstopFor(7); this.shakeFor(3, 8);
          } else if ((kind === 'fore' || kind === 'back') && e.kb && !e.kb.launched) {
            // the blade carries them along its sweep, not straight away from you
            const sgn = kind === 'fore' ? 1 : -1;
            const tx = -Math.sin(a) * sgn, ty = Math.cos(a) * sgn;
            const dx = e.kb.dx * 0.6 + tx * 0.8, dy = e.kb.dy * 0.6 + ty * 0.8, n = Math.hypot(dx, dy) || 1;
            e.kb.dx = dx / n; e.kb.dy = dy / n;
          }
        }
      }
      for (const b of boxes) {
        /* THE BLADE CUTS TILES; THE THRUST'S FAR PROBES DO NOT (LS-207). The
         * stab grew a hitbox a tile longer for bodies, and that box also cut
         * nests, thorn and braziers two tiles off -- the Broodmother's rule
         * ("cut her nests, then her") fell to a bot that only stabbed at her,
         * 16 wins in 48 against a bar of 4. A tile is struck within the blade's
         * own reach of the hand, which is where the sweep's boxes always were. */
        if (Math.hypot(b.x + b.w / 2 - this.player.cx, b.y + b.h / 2 - this.player.cy) > 24) continue;
        const hit = this.tileAtPoint(b.x + b.w / 2, b.y + b.h / 2);
        if (!hit) continue;
        // the blade meets stone (LS-213): rock, a cliff face, a standing stone, a wall, a cairn
        // (from the blade's outer half only: the hand's own box brushes the wall you stand against every swing, and that is not a strike)
        if ((hit.t === T.ROCK || hit.t === T.CLIFF || hit.t === T.STONE || hit.t === T.CAIRN) && Math.hypot(b.x + b.w / 2 - this.player.cx, b.y + b.h / 2 - this.player.cy) >= 12) { if (this.player.recoilFrom(this, b.x + b.w / 2, b.y + b.h / 2, 'stone')) break; continue; }
        /* THROUGH THE THINGS (LS-225 step 2): the weapon in hand's verb is sent to the thing struck, once a swing.
         * A brazier lights from any strike as it did; the rest is the thing's parts answering. */
        if (hit.t === T.BRAZIER) this.lightBrazierAt(hit.tx, hit.ty);
        else {
          /* THE SWING ID IS THE GAME'S, NOT THE PLAYER'S (2026-09-10). `Player`
           * has no `swingId` -- only the game does, incremented once a swing --
           * so this key was `undefined` on every swing and
           * `this.struckThisSwing.id !== sid` was `undefined !== undefined`,
           * which is false. The set of tiles already struck was therefore
           * created once and NEVER CLEARED: measured over three swings it went
           * 9, 11, 11 tiles and stayed, and every tile in it was deaf to the
           * blade for the rest of the visit. Cut a thicket, let it grow back,
           * and it could not be cut again until you left the screen and
           * returned. Found while wiring the pitch pot, whose coating is spent
           * on the same key and was never spent at all. */
          const sid = this.swingId, key = hit.tx + ',' + hit.ty;
          if (!this.struckThisSwing || this.struckThisSwing.id !== sid) this.struckThisSwing = { id: sid, tiles: new Set() };
          if (!this.struckThisSwing.tiles.has(key)) {
            this.struckThisSwing.tiles.add(key);
            const w = new SimWorld(this.systems.grid(this), this.seedNum);   // the world's grid, in world tiles (LS-511)
            const t = w.tileAt(hit.tx, hit.ty);
            const verb = swingWeaponOf(this.player).weapon.verb;
            /* THE PITCH POT ADDS FIRE, IT DOES NOT REPLACE THE BLOW (LS-335).
             * Overriding the verb was the first thought and it takes the axe's
             * crush away, so a pitched axe stops opening rock -- the point of
             * the pot is that you do NOT have to swap the axe for the torch.
             * The tile answers the weapon's own verb and then 'burn', and the
             * pot is spent where a blow touches something rather than on a
             * swing through the air. */
            if (t && (this.state.pitch || 0) > 0 && verb !== 'burn') {
              const took = emit(w, 'burn', t, { game: this, ignite: (x, y) => this.systems.ignite(this, x, y) });
              if (took === true) this.spendPitch(sid);   // ...and only where the tile ANSWERS: bare sand and water have nothing to burn, and a swing over them was costing a blow (2026-09-19)
            }
            /* STONEMILK ADDS CRUSH, IT DOES NOT REPLACE THE BLOW (2026-09-17, the same rule the pitch pot above follows).
             * This swapped the weapon's own verb for 'crush' while the milk was up, so a coated sickle could not cut a
             * hedge and a coated torch could not light one -- the drink took the tool away instead of adding to it. The
             * tile answers 'crush' first while a blow is owed, and the weapon's own verb after. */
            if (t) { const ctx = { game: this, ignite: (x, y) => this.systems.ignite(this, x, y) };
              let answered: any = false;
              if (verb !== 'crush' && (this.state.stone || 0) > 0) {
                answered = emit(w, 'crush', t, ctx);
                if (answered === true) { this.spendStone(this.swingId); this.sfx('crush', hit.tx * TS); }
              }
              if (answered !== true) { answered = emit(w, verb, t, ctx); if (verb === 'crush' && answered === true) this.sfx('crush', hit.tx * TS); }   // the axe's own sound where a thing answers it (LS-233)
            }
          }
        }
      }
    }
    this.updatePush();

    this.tickBeasts();
    for (const e of this.enemies) if (!e.dead) e.update(this);
    this.tickRemains();
    if ((this.runFrames || 0) % 30 === 0) this.cookMeat();
    if ((this.runFrames || 0) % 300 === 150) this.breed();
    if (this.systems) this.systems.update(this);
    for (const s of this.shots) if (!s.dead) s.update(this);
    for (const p of this.pods) if (!p.dead) p.update(this);
    this.updateWildNest();
    for (const it of this.items) if (!it.dead) it.update(this);
    for (const f of this.fx) f.update();
    this.enemies = this.enemies.filter(e => !e.dead);
    this.shots = this.shots.filter(s => !s.dead);
    if (this.flames && this.flames.length) {
      for (const f of this.flames) f.update(this);
      this.flames = this.flames.filter(f => !f.dead);
    }
    this.pods = this.pods.filter(p => !p.dead);
    this.items = this.items.filter(i => !i.dead);
    this.fx = this.fx.filter(f => !f.dead);

    this.checkRoomClear();
    this.checkEdges();
    // ...and the latch is spent AFTER the seam has been looked at, not inside
    // the update that set it. Decrementing it in `Player.update` meant it
    // could reach zero before `checkEdges` ever ran on the frame that
    // mattered, which left 3 of 2,344 real blasts still crossing.
    if (this.player.launchEnded > 0 && !this.player.kb) this.player.launchEnded--;
    /* A launch, a knockback or a ragdoll flies over seams like anything else (LS-511: it was clamped to the screen it
     * began on, when a seam led to another screen's gates); where it comes down is decided by the landing rules below. */
    const lp = this.player;
    const wasLaunch = (lp.kb && lp.kb.launched) || lp.launchEnded > 0;
    if ((wasLaunch || (lp.kb && !lp.kb.launched) || lp.ragdoll > 0)) {
      /* ...AND A LAUNCH NEVER ENDS INSIDE THE THING IT FLEW OVER.
       *
       * A blast carries the player through water and thorns on purpose. While
       * a tier barrier was a plug two tiles wide in a doorway, landing inside
       * one was rare enough to be handled by the edge guard. A barrier is a
       * BAND now — hundreds of tiles of rubble or thicket along a tier
       * boundary — so a launch that runs out of momentum over it lands in it:
       * measured, 5 of 5,056 off-axis launches ended inside a wall with the
       * walls down, against 0 before.
       *
       * `freeThePlayer` is the right instrument and it was only ever being
       * called on a screen crossing. It floods for somewhere with a way out
       * and refuses to cross a barrier to find it, so it puts the player back
       * on the side they came from rather than through the gate. */
      /* AFTER IT LANDS, NOT WHILE IT IS IN THE AIR. `bodyFits` is false over
       * open water, which is the whole point of a blast — rescuing on it
       * mid-flight cancelled every launch the game has: 0 of 146 cleared a
       * two-tile lake. `kb` is the flight; `launchEnded` is the moment after
       * it. */
      // (a body held at a solid edge is freed at once unless it is a launch still flying: a knockback has nothing to wait for)
      if ((!lp.kb || !wasLaunch) && !this.bodyFits(lp.x, lp.y)) {
        lp.launchEnded = 0;
        this.freeThePlayer();
        /* ...AND IF NOTHING NEAR CAN HOLD THE BODY, IT GOES BACK WHERE IT
         * FLEW FROM. `freeThePlayer` floods six tiles for standing ground and
         * skips the sea; a blast at a shore gate on run-0 threw the body
         * four tiles out into open sea, where the flood found nothing, and
         * the run stood in the water for nine minutes 'looking for a way'. */
        if (!this.bodyFits(lp.x, lp.y) && lp.launchFrom && wasLaunch) {
          lp.x = lp.launchFrom[0]; lp.y = lp.launchFrom[1]; lp.recentre();
          if (!this.bodyFits(lp.x, lp.y)) this.freeThePlayer();
        }
      }
      /* ...AND NEVER IN A POCKET. A landing on good ground inside a closed
       * region -- a few tiles walled by rock and water with no seam, no
       * mouth and nothing to blast -- stranded 1 launch in a hundred (the
       * launch proofs: 7 of 688 over the corridor runs). The region under
       * the landing is flooded to 25 tiles; if it closes with no way on, the
       * launch comes back where it flew from, which is where the player
       * chose to stand. */
      // ...by the game's own rule for a stranded body (`canLeaveFrom`, the
      // one the launch proofs ask), and by the pocket flood for the small
      // closed regions that rule counts as leavable through a seam it cannot walk to
      if (!lp.kb && lp.launchFrom && (!this.canLeaveFrom(lp.cx, lp.cy) || this.landedInPocket(lp))) {
        lp.x = lp.launchFrom[0]; lp.y = lp.launchFrom[1]; lp.recentre();
        
      }
    }
  }

  /** Is the ground under a landed body a closed pocket: fewer than 25 tiles
   *  a bare hand can walk, touching no screen edge, no mouth, hole or stair,
   *  and no rubble or thorn a held tool could open? */
  /* ---------------------------------------------------------- rosters --- */
/* ONE WORLD OF BODIES (LS-511 stage 3). The flip-screen game kept a roster per screen and handed creatures across
   * each seam; the one in front of you was ticked, its neighbours a quarter of the frames with the game standing on
   * their screen, and a body that walked over the line changed lists. Now there is one active list round the body --
   * every creature and thing within a chunk of the chunk underfoot, all ticked every frame in world space -- and what
   * lies further off sleeps under the chunk it stands in until the body comes near again. A chunk grows its roster the
   * first time it comes within reach, one a frame, off the glass; `enterChunk` lets an emptied chunk grow another. */
  filled: Set<string> = new Set();   // chunks whose roster has been grown
  closedKey: string | null = null;
  bandFrames = 0;   // frames the body has stood in the hysteresis band of another chunk (updateArea)   // the chunk underfoot when it is closed to other chunks' creatures (see `closeTheGround`)
  chunkKeyAt(x: number, y: number) { return Math.floor(x / PW) + ',' + Math.floor(y / PH); }
  inReach(sx: number, sy: number, r = 1) { return !!this.area && Math.abs(sx - this.area.x) <= r && Math.abs(sy - this.area.y) <= r; }
  /** A chunk's roster, grown now. `offGlass` keeps only the bodies nobody is looking at. */
  fillChunk(sx: number, sy: number, offGlass = true) {
    const a = { kind: 'wild', x: sx, y: sy, level: 0 }, key = sx + ',' + sy;
    const made = this.fillArea(a, []);
    for (const e of made) if (e.home === undefined) e.home = key;
    this.filled.add(key);
    if (!offGlass) return made;
    for (const e of made) e.spawnFx = 0;
    return made.filter((e) => !this.inWindow(e, TS));
  }
  sleptAt: Map<string, number> = new Map();   // when each sleeping chunk's bodies were put to sleep (runFrames)
  stashRoster(sx, sy, list) {
    const key = sx + ',' + sy;
    if (!this.sleptAt.has(key)) this.sleptAt.set(key, this.runFrames || 0);
    const had = this.rosters.get(key) || [];
    const merged = had.concat(list.filter((e) => !had.includes(e)));
    if (merged.length) this.rosters.set(key, merged); else this.rosters.delete(key);
  }
  /* THE COUNTRY GOES ON WITHOUT YOU (LS-519, slice three). What sleeps far off is not ticked, so a chunk you walked
   * away from was the same chunk when you came back, however long you were gone. Waking it now plays out the time it
   * slept, coarsely: every hunter asleep there has taken prey of its kind -- one every three minutes, while there is
   * any -- and every grazer has cropped the meadow round it, three tiles a minute, which grows back on its own clock.
   * Deterministic: from the seed, the chunk and the minute it woke. */
  catchUp(key: string, list: any[]) {
    const since = this.sleptAt.get(key); this.sleptAt.delete(key);
    if (since === undefined) return;
    const minutes = Math.floor(((this.runFrames || 0) - since) / 3600);
    if (minutes < 1) return;
    const live = list.filter((e) => e && !e.dead);
    for (const hunter of live) {
      const diet = DIETS[hunter.type]; if (!diet || !diet.hunts || hunter.dead) continue;
      let meals = Math.floor(minutes / 3);
      for (const prey of live) {
        if (meals <= 0) break;
        if (prey === hunter || prey.dead || !diet.hunts.includes(prey.type)) continue;
        prey.dead = true; meals--; hunter.hunger = 0;
      }
    }
    const rng = new RNG(mix(this.seedNum, key.length ? Number(key.split(',')[0]) * 97 + Number(key.split(',')[1]) : 0, Math.floor((this.runFrames || 0) / 3600), 5193));
    for (const g0 of live) {
      const diet = DIETS[g0.type]; if (!diet || !diet.grazes || g0.dead) continue;
      const cx = Math.floor(g0.cx / TS), cy = Math.floor(g0.cy / TS);
      for (let n = 0; n < Math.min(24, minutes * 3); n++) {
        const tx = cx + rng.int(9) - 4, ty = cy + rng.int(7) - 3;
        if (this.tileAt(tx, ty) === T.MOSS && this.systems) this.systems.layTuft(this, tx, ty);
      }
    }
  }
  /* A CREATURE FED FROM THE HAND IS YOURS FOR A WHILE (LS-521, after Caves of Qud's befriending). Meat in hand, facing a
   * lesser creature within a tile, the tool offers it instead of eating it: the creature eats and is a companion for
   * three minutes (Enemy.friendStep). Not a Great Beast, not the night's hunters. */
  feedAhead(sel): boolean {
    const p = this.player, [fx, fy] = ({ up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] } as any)[p.dir] || [0, 1];
    const ax = p.cx + fx * 12, ay = p.cy + fy * 12;
    let best: any = null, bd = 22;
    for (const e of this.enemies) {
      if (!e || e.dead || e.def.boss || e.prowler || e.hiding || e.spawnFx > 0) continue;
      const d = Math.hypot(e.cx - ax, e.cy - ay); if (d < bd) { bd = d; best = e; }
    }
    if (!best) return false;
    removeItem(this.state, sel.kind, 1);
    nudgeStanding(this.state, best.type, 1);   // its kind remembers that too (LS-522)
    best.friendUntil = this.runFrames + FRIEND_FRAMES; best.hunger = 0; best.routed = 0; best.meal = null; best.eating = 0;
    for (let i = 0; i < 5; i++) this.puff(best.cx + (i - 2) * 4, best.cy - 8, UI.gold, 2, 'mote');
    this.sfx('eat', best.x + 8); this.save();
    return true;
  }
  /** Every living body, active or asleep, by the chunk it stands in (the pack hunts by it). */
  bodiesByChunk(): Map<string, any[]> {
    const m = new Map<string, any[]>();
    const add = (e: any) => { if (!e || e.dead) return; const k = this.chunkKeyAt(e.cx, e.cy); const l = m.get(k); if (l) l.push(e); else m.set(k, [e]); };
    for (const e of this.enemies) add(e);
    for (const l of this.rosters.values()) for (const e of l) add(e);
    return m;
  }
  /** A waking after a fall: the creatures round the body are grown afresh, as a new visit's would be. */
  renewRoundTheBody() {
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const k = (this.area.x + dx) + ',' + (this.area.y + dy); this.rosters.delete(k); this.filled.delete(k); }
    this.spawnEnemies();
  }
  /** Walking into a chunk: if none of its own creatures is left anywhere, it grows a new roster (off the glass). */
  enterChunk(sx: number, sy: number) {
    const k = sx + ',' + sy;
    if (!this.filled.has(k)) return;
    const mine = (e: any) => e && !e.dead && e.home === k;
    if (this.enemies.some(mine)) return;
    for (const l of this.rosters.values()) if (l.some(mine)) return;
    this.filled.delete(k);
  }
  /** Wake what is in reach, grow what has never been, put to sleep what is far. Called every frame of play. */
  streamWorld(order: number[][] | null = null) {
    if (!this.world || !this.area) return;
    const ax = this.area.x, ay = this.area.y;
    if (!this.filled.has(ax + ',' + ay)) this.enemies.push(...this.fillChunk(ax, ay, true));
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const k = (ax + dx) + ',' + (ay + dy);
      const asleep = this.rosters.get(k);
      if (asleep) { this.rosters.delete(k); this.catchUp(k, asleep); for (const e of asleep) if (!e.dead && !this.enemies.includes(e)) this.enemies.push(e); }
      const lying = this.itemStash.get(k);
      if (lying) { this.itemStash.delete(k); for (const it of lying) if (!it.dead && !this.items.includes(it)) this.items.push(it); }
    }
    if (this.offscreenSim !== false) {
      for (const [dx, dy] of order || [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]) {
        const nx = ax + dx, ny = ay + dy;
        if (nx < 0 || ny < 0 || nx >= WORLD_W || ny >= WORLD_H || this.filled.has(nx + ',' + ny)) continue;
        this.enemies.push(...this.fillChunk(nx, ny, true));
        break;   // one a frame: a whole roster is a few placements, never a hitch
      }
    }
    if ((this.runFrames || 0) % 30 === 0) this.sleepFar();
    this.closeTheGround();
  }
  /** What stands or lies more than a chunk from the chunk underfoot sleeps under the chunk it is in. A beast is kept by
   *  its record (`tickBeast`), not here. What the hour no longer admits, off the glass, leaves (2026-09-19). */
  sleepFar() {
    const keep: any[] = [];
    for (const e of this.enemies) {
      if (!e || e.dead) continue;
      const sx = Math.floor(e.cx / PW), sy = Math.floor(e.cy / PH);
      if ((e.def && e.def.boss) || this.inReach(sx, sy) || sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) { keep.push(e); continue; }
      if (e.prowler) { e.dead = true; continue; }   // the night's hunters came for the body; one that has lost it is gone, not asleep somewhere
      this.stashRoster(sx, sy, [e]);
    }
    const gated = keep.filter((e) => e.def && !e.def.boss && (e.def.lowTide || e.def.rain || e.def.day || e.def.night || e.def.dusk) && !this.inWindow(e, TS * 2));
    if (gated.length) {
      const byHome = new Map<string, any[]>();
      for (const e of gated) { const k = e.home || this.chunkKeyAt(e.cx, e.cy); const l = byHome.get(k); if (l) l.push(e); else byHome.set(k, [e]); }
      for (const [k, list] of byHome) {
        const [hx, hy] = k.split(',').map(Number);
        if (!Number.isFinite(hx) || !Number.isFinite(hy)) continue;
        const admitted = new Set(this.rosterKinds(Array.from(new Set<string>(list.map((e) => e.type))), { kind: 'wild', x: hx, y: hy, level: 0 }));
        for (const e of list) if (!admitted.has(e.type)) e.dead = true;
      }
    }
    this.enemies = keep.filter((e) => !e.dead);
    const near: any[] = [];
    for (const it of this.items) {
      if (!it || it.dead) continue;
      const sx = Math.floor(it.x / PW), sy = Math.floor(it.y / PH);
      if (this.inReach(sx, sy) || sx < 0 || sy < 0 || sx >= WORLD_W || sy >= WORLD_H) { near.push(it); continue; }
      const k = sx + ',' + sy; const l = this.itemStash.get(k); if (l) l.push(it); else this.itemStash.set(k, [it]);
    }
    this.items = near;
  }
  /* THE GROUND UNDERFOOT CAN BE CLOSED TO OTHER CHUNKS' CREATURES -- a territory rule, kept from the rules the seams used
   * to enforce: a living beast's lair is its own (2026-09-13), a roused beast's fight is not joined by a crowd (round
   * 46), the glade and the screens round it take no wanderers past their small cap (LS-455). A body whose home is
   * elsewhere does not step into a closed chunk (`solid`) and cannot bite across its edge (`Enemy.update`). */
  closeTheGround() {
    this.closedKey = null;
    if (!this.world || !this.area || this.mode !== 'play') return;
    const ax = this.area.x, ay = this.area.y, key = ax + ',' + ay;
    const onLivingLair = (this.beastLairs() || []).some((l) => l.x === ax && l.y === ay && !(this.state.beastsSlain || []).includes(l.kind));
    const beastAwake = this.enemies.some((b) => b && b.def && b.def.beast && !b.dead && this.beastRoused(b));
    const inTheGlade = !!(this.world.start && ax === this.world.start.x && ay === this.world.start.y);
    const cap = this.gladeCrowdCap(ax, ay);
    const crowded = cap < Infinity && this.enemies.filter((e: any) => e && !e.dead && !(e.def && e.def.beast) && this.chunkKeyAt(e.cx, e.cy) === key).length >= cap;
    if (onLivingLair || beastAwake || inTheGlade || crowded) this.closedKey = key;
  }
  /** Is this point closed to this creature? Only a step INTO the closed chunk from outside it is refused. */
  closedTo(e: any, px: number, py: number) {
    const k = this.closedKey;
    if (!k || !e || e === this.player || !e.def || e.def.boss || e.home === k || (e.friendUntil || 0) > (this.runFrames || 0)) return false;   // a companion goes where you go
    return this.chunkKeyAt(px, py) === k && this.chunkKeyAt(e.cx, e.cy) !== k;
  }

  /* A POCKET IS FEWER THAN FORTY TILES WITH NO MOUTH. The first rule counted
   * twenty-four tiles on this screen only, took a seam for a way on and a
   * blastable wall for a door; the full soak's launch section, which floods
   * the world's own tiles and takes only a hollow's mouth, a hole or a stair
   * as a way out, still found thirteen landings in 1,109 that it called
   * pockets. This is that flood, so the game and its proof agree on the word. */
  landedInPocket(lp) { return this.pocketAt(lp.cx, lp.cy); }
  /** Is the ground under this point a closed pocket: fewer than forty tiles a body fits on, with no way out? */
  pocketAt(cx, cy) {
    const wx0 = Math.floor(cx / TS), wy0 = Math.floor(cy / TS);
    const tile = (x, y) => {
      const sx = Math.floor(x / COLS), sy = Math.floor(y / ROWS);
      if (x < 0 || y < 0 || sx >= WORLD_W || sy >= WORLD_H) return -1;
      const sc = this.screenAt(sx, sy);
      return sc && sc.tiles ? sc.tiles[y - sy * ROWS][x - sx * COLS] : -1;
    };
    const seen = new Set([wx0 + ',' + wy0]);
    const q = [[wx0, wy0]];
    let n = 0;
    while (q.length) {
      const [x, y] = q.shift();
      const [bx, by] = this.bodyAtTile(x, y);
      if (!this.bodyFits(bx, by)) continue;
      if (++n >= 40) return false;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy, k = nx + ',' + ny;
        if (seen.has(k)) continue;
        seen.add(k);
        const v = tile(nx, ny);
        if (v >= 0) q.push([nx, ny]);
      }
    }
    return true;
  }
  updatePush() {
    const p = this.player;
    const dir = p.dir;
    const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[dir];
    const holding = this.input[dir];
    const ahead = this.tileAtPoint(p.cx + dx * (p.w / 2 + 4), p.cy + dy * (p.h / 2 + 4));
    // HEAVY THINGS ARE PUSHED (LS-176 step 2): in the wilds a boulder is a
    // push block too, held into for the same eighteen frames
    const boulder = !!(ahead && ahead.t === T.ROCK);   // (the hollows' push-block was never generated after LS-226; its branch is gone, LS-511)
    if (!holding || !boulder) {
      this.pushing = null;
      return;
    }
    const key = ahead.tx + ',' + ahead.ty + ',' + dir;
    if (!this.pushing || this.pushing.key !== key) this.pushing = { key, t: 0 };
    if (++this.pushing.t < 18) return;
    this.pushing = null;
    if (!this.pushBoulder(ahead.tx, ahead.ty, dx, dy)) this.pushing = { key, t: -60 };
  }

  /** A BOULDER MOVES ONE TILE THE WAY IT WAS PUSHED (LS-176 step 2), and what
   *  it lands on decides what happens: open ground takes it; deep water
   *  swallows it and is a shallow after (a stepping stone -- never a gate
   *  cell, so the tide's rules do not move); a shallow swallows it whole; a
   *  fire under it is smothered; a body in the way is struck and the rock
   *  stays put. It never goes onto the screen's rim (the exits live there)
   *  and never where it would seal a route -- the nests' flood says so,
   *  bare-handed and with every charm -- because a rock a bare hand cannot
   *  pull back is a soft-lock waiting to happen. Both tiles are remembered
   *  per screen. Returns false when the push is refused. */
  pushBoulder(tx, ty, dx, dy) {
    const nx = tx + dx, ny = ty + dy;
    const sys = this.systems;
    if (this.tileAt(nx, ny) === undefined) { this.sfx('refuse'); return false; }   // world tiles (LS-511): a boulder may cross a seam
    const grid = sys.grid(this);
    const dest = this.tileAt(nx, ny);
    const body = this.enemies.find((e) => !e.dead && Math.floor(e.cx / TS) === nx && Math.floor(e.cy / TS) === ny);
    if (body) {
      if (typeof body.hurt === 'function') body.hurt(this, 2, tx * TS + 8, ty * TS + 8, 'boulder:' + this.runFrames);   // the tag names THIS push (2026-09-17): a constant one could only ever hurt a given body once in its life
      this.puff(nx * TS + 8, ny * TS + 8, UI.linen, 4);
      this.sfx('thud', nx * TS + 8);
      return false;
    }
    // a nest under a boulder is crushed, quietly: the rock stays where it was
    if (dest === T.NEST) {
      this.breakNestAt(nx, ny);
      grid.set(nx, ny, T.SCORCH); sys.mark(this, nx, ny, T.SCORCH);
      return false;
    }
    const OPEN_GROUND = [T.GROUND, T.MOSS, T.TUFT, T.SCORCH, T.DEBRIS, T.SAND];
    let landed;
    // water takes it: deep water is a shallow after (a stepping stone), a
    // shallow swallows it whole; the sea is the world's rim and takes nothing
    if (isWet(dest) && dest !== T.SEA) landed = T.SHALLOW;
    else if (OPEN_GROUND.includes(dest)) {
      if (!sealsNothing(grid, nx, ny)) { this.sfx('refuse'); return false; }
      if (sys.smother(this, nx, ny)) this.puff(nx * TS + 8, ny * TS + 4, UI.wispDim, 8);
      landed = T.ROCK;
    } else { this.sfx('refuse'); return false; }
    grid.set(tx, ty, T.GROUND); sys.mark(this, tx, ty, T.GROUND);
    grid.set(nx, ny, landed); sys.mark(this, nx, ny, landed);
    /* WHAT LAY ON THE ROCK GOES WITH IT (2026-09-19). A push orphaned the pile: `offered` items keyed to the old tile
     * could be neither picked up nor forged, and a rock shoved into water took them for good. The pile rides the
     * rock; a rock the water swallows frees its offerings as ordinary pickups. */
    for (const it of this.items) if (this.offeredAtForge(it, tx, ty)) {
      if (landed === T.ROCK) { it.forgeTx = nx; it.forgeTy = ny; it.x += dx * TS; it.y += dy * TS; } else it.offered = false;
    }
    this.wind(nx * TS + 8, ny * TS + 8, dx, dy, 3, 0.6, 0.8);
    this.puff(nx * TS + 8, ny * TS + 14, landed === T.ROCK ? UI.linen : UI.wispDim, 5);
    this.shakeFor(2, 6);
    this.sfx(landed === T.ROCK ? 'grind' : 'splash', nx * TS + 8);
    return true;
  }

  burnThornAt(tx, ty) {   // a world tile (LS-511)
    if (!this.state.hasEmber) return;   // the scorched floor beyond says why
    this.markBrokenAt(tx, ty);
    // the struck thorn is gone at once, but it burns where it stood: a flame, then scorch that regrows, and the fire
    // runs on from here into whatever beside it can burn
    this.setTileAt(tx, ty, T.SCORCH); this.systems.flare(this, tx, ty, 8);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) this.systems.ignite(this, tx + dx, ty + dy);
    this.puff(tx * TS + 8, ty * TS + 8, UI.gold, 8, 'ember');   // (these embers were thrown at the world's origin screen: the tile was screen-local and the puff is not)
    this.sfx('flame');
    this.save();
  }

  /** STANDING AT A STONE PUTS ITS HOLLOW ON YOUR MAP.
   *
   * A lore reviewer measured the deepmark's whole mechanical payload and
   * found it zero: 1,872 of 1,872 stones pointed at a hollow the MAP ALREADY
   * PINNED EXACTLY. `reachedTier(level)` is `level <= charms held`, and a
   * stone beside a tier-T barrier stands on a tier-(T-1) screen — so by the
   * time you can stand at the stone, its hollow is already a brass mark. At
   * frame 0 with no charms, 48.1% were redundant before the run began. The
   * stone gave one quadrant of four; the map gave one screen of sixty-four.
   *
   * So the map stops knowing. A hollow is rumoured only if it has been
   * CHARTED, and reading the stone that names it is what charts it. The
   * inscription becomes the thing that fills the map in, which is the only
   * way a script has weight: not lore that decorates a mechanic, but lore
   * that IS one.
   *
   * The keeper's spoken bearing survives on purpose — but NOT because it is
   * coarser, which is what this used to say. `keeperHint`'s bearing has nine
   * outcomes (four cardinals, four diagonals, "close by") against the carved
   * one's four, so the spoken heading is strictly FINER. What makes the stone
   * the answer and the rumour the hint is that the stone also CHARTS the exact
   * screen, and the keeper only points. Direction was never the difference.
   */
  readStone() {
    if (!this.world.stones) return;
    /* THE AFTERMATH HAS NO SECOND WAY OUT (LS-379 -> 2026-09-11). Under LS-304 a
     * stone ended the run the moment you stood beside it once the third beast
     * was down, with a second of grace against ending it by accident. Since
     * LS-379 the way out is a HELD action at any stone at any time, and this
     * branch survived beside it: a player who had felled all three and walked
     * up to read a stone was on the record screen before the hold began. One
     * rule now; the hold is in `tickLeave`, aftermath or not. */
    /* THE STONES ARE ASKED FOR IN WORLD TILES (2026-09-18, the same fault as `stoneBeside`): the map is keyed by a
     * screen and a tile inside it, `updateArea` keeps a tile of hysteresis, and so a body just over a seam was
     * asking the OLD screen about rows that do not exist -- a stone a step away on the far side could not be read
     * any more than it could be left at. */
    const wx = Math.floor(this.player.cx / TS), wy = Math.floor(this.player.cy / TS);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const mark = this.stoneAtWorld(wx + dx, wy + dy);
        if (!mark) continue;
        // A READ STONE PUTS ITS WORD ON THE MAP (the lore lens, round 43): the beast it names is 'heard' -- its word glossed beside the map, its English withheld until it is met
        // ...the beast the stone SAYS (the lore lens, round 44): the stones turn toward the living, so the sentence, not the charm's index, names it
        if (typeof mark === 'string') {
          const said = bearingWords(this.world, this.area.x, this.area.y, mark);
          /* ...AND THE RAKO STONE, ONCE IT POINTS (LS-302). Its first word names
           * nothing, so nothing was ever marked heard by reading it. With two
           * beasts down it carries a bearing to the last one standing, and
           * reading it is how a player learns that beast's word -- which is
           * the whole use of a stone that says anything at all. */
          const bare = said && said[0] === WORDS.unknown;
          const kind = bare
            ? (said.length > 1 ? ((this.state.beasts || []).find((r: any) => r.alive) || {}).kind : null)
            : said && Object.keys(WORDS).find((k) => WORDS[k] === said[0]);
          if (kind && !(this.state.beasts && this.state.beasts.length) && this.beasts) this.beasts();   // the records, if nothing has made them yet (a stone read before the first fill)
          const rec = kind ? (this.state.beasts || []).find((r) => r.kind === kind) : null;
          /* THE BEARING AS LIGHT (2026-09-12; Vijay: "the game is quite confusing to play right now"). The stone's words
           * name the beast and its quarter in the script; the motes carry the meaning: they stream from the stone the
           * beast's way, and the dial keeps a needle toward the beast heard last (hud.ts), until that beast falls. */
          if (rec && rec.alive !== false && (this.world.lairs || this.beastLairs())) {
            const lair = (this.world.lairs || []).find((l) => l.kind === kind);
            if (lair) {
              rec.heardAt = this.runFrames || 1;
              const sx = (wx + dx) * TS + TS / 2, sy = (wy + dy) * TS + TS / 2;
              const ddx = lair.px - sx, ddy = lair.py - sy, len = Math.hypot(ddx, ddy) || 1;
              /* AND NOT AS GOLD RIBBONS OFF THE STONE (2026-09-20; Vijay: "the gold shimmer thing on stones looks terrible
               * get rid of it"). Ten a second streamed from the stone toward the lair -- thinned once already after "wtf is
               * this" in 3D, and still a straw fan over the script a body stands in. The bearing is kept where it reads as
               * an instrument rather than as weather: the dial's needle holds the beast heard last until it falls, and the
               * stone's own words name the quarter. `heardAt` above is what the needle reads. */
              /* THE STONES REMEMBER (LS-294, decided 2026-09-13): a beast felled on any earlier run is known, and the stone
               * that names it shows its likeness rising over the script for a few seconds -- the world grows more legible
               * the more you have played, with no word added. */
              if (S.everFelled(Game, kind)) this.stoneLikeness = { kind, x: sx, y: sy - 4, from: this.runFrames, until: this.runFrames + 180 };
            }
          }
          if (rec && !rec.heard) { rec.heard = true; this.sfx('reveal'); this.save(); return; }
        }
      }
    }
  }

  lightBrazierAt(tx, ty, byLight = false) {   // a world tile (LS-511)
    if (this.tileAt(tx, ty) !== T.BRAZIER) return;
    /* OUTDOORS IT TAKES THE EMBERWAKE, and round 26 claimed that without
     * checking it.
     *
     * `lightBrazier` had no charm test at all — a melee hit calls it, a pod
     * blast calls it — so a reviewer lit the wayshrine with a plain sickle
     * holding zero charms on 30 of 30 seeds, while the round's own note said
     * it "gives the third charm something to do in the wilds". It did not. A
     * shrine anyone can light with a stick is a switch, not a use for a charm.
     *
     * Indoors is untouched: a hollow's brazier puzzle is lit by any hit, that
     * is what it has always been, and its difficulty lives in doing three
     * inside one Emberwake budget rather than in the lighting itself.
     *
     * SILENTLY. The refusal first said "cold - it wants a real flame", and it
     * is called from the MELEE HIT path — so a player without the charm got a
     * caption on every swing near a shrine. It also cost the bot three areas
     * of coverage, swinging at something that could never answer. The bowl is
     * drawn with cold ash in it; a caption explaining that a fire needs fire
     * is the kind of instruction GOAL.md scores negatively, and the world
     * already says it. */
    // a wisp is made of light and needs no ember (LS-178); the player does
    if (!this.state.hasEmber && !byLight) return;
    this.setTileAt(tx, ty, T.BRAZIER_LIT);
    this.puff(tx * TS + 8, ty * TS + 6, UI.gold, 6);
    this.sfx('flame');
    /* ...AND IT STAYS LIT — IN THE WILDS ONLY.
     *
     * This recorded the break BEFORE the guard below, so a hollow's puzzle
     * brazier went into the `broken` table too. `loadArea` runs
     * `resetPuzzle(room)` and then `applyBroken()`, and `applyBroken` always
     * wins — so re-entering the room left every lit bowl burning. Measured by
     * a reviewer at 52 of 52 puzzle rooms, and independently here at 20 of
     * 20: the puzzle could be assembled one brazier per visit and its whole
     * difficulty, which is lighting three inside one Emberwake budget,
     * stopped existing.
     *
     * The comment this replaced said "in a hollow the room state carries
     * this". The code did not check which it was in. */
    this.markBrokenAt(tx, ty);
    /* A WAYSHRINE SHOWS YOU THE COUNTRY.
     *
     * Two lenses have said the same thing for five rounds from opposite ends:
     * 42.3% of wild screens carry nothing a player can act on, and five
     * interactive tile kinds exist and appear on ZERO of 2,560 wild screens.
     * The verb was already built and wired — `entities.js` lights a brazier
     * with the Emberwake — it had simply never been placed outdoors.
     *
     * Lighting one charts the ring of screens around it. That is a real
     * decision (a charm cast for information rather than passage), it gives
     * the Emberwake something to do in the wilds where the design lens
     * measured the third charm opening 0.2% more world, and the reward is
     * knowledge rather than another number: the map is how this game already
     * says "there is something over there".
     */
    let found = 0;
    const ring = wearing(this.state, 'lodestone') ? 2 : 1;   // the lodestone (LS-335): a lit brazier charts two rings
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        const nx = Math.floor(tx / COLS) + dx, ny = Math.floor(ty / ROWS) + dy;   // the ring of chunks round the brazier's own
        if (nx < 0 || ny < 0 || nx >= WORLD_W || ny >= WORLD_H) continue;
        const k = nx + ',' + ny;
        if (this.charted.has(k)) continue;
        this.charted.add(k);
        found++;
      }
    }
    
    this.save();
  }

  clearInput() {
    if (this.player) this.player.buffered = false;
    this.input.attackPressed = false;
    this.input.podPressed = false;
  }

  checkRoomClear() {
    if (!this.enemies.length && this.spawnedHere > 0) {
      this.spawnedHere = 0;
      this.quiet.set(this.quietKey(), 2);
    }
  }

  /** Kept only for the storage warning, which is a fault report rather than
   *  instruction. Round 10 added three of these for the new tools — YOU GO
   *  UNDER, THE BREATH GOES OUT, THE EDGE TAKES — which is a first-time
   *  tutorial popup by construction, on exactly the verbs GOAL.md says the
   *  world should teach. They are gone: the hollow that hands you a tool now
   *  contains the thing that tool acts on, in 40 of 40 worlds for the Rattle
   *  and the Emberwake and 37 of 40 for the Silt Lung. The game teaches its verbs with the world: cracked stone
   *  spills debris, thorns grow out of scorched ground, a worn trail runs to
   *  a hollow mouth, and the sickle sparks when it is ready to be thrown.
   *
   *  THE METHOD IS GONE TOO. `teach()` outlived its last caller and sat here
   *  with zero call sites while `taught` was still built on every new game,
   *  written into every autosave and validated on every load — a save field
   *  carrying the state of a feature that could no longer happen. A reviewer
   *  flagged it in two consecutive rounds. The comment above is kept because
   *  it records the decision; the machinery is not, because dead code in the
   *  save path is how a format grows fields nobody can explain. */
  /** WALKING OFF A LEDGE, WHICH IS THE OTHER HALF OF A RAMP.
   *
   * The world is cut into terraces and until now the only way between two of
   * them was a ramp -- in both directions. A wall you cannot climb is right; a
   * wall you cannot STEP OFF is not, and asked for in exactly those words:
   * "i also should be able to jump/fall off cliffs".
   *
   * It only ever goes DOWN, so it cannot hand out a tier: the region tree
   * earns height through ramps and a drop spends it. What a drop can do is
   * strand you, which is why the landing has to be somewhere the world already
   * knows how to leave -- see the tier guard below.
   */
  ledgeHold = 0;
  ledgeHoldAt: any = null;
  checkLedge() {
    if (this.fall || this.mode !== 'play') return;
    const tr = this.terrain;
    if (!tr || !this.world) return;
    const p = this.player;
    const dirs = [];
    if (this.input.up) dirs.push([0, -1]);
    if (this.input.down) dirs.push([0, 1]);
    if (this.input.left) dirs.push([-1, 0]);
    if (this.input.right) dirs.push([1, 0]);
    /* ...OR THE STICK. The touch pad steers by a vector (`input.ax/ay`) and
     * the body moves by it, but the ledge only ever read the four key flags,
     * so a player on a phone could walk into a rim all day and never drop:
     * "still no way to fall down a cliff?" The dominant axis of the vector
     * is a direction like any key. */
    if (!dirs.length && (this.input.ax || this.input.ay)) {
      const ax = this.input.ax || 0, ay = this.input.ay || 0;
      if (Math.abs(ax) >= Math.abs(ay)) { if (Math.abs(ax) > 0.25) dirs.push([ax < 0 ? -1 : 1, 0]); }
      else if (Math.abs(ay) > 0.25) dirs.push([0, ay < 0 ? -1 : 1]);
    }
    if (!dirs.length) { this.ledgeHold = 0; this.ledgeHoldAt = null; return; }
    const tx = Math.floor(p.cx / TS), ty = Math.floor(p.cy / TS);
    const at = (x, y) => tr.at(Math.max(0, Math.min(tr.TW - 1, x)),
      Math.max(0, Math.min(tr.TH - 1, y)));
    const here = tr.terrace[at(tx, ty)];
    for (const [dx, dy] of dirs) {
      if (this.tileAt(tx + dx, ty + dy) !== T.CLIFF) continue;
      /* PAST THE WHOLE THICKNESS OF THE ROCK. A cliff is often two or three
       * tiles deep, and landing in the middle of one is landing inside a wall. */
      let n = 1;
      // the WHOLE stack: the walk gave up at five and the tallest stacks are five
      while (n < 9 && this.tileAt(tx + dx * n, ty + dy * n) === T.CLIFF) n++;
      const lx = tx + dx * n, ly = ty + dy * n;
      const t = this.tileAt(lx, ly);
      if (t === undefined || isSolid(t) || isWet(t)) continue;
      if (tr.terrace[at(lx, ly)] >= here) continue;      // not a drop: a wall
      /* AND ONLY SOMEWHERE THE WORLD KNOWS HOW TO LEAVE.
       *
       * A drop is one-way, so landing in a pocket with no ramp out of it is a
       * softlock -- the shape of bug this project has shipped more than once.
       * The region tree already answers this: every region reachable from the
       * start has a tier, assigned by breadth-first search through the ramps,
       * and a region with no tier is one nothing connects to. Landing is only
       * allowed in a region that has one. */
      /* A HOLD BEFORE THE HOP, as Minish Cap does it: the jump starts after
       * sixteen frames of pushing into the edge, not on the first touch, so
       * brushing a ledge does not throw you off it. Stop pushing, or push
       * another way, and the count starts over. */
      /* ...AND NEVER INTO A POCKET (LS-206). A lower terrace can be a corner
       * closed on every side but the cliff above it -- the full soak found
       * one on soak-pocket-29, screen 1,2: eleven tiles walled by trees,
       * standing stones, the cliff and an unlinked seam -- and a body that
       * dropped into it had no way out but dying. A launch already refuses
       * to end where `canLeaveFrom` says no; a drop keeps the same promise:
       * the ledge simply is not a ledge there, and the body stands at the lip. */
      if (!this.canLeaveFrom(lx * TS + TS / 2, ly * TS + TS / 2)) continue;
      const key = lx + ',' + ly;
      this.ledgeHold = this.ledgeHoldAt === key ? this.ledgeHold + 1 : 1;
      this.ledgeHoldAt = key;
      if (this.ledgeHold < LEDGE_HOLD) return;
      this.ledgeHold = 0; this.ledgeHoldAt = null;
      this.beginFall(lx, ly, n);
      return;
    }
  }

  /** The drop itself: a hop out over the brink and down to the ground below.
   *  The arc is in the position rather than a separate offset, because nothing
   *  collides with the player while this runs -- `update` gives the frame to
   *  `updateFall` and returns. */
  beginFall(lx, ly, depth) {
    const p = this.player;
    this.fall = {
      x0: p.x, y0: p.y,
      x1: lx * TS + (TS - p.w) / 2, y1: ly * TS + (TS - p.h) / 2,
      t: 0, dur: 10 + 3 * depth, lift: 5 + 2 * depth,
    };
  }

  updateFall() {
    const f = this.fall, p = this.player;
    f.t++;
    const u = Math.min(1, f.t / f.dur);
    p.x = f.x0 + (f.x1 - f.x0) * u;
    p.y = f.y0 + (f.y1 - f.y0) * u - Math.sin(u * Math.PI) * f.lift;
    p.recentre();
    if (u < 1) return;
    p.x = f.x1; p.y = f.y1; p.recentre();
    this.fall = null;
    p.landing = 6;                         // the dip on landing, drawn by the player
    this.sfx('drop');
    this.shakeFor(2, 6);
    // a moment of nothing, so the landing reads as an impact and not a slide
    this.hitstopFor(4);
    p.invuln = Math.max(p.invuln, 16);
    // a drop is not a hurt: the grace does not strobe the body (Vijay saw the
    // forager flicker walking off every ledge, LS-196)
    // ...for longer than the invulnerability: `draw` spends one grace frame per
    // frame, and the strobe's last window (invuln 1..3) hid the body for a frame
    p.launchGrace = Math.max(p.launchGrace || 0, 20);
  }

  /** THE WORLD'S RIM (LS-511). A seam is not an edge any more: a launch over one flies on and lands by its own rule
   *  (`land`, the pocket test); only the map's outside holds the body in. */
  checkEdges() {
    const p = this.player;
    const maxX = WORLD_W * COLS * TS - p.w, maxY = WORLD_H * ROWS * TS - p.h;
    if (p.x < 0 || p.y < 0 || p.x > maxX || p.y > maxY) {
      p.x = Math.max(0, Math.min(maxX, p.x));
      p.y = Math.max(0, Math.min(maxY, p.y));
      p.recentre();
    }
  }

  /** Last-resort safety net: if the player has been placed inside something
   *  solid, walk outwards until a free tile is found. Nothing should ever
   *  need this, but being embedded in a wall is unrecoverable for the player
   *  and cheap for us to rule out. */
  /** If a placement lands inside something solid, step back the way the player
   *  came from. It must never search past a barrier: an earlier version walked
   *  outward in all directions and cheerfully teleported the player through a
   *  thorn gate into the Warden's room in 47% of seeds. */
  /** Can a player standing here walk to any way out of this area, with what
   *  they are holding? A launch that lands behind a lake with no exit is a
   *  dead run, so the landing is validated and reverted rather than saved. */
  /** Can a BODY standing here fit? The player is 13x13 and collides on eight
   *  points; asking whether one point is solid says nothing about whether the
   *  thing that has to move actually fits. */
  bodyFits(x, y) {
    const p = this.player;
    for (const [px, py] of bodyPoints(x, y, p.w, p.h)) {
      if (this.solid(px, py, false, p)) return false;
    }
    return true;
  }
  /** The top-left a body sits at when it stands in the middle of a tile. */
  /* A tuple, for the same reason `canopyArgs` is: it is spread into
   * `bodyFits(x, y)`, which takes exactly two. */
  /* A TILE OF THIS AREA'S GRID, AS A PLACE IN THE WORLD. Same boundary as
   * `fitDrop`: callers name a tile, the world wants pixels. */

  /* NOTE. A `reachableFrom(cx, cy, cap)` helper lived here, added for a
   * launch guard that let a landing stand whenever the region around it was
   * bigger than a pocket. That guard was wrong — soak's own "never strands
   * you where you land" caught 9 of 145 launches stranded, and an examiner
   * confirmed every stranded region had zero blastable tiles on its boundary,
   * so the justification ("you can blast your way out") was false. The
   * replacement remembers the last point on the flight with a way out; it
   * needs no region size, so the helper went with the idea. It sat here for a
   * while afterwards, exported to nothing, under fifteen lines arguing for a
   * design the code beside it no longer implemented.
   */
  /** Can a body standing here walk away? It can unless the ground it stands on is a closed pocket (LS-511: this flooded
   *  one screen and asked whether the flood reached its edge, so a seam counted as a way out). */
  canLeaveFrom(cx, cy) { return !this.pocketAt(cx, cy); }
  /** Can someone arriving on screen (sx, sy) through the (dx, dy) border get
   *  anywhere but back where they came from? Used by `canLeaveFrom` so a
   *  border tile is not counted as an exit into a second sealed pocket. */

  /* `back` is OPTIONAL — most callers have no direction to prefer and want
   * the net's own search. Eight of nine call sites omitted it, which reads at
   * runtime as `undefined` and worked, and reads to a checker as eight
   * mistakes. */
  freeThePlayer(back: number[] | null = null) {
    const p = this.player;
    // THE BODY. `solid(p.cx, p.cy)` asks whether the player's centre point is
    // inside something, and the player is a 13x13 box that collides on eight
    // points: a position straddling four tiles with one of them solid is
    // completely stuck while its centre stands in open floor, and this
    // returned early on every one of them. 31% of wake points and 36 of 150
    // long runs froze there, and the save recorded the frozen position.
    if (this.bodyFits(p.x, p.y)) return;
    const tx = Math.floor(p.cx / TS), ty = Math.floor(p.cy / TS);
    // A breadth-first search, not four neighbours. Looking one tile out left
    // the player embedded in 33% of the cases it was asked to rescue — and
    // because the save reloads them to the same place, no reload recovered it.
    // It still refuses to cross a barrier: walking a player through a thorn
    // wall or a sealed door to "rescue" them would hand them the gate.
    // The tier barriers come from `passable.js`, so this cannot drift from the
    // generator's idea of what a gate is. LOCK and BARS are a hollow's own
    // doors, which are not that module's business.
    //
    // WATER AND SHALLOWS ARE THE ONE EXCEPTION, deliberately. They are the
    // tier barrier in a third of worlds, but they are also ambient — about a
    // tenth of every world — and a launch can put the player in the middle of
    // a lake. Refusing to cross them at all left 132 of 1,265 embedded players
    // with no rescue at all. The gate case they cover is closed one rule up,
    // by `outsideGates`: the rescue may swim, but it may not come down in the
    // mouth beyond a barrier. Measured 0 of 650 gated mouths.
    const wet = isWet;
    const barrier = (v) => (BARRIERS.has(v) && !wet(v)) || v === T.CRACK;
    // Refusing to CROSS a barrier still handed over the gate when the player
    // was standing IN one. From inside the gate tile both sides are one
    // non-barrier step away and the search took whichever fitted first:
    // measured over 650 gated mouths, 326 (50.2%) came down in the mouth
    // BEYOND the barrier, on a screen they had no charm for.
    const seen = new Set([tx + ',' + ty]);
    // the direction they came from goes first, so a rescue prefers backwards
    const dirs = [];
    if (back) dirs.push([back[0], back[1]]);
    dirs.push([0, 1], [0, -1], [1, 0], [-1, 0]);
    // ...AND A BARRIER YOU ARE STANDING IN IS ONE YOU MAY WALK OUT OF.
    // Refusing to cross a gate is right. Refusing to LEAVE one is how a body
    // that ends up inside a thicket stays there forever — which is exactly
    // what round 10's waterline hedge did, 250 of 250 times, with the position
    // saved. So: never step INTO a barrier, but keep going while still inside
    // the one you woke up in. Landing past a tier gate is refused a rule up,
    // by `outsideGates`.
    // ...BUT NOT OUT OF A GATE. A body inside a tier barrier is inside the one
    // kind of barrier that has two sides worth being on, and walking it out of
    // its own blob hands over the gate — measured, 5 of 405 gates opened to a
    // charmless player the moment this rule was added. Wedged in a gate cell,
    // the rescue is strict again and `wakeSomewhereOpen` and the launch revert
    // take it from there.
    const startsInside = barrier(this.tileAt(tx, ty) ?? 0);
    const q = [[tx, ty, startsInside]];
    while (q.length) {
      const [cx, cy, inside] = q.shift();
      for (const [dx, dy] of dirs) {
        const nx = cx + dx, ny = cy + dy;
        /* THE BOUNDS ARE THE SPACE THE PLAYER IS IN. `tx`/`ty` come from
         * `p.cx / TS`, and in the wilds that is a WORLD tile — 0 to 128, not 0
         * to 16. Testing them against COLS meant the search ended on its first
         * step for any player standing east of the first screen, so the rescue
         * that exists to stop a run being lost simply did not run. */
        if (nx < 0 || ny < 0 || nx >= this.gridW() || ny >= this.gridH()) continue;
        const k = nx + ',' + ny;
        if (seen.has(k)) continue;
        seen.add(k);
        const v = this.tileAt(nx, ny);
        /* THROUGH anything, ONTO only what holds a body. A version of this
         * refused to enter cliffs, sea and deep water and stopped six tiles
         * out (for a launch that landed 49 tiles up the map, soak-pocket-2),
         * and left 688 of 1530 embedded bodies where they were: a body put
         * down in open sea has to be walked to land. The launch that needed
         * the leash was checkEdges pushing a landing onto the next screen
         * (LS-175), which is fixed, and a landing nothing near can hold goes
         * back where it flew from. So the flood ranges, as it first did. */
        if (barrier(v) && !inside) continue;         // never INTO a gate
        const [bx, by] = this.bodyAtTile(nx, ny);
        if (this.bodyFits(bx, by)) {
          p.x = bx; p.y = by;
          p.recentre();
          return;
        }
        q.push([nx, ny, inside && barrier(v)]);
      }
    }
  }


  // ------------------------------------------------------------ pods & fx
  placePod() {
    if (!this.state.hasPods || this.state.pods <= 0) return;
    if (this.pods.length >= 2) return;
    removeItem(this.state, 'pod', 1);   // out of the bag (LS-225)
    const tx = Math.floor(this.player.cx / TS), ty = Math.floor(this.player.cy / TS);
    const pod = new Pod(tx * TS + 2, ty * TS + 2);
    if (this.player.lit > 0) pod.fuse = 2;   // a pod placed by a hand that carries fire goes off at once (LS-176)
    this.pods.push(pod);
    // ...AND IT IS SPENT WHEN IT LEAVES THE POUCH, not when it goes off.
    // Only `blast()` wrote a save, so a pod placed and not yet detonated came
    // back if you died on its fuse — and the comment beside the ward's save
    // claimed placing already did this, which was simply false. An examiner
    // measured it: pods 5, place, 4, die, 5. Small money (a pod is 2 glim
    // against a 6-glim death toll) and still the wrong rule, sitting under a
    // comment asserting the opposite.
    this.save();
    this.sfx('drop');
  }

  blast(cx, cy) {
    this.sfx('blast');
    this.shakeFor(5, 18);
    this.hitstopFor(3);
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      this.fx.push(new Puff(cx, cy, i % 3 ? UI.gold : UI.linen,
        Math.cos(a) * (1 + Math.random() * 1.6), Math.sin(a) * (1 + Math.random() * 1.6), 18 + (i % 5) * 3));
    }
    // the pod stands in world px; the tiles it breaks are the screen's own.
    // Read as a world tile, every blast off screen 0,0 fell outside the 16x12
    // box below and broke nothing -- the rattle did nothing in play (LS-164)
    /* THE NINE TILES ROUND IT, IN WORLD TILES (LS-511): a blast on a seam reached the far side through its own path,
     * which broke only the bombable kinds; now every tile hears 'blast' from the one grid, whichever chunk holds it. */
    const btx = Math.floor(cx / TS), bty = Math.floor(cy / TS);
    const w = new SimWorld(this.systems.grid(this), this.seedNum);
    for (let ty = bty - 1; ty <= bty + 1; ty++) for (let tx = btx - 1; tx <= btx + 1; tx++) {
      const t = w.tileAt(tx, ty);
      if (t) emit(w, 'blast', t, { game: this, handled: false, ignite: (x, y) => this.systems.ignite(this, x, y) });
    }
    for (const e of this.enemies) {
      if (!e.dead && Math.hypot(e.cx - cx, e.cy - cy) < 26) e.hurt(this, 3, cx, cy);
      // ...and every body near it is thrown, as the player is (LS-176): a
      // bramble into the lake drowns, a wisp floats, a boss stands
      if (e.dead || e.def.boss) continue;
      const d = Math.hypot(e.cx - cx, e.cy - cy);
      if (d < 40) {
        const a = Math.atan2(e.cy - cy, e.cx - cx);
        e.kb = { dx: Math.cos(a), dy: Math.sin(a), t: 14, launched: true, fromX: e.x, fromY: e.y };
      }
    }
    // --- THE RATTLE'S SECOND USE ---------------------------------------
    // The blast throws the player too, and throws them further than it hurts
    // them. Stand beside your own pod and it will pitch you a good three tiles
    // — over water you have no charm for, over a hedge you cannot burn. The
    // obvious use of a seed pod is breaking stone. This is the other one, and
    // it is the reason a skilled player does not have to take the intended
    // route through this game.
    const dp = Math.hypot(this.player.cx - cx, this.player.cy - cy);
    if (dp < 22) this.player.hurt(this, 1, cx, cy);
    if (dp < 34) {
      // Standing exactly on your own pod makes atan2(0, 0) — which is 0, so
      // the throw always went east regardless of where you were facing. Fall
      // back to the way you are looking, which is the answer the player
      // expects and the only one they can aim.
      // a pod set at your own feet sits a few px off your centre (it stands
      // at its tile's corner, you at yours): within a tile it is "at your
      // feet" and throws you the way you face, not the way you happen to lean
      const a = dp < TS * 0.75
        ? { up: -Math.PI / 2, down: Math.PI / 2, left: Math.PI, right: 0 }[this.player.dir]
        : Math.atan2(this.player.cy - cy, this.player.cx - cx);
      this.player.launch(Math.cos(a), Math.sin(a));
    }
    this.save();
  }


  /** Three blows of the axe (LS-384): the tree comes down, litter where it stood, two logs beside it. */
  fellTreeAt(tx, ty) {   // a world tile (LS-511)
    if (this.tileAt(tx, ty) !== T.TREE) return false;
    this.markBrokenAt(tx, ty);
    this.setTileAt(tx, ty, T.DEBRIS);
    this.dropAt('log', tx * TS + 1, ty * TS + 4, { dropped: true }); this.dropAt('log', tx * TS + 6, ty * TS + 9, { dropped: true });   // saved, like any drop (2026-09-18): the tree does not come back, so its logs must not vanish with a reload
    const cx = tx * TS + 8, cy = ty * TS + 8;
    for (let i = 0; i < 8; i++) this.puff(cx + (Math.random() - 0.5) * 10, cy + (Math.random() - 0.5) * 10, UI.bone, 1, 'chunk');
    this.sfx('crush', cx); this.shakeFor(2, 6);
    this.state.felledTrees = (this.state.felledTrees || 0) + 1;
    return true;
  }
  treeChops = new Map<string, number>();   // blows taken by each standing tree, by world tile (LS-384)
  chopTreeAt(tx, ty, of) {   // a world tile
    const key = tx + ',' + ty;
    const n = (this.treeChops.get(key) || 0) + 1;
    if (n >= of) { this.treeChops.delete(key); return this.fellTreeAt(tx, ty); }
    this.treeChops.set(key, n); this.chipTree(tx, ty, n, of); return false;
  }
  chipTree(tx, ty, n, of) {   // a world tile
    const cx = tx * TS + 8, cy = ty * TS + 8;
    for (let i = 0; i < 3; i++) this.puff(cx + (Math.random() - 0.5) * 8, cy + 2 + (Math.random() - 0.5) * 6, UI.bone, 1, 'chunk');
    this.sfx('chip', cx);
  }
  breakTileAt(tx, ty) {   // a world tile (LS-511)
    const was = this.tileAt(tx, ty);
    if (was === undefined) return;
    this.markBrokenAt(tx, ty);
    this.setTileAt(tx, ty, T.GROUND);
    if (was === T.ROCK || was === T.RUBBLE) this.dropAt('stone', tx * TS + 2, ty * TS + 2, { dropped: true });
    if (was === T.ROCK) for (const it of this.items) if (this.offeredAtForge(it, tx, ty)) it.offered = false;   // only this rock's pile is freed; neighboring screens can share local tile coordinates
    /* WHAT THE DIGGERS LEFT IN A CAIRN.
     *
     * The same two things the cracked stash holds underground, for the same
     * reason: they are the same people, and this is what they were carrying.
     * The pod comes back, so opening a cairn costs the player nothing but
     * having a pod in the first place — which is the gate. A tier-0 player
     * walks past cairns for an hour and cannot open one, sees them the whole
     * time, and the charm that finally opens them is the one they were told
     * opens the RUBBLE at the border. Learning that a key is also a tool is
     * the moment this is built for.
     *
     * `markBroken` above already ran, so the ground stays ground on the next
     * visit and a cairn pays out exactly once. */
    if (was === T.CAIRN) {
      this.dropAt('glim', tx * TS + 1, ty * TS + 1);
      this.dropAt('pod', tx * TS + 1, ty * TS - 15);
      this.sfx('reveal');
    }
    for (let i = 0; i < 6; i++) {
      this.fx.push(new Puff(tx * TS + 8, ty * TS + 8, UI.parchment,
        (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3, 14 + i));
    }
  }

  /** @param {string} [kind] which of the four particle sheets to throw:
   *  `spark` for something struck, `splash` for water, `ember` for fire,
   *  `smoke` for everything that is only dust. */
  /** Streaks of moved air (LS-207, "give everything wind effects"): `n` of
   *  them from (x, y) along (dx, dy), fanned a little, fading as they fly. */
  wind(x, y, dx, dy, n = 3, spread = 0.35, speed = 1, extra: any = null) {
    const base = Math.atan2(dy, dx);
    for (let i = 0; i < n; i++) {
      const a = base + (Math.random() - 0.5) * spread * 2;
      const sp = (1.6 + Math.random() * 1.6) * speed;
      this.fx.push(new Puff(x + (Math.random() - 0.5) * 6, y + (Math.random() - 0.5) * 6, (extra && extra.colour) || (i % 3 === 0 ? UI.paper : UI.linen), Math.cos(a) * sp, Math.sin(a) * sp, (extra && extra.life) || 16 + Math.floor(Math.random() * 10), 'wind', { ...(extra || {}), span: 8 + (i % 3) * 2 }));
    }
  }

  puff(x, y, colour, n, kind = 'smoke') {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = kind === 'chunk' ? 1.2 + Math.random() * 2.2 : kind === 'dust' ? 0.6 + Math.random() * 0.8 : 1.4;
      const life = kind === 'chunk' ? 28 + i * 2 : kind === 'ring' ? 12 : kind === 'flash' ? 4 : kind === 'dust' ? 16 + i : 12 + i;
      this.fx.push(new Puff(x, y, colour, Math.cos(a) * sp, kind === 'chunk' ? Math.sin(a) * sp * 0.6 - 1.4 : kind === 'dust' ? Math.sin(a) * sp * 0.3 : Math.sin(a) * sp, life, kind, { floor: y + 5 + Math.random() * 4 }));
    }
  }
  /* A BODY COMES APART (LS-190). Where a death was seven puffs of linen, it is
   * the body's own colours flying out and falling: chunks that bounce and lie
   * where they land, a ring of dust, a flash at the heart of it, sparks. A
   * hit chips a couple of pieces off, so a fight leaves a floor. */
  burst(e, big = false) {
    const cols = coloursOf(e.type, e.alt || null, !!e.enraged);
    const n = big ? 18 : 9;
    for (let i = 0; i < n; i++) this.puff(e.cx + (Math.random() - 0.5) * 6, e.cy + (Math.random() - 0.5) * 6, cols[i % cols.length], 1, 'chunk');
    this.puff(e.cx, e.cy + 4, UI.linen, big ? 10 : 5, 'dust');
    this.fx.push(new Puff(e.cx, e.cy, UI.paper, 0, 0, 4, 'flash', { size: big ? 9 : 5 }));
    this.fx.push(new Puff(e.cx, e.cy, big ? UI.gold : UI.linen, 0, 0, big ? 16 : 10, 'ring', { size: 3 }));
    this.puff(e.cx, e.cy - 3, UI.gold, big ? 14 : 8, 'spark');
  }
  chip(e, fromX, fromY) {
    const cols = coloursOf(e.type, e.alt || null, !!e.enraged);
    const ang = Math.atan2(e.cy - fromY, e.cx - fromX);
    for (let i = 0; i < 3; i++) {
      const a = ang + (Math.random() - 0.5) * 1.2, sp = 1.5 + Math.random() * 1.5;
      this.fx.push(new Puff(e.cx, e.cy - 2, cols[i % cols.length], Math.cos(a) * sp, Math.sin(a) * sp * 0.5 - 1.2, 26 + i * 3, 'chunk', { floor: e.cy + 4 + Math.random() * 4 }));
    }
  }

  shakeOff = false;   // the menu's shake toggle (LS-204)
  shakeFor(mag, t) {
    if (this.shakeOff) return;
    if (!this.shake || this.shake.mag < mag) this.shake = { mag, t };
    else this.shake.t = Math.max(this.shake.t, t);
  }

  hitstopFor(n) { this.stop = Math.max(this.stop, n); }
  slowmo = 0;

  /* A SUMMONED ENEMY HAS TO BE STANDING SOMEWHERE THE SWORD CAN GO.
   *
   * This took a CENTRE and handed it to `new Enemy`, which takes a TOP-LEFT —
   * so every wisp the Warden called was born half a body down and to the
   * right of it, and nothing checked what was there. It was the only spawn
   * path in the game that vetted nothing: `placeEnemy` and `placeEnemyNear`
   * both refuse solid tiles and both stay inside the border ring.
   *
   * The corner case is a softlock, not a blemish. A wisp is `ghost: true`,
   * and `solid()` still calls T.WALL solid for ghosts — so a wisp born inside
   * the wall ring fails `hits()` in every direction and `moveAxis` can never
   * walk it out. It sits there at 1hp, out of the sword's reach, and the room
   * never clears. Seed `run-39` summoned one at (248,174) in a 256x192 room,
   * where the Warden's own door is the only way on: the bot stood in that
   * room for 36,000 frames — ten minutes — and the hollow could not be
   * finished by anyone. `and no charm order fails to take a seed` is what
   * caught it.
   *
   * `placeEnemyNear` already does the whole job, ring-searching outward from
   * a tile for somewhere legal, so this is now a tile lookup and a delegation.
   * If it finds nowhere, nothing spawns — the Warden re-checks its wisp count
   * every pass and calls another, which is the right way to fail.
   */
  summon(type, cx, cy) {
    return this.placeEnemyNear(type, new RNG(mix(this.seedNum, this.runFrames)),   // the run's clock, not the title's: an idle title moved every beast (the robustness lens)
      Math.floor(cx / TS), Math.floor(cy / TS));
  }

  // ------------------------------------------------------------- events
  /** THE SICKLE HARVESTS (LS-176 step 4): every tile of grass the blade
   *  takes down is laid as a tuft -- dry straw on the soil that a fire runs
   *  along and that grows back into moss on the clock. The turf's blades on
   *  that tile stay down until the tile is moss again (their own timer is
   *  seven seconds; the tile's is game-minutes), or the cut grass would stand
   *  up through its own clippings. */
  layTuft = (tx: number, ty: number) => {
    if (!this.systems || !this.systems.layTuft(this, tx, ty)) return;
    // the turf cell keeps the cut clock `cutTurf` gave it (LS-236): it was pinned uncuttable-for-ever here, and the swath never grew back
  };

  /** The rule reads tiles, not the turf: every MOSS tile whose centre is
   *  within `reach` of the blade's point (screen px) is grass the blade took
   *  down, whether or not this game grew the turf that draws it (the sim
   *  does not). */
  reap(wx: number, wy: number, reach: number) {   // world pixels (LS-511)
    const x0 = Math.floor((wx - reach) / TS), x1 = Math.floor((wx + reach) / TS);
    const y0 = Math.floor((wy - reach) / TS), y1 = Math.floor((wy + reach) / TS);
    for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
      if (this.tileAt(tx, ty) !== T.MOSS) continue;
      if (Math.hypot(tx * TS + TS / 2 - wx, ty * TS + TS / 2 - wy) > reach) continue;
      this.systems.layTuft(this, tx, ty);
    }
  }

  onSwing(kind = 'fore') {
    // the camera kicks with the swing (LS-203): one pixel, a few frames --
    // enough to feel the blade leave the hand, not enough to blur the read
    this.shakeFor(kind === 'spin' ? 2 : 1, kind === 'thrust' ? 6 : 4);
    this.swingId = (this.swingId || 0) + 1;
    // one cue per swing: the thrust and the spin have their own (a clip with sound
    // showed the thrust firing both, LS-191)
    this.sfx(kind === 'thrust' ? 'thrust' : kind === 'spin' ? 'spin' : 'swing', this.player.x + 8);
    // A SWUNG BLADE CUTS WHAT IT PASSES THROUGH. The sickle is a sickle: it
    // takes the grass down in front of you and leaves clippings. The stand
    // grows back — this is a wood, not a lawn — but slowly enough that a path
    // you cut through a field is a path you can still see on the way back.
    const mows = swingWeaponOf(this.player).weapon.verb !== 'strike';   // bare hands and a worn blade cut no grass (LS-238)
    if (mows) {
      const p = this.player, reach = 16;   // a swath three cells wide (LS-236): twelve cut one or two, which did not read as mowing
      if (kind === 'spin') for (let i = 0; i < 8; i++) this.reap(p.cx + Math.cos(i * Math.PI / 4) * reach, p.cy + Math.sin(i * Math.PI / 4) * reach, reach);
      else { const v = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[p.dir] || [0, 1]; this.reap(p.cx + v[0] * reach, p.cy + v[1] * reach, reach); }
    }
    if (mows && this.turf && kind === 'spin') {
      // the spin mows a ring
      const p = this.player, reach = 16;   // a swath three cells wide (LS-236): twelve cut one or two, which did not read as mowing
      let cut = 0;
      // the turf grid is this screen's; the player is in world px (LS-164)
      const lx = p.cx - this.originX(), ly = p.cy - this.originY();
      /* THE TURF CUTS THE PICTURE; THE TILES ARE CUT BY `reap` (2026-09-08). Both of these passed
       * `this.layTuft` into `cutTurf`, so every turf cell the blade crossed ALSO changed a tile --
       * inside a branch guarded by `this.turf`, which exists only when the game is drawing. The note
       * over `layTuft` says the opposite in as many words: "the rule reads tiles, not the turf ...
       * whether or not this game grew the turf that draws it (the sim does not)". It was stated
       * correctly and violated four lines later.
       *
       * That is the whole of the sim/drawn divergence LS-346 turned up: `reap` above lays tufts on
       * MOSS tiles and runs in BOTH games, and this laid more of them, on any cell that happened to
       * have grown grass, and only in one. A body walking next door then took a different step.
       * Found because a sickle that ends its chain in a spin does this eight times a swing instead of
       * once, which made two pixels of drift into something a check could see. */
      for (let i = 0; i < 8; i++) cut += cutTurf(this.turf, COLS, ROWS, lx + Math.cos(i * Math.PI / 4) * reach, ly + Math.sin(i * Math.PI / 4) * reach, reach, null) || 0;
      if (cut) this.refreshTurfArt();
      if (cut) for (let i = 0; i < 4 + Math.min(8, cut); i++) { const a = Math.random() * Math.PI * 2; this.fx.push(new Puff(p.cx + Math.cos(a) * 8, p.cy + Math.sin(a) * 5, UI.leaf, Math.cos(a) * 1.2, -1.5 - Math.random(), 30 + i, 'leaf', { floor: p.cy + 8 })); }
    } else if (mows && this.turf) {
      const p = this.player;
      const reach = 16;   // a swath three cells wide (LS-236): twelve cut one or two, which did not read as mowing
      const at = { up: [p.cx, p.cy - reach], down: [p.cx, p.cy + reach],
        left: [p.cx - reach, p.cy], right: [p.cx + reach, p.cy] }[p.dir];
      // the turf grid is this screen's; the player is in world px (LS-164): the
      // cut had been asked for at world px since the camera, and never landed
      const cut = cutTurf(this.turf, COLS, ROWS, at[0] - this.originX(), at[1] - this.originY(), reach, null);   // the picture only: `reap` lays the tufts, in both games (see above)
      if (cut) this.refreshTurfArt();
      if (cut) for (let i = 0; i < 3 + cut; i++) this.fx.push(new Puff(at[0], at[1], UI.leaf, (Math.random() - 0.5) * 2, -1.2 - Math.random(), 26 + i, 'leaf', { floor: at[1] + 6 }));
    }
    // A binary gate on full health meant one chip hit removed the throw for the
    // rest of a fight, in a room with no healing. It is a charge now.
    if (this.state.beam >= 1 && swingWeaponOf(this.player).kind === 'sickle' && !swingWeaponOf(this.player).worn) {   // only the sickle throws, and a keen one (LS-238: a full gauge threw a sickle from bare hands and from a spear)
      this.state.beam = 0;
      const v = { up: [0, -3.4], down: [0, 3.4], left: [-3.4, 0], right: [3.4, 0] }[this.player.dir];
      const shot: any = new Shot('arc', this.player.cx - 4, this.player.cy - 4, v[0], v[1], true);
      /* THE BLADE THAT IS THROWN IS THE BLADE THAT WEARS (the robustness lens, rounds 51 and 52). `wearWeapon` had
       * exactly one call site, in the melee loop, and `Shot.update` did its damage with none -- so all three Great
       * Beasts fell to thrown sickles at 110 blows of 110, and sixty throws cost nothing. Every landed blow spends
       * one of a weapon's blows, and a throw that lands is a blow: the shot carries the slot it came from so the
       * wear goes on the stack that threw it, which is the same rule a swing already follows (a keen copy in
       * another slot stayed keen for ever, round 47). */
      shot.slot = this.player.swingSlot;
      shot.weapon = swingWeaponOf(this.player).kind;
      this.shots.push(shot);
    }
  }

  spawnShot(kind, x, y, vx, vy, friendly) {
    this.shots.push(new Shot(kind, x, y, vx, vy, friendly));
  }

  /** Wardens shed sap as they are worn down: there was no healing of any kind
   *  inside a boss room, so an early hit was unrecoverable. */
  bossThreshold(e) {
    const frac = Math.max(0, e.hp) / e.maxHp;
    const step = frac <= 0.33 ? 2 : frac <= 0.66 ? 1 : 0;
    if (step > (e.shed || 0)) {
      e.shed = step;
      const [sx, sy] = this.fitDrop(e.cx - 7, e.cy + 18);   // world pixels (LS-511; the screen's own pixels before, and a missing subtraction once threw this sap at the screen's far corner)
      this.items.push(new Item('sap', sx, sy, false, { tossedAt: this.runFrames }));
    }
  }

  /* REMAINS (LS-519): what a death leaves lying -- the body's own picture, slumped, for three minutes or until it is
   * eaten down. Scavengers come to it (sim/food.ts). Kept in world px, a few dozen at most in the world. */
  remains: any[] = [];
  leaveRemains(e) {
    if (!e || !e.def || e.def.boss || e.def.ghost) return;   // a wisp or a gull leaves nothing on the ground
    this.remains.push({ of: e, type: e.type, dir: e.dir || 'down', x: e.cx, y: e.cy, life: REMAINS_FRAMES, bites: REMAINS_BITES });
    if (this.remains.length > REMAINS_CAP) this.remains.splice(0, this.remains.length - REMAINS_CAP);
  }
  /** BREEDING (LS-524, sim/food.ts): well-fed pairs bear young, where the player is not standing, under a chunk's cap. */
  breed() {
    if (!this.player || this.mode !== 'play') return;
    const now = this.runFrames || 0, byChunk = new Map<string, number>();
    for (const e of this.enemies) if (e && !e.dead) { const k = this.chunkKeyAt(e.cx, e.cy); byChunk.set(k, (byChunk.get(k) || 0) + 1); }
    const born: any[] = [];
    for (const a of this.enemies) {
      if (!a || a.dead || a.def.boss || a.prowler || (a.friendUntil || 0) > now || !DIETS[a.type] || a.hunger === undefined || a.hunger > BREED_FED) continue;
      if (now - (a.bredAt ?? -1e9) < BREED_FRAMES) continue;
      const k = this.chunkKeyAt(a.cx, a.cy), [ksx, ksy] = k.split(',').map(Number);
      if ((byChunk.get(k) || 0) >= Math.min(CHUNK_CAP, this.gladeCrowdCap(ksx, ksy))) continue;   // the glade's ring stays gentle (LS-455): it breeds only up to its own cap
      if (Math.hypot(a.cx - this.player.cx, a.cy - this.player.cy) < 48) continue;
      const mate = this.enemies.find((b: any) => b && b !== a && !b.dead && b.type === a.type && b.hunger !== undefined && b.hunger <= BREED_FED && now - (b.bredAt ?? -1e9) >= BREED_FRAMES && Math.hypot(b.cx - a.cx, b.cy - a.cy) < BREED_REACH);
      if (!mate) continue;
      const rng = new RNG(mix(this.seedNum, Math.floor(now / 60), Math.floor(a.cx), 5240));
      const ang = rng.next() * Math.PI * 2, x = a.x + Math.cos(ang) * 14, y = a.y + Math.sin(ang) * 14;
      if (!this.bodyFitsFor(a, x, y)) continue;
      const young: any = new Enemy(a.type, x, y, new RNG(mix(this.seedNum, now, Math.floor(x), 5241)), {});
      young.home = a.home; young.hunger = 0.5; young.bredAt = now; young.fromBrood = true;
      born.push(young); a.bredAt = now; mate.bredAt = now; byChunk.set(k, (byChunk.get(k) || 0) + 1);
    }
    if (born.length) this.enemies.push(...born);
  }
  /** Would a body of this one's size stand at (x, y)? Its four corners, by its own collision. */
  bodyFitsFor(e: any, x: number, y: number) { return [[0, 0], [e.w - 1, 0], [0, e.h - 1], [e.w - 1, e.h - 1]].every(([ox, oy]) => !this.solid(x + ox, y + oy, false, e)); }
  /* THE FIRST THING LYING IN THE GRASS (LS-435). A fresh run's glade holds a boar's remains a few tiles from where the
   * body wakes -- something to walk to, and the blade in hand glints over it (actTarget 'carve'), which is how a player
   * first meets meat, and through it bait, a fire's cooking and a creature fed from the hand. Ten minutes, not three.
   * Placed by the seed on open ground; never on a reload (remains are not saved). */
  openingRemains() {
    if (!this.world || !this.player || (this.runFrames || 0) > 600 || this.remains.length) return;
    const px = Math.floor(this.player.cx / TS), py = Math.floor(this.player.cy / TS);
    const rng = new RNG(mix(this.seedNum, 4350));
    for (let n = 0; n < 40; n++) {
      const ang = rng.next() * Math.PI * 2, d = 4 + rng.int(3), tx = px + Math.round(Math.cos(ang) * d), ty = py + Math.round(Math.sin(ang) * d);
      const v = this.tileAt(tx, ty); if (v !== T.GROUND && v !== T.MOSS && v !== T.TUFT && v !== T.SAND) continue;
      if (!this.bodyFits(...this.bodyAtTile(tx, ty))) continue;
      this.remains.push({ of: null, type: 'charger', dir: rng.chance(0.5) ? 'left' : 'right', x: tx * TS + 8, y: ty * TS + 8, life: 60 * 600, bites: REMAINS_BITES, opening: true });
      return;
    }
  }
  /** MEAT BY A FIRE COOKS (LS-519): raw meat lying within a tile of a fire or a lit brazier for four seconds is cooked. */
  cookMeat() {
    for (const it of this.items) {
      if (it.dead || it.kind !== 'meat' || !this.systems) continue;
      const tx = Math.floor((it.x + 4) / TS), ty = Math.floor((it.y + 4) / TS);
      let hot = false;
      for (let dy = -1; dy <= 1 && !hot; dy++) for (let dx = -1; dx <= 1 && !hot; dx++) if (this.systems.burning(tx + dx, ty + dy) || this.tileAt(tx + dx, ty + dy) === T.BRAZIER_LIT) hot = true;
      if (!hot) continue;
      (it as any).cook = ((it as any).cook || 0) + 30;
      if ((it as any).cook % 60 === 0) this.puff(it.x + 4, it.y, UI.linen, 2, 'smoke');
      if ((it as any).cook >= 240) { it.kind = 'cookedmeat'; this.puff(it.x + 4, it.y, UI.ember, 4, 'spark'); }
    }
  }
  tickRemains() {
    if (!this.remains.length) return;
    for (const r of this.remains) r.life--;
    this.remains = this.remains.filter((r) => r.life > 0 && r.bites > 0);
  }
  onEnemyKilled(e) {
    this.leaveRemains(e);
    /* its kind remembers what YOU did (LS-523): a death your blade dealt in the last two seconds -- not the dry, the deep, a fire or a hunter */
    if (e && e.def && !e.def.boss && e.huntedAt !== this.runFrames && (this.runFrames || 0) - (e.hitByPlayerAt ?? -1e9) <= 120) nudgeStanding(this.state, e.type, -1);
    /* A HUNTER'S KILL IS NOT YOURS (LS-519): no count, no drops, no beat -- the country fed itself. */
    if (e && e.huntedAt === this.runFrames && !(e.def && e.def.boss)) {
      if (e.type === 'sporeling' && this.systems) { const f = this.systems.fieldsFor(this); f.bump('spores', Math.floor(e.cx / TS), Math.floor(e.cy / TS), 0.7); }
      this.puff(e.cx, e.cy, UI.bone, 4, 'dust');
      return;
    }
    if (e && e.def && e.def.beast) this.state.fellBy = { ...(this.state.fellBy || {}), [e.type]: e.lastCause || 'blow' };   /* which answer it was (2026-09-27) */
    // a hunter of the pack cut down on the glass is one fewer in the pack tomorrow (LS-380)
    if (e && e.ofPack && this.systems && this.systems.packHere(this)) this.systems.pack.size = Math.max(1, this.systems.pack.size - 1);
    this.state.kills++;
    /* JUICE (LS-187): the last body in a fight falls in slow motion for a
     * beat. `slowmo` is read by the frame loop alone (main.ts), so a sim
     * stepping `update()` directly sees the same frames it always did. */
    if (!e.def.boss && this.enemies.every((o) => o === e || o.dead || o.def.ghost || !this.inWindow(o, TS * 2))) { this.slowmo = 24; this.shakeFor(3, 8); }   // the last in the fight: nothing else alive on the glass (LS-511: the screen's list was the fight)
    this.burst(e, !!e.def.boss);
    if (e.def && e.def.beast) { this.slowmo = 48; this.shakeFor(8, 22); for (let i = 0; i < 18; i++) { const an = i / 18 * Math.PI * 2; this.puff(e.cx + Math.cos(an) * 20, e.cy + 6 + Math.sin(an) * 12, UI.linen, 5, 'dust'); } }   // a Great Beast goes down heavily: the world slows, the ground shakes, a ring of dust (2026-09-27)
    this.sfx('kill', e.x + 8);
    /* WHAT A CREATURE WAS MADE OF GOES BACK INTO THE WORLD (LS-242): a sporeling bursts into the spore field where it
     * fell, so killing one in a doorway leaves the doorway dangerous for a while. */
    if (e.type === 'sporeling') {
      const f = this.systems.fieldsFor(this); const tx = Math.floor(e.cx / TS), ty = Math.floor(e.cy / TS);
      f.bump('spores', tx, ty, 0.7);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) f.bump('spores', tx + dx, ty + dy, 0.3);
    }
    if (e.def.beast) {
      /* A GREAT BEAST FALLS (LS-225): the mark on the bar lights, and it
       * leaves a hoard where it lay. */
      const st = this.state;
      if (!st.beastsSlain.includes(e.type)) { st.beastsSlain.push(e.type); S.recordFelled(Game, e.type); st.maxHp = Math.min(MAX_HP_CAP, st.maxHp + BEAST_VIGOR); st.hp = st.maxHp; this.sfx('toll', this.player.x);
        // THE OTHERS GROW (the design lens): each beast still standing takes on more life, so the third fight is not the first again
        this.world.slain = new Set(st.beastsSlain);
        let grew = 0; for (const r of st.beasts || []) if (r.alive && r.kind !== e.type) { r.hp = Math.round(r.hp * BEAST_GROWTH); r.max = Math.round((r.max || ENEMY_DEFS[r.kind].hp) * BEAST_GROWTH); grew++; }   // the record carries its ceiling: a grown beast hurt to 20 reloaded at 20 of 50 (the robustness lens, round 40)
        // ...and a beast STANDING when another falls grows in the body too: tickBeast writes the body's life onto the record every frame, so growing the record alone was undone next frame (the robustness lens, round 39)
        for (const o of this.enemies) if (o !== e && o.def && o.def.beast && !o.dead) { o.hp = Math.round(o.hp * BEAST_GROWTH); o.maxHp = Math.round((o.maxHp || o.def.hp) * BEAST_GROWTH); }
        }   // the others grow and the stones turn toward what stands; neither is announced (LS-373)
      const rec = (st.beasts || []).find((b) => b.kind === e.type); if (rec) rec.alive = false;
      /* WHAT IT LEAVES (LS-335, 2026-09-16): one of its own, every time, beside the hoard -- the best things in the game are made of these. */
      /* ...AND IT IS KEPT THE WAY THE PIECE IS (2026-09-17). This went straight onto `this.items` as an ordinary
       * drop: it timed out in 460 frames and was in no save record, so a Great Beast's own material -- the only
       * source of the tusk pike, the tusk broth, the thorn whip, the shell crown and the heart draught -- was lost
       * for ever to a walk home, a death or a reload. It gets the gear piece's treatment: a hoard key, a line in
       * the saved state, and a body that does not rot. */
      /* ...NO LONGER (2026-09-27): the beast leaves its trophy, which is a verb, and not a material for recipes the forge no longer makes */
      { const own = (globalThis as any).__beastMaterials ? ({ oldshell: 'greatshell', bramblemother: 'brambleheart', sow: 'sowtusk' } as Record<string, string>)[e.type] : undefined;
        if (own) {
          const [mx, my] = this.fitDrop(e.cx - 7, e.cy + 9);
          const key = 'hoard:' + Math.floor(e.cx / PW) + ',' + Math.floor(e.cy / PH) + ':' + own;
          (st.hoard ||= []).push({ kind: own, x: Math.floor(e.cx / PW), y: Math.floor(e.cy / PH), px: mx, py: my, key });
          const it = new Item(own, mx, my, true, { tossedAt: this.runFrames });
          (it as any).loot = key;
          this.items.push(it);
        } }
      /* TEN GLIM AND TWO SAP: the two vigor the comment above promises, at the ten-glim press (the design lens,
       * round 41: five-minute bouts want a purse).
       *
       * ...AND THEY ARE SAVED, WHICH THEY WERE NOT (the design lens, round 52). The hoard was taught to survive a
       * reload earlier the same day and ONLY THE TROPHY LEARNED IT: these twelve went straight onto `this.items`,
       * which `save()` does not write, so a death or a reload after the kill left the piece on the ground and took
       * the purse. Measured: 10 glim and 2 sap before, 0 and 0 after, with the trophy still there -- and that is
       * about thirty of a run's forty glim of spending money, deleted by the death the fight causes. Each carries
       * its own `loot` key, so it comes back where it fell and is retired when it is taken, exactly as the piece
       * and the world's caches are: found once, not farmed. */
      for (let k = 0; k < 12; k++) {
        const [dx2, dy2] = this.fitDrop(e.cx - 7 + ((k * 11) % 31) - 15, e.cy - 7 + ((k * 7) % 23) - 11);
        const kind = k < 10 ? 'glim' : 'sap';
        const key = 'hoard:' + Math.floor(e.cx / PW) + ',' + Math.floor(e.cy / PH) + ':' + kind + ':' + k;
        (st.hoard ||= []).push({ kind, x: Math.floor(e.cx / PW), y: Math.floor(e.cy / PH), px: dx2, py: dy2, key });
        const it = new Item(kind, dx2, dy2, false, { tossedAt: this.runFrames });
        (it as any).loot = key;
        this.items.push(it);
      }
      /* ...AND WHAT IT WAS MADE OF (LS-239): the sow its hide, the shell its plate, the mother her mantle.
       *
       * THE HOARD USED TO DIE WITH A RELOAD (the robustness lens, round 51). This pushed the piece onto
       * `this.items` and called `save()` on the next line -- and `save()` writes no ground items at all, so the
       * piece existed only in memory while the KILL was recorded in `state.beastsSlain` for ever. Reload, or die
       * (retry() reloads the checkpoint), and the beast never came back and its gear was nowhere: one of only five
       * pieces, and the sole source of three of them, deleted by dying to a sporeling on the walk home. Proved by
       * felling the sow, saving, loading and returning: the hide was gone and `beastsSlain` still held 'sow'.
       *
       * The world's caches had solved this already and the hoard never borrowed it. A piece is recorded in the
       * SAVED state and re-dropped where it fell until it is picked up, with the same `loot` key that retires a
       * cache -- so it cannot be farmed by reloading, which a bare re-drop would allow. */
      { const piece = GEAR_OF[e.type]; if (piece) {
        const [gx2, gy2] = this.fitDrop(e.cx - 7 + 18, e.cy - 7 - 14);
        const key = 'hoard:' + Math.floor(e.cx / PW) + ',' + Math.floor(e.cy / PH) + ':' + piece;
        (st.hoard ||= []).push({ kind: piece, x: Math.floor(e.cx / PW), y: Math.floor(e.cy / PH), px: gx2, py: gy2, key });
        this.items.push(new Item(piece, gx2, gy2, true, { tossedAt: this.runFrames }));
        this.items[this.items.length - 1].loot = key;
      } }
      
      this.save();
      /* THE WIN IS NOT TAKEN AWAY BEFORE YOU CAN SEE IT (LS-304). The third beast fell and the win screen replaced
       * the world in the same frame, so everything the run changed -- a quiet wilds, stones with nothing left to
       * point at, ground you can now cross -- was DESCRIBED on a black screen instead of being walked through. The
       * win screen even says "the stones have nothing left to point at", which was a claim the player had no way
       * to check. Now the third kill opens an aftermath: the world stays, the wilds are quiet because the beasts
       * that made them dangerous are dead, and the run ends when you go and touch a stone -- which is the moment
       * that line becomes something you can see rather than something you are told.
       *
       * Bank the objective here, but keep the world resumable. Completing a
       * beast hunt must not discard the player's sandbox (LS-368). */
      if (this.beastLairs().every((l) => st.beastsSlain.includes(l.kind))) {
        if (!this.aftermath) {
          this.aftermath = Math.max(1, this.runFrames);
          this.recordWin('quieted');   // the wilds quieted: the one thing that is still a win
        }
        this.save();
        return;
      }   // won against the lairs, not a count: three strange names in a save plus one kill was a win (the robustness lens, 2026-09-05)   // the quest (LS-225): three beasts, and the wilds are quiet
    }
    if (e.def.boss) { this.shakeFor(6, 24); this.hitstopFor(12); return; }
    const rng = new RNG(mix(this.seedNum, this.runFrames, Math.floor(e.x), Math.floor(e.y)));
    const roll = rng.next();
    // An enemy can die against a wall — a wisp crosses water and thorns — so
    // the spot it fell on is not necessarily a spot the player can reach.
    // fitDrop takes the screen's own px and returns world px: fed world px it
    // added the origin twice and a drop landed a screen away (1 of 219 unreachable)
    const [dx2, dy2] = this.fitDrop(e.cx - 7, e.cy - 7);
    const gb = (this.state.glim < 10 ? 0.85 : 0.60) + (wearing(this.state, 'bell') ? 0.10 : 0);   // the wisp bell (LS-239): glim a tenth more often   // a glim nearly every kill while the bag is under ten (the design lens, round 45: five-minute bots ended two short of the first press)
    /* THE THREE BANDS SHARE ONE ROLL, SO THE LAST TWO SHARE WHAT GLIM LEAVES (2026-09-18). The bands were added on
     * top of each other -- glim below `gb`, sap below `gb + 0.15`, a pod below `gb + 0.23` -- and with a purse
     * under ten `gb` is 0.85, so sap took every roll that was left and THE POD BAND SAT ABOVE 1.0: no kill dropped
     * a pod until the player was holding ten glim, which is exactly the round-40 fault ("glim's 0.45 had swallowed
     * sap's 0.40 and no sap dropped in two hundred kills") in the next band along. The remainder above glim's own
     * rate is split in the same fifteen-to-eight ratio the numbers were written in, so both stay reachable
     * whatever glim's band is. */
    const left = Math.max(0, 1 - gb), sapTo = gb + left * (0.15 / 0.23);
    if (roll < gb) this.items.push(new Item('glim', dx2, dy2, false, { tossedAt: this.runFrames }));   // 0.60 (round 42): five-minute bots held four to nine against the ten-glim press   // often enough to press ten (the design lens, round 39: a five-minute bot held four to six)
    else if (roll < sapTo) this.items.push(new Item('sap', dx2, dy2, false, { tossedAt: this.runFrames }));   // the bands moved with glim's (round 40: glim's 0.45 had swallowed sap's 0.40 and no sap dropped in two hundred kills)
    else if (this.state.rattle || this.state.hasPods) this.items.push(new Item('pod', dx2, dy2, false, { tossedAt: this.runFrames }));   // THE RATTLE, NOT THE POUCH (2026-09-18): read as the bag's count, both sources of pods switched off at the moment a pod was needed -- an emptied pouch could never refill
    /* WHAT THE KIND LEAVES (LS-335, 2026-09-16): a second, independent roll for the creature's own material, so the
     * glim and sap bands above are untouched. The odds are per kind: what a thing is made of comes off it often. */
    if ((globalThis as any).__creatureMaterials) { const own = ({ tidecrab: ['shell', 0.5], shellback: ['shell', 0.35], screehopper: ['chitin', 0.6], sunbasker: ['scale', 0.6], mossback: ['moss', 0.7], sporeling: ['spore', 0.3], charger: ['horn', 0.5], wisp: ['mote', 0.4], gloam: ['gloamink', 0.5], cinder: ['cinderash', 0.6], eel: ['eelfin', 0.5] } as Record<string, [string, number]>)[e.type];
      if (own && rng.next() < own[1]) { const [mx, my] = this.fitDrop(e.cx - 3, e.cy + 5); this.items.push(new Item(own[0], mx, my, false, { tossedAt: this.runFrames })); } }   // creature materials drop only under __creatureMaterials since the clutter cut (2026-09-26)
    /* ...AND SINEW COMES OFF WHAT RUNS AND CLIMBS (2026-09-18). Four worn pieces are forged from sinew -- the hopper
     * greaves, the shell mail, the fin charm and the shell crown -- and the forge is the ONLY way to any of them: no
     * cache row, no beast leaves them. Sinew itself had a single banded cache and no other source in the game, and it
     * is an optional kind, of which a world places 33 of 44: eleven worlds in thirty held no sinew at all, so on those
     * seeds four of the game's worn pieces did not exist. The cord that pulls a leg is off the things whose legs do
     * the work, on its own roll, so the material bands above are untouched. */
    if ((globalThis as any).__creatureMaterials) { const sinewy: Record<string, number> = { charger: 0.35, screehopper: 0.3, mossback: 0.25 };
      const q = sinewy[e.type];
      if (q && rng.next() < q) { const [mx, my] = this.fitDrop(e.cx + 4, e.cy + 5); this.items.push(new Item('sinew', mx, my, false, { tossedAt: this.runFrames })); } }
  }

  canAfford(item) { return this.state.glim >= item.price; }
  /** Whether buying this would do nothing, so the shop can refuse rather than
   *  take the glim. The caps live at the purchase, not only at the loader —
   *  a cap only the loader knows about is a refund the player never gets. */
  /** Would buying this change anything? Two of the ten shop kinds answered
   *  honestly and the other six took the glim and did nothing — worst case 30
   *  for a Glimstone at both caps. A cap the player can hit is a rule; a cap
   *  that quietly eats the price is a pickpocket. */
  atCap(kind) {
    const st = this.state;
    // it gives both, so it is only worthless when both are full
    if (kind === 'sap') return st.hp >= st.maxHp;
    if (kind === 'pods' || kind === 'pod') return st.pods >= st.maxPods;
    return false;
  }

  /** The one thing wrong with this run that the player has to be told, or
   *  null. One function, because two places need the answer: the report
   *  itself, and the place name it would otherwise be drawn on top of. */
  /** A fault worth interrupting the HUD for — and it does not interrupt it
   *  forever. `saveQuarantined` is set once at boot and cleared only by
   *  `reset()`, and the place name yields to whatever this returns: measured,
   *  2,480 of 2,480 play frames across two areas never showed the name of
   *  where the player was. A report that a run has been set aside is urgent
   *  for about ten seconds; after that it is a permanently occupied line.
   *  The two live faults — storage refusing writes, and another tab owning
   *  the seed — are conditions, not events, and keep their place. */
  faultLine() {
    if (this.storageFailed) return ['not saving', UI.danger];
    // Say it. A run that vanished without a word is the same as a run the
    // game threw away on purpose, from where the player is sitting.
    if (this.saveQuarantined && this.runFrames < 600) return ['save set aside', UI.gold];
    // Another tab is further along on this seed. Refusing to write is the
    // only thing standing between two windows and a destroyed run.
    if (this.otherTab) return ['other tab owns this', UI.danger];
    return null;
  }

  /** What the shopkeeper says when there is nothing in it for you. */
  capLine(kind) {
    return {
      sap: 'no wound to close', pods: 'you carry all you can',
      pod: 'you carry all you can', chart: 'the chart tells you nothing new',
    }[kind] || 'it would do nothing';
  }

  /** The next hotbar slot (LS-225). */
  /* A TAP IN THE GAME'S PIXELS (mobile): the hotbar picks a slot; in the bag
   * a slot lifts or places, the box under the grid marked 'drop' puts the
   * held stack on the ground, and 'close' shuts it. Geometry shared with
   * hud.ts (drawHotbar at 62,3; drawBag's grid and its two boxes). */
  tapAt(gx, gy) {
    const b = bagOf(this.state);
    if (this.bagOpen) {
      // the nearest slot centre, inside the cell plus a three-pixel halo (LS-229: a 21 CSS px target needs every pixel it can get)
      for (const [slot, bx, by] of loadoutBoxes(!!this.touch)) {   // a tap on a worn piece takes it off into the bag (LS-239)
        // 44 CSS px whatever the glass, and never up into the strip that closes the bag (the mobile lens, round 48)
        const halo = Math.max(11, Math.ceil(22 / (this.cssScale || 1.5)));
        if (Math.abs(gx - (bx + 7)) > halo || (this.touch && gy < HUD_H + 25) || Math.abs(gy - (by + 7)) > halo) continue;   // the strip guard is a thumb's, not a pointer's (2026-09-17): on a desk it swallowed the hand box, and the click fell through to 'close the bag'
        /* THE HAND BOX IS WHERE A BLADE IS LOOKED AFTER (LS-374). It used to be
         * skipped here -- there was nothing to do to it, because the tool
         * button whetted whatever was picked. The one action button swings
         * instead, so this is the home for the whet and the mend: tap the
         * picture of the thing in your hand. */
        if (slot === 'hand') { this.lookAfterHeld(); return true; }
        const worn = gearOf(this.state)[slot];
        /* AN EMPTY BOX ANSWERS (2026-09-18). With a piece lifted out of the bag, a tap on the empty box it belongs in
         * did nothing at all -- no wear, no refusal, no sound -- while the box is drawn exactly as the filled ones
         * are and the tap on a FILLED one takes its piece off. Tapping the right empty box wears what is lifted,
         * which is what `wear()` would do with nothing there; any other tap says no. */
        if (!worn) {
          const lifted = bagOf(this.state).lifted;
          const fits = lifted && ITEMS[lifted.kind] && ITEMS[lifted.kind].gear && ITEMS[lifted.kind].gear.slot === slot;
          if (fits) { gearOf(this.state)[slot] = lifted; bagOf(this.state).lifted = null; syncFlags(this.state); this.sfx('bag'); this.save(); }
          else this.sfx('refuse');
          return true;
        }
        if (takeOff(this.state, slot)) { this.sfx('bag'); this.save(); } else this.sfx('refuse');
        return true;
      }
      const P = bagPitch(!!this.touch, this.cssScale, b.cursor), cols = P.cols, x0 = P.x0, y0 = P.y0;
      const col = Math.round((gx - x0 - P.px / 2) / P.px), row = Math.round((gy - y0 - P.py / 2) / P.py);
      const cx = x0 + col * P.px + P.px / 2, cy = y0 + row * P.py + P.py / 2;
      const halo = this.touch ? 0 : 3;
      if (col >= 0 && col < cols && row >= 0 && row < P.rows && P.offset + row * cols + col < BAG_SLOTS && Math.abs(gx - cx) <= P.px / 2 + halo && Math.abs(gy - cy) <= P.py / 2 + halo) {
        b.cursor = P.offset + row * cols + col; liftOrDrop(this.state); this.sfx('tick'); return true;
      }
      for (const button of bagButtons(!!this.touch, this.cssScale)) {
        const hx = this.touch ? 0 : 4, hy = this.touch ? 0 : 3;
        if (gx < button.x - hx || gx >= button.x + button.w + hx || gy < button.y - hy || gy >= button.y + button.h + hy) continue;
        if (button.action === 'drop') { const held = takeOutHeld(this.state); if (held) { this.dropStack(held); this.sfx('drop'); } }
        else if (button.action === 'close') this.toggleBag();
        else {
          const page = Math.max(0, Math.min(P.pages - 1, P.page + (button.action === 'next' ? 1 : -1)));
          b.cursor = Math.min(BAG_SLOTS - 1, page * P.count + b.cursor % P.count);
          this.sfx(page === P.page ? 'refuse' : 'tick');
        }
        return true;
      }
      if (gy < HUD_H + 24) { if (this.paused) return false; this.toggleBag(); return true; }   // not under the open map (2026-09-19): the tap falls through and closes the map instead
      return false;
    }
    if (gx >= HOTBAR_X - 2 && gx < HOTBAR_X + HOTBAR * HOTBAR_PITCH) {
      if (gy >= 42 && gy < 59) {
        if (this.paused) return false;   // not under the open map (2026-09-19): the tap falls through and closes the map instead
        this.toggleBag(); bagOf(this.state).cursor = HOTBAR + Math.max(0, Math.min(HOTBAR - 1, Math.floor((gx - HOTBAR_X) / HOTBAR_PITCH))); return true;
      }
      if (gy >= (this.touch ? 0 : HOTBAR_Y - 2) && gy < 40) {
        const was = bagSelected(this.state);
        this.selectSlot(Math.max(0, Math.min(HOTBAR - 1, Math.floor((gx - HOTBAR_X) / HOTBAR_PITCH))));
        if (bagSelected(this.state) === was) this.sfx('tick'); return true;
      }
    }
    return false;
  }

  /* ...AND IT SAYS WHETHER ANYTHING CHANGED (the mobile lens, round 52). It ended in an unconditional `return
   * true`, so with every slot empty the `next` button answered with its accept fill, its tick and a buzz and
   * changed nothing -- against its own docstring, which promises it refuses out loud when there is nothing to swap
   * to. It already knew: `was` is computed on the first line and compared on the second. A control that reports
   * success for a no-op teaches a player their tap did not land. */
  cycleWeapon(dir = 1) { const was = bagSelected(this.state); cycleSelected(this.state, dir); if (bagSelected(this.state) !== was) { this.sfx('wield'); this.wieldFlash(); } return bagSelected(this.state) !== was; }   // it names nothing now (LS-373)
  /* WHAT STANDS UP THROWS ITS OWN SHAPE (2026-09-05, against the reference renders, whose note came back three times:
   * "soft cast shadows from the cliffs onto the ground"). Painted per-tile as bands, a shadow ruled straight lines
   * along the tile grid and read as a hem. This is the MASS's own silhouette -- every tile that stands up -- offset
   * down and to the right, with the casters themselves punched back out, so the shape on the ground is the shape of
   * the thing above it. One canvas a screen, blitted at a third. */
  drawCastShadows(g, parts) {
    if (!parts || !parts.tiles || !this.area) return;
    /* CACHED ON THE GAME, NOT ON THE PARTS. The parts object a frame hands us is not always the same one (a re-bake
     * makes a new one), so keying the picture off it rebuilt two canvases and several hundred fills every frame --
     * 1.5 ms of a 6 ms budget, measured. It is keyed by the screen, and thrown away when that screen is re-baked.
     *
     * ...BY THE SCREEN BEING DRAWN, NOT THE ONE THE PLAYER STANDS ON (2026-09-06). Vijay, from play: "baked lighting
     * shadows changing as i move around". `drawWorld` runs once for EVERY screen in the window -- the camera scrolls
     * and never aligns, so there are four -- and this keyed the picture on `this.area`. So the first screen drawn
     * baked its shadows, stored them under the PLAYER'S key, and every other screen on the glass was handed the same
     * picture; cross a seam and `this.area` changed, a different picture was baked, and the shadows under every tree
     * in the window changed at once. The bake was always right -- it reads the `parts` it is given -- and only the
     * key was wrong, which is the same fault the sun's wash had two commits ago and in the same place: a per-screen
     * picture identified by where the player happens to be. `parts.coords` is the screen being drawn; the player's
     * own screen is the one call that passes none. */
    const lkey = parts.coords ? parts.coords.x + ',' + parts.coords.y : this.area.x + ',' + this.area.y;
    let art = (this.lightArt ||= new Map()).get(lkey);
    if (art === undefined) {
      const c = document.createElement('canvas'); c.width = COLS * TS; c.height = ROWS * TS;
      const cg = c.getContext('2d'); let any = false;
      /* THE EDGE IS A DITHERED PENUMBRA. A shadow that stops on a hard line reads as paint; the outer four pixels are
       * laid on a checker and the four beyond that on one pixel in four, which at this scale is a soft edge and is
       * still made of whole palette pixels. Two little patterns, painted under the solid core. */
      const dot = (n: number) => { const q = document.createElement('canvas'); q.width = q.height = n; const qg = q.getContext('2d'); qg.fillStyle = '#8478a8'; qg.fillRect(0, 0, 1, 1); return cg.createPattern(q, 'repeat'); };
      const half = dot(2), quarter = dot(3);
      /* THE SHADOW IS THE CLIFF'S OWN SILHOUETTE. A rectangle a tile is a rectangle on the ground, and a cliff is not
       * rectangular: its painted mass -- the layer the frame has already cached -- is taken as a stencil, tinted, and
       * laid down offset. Everything else that stands (a hedge, a nest) keeps a small inset shape of its own. */
      const cliffs = (this.layerCache && this.layerCache.get(lkey) && this.layerCache.get(lkey).canvases.cliffs) || null;
      if (cliffs) {
        const sil = document.createElement('canvas'); sil.width = COLS * TS; sil.height = ROWS * TS;
        const sg = sil.getContext('2d');
        sg.drawImage(cliffs, 0, 0);
        sg.globalCompositeOperation = 'source-in'; sg.fillStyle = '#6d6192'; sg.fillRect(0, 0, sil.width, sil.height);   // a tone to multiply by: the ground keeps its texture and goes into shade
        /* SMEARED, SO THE EDGE IS SOFT. The same silhouette three times, each a little further along the light's line
         * and fainter: the core where the cliff blocks the sun outright, then two steps of penumbra. A shadow with one
         * hard edge is the note the judge kept returning; this is as soft as whole pixels get. */
        cg.globalAlpha = 0.45; cg.drawImage(sil, 9, 12);
        cg.globalAlpha = 0.7; cg.drawImage(sil, 7, 10);
        cg.globalAlpha = 1; cg.drawImage(sil, 5, 7); cg.globalAlpha = 1; any = true;
      }
      const inset = (k) => CAST_INSET[k] ?? 4;
      for (const [grow, style] of [[6, quarter], [3, half]] as Array<[number, any]>) {
        cg.fillStyle = style;
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) { const k = parts.tiles[y][x]; if (!CASTS.has(k) || k === T.CLIFF) continue; const n = inset(k);
          cg.fillRect(x * TS + 5 + n - grow, y * TS + 7 + n - grow, TS - n * 2 + grow * 2, TS - n * 2 + grow * 2); }
      }
      cg.fillStyle = '#4a3c6e';
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) { const k = parts.tiles[y][x]; if (!CASTS.has(k) || k === T.CLIFF) continue; const n = inset(k);
        cg.fillRect(x * TS + 5 + n, y * TS + 7 + n, TS - n * 2, TS - n * 2); any = true; }
      // the occlusion line: right under the overhang, where no light of any kind reaches
      // the occlusion under an overhang comes from the smeared silhouette above; a dashed line of its own read as litter on the ground
      cg.globalCompositeOperation = 'destination-out';
      // punched back out with a ragged edge: a clean rect here would put the shadow's own boundary on the tile lattice
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) if (CASTS.has(parts.tiles[y][x])) {
        for (let i = 0; i < TS; i++) {
          const j = (((x * TS + i) * 7 + y * 13) & 3) === 0 ? 1 : 0;
          cg.fillRect(x * TS + i, y * TS - j, 1, TS + j * 2);
        }
      }
      /* AND THE SAME PICTURE CARRIES THE LINES OF LIGHT: the lit lip on every sunward edge, the dark brink where a
       * mass drops away, and the damp band where the water reaches the land. None of them animates, and drawn per
       * tile per frame they cost a quarter of the frame's budget; baked here they cost one blit. Every one of them is
       * frayed, because a line along a tile's edge is a line on the lattice. */
      const trim = document.createElement('canvas'); trim.width = COLS * TS; trim.height = ROWS * TS;
      const tg = trim.getContext('2d');
      const kindAt = (nx, ny) => (parts.tiles[ny] || [])[nx];
      const flat = (nx, ny) => { const v = kindAt(nx, ny); return v !== undefined && SHADOW_ON.has(v); };
      const wetAt = (nx, ny) => { const v = kindAt(nx, ny); return v === T.WATER || v === T.SEA || v === T.SHALLOW; };
      const dash = (bx, by, horiz, len, col) => { tg.fillStyle = col; for (let i = 0; i < len; i++) { const wx2 = horiz ? bx + i : bx, wy2 = horiz ? by : by + i; if (((wx2 * 7 + wy2 * 13 + (wx2 ^ wy2)) % 3) === 0) continue; tg.fillRect(horiz ? bx + i : bx, horiz ? by : by + i, 1, 1); } };
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
        const k = parts.tiles[y][x], X = x * TS, Y = y * TS;
        /* NO LINE OF LIGHT ALONG A TILE'S EDGE. A lit lip and a dark brink on the sunward and falling sides read well
         * in a still, and they are a grid: the straight-edge finder counted 220 more pixels on the sixteen-pixel
         * lattice with them in, which is the defect this game has spent four rounds removing. The cliff painter draws
         * its own brink from the mass's shape, where it belongs, and the shadow below is what says "this stands up".
         * Only the sunward CORNER is marked -- four pixels, off the run of the edge. */
        if (CASTS.has(k)) {
          if (flat(x, y - 1) && flat(x - 1, y)) { dash(X, Y, true, 4, 'rgba(255,228,150,0.6)'); dash(X, Y + 1, false, 3, 'rgba(255,228,150,0.6)'); any = true; }
        }
        if (SHADOW_ON.has(k)) {
          const damp = 'rgba(46,58,74,0.34)', glint = 'rgba(226,244,255,0.5)';
          /* THE DAMP WANDERS. Painted at a fixed row with one pixel in three missing, a run of shore tiles still put
           * two thirds of its damp on ONE row across the whole run -- a teal rule along the tile lattice. Each tile
           * takes its own offset from its own hash, and the skips come in runs of two and three rather than singly. */
          const band = (bx, by, horiz) => {
            tg.fillStyle = damp;
            const jog = ((x * 7 + y * 13) & 3) - 1;                      // this tile's own row, a pixel either way
            for (let i = 0; i < TS; i++) {
              const wx2 = horiz ? bx + i : bx, wy2 = horiz ? by : by + i;
              if ((((wx2 * 11 + wy2 * 5) >> 1) % 3) === 0) continue;      // gone in twos, not in ones
              const deep = 1 + (((wx2 * 7 + wy2 * 3) & 3) === 0 ? 2 : 1);
              if (horiz) tg.fillRect(bx + i, by + jog, 1, deep); else tg.fillRect(bx + jog, by + i, deep, 1);
            }
          };
          if (wetAt(x, y - 1)) { band(X, Y, true); tg.fillStyle = glint; tg.fillRect(X + ((x * 5) % 11), Y + 1, 1, 1); any = true; }
          if (wetAt(x, y + 1)) { band(X, Y + TS - 2, true); tg.fillStyle = glint; tg.fillRect(X + ((x * 7) % 13), Y + TS - 2, 1, 1); any = true; }
          if (wetAt(x - 1, y)) { band(X, Y, false); tg.fillStyle = glint; tg.fillRect(X + 1, Y + ((y * 5) % 11), 1, 1); any = true; }
          if (wetAt(x + 1, y)) { band(X + TS - 2, Y, false); tg.fillStyle = glint; tg.fillRect(X + TS - 2, Y + ((y * 7) % 13), 1, 1); any = true; }
        }
      }
      /* ONE PICTURE, ONE BLIT. The shadow at its own weight and the lines of light over it are composited here, into
       * a single canvas with the alpha already in its pixels: two translucent full-screen blits a frame cost 1.5 ms
       * of a 6 ms budget, and this is a picture that never changes while you stand on the screen. */
      /* THE SHADOW MULTIPLIES, THE LIGHT DOES NOT. Laid over the ground as a flat violet it washed the sand's own
       * texture out inside it, which is what an art director means by "opaque". Multiplied, the ground keeps every
       * speck it had and simply stands in shade -- so the two are kept apart: the shade is multiplied, the lit lines
       * and the damp bands are drawn over. */
      art = any ? { shade: c, trim } : null;
      this.lightArt.set(lkey, art);
      if (this.lightArt.size > 40) this.lightArt.delete(this.lightArt.keys().next().value);
    }
    if (!art) return;
    g.save(); g.globalCompositeOperation = 'multiply'; g.drawImage(art.shade, 0, 0); g.restore();
    g.drawImage(art.trim, 0, 0);
    this.layers.push('cast');
  }
  /** FROST (LS-240): where the ground is under the frost line a pale rime lies on the tile, a few specks and more the
   *  colder it is, deterministic per tile so it does not flicker. Cheap: a cold screen is a few hundred rects. */
  /* RIME BELONGS TO THE GROUND IT IS ON (LS-240, and round 54: found by codex
   * reading for this shape after Vijay reported "the lighting is still just
   * suddenly changing when i move across different sections of the map").
   *
   * This took `fieldsFor(this)` -- the PLAYER's screen's temperatures -- and
   * painted them onto whichever screen was being drawn, at that screen's own
   * tile coordinates. Two screens are on the glass at once, so the far one wore
   * the near one's frost; and the moment `this.area` changed, the pale rime
   * across the WHOLE WINDOW was replaced by the new screen's pattern. A sudden,
   * whole-picture change in pale paint, triggered by walking over a line.
   *
   * MEASURED: one screen at temperature 0.0 and its neighbour at 0.9, drawing
   * the same screen both times and changing only which one the player stood on
   * -- 1,536 frost pixels against 0.
   *
   * This is the third instance of one fault: the cast shadows were keyed on the
   * player's screen (round 51) and the sun's wash was nailed to the window
   * (round 49). `parts.coords` is the screen being drawn; the player's own
   * screen is the one call that passes none. */
  drawFrost(g, parts?) {
    if (!this.systems || !this.area) return;
    /* ...THROUGH `areaKey`, which is 'W|x,y' and not 'x,y'. Building the key by
     * hand here looked right and silently matched nothing, so the rime stopped
     * being drawn at all -- caught by this fix's own check within the minute,
     * because it asks the freezing screen to actually HAVE frost on it. */
    /* one world field (LS-511): the chunk being drawn reads its own tiles of it; ground the tick has not reached is at its base */
    const c = parts && parts.coords ? parts.coords : this.area, ox = c.x * COLS, oy = c.y * ROWS;
    const f = this.systems.fieldsFor(this);
    if (!f || !f.temp) return;
    g.fillStyle = 'rgba(232,240,255,0.55)';
    for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
      const t = f.tempAt(ox + tx, oy + ty); if (t >= FROST_LINE) continue;
      const k = Math.min(1, (FROST_LINE - t) / 0.1); const n = 2 + Math.round(k * 6);
      for (let i = 0; i < n; i++) { const h = (tx * 73 + ty * 151 + i * 37) & 255; g.fillRect(tx * TS + (h & 15), ty * TS + ((h >> 4) & 15), 1, 1); }
    }
  }
  /** The bosses' bars, drawn after the canopy so no crown covers them (the art lens, round 47). */
  drawBossBars(g) { this.barsAfterCanopy = true; for (const e of this.enemies) if (e && !e.dead && e.def && e.def.boss && e.drawBar) e.drawBar(g, this); }
  /** A Great Beast counts as awake while it winds, charges, lies stunned -- or for fifteen seconds after its last wind-up or blow (the design lens, round 47: resting between charges it read as asleep, and the parked pack walked in). */
  beastRoused(b) { return b.state !== 'idle' || b.stun > 0 || (b.roused !== undefined && this.runFrames - b.roused < 900); }
  /** Picking a weapon raises it (LS-238): eighteen frames of the charge pose and a spark at the hand. */
  wieldFlash() { const s = bagSelected(this.state); if (!(s && ITEMS[s.kind] && ITEMS[s.kind].weapon) || !this.player) return; this.player.wieldShow = 18; this.puff(this.player.cx, this.player.cy - 6, UI.gold, 5, 'spark'); }
  selectSlot(i) {
    const was = bagSelected(this.state); selectSlot(this.state, i);
    const s = bagSelected(this.state);
    if (s && s !== was) { this.sfx('wield'); this.wieldFlash(); } }   // a hand closing on a haft, not a thing dropped (the art lens)
  /** The bag screen (LS-225): E opens it, arrows move, Z lifts and drops, X drops on the ground. */
  toggleBag() { this.bagOpen = !this.bagOpen; this.sfx('bag'); if (this.player) this.player.buffered = false;   /* a press kept through the bag fired the instant it closed */ if (!this.bagOpen) { const b = bagOf(this.state); if (b.lifted) { /* WITH ITS WEAR (LS-286). Both of these dropped `dur`, and `addItem` reads an omitted durability as a NEW one -- so lifting a sickle worn down to one blow and shutting the bag handed it back at 110. A free repair, by the one gesture nobody would think to check, next to a whetstone that charges glim for the same thing. */ const left = addItem(this.state, b.lifted.kind, b.lifted.n, b.lifted.dur); if (left) this.dropStack({ kind: b.lifted.kind, n: left, dur: b.lifted.dur }); b.lifted = null; } } this.sfx('tick'); }
  bagKey(code) {
    if (!this.bagOpen) return false;
    const cols = bagPitch(!!this.touch, this.cssScale).cols;
    if (code === 'ArrowLeft') moveCursor(this.state, -1, 0, cols); else if (code === 'ArrowRight') moveCursor(this.state, 1, 0, cols);
    else if (code === 'ArrowUp') moveCursor(this.state, 0, -1, cols); else if (code === 'ArrowDown') moveCursor(this.state, 0, 1, cols);
    /* THE BAG'S KEYS ARE THE PLAYER'S KEYS (2026-09-17). These were the literal codes 'KeyZ', 'KeyX' and 'KeyE', so a
     * player who had moved swing, use or bag in the settings panel found the bag screen deaf to their own keys --
     * and worse, the press fell through to the world chain, so the rebound swing key swung the weapon with the bag
     * up while the abandoned default key went on lifting stacks. `keyBinding` is handed over by main.ts. */
    const is = (action: string, fallback: string) => (this.keyBinding ? this.keyBinding(code, action) : code === fallback);
    if (is('swing', 'KeyZ')) { liftOrDrop(this.state); this.sfx('tick'); }
    else if (is('use', 'KeyX')) { const st = takeOut(this.state); if (st) this.dropStack(st); else { const b = bagOf(this.state); const slot = GEAR_SLOTS.find((k) => gearOf(this.state)[k]); if (slot && takeOff(this.state, slot, b.cursor)) this.sfx('bag'); } }   // X on an empty slot takes off what is worn, body first, into that slot (LS-239)
    else if (is('bag', 'KeyE') || code === 'Escape') this.toggleBag();   // Escape closes it whatever the bag key is
    else return false;
    this.save();
    return true;
  }
  /** A stack put down at the player's feet, as an item on the ground. */
  dropStack(st) {
    const p = this.player;
    /* `dropped` MARKS IT AS THE PLAYER'S, which is what makes it worth saving.
     * The world's own loot has its own bookkeeping -- a hoard key retired in
     * `lootTaken` -- and writing that back out of the save would resurrect
     * things the player already took. Only what a player put down by hand is
     * restored (LS-284). */
    /* ...AND IT IS NOT SNATCHED BACK ON THE NEXT FRAME (2026-09-18). `Item.update` collects anything the body
     * overlaps, and a dropped stack lands under the body's feet, so the bag's DROP box put a thing down and took it
     * straight back: measured, a full bag could not be emptied at all -- 27 slots used, drop a scale, 27 slots used
     * again two frames later, and no room for the axe lying two tiles away. Half a second of grace, which is long
     * enough to step off it and short enough that nothing else notices. */
    for (let k = 0; k < st.n; k++) { const it = new Item(st.kind, p.x + ((k * 7) % 21) - 7, p.y + (k % 3) * 3 - 3, true, { dropped: true, noPick: 30, tossedAt: this.runFrames });   /* under the body, where it can stand (2026-09-19): ten px below put the second and third pieces in the wall at its back */ if (st.dur !== undefined) it.dur = st.dur; this.items.push(it); }   // a weapon keeps its wear across the ground (LS-238)
    this.sfx('drop');
  }
  collect(item) {
    if (ITEMS[item.kind] && !item.price) {   // an item on the ground goes into the bag, if it fits (LS-225)
      const slots = bagOf(this.state).slots;
      const counts = slots.map(s => s?.n || 0);
      const left = addItem(this.state, item.kind, item.bulk || 1, item.dur);   // a weapon keeps its wear across the ground (LS-238)
      // Highlight only stacks that received something, including a partial pickup.
      slots.forEach((s, i) => { if (s && s.n > counts[i]) this.pickupUntil.set(s, this.runFrames + 120); });
      /* WHAT FITTED IS GONE FROM THE PILE (LS-287). `addItem` has ALREADY put
       * in as much as it could and returns what would not go, and this threw
       * that number away -- so a cache the bag could only partly take stayed
       * at its full count for ever. Measured on an eight-pod cache with one
       * slot free: ten pods spent, the bag still held fifteen, the cache still
       * held eight, and its loot key was never retired. An endless supply of
       * the one thing that heals you, for anyone who walks up with a full bag. */
      if (left) {
        // a cache partly taken is retired, and the rest becomes the player's own drop -- saved, and never back at its full count after a reload (2026-09-19)
        if (item.loot) { if (!this.state.lootTaken) this.state.lootTaken = []; if (!this.state.lootTaken.includes(item.loot)) this.state.lootTaken.push(item.loot); this.dropStack({ kind: item.kind, n: left }); return true; }
        item.bulk = left; return false;   // the rest of it stays where it lies
      }
      /* A THING PICKED UP SAYS WHAT IT IS, and a piece of gear says what to do with it (the design lens, round 50:
       * ten of fourteen pickable kinds were taken in silence, the five loadout pieces among them, so a player could
       * carry a sow hide for a whole run without learning it goes on). A weapon names itself; a piece names itself
       * and the key that wears it. */
      /* AND NOW IT NAMES NOTHING (2026-09-10, LS-373). A weapon named itself
       * and a worn piece named itself and the key that wears it, because ten
       * of fourteen pickable kinds were taken in silence. Every one of them is
       * taken in silence now: what a thing is, is what it looks like in the
       * hand and in the bag, and the pickup has its own sound by kind. */
      if (item.loot) { if (!this.state.lootTaken) this.state.lootTaken = []; if (!this.state.lootTaken.includes(item.loot)) this.state.lootTaken.push(item.loot); }
      if (item.kind === 'pod') this.state.rattle = true;   // the Rattle is yours from the first pod (2026-09-19): the cache path never said so, and an emptied pouch never refilled
      this.sfx(ITEMS[item.kind].weapon ? 'wield' : item.kind === 'sap' ? 'pickup_sap' : 'pickup'); this.save();   // a pickup by kind: a glim chimes, sap pops low, a weapon is a hand on a haft (the art lens, round 41)
      return true;
    }
    const st = this.state;
    // REFUSED BEFORE IT IS CHARGED. The cap used to be enforced inside the
    // switch, which runs after the glim comes out of the pouch — so a
    // seventh Long Haft cost 34 and did nothing.
    if (item.price && this.atCap(item.kind)) {
      
      return false;
    }
    if (item.price) {
      removeItem(st, 'glim', Math.max(1, Math.round(item.price * (wearing(st, 'miser') ? 0.8 : 1))));   // out of the bag (LS-225); a fifth off with the miser charm (LS-335)
      this.sfx('buy');
    } else {
      this.sfx('pickup');
    }

    switch (item.kind) {
      case 'glim': st.glim = Math.min(999, st.glim + 1); break;
      case 'sap': st.hp = Math.min(st.maxHp, st.hp + (item.price ? st.maxHp : 2)); cure(this.player, 'Bleeding'); cure(this.player, 'Poisoned'); break;   // sap closes a wound and clears a poison (LS-224)
      case 'pod':
        { const had = st.hasPods; addItem(st, 'pod', item.bulk || 1); st.rattle = true; if (!had) { st.tool = 'pods'; } }   // into the bag (LS-225), and the Rattle is yours from now on
        break;
      case 'gear': this.takeGear(item.gear); break;
      /* THE SHOP'S UPGRADES WENT WITH THE HOLLOWS (the lore lens, round 50). Six branches here bought a deeper
       * pouch, a glimstone, a spark, a longer haft, a deep pouch and a ward, and nothing in the game has spawned an
       * item of any of those kinds since the pivot: `grep "kind: 'pouch'"` and its five siblings find nothing. Four
       * toasts a player could never read, and a switch that told a reader the game had a shop.
       *
       * ...AND `state.reach`, `state.ward` AND `state.broke` WENT WITH THEM (2026-09-06). All three were the shop's:
       * nothing has granted any of them since the pivot, so all three were permanently zero, every read of them was
       * dead arithmetic (`16 + 0 * 3`), and the ward's whole absorb-a-hit branch could not run. They also cost a
       * real bug -- `save.ts` sanitised them on load while a fresh state had never declared them, so save -> load ->
       * save did not give back the same file. A mechanic with no source is not a mechanic. */
    }
    this.save();
  }

  takeGear(gear) {
    const st = this.state;
    if (gear === 'pods') {
      addItem(st, 'pod', 0); st.hasPods = true; st.rattle = true;
      // ...AND IT GOES IN THE SLOT, like the other two. The `tide` and `ember`
      // branches claimed it and this one did not, so finding the Rattle after
      // the Silt Lung left the button reading DIVE with five pods in the
      // pouch — and the Rattle is not the first charm in 77% of worlds.
      st.tool = 'pods';
      addItem(st, 'pod', Math.max(0, 5 - bagCount(st, 'pod')));
      
    } else if (gear === 'tide') {
      if (!bagCount(st, 'tide')) addItem(st, 'tide', 1); st.hasTide = true;
      st.tool = 'tide';        // a tool you just found goes in the slot
      
    } else {
      if (!bagCount(st, 'ember')) addItem(st, 'ember', 1); st.hasEmber = true;
      st.tool = 'ember';
      
    }
    this.sfx('fanfare');
  }

  /** Death costs you the run since the last threshold, not the whole world:
   *  reload the save and stand back up at the nearest doorway. */
  retry() {
    let saved = null;
    // what the beasts stood at when you fell, read before the restore puts the record back (the design lens, round 50)
    const live = Array.isArray(this.state && this.state.beasts) ? this.state.beasts.map((b: any) => ({ kind: b.kind, hp: b.hp })) : null;
    try { saved = Game.peekSave(this.seed); } catch (err) { saved = null; }
    let restored = false;
    /* THE RUN'S OWN CHECKPOINT, NOT THE SEED'S SLOT (the robustness lens, round 49). This restored whatever was in
     * localStorage under this seed, which is not necessarily anything this run ever wrote: a second Game on the same
     * seed in one process inherited the FIRST one's checkpoint the moment it died, and the two ran on with different
     * worlds from there. A slot is ours when its write number is one of ours -- `saveSeq` is set by our own `save()`
     * and by the `loadSave` that adopted the slot at boot -- and a run that has written nothing restores nothing. */
    const mine = Number.isFinite(this.saveSeq) ? this.saveSeq : -1;
    const ours = !!(saved && saved.seed === this.seed && Number.isFinite(saved.writeSeq) && saved.writeSeq <= mine);
    try { restored = !!(ours && this.loadSave(saved)); }
    catch (err) { console.warn('save unreadable on retry', err); restored = false; }
    // ...AND SET IT ASIDE HERE TOO. Dying while the slot is unreadable ran on
    // through this method to the `save()` at the end, which overwrote the
    // bytes — the guard in `save()` only protects a slot that PASSES
    // `looksSane`, and this one by definition does not. 40 of 40 trials. The
    // quarantine was written for the boot path and only ever wired there;
    // death is the one moment a player is least able to notice the loss.
    if (!restored && (!saved || ours) && Game.hasSaveBytes(this.seed)) {
      if (Game.quarantineSave(this.seed)) {
        this.saveQuarantined = true;
        console.warn('save unreadable on retry; the slot has been set aside as .bad');
      }
    }
    // No readable save — private mode, a full quota, a blocked origin? The run
    // in memory is still the run: apply the penalty and carry on rather than
    // resetting the world and destroying it.
    // NB: do not set storageFailed here — that suppressed the very warning
    // the save path exists to show.
    //
    // One placement path for both outcomes. There used to be two copies of
    // this, and the checkpoint fix landed in only the one that runs when the
    // save is UNREADABLE — so on every normal death it was dead code, and
    // every boss attempt still started six to nine rooms back at the entrance.
    /* A BEAST KEEPS THE DAMAGE IT TOOK (the design lens, round 50). The restore put the beasts' record back with
     * everything else, so a death handed a Great Beast every point back: measured over twelve gated fights, the
     * beast was at full life again on 9 of 12 and 20 to 26 points -- half its bar -- were undone in 96 frames, and
     * how much depended on whether the player happened to walk over a dropped sap. The lower of the two stands. */
    if (restored && Array.isArray(live) && Array.isArray(this.state.beasts)) {
      for (const b of this.state.beasts) {
        const was = live.find((o: any) => o && o.kind === b.kind);
        if (was && Number.isFinite(was.hp) && Number.isFinite(b.hp)) {
          /* ...BUT A DEATH GIVES HALF OF IT BACK (the design lens, round 51, difficulty 6 -> 4).
           *
           * Keeping the damage was right and keeping ALL of it was not: with the player restored to full health and
           * the beast restored to none of it, attrition banked across deaths beats any number the beast has. The
           * lens felled the grown 98-hp old shell -- the hardest thing this game can make -- in 78 seconds with a
           * bot mashing one button and the starting sickle. Measured here on the sow: wound it a quarter, die,
           * repeat, and it is dead in FOUR DEATHS having never lost a fight, its life falling 40, 30, 20, 10, 1
           * with the retry giving back nothing at all.
           *
           * Half the missing life comes back, so a fight is still worth finishing -- the damage does not reset, and
           * round 50's complaint (a death handed the beast every point back, and the attempt started again) stays
           * answered -- but dying is no longer a free way to wear one down. On the same measurement the sow now
           * settles at 35, 33, 32, 31, 31, 31 and NEVER FALLS FURTHER however many times the player dies: what a
           * death buys is halved every time, so it converges instead of ratcheting. A beast has to be beaten in a
           * fight; what a fight leaves behind is half of what it took. */
          const kept = Math.min(b.hp, was.hp);
          const cap = b.max || (ENEMY_DEFS[b.kind] ? ENEMY_DEFS[b.kind].hp : kept);
          b.hp = Math.max(1, Math.min(cap, Math.round(kept + (cap - kept) * 0.5)));
        }
      }
    }
    /* A DEATH PUTS YOU ASHORE (2026-09-18). The checkpoint carries `aboard`, and the restore above put it back --
     * and then `wakeFrom` chose a respawn tile the body fits on and can leave from, without reconsidering it. The
     * body woke standing on ground while the game still thought it was in a boat: the dodge was refused, deep water
     * was not solid to it, and the first steered frame pinned it back onto a hull that might be a screen away. Two
     * placements disagreeing, one of them silent. The wake wins; the hull waits where it was, moored. */
    /* ...AND IT IS MOORED ONLY WHERE IT CAN BE STEPPED ONTO (2026-09-19). A death at sea moored the hull in open water
     * for ever; the body woke on the nearest land, on five worlds in eight an island with no forge, and the raft the
     * wind would have brought to a shore sat where it sank. A hull with no ground beside it is left adrift. */
    if (this.aboard) { this.aboard = false; const b = this.state.boat; if (b) b.moored = this.hullBesideLand(b); }
    this.wakeFrom();
    this.nestTimer = 0;   // and the brood's beat of warning starts again after a death (2026-09-17): the counter survived the restore and the next nest fired the moment you stood beside it
    // Full health on retry. Starting an attempt already hurt made every
    // attempt strictly worse than the last, and a measured 0.0% of Warden
    // fights were winnable from there.
    this.state.hp = this.state.maxHp;
    if (this.player && this.player.afflictions) this.player.afflictions.clear();   // a whole body (2026-09-19): the checkpoint's bleed came back at full length on a body woken at full vigor
    /* ...AND A DEATH WITH AN EMPTY PURSE COSTS THE BLADE (the design lens, round 50: over four walked runs, 17 of 23
     * deaths took nothing at all -- 0.30 glim a death against 230 vigor handed back). What a player who cannot pay
     * always has is an edge, and the whet already prices it: fifteen blows is five glim of whetting, taken from the
     * weapon in hand, and never enough on its own to leave the blade dull mid-fight. */
    // Death has to cost something you feel. At 0 glim it used to be a free
    // full heal on the spot — cheaper than the 10-glim flask in the shop.
    // Only the wilds take your glim. Charging it per boss attempt stacked the
    // tax onto exactly the failure the difficulty guarantees.
    /* THE TOLL IS THE SAME WHEREVER YOU FALL.
     *
     * This read `inHollow ? 0 : ...`, on the argument that charging per boss
     * attempt stacks a tax onto the failure the difficulty guarantees. What
     * it actually produced was a hollow with no stakes in it at all: dying
     * inside one cost a single pod and restored full HP at the checkpoint, so
     * a Warden was a time cost and never a risk. Measured over 60 runs, 98 of
     * 98 Warden encounters were cleared, median 3 deaths, and one seed died
     * TWENTY-NINE times in a single run and paid six glim for the privilege
     * while ending it holding 139.
     *
     * A boss you cannot lose to is scenery, which is GOAL.md's phrase for the
     * thing this exemption created. The toll is flat — a fraction was tried
     * and made holding glim strictly wrong — so a player with nothing still
     * loses nothing, and the tax lands on the run that is already winning.
     */
    // A flat toll, not a fraction. Taking half of everything carried made
    // holding glim strictly wrong — measured bot runs ended holding two or
    // three coins because banking anything was a liability.
    const held = bagOf(this.state).slots.reduce((n, sl) => n + (sl && sl.kind === 'glim' ? sl.n : 0), 0);   // the bag is the count (the robustness lens, round 45: purse and bag had drifted apart)
    const lost = Math.min(6, Math.max(0, held - 9));   // taken from what the press would leave: a bag under ten is not the winning run the toll is for (the design lens, round 44)
    removeItem(this.state, 'glim', lost);   // out of the bag, and the purse is written from it by syncFlags: no hand write here (the robustness lens, round 47)
    let toll = lost > 0 ? lost + ' glim lost' : '';
    if (!lost && this.state.pods > 0) { removeItem(this.state, 'pod', 1); toll = 'a pod lost'; }
    else if (!lost) {
      /* NOTHING IN THE PURSE, SO IT COMES OFF THE EDGE (the design lens, round 50). "Nothing left to lose" was true
       * of 17 of 23 measured deaths, and a death that costs nothing is a rest stop. What a player who cannot pay
       * always has is a blade: fifteen blows, which the whet prices back at five glim, taken from the weapon in
       * hand -- and never below one, so a death cannot leave you dull in the middle of a beast fight. */
      const held0 = bagSelected(this.state);
      const mx0 = held0 && ITEMS[held0.kind] && ITEMS[held0.kind].dur;
      if (mx0 && held0.dur === undefined) held0.dur = mx0;
      if (mx0 && held0.dur > 1) { held0.dur = Math.max(1, held0.dur - 15); toll = 'the ' + ITEMS[held0.kind].name + ' blunted'; }
      else toll = 'nothing left to lose';
    }
    this.mode = 'play';
    this.paused = false;
    this.renewRoundTheBody();
    
    this.save();
  }

  onDeath() {
    this.sfx('fall');   // the forager's own fall, not a small creature's squelch (the music lens, round 40); before the mode turns, which mutes the mix
    this.mode = 'dying';
    this.deathTimer = DEATH_FRAMES;
    /* AND THE FORAGER IS SEEN TO FALL (2026-09-10; Vijay: "why is there no
     * death animation why do i immediately see the death screen its very
     * jarring").
     *
     * There were already 96 frames here and nothing in them. The body was not
     * DRAWN during them -- `if (this.mode !== 'dying') this.player.draw(...)`
     * in the render -- so the forager vanished on the killing blow, a few rust
     * motes played over a frozen world, and the screen arrived out of nowhere.
     * The animation the fall needs already existed: the ragdoll of LS-212
     * flings the body back with the head trailing, on its back by a third of
     * the way through, lying until three quarters, then UP again. A death is
     * that fall stopped where it lies, so the ragdoll runs from its start to
     * halfway and holds there. The way it flew is the way the last blow threw
     * it; a death from a bleed or a scorching has no blow, so it falls back
     * from where it was facing. */
    this.player.ragdollMax = DEATH_FRAMES;
    this.player.ragdoll = DEATH_FRAMES;
    if (!this.player.ragdollDir) {
      const [dx, dy] = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[this.player.dir] || [0, 1];
      this.player.ragdollDir = [dx, dy];
    }
    this.player.attack = 0; this.player.dodge = 0; this.player.kb = null;
    this.deathRush = false;
    this.puff(this.player.cx, this.player.cy, UI.rust, 14);
  }

  /* WHAT THE GAME IS ALLOWED TO SAY.
   *
   * GOAL.md scores tutorial captions NEGATIVELY, and thirteen lines that were
   * not tutorials had accumulated anyway — narration. `the seal breaks` while
   * the bars visibly leave the doorway; `the brazier takes it` while the
   * brazier lights; `the carapace turns it` while the blade clangs and throws
   * a spark, which the code comment beside it already calls "the whole
   * tutorial". Each one told the player a thing the frame had just shown them,
   * which is the same failure as a caption wearing a different hat.
   *
   * They are gone, each with a note at its old call site naming the picture
   * that replaced it, so nobody puts one back thinking the event is silent.
   *
   * What is left, and why:
   *   - the premise, once, on the title screen;
   *   - the NAME of a thing you have just picked up, because a name is not an
   *     instruction and the world cannot show you a proper noun;
   *   - progress you cannot see from the picture (`seed 2/3`, `hollow II`);
   *   - fault reports (`progress is not saving`), which are about the program
   *     rather than the world;
   *   - the keeper's lines, which are lore and wayfinding, not teaching.
   */
  say(text) { this.message = { text, t: 170 }; }

  /* ------------------------------------------------------------- saves
   *
   * Every one of these is a one-line delegator to `src/save.ts`, which owns
   * the format, the key grammar, the loader's clamps and the records store.
   * They stay on `Game` because `main.ts`, `hud.ts` and the suites call them
   * here — `Game.peekSave(seed)`, `game.save()`, `Game.records()` — and a
   * refactor that renamed a call site would be a refactor that could lose a
   * run. The comments explaining WHY each one is shaped the way it is came
   * across with the code, in `save.ts`. */
  // Completion is restored from validated beast marks; the world remains saveable.
  save() {
    return S.save(this);
  }

  /** The canonical form of a seed string: what the save key and the world are
   *  both built from. */
  static normaliseSeed(seed) { return S.normaliseSeed(seed); }

  /** The save for one seed, or the most recent run if no seed is named. */
  static peekSave(seed?) { return S.peekSave(seed); }

  /** Set an unreadable slot aside instead of letting the next write destroy
   *  it. @returns {boolean} whether anything was set aside. */
  static quarantineSave(seed) { return S.quarantineSave(seed); }

  /** Is there a save here at all, readable or not? */
  static hasSaveBytes(seed) { return S.hasSaveBytes(seed); }

  /** Has this player earned the right to be TOLD about the night? */
  static nightUnlocked() { return S.nightUnlocked(Game); }

  /** The last parse of the records blob, keyed on the exact bytes it came
   *  from. Null after our own writes, so the next read re-parses.
   *
   *  IT LIVES HERE, NOT IN `save.ts`. The suite clears it with
   *  `(Game as any).recCache = null` to force a re-parse, so the field has to
   *  be the one the reader consults — `records()` is handed this class as its
   *  cache holder rather than keeping a second cache of its own. */
  static recCache: { raw: string; value: { seeds: Record<string, RunRecord>; total: Totals } } | null = null;

  static records(): { seeds: Record<string, RunRecord>; total: Totals } { return S.records(Game); }

  /** A copy a caller may write to. */
  static mutableRecords(): { seeds: Record<string, RunRecord>; total: Totals } { return S.mutableRecords(Game); }

  /** The rows worth keeping: most recently set first. */
  static prune(seeds: Record<string, RunRecord>): Record<string, RunRecord> { return S.prune(seeds); }

  /** Would loadSave actually accept this seed's slot? */
  static canLoad(seed) { return S.canLoad(seed); }

  /** Write the run down. Winning should leave a mark. */
  /* THE STONES CALL UNTIL YOU HAVE LISTENED TO ONE (2026-09-08, LS-295: "the first ten minutes teach
   * nothing", and Vijay: no instructions).
   *
   * Measured across 24 worlds: the screen you start on holds NO creature in every one of them -- it is
   * a glade and it is safe on purpose -- nothing on it can be cut, and the country you open in is ash
   * or scree, the two dead ones, in ten of the twenty-four. So the opening is an empty screen, and the
   * one thing in this world that points anywhere is a standing stone. Seventeen worlds in twenty-four
   * put one within a screen of the start; the line that used to say "the standing stones point the
   * way" is gone, as it should be, and nothing replaced it.
   *
   * So they light, the way they light in the aftermath, until the player has read one. Reading a stone
   * is what marks a beast `heard`, so the game stops calling the moment the lesson lands. It costs
   * nothing where no stone is near: nothing lights, which is what happens today. */
  stonesCalling() {
    if (this.aftermath > 0) return true;
    /* A run whose records have not been built yet has certainly read nothing, so the absent case is
     * CALLING rather than silent. Asking `beasts()` here instead would have materialised them a frame
     * early and allocated a Set every frame beside the one `tickBeasts` already makes. */
    const recs = this.state && this.state.beasts;
    return !recs || !recs.some((r) => r && r.heard);
  }

  /** The three beasts are down. True through the aftermath as well as on the win screen (LS-304),
   *  because the run is WON at the third kill -- the stone only ends it. */
  won() { return this.aftermath > 0 || this.mode === 'win'; }

  recordWin(kind: 'run' | 'quieted' | 'again' = 'run') { return S.recordWin(this, Game, kind); }

  /** Forget one run on purpose. The only deletion path in the game. */
  static forgetRun(seed) { return S.forgetRun(seed); }

  /** Every run this browser remembers, newest first. */
  static listRuns() { return S.listRuns(); }

  /** A loader that throws is a loader that loses runs: anything that goes
   *  wrong in here is a refusal, and a refusal is a thing this project
   *  already knows how to survive. */
  loadSave(data) { return S.loadSave(this, data); }

  loadSaveUnsafe(data) { return S.loadSaveUnsafe(this, data); }

  /** A save is data from disk: never trust its shape, and never throw out of
   *  the boot path on it. Kept as a method because half the file calls it
   *  that way; the rule itself is module scope in `save.ts`, so the static
   *  picker asks the same question the loader will. */
  saveLooksSane(d) { return S.looksSane(d); }

  /** Clamp anything the save could carry into a legal range. */
  sanitiseState() { return S.sanitiseState(this); }

  /* ONE LAYER ORDER, DRAWN BY ONE FUNCTION.
   *
   * The transition used to be a SECOND, hand-written copy of this sequence,
   * and it was a lossy one: it drew the canopy and the blades but not the
   * ground cover, drew every blade behind the player instead of splitting
   * them at his feet, and had no critters, enemies, shots or effects at all.
   * Nothing said so, because nothing compared the two.
   *
   * Every rendering fault reported during a transition traces to that split —
   * the wood vanishing because its draw was gated on the screen having GRASS,
   * the whole picture resampled onto half pixels, the water mask reaching
   * into the HUD, and grass and leaves stacking in the wrong order. Four
   * separate bug reports, one cause: two copies of a sequence, one of which
   * nobody updated when the other changed.
   *
   * So there is one copy now and both paths call it. `parts` is whichever
   * screen is being drawn — the outgoing one carries its own turf, canopy and
   * decals — and `bodies` says whether the living things belong to it, which
   * only the screen the player is standing on can claim.
   *
   * `LAYERS` names the sequence for `test/soak.ts`, which asserts that a
   * transition emits the same layers in the same order as ordinary play. A
   * layer added to one path and not the other now fails a check instead of
   * being seen months later in a screen recording.
   */
  /* THE LAND COMES IN STEPS, AND A STEP HAS A FACE.
   *
   * This is the depth cue Minish Cap's overworld is built on and this game has
   * never had: the ground is layered, and where it drops you can SEE the drop
   * — a band of rock under the lip with a shadow at its foot. Everything here
   * has been drawn as though the world were perfectly flat, which is why a
   * screen reads as a plan rather than a place.
   *
   * `terrain.ts` cuts elevation into four terraces at percentiles, so every
   * world has all of them and only their shape changes. A face is drawn where
   * the terrace SOUTH of a tile is lower — south only, because that is the
   * edge a top-down view can see. An east or west step gets nothing; the
   * player is looking down, not along.
   *
   * It draws nothing and decides nothing about walkability. A drop you can
   * fall off is the next step and it is a rule, not a picture; this is the
   * picture, and it can ship on its own.
   */
  /* WHERE TWO COUNTRIES MEET, AN AUTHORED EDGE — THE WHOLE WANG SET.
   *
   * Three cheaper answers were tried and each failed in its own way. Blending
   * the two palettes MUDDIES: half of a purple-grey ash and half of a tan dust
   * is neither, and a border screen lost all its colour. Choosing one country
   * or the other per TILE dithers at sixteen pixels, which came out as a
   * chessboard. Stippling inside the tile keeps both palettes pure but a
   * uniform ordered matrix reads as moire — a texture laid over the art rather
   * than a boundary in it.
   *
   * What a 16-bit overworld actually ships is an authored edge: the far
   * country's ground reaches into this tile along a drawn silhouette, with a
   * piece for every arrangement of neighbours. That is the full Wang set and
   * all of it is here — four sides, the four OUTER corners where two bands
   * meet and must join without a notch, and the four INNER corners where only
   * the diagonal differs and the edge has to turn into the tile.
   *
   * Drawn as a pass rather than baked into the tile art, because the tile
   * cache is keyed by palette and variant: six countries against six, times
   * 256 neighbourhoods, times four terrace shades is a cache no phone should
   * be asked to hold. Here it costs nothing but the fill.
   */
  drawBiomeEdges(g, parts) {
    const at = this.world.biomeAtTile;
    if (!at || !parts.coords) return;
    /* THE FIELD DRAWS ITS OWN MARGINS. The reach below wanders along a border
     * but starts at the tile edge, so the margin's far side was a ruled line
     * sixteen pixels apart; the ground field's rim bands (groundfield.ts) are
     * the same idea cut along the meander, so where the field is baked this
     * pass has nothing to add (LS-218). */
    if (parts.groundKinds) return;
    const land = parts.land;
    const ox = parts.coords.x * COLS, oy = parts.coords.y * ROWS;
    /* The profiles are authored, not random: each is a lobe that swells and
     * thins across the tile, so a run of them reads as an organic margin. A
     * flat depth would be a ruled band and noise would be a dither, which are
     * the two things this replaces. */
    /* ONE CONTINUOUS DEPTH FUNCTION ALONG THE BORDER, not a profile per tile.
     *
     * The profiles were a table of per-tile arrays, and neighbouring tiles
     * picked different ones — so the depth jumped at every tile boundary and
     * the margin came out as a chain of isolated blobs with gaps between them.
     * An art review named it exactly: beads, not lobes, and worse than a hard
     * edge, because the eye completes a dotted line and reads it as a line.
     *
     * Borrowed from the sibling project's field approach: evaluate ONE smooth
     * function of the world coordinate along the border and fill every column
     * under it. Two octaves of interpolated value noise, so the margin swells
     * and thins the way a real one does but is never cut. A minimum of two
     * pixels wherever it is drawn — below that it is a line again.
     */
    const wave = (u, salt) => {
      const cell = (c, k) => {
        const i = Math.floor(u / c), f = u / c - i;
        const sm = f * f * (3 - 2 * f);
        const at2 = (n) => {
          let h2 = (n * 374761393 + salt * 668265263 + k * 2246822519) | 0;
          h2 = (h2 ^ (h2 >>> 13)) * 1274126177 | 0;
          return ((h2 ^ (h2 >>> 16)) >>> 0) / 4294967296;
        };
        return at2(i) * (1 - sm) + at2(i + 1) * sm;
      };
      // 0..1, mostly mid, occasionally none — a margin that breathes
      return cell(11, 1) * 0.68 + cell(4, 2) * 0.32;
    };
    /** How deep the far country reaches into this tile at world pixel `u`. */
    const reach = (u, salt) => {
      const v = wave(u, salt);
      return v < 0.30 ? 0 : Math.max(2, Math.round(v * 5.2));
    };
    const hash = (a, b, c) => {
      let h = (a * 374761393 + b * 668265263 + c * 2246822519) | 0;
      h = (h ^ (h >>> 13)) * 1274126177 | 0;
      return (h ^ (h >>> 16)) >>> 0;
    };
    for (let ty = 0; ty < ROWS; ty++) {
      for (let tx = 0; tx < COLS; tx++) {
        const wx = ox + tx, wy = oy + ty;
        /* NOT ON THE ROCK. A country's margin is a tongue of soil reaching into
         * the next country's soil; drawn onto a cliff face it is a fringe of
         * random-length spikes hanging off the wall, which is exactly where the
         * base line has to be hard for the drop to read. Reported from a
         * screenshot as the cliffs looking like melting tape. */
        /* THE TILE, NOT THE FACE ARRAY. `terrain.face` marks where the rock
         * IS, and a ramp is cut through rock — so the array still marks it and
         * a guard reading the array skips the one tile the transition exists
         * for. Measured with the guard on the array: zero elevation
         * transitions in 3,915 tiles, because every level change either has a
         * wall between it or is a ramp, and ramps were being skipped. */
        if (parts.tiles && parts.tiles[ty][tx] === T.CLIFF) continue;
        /* A RAMP IS A TRANSITION TILE — so the material is the COUNTRY AND
         * THE LEVEL, not the country alone.
         *
         * A slope between two elevations is the same event as a border between
         * two grounds: one material reaching into another. Given that, it needs
         * no pass of its own — four attempts at a bespoke ramp tile all failed
         * by treating it as a third thing between two walls. Widen what counts
         * as "a different material" to include the terrace, and the authored
         * profiles and the whole Wang corner set apply to elevation for free.
         *
         * Where a WALL stands between two levels there is no transition, only
         * rock, so a neighbour that is a cliff face is skipped — the margin
         * belongs to the places you can walk between. */
        const mat = (dx, dy) => {
          const nx = wx + dx, ny = wy + dy;
          const row = parts.tiles && parts.tiles[ty + dy];
          if (row && row[tx + dx] === T.CLIFF) return null;   // a wall, not a margin
          return at(nx, ny) + '|' + (land ? land.step(tx + dx, ty + dy) : 0);
        };
        const mine = mat(0, 0);
        const x = tx * TS, y = ty * TS;   // inside render()'s HUD translate already
        const nb = (dx, dy) => mat(dx, dy) ?? mine;
        const N = nb(0, -1), S = nb(0, 1), W = nb(-1, 0), E = nb(1, 0);
        /* SHADED BY THE GROUND IT LIES ON, not by the country it came from.
         *
         * The margin is a tongue of the neighbour's ground lying on THIS
         * tile's plane, so it takes this tile's terrace shading — the raw
         * palette entry belongs to no tile anywhere once the terraces darken
         * and lift them. Drawn unshaded it came out a bright patch wherever a
         * country border happened to fall on a terrace step, which is often,
         * because both follow the land. */
        const col = (m) => {
          const bar = String(m).indexOf('|');
          return terracePal(String(m).slice(0, bar), +String(m).slice(bar + 1)).ground;
        };

        // the along-border coordinate is the WORLD pixel, so the depth carries
        // across tile boundaries instead of restarting at each one
        if (N !== mine) {
          g.fillStyle = col(N);
          for (let i = 0; i < TS; i++) {
            const d = reach(wx * TS + i, 1);
            if (d) g.fillRect(x + i, y, 1, d);
          }
        }
        if (S !== mine) {
          g.fillStyle = col(S);
          for (let i = 0; i < TS; i++) {
            const d = reach(wx * TS + i, 2);
            if (d) g.fillRect(x + i, y + TS - d, 1, d);
          }
        }
        if (W !== mine) {
          g.fillStyle = col(W);
          for (let i = 0; i < TS; i++) {
            const d = reach(wy * TS + i, 3);
            if (d) g.fillRect(x, y + i, d, 1);
          }
        }
        if (E !== mine) {
          g.fillStyle = col(E);
          for (let i = 0; i < TS; i++) {
            const d = reach(wy * TS + i, 4);
            if (d) g.fillRect(x + TS - d, y + i, d, 1);
          }
        }
        /* OUTER CORNERS. Two bands meeting leave a square of this tile's own
         * ground in the angle between them, which reads as a bite taken out of
         * the corner. Filling it is what makes the margin turn. */
        const fillCorner = (cx, cy, b) => {
          g.fillStyle = col(b);
          g.fillRect(cx, cy, 3, 1); g.fillRect(cx, cy, 1, 3);
        };
        if (N !== mine && W !== mine) fillCorner(x, y, N);
        if (N !== mine && E !== mine) fillCorner(x + TS - 3, y, N);
        if (S !== mine && W !== mine) fillCorner(x, y + TS - 3, S);
        if (S !== mine && E !== mine) fillCorner(x + TS - 3, y + TS - 3, S);
        /* INNER CORNERS. Both sides match and only the DIAGONAL differs, so no
         * band is drawn and the far country pokes a hard right angle into this
         * tile. A lobe in that corner is the piece a side-only set is missing,
         * and it is the commonest defect in a naive tileset. */
        const nib = (b, cx, cy, sx, sy) => {
          g.fillStyle = col(b);
          for (let i = 0; i < 3; i++) {
            const run = 3 - i;
            g.fillRect(cx + (sx < 0 ? -(run - 1) : 0), cy + sy * i, run, 1);
          }
        };
        if (nb(-1, -1) !== mine && N === mine && W === mine) nib(nb(-1, -1), x, y, 1, 1);
        if (nb(1, -1) !== mine && N === mine && E === mine) nib(nb(1, -1), x + TS - 1, y, -1, 1);
        if (nb(-1, 1) !== mine && S === mine && W === mine) nib(nb(-1, 1), x, y + TS - 1, 1, -1);
        if (nb(1, 1) !== mine && S === mine && E === mine) nib(nb(1, 1), x + TS - 1, y + TS - 1, -1, -1);
      }
    }
    this.layers.push('biome-edges');
  }

  /* THE GROUND OF A DRY COUNTRY IS ONE FLAT COLOUR, AND IT SHOWS.
   *
   * The green countries look alive because the turf field paints patches over
   * them; scree and dust and ash grow almost nothing -- cover 0.12, 0.20, 0.08
   * -- so their floor is the palette entry and a speckle, tile after tile. On a
   * whole-island render the stone plateau reads as a paved plaza: an area
   * fifteen screens across with no tonal event anywhere in it, next to a meadow
   * that has one every few tiles.
   *
   * The warm/cool drift the turf already uses is the answer, applied to the
   * bare floor instead of to the grass, and at a tighter cell -- `DRIFT_CELL`
   * is 44 tiles, which is the world warming and cooling as you cross it, and
   * what a flat floor wants is the grain UNDER that: damp hollows, sun-bleached
   * rises, a few tiles across.
   *
   * A wash rather than a palette change, because the tile art is baked and
   * cached by (kind, palette, variant) and a per-tile tone would miss that
   * cache on every tile in the world. One image over ground that is already
   * drawn costs nothing and cannot change what a tile IS -- which matters,
   * because the same floor has to stay tellable from the walls standing on it.
   *
   * SMOOTH, NOT PER TILE. The first cut of this filled one rect per tile at the
   * field's value there, and a mottle quantised to the tile grid is a
   * CHECKERBOARD: on the dust plateau it came out as hard 16px squares of warm
   * and cool, which is the grid artefact this whole round has been removing,
   * only now painted in on purpose. So the field is rasterised one pixel per
   * tile into a small image and blown up with smoothing on, which is bilinear
   * interpolation done by the compositor -- the tint then crosses a tile
   * boundary without an edge, and the only thing quantised is where it is
   * sampled.
   *
   * Sampled one tile beyond the screen on every side, and drawn from -TS, so
   * the interpolation at the window's edge has real neighbours to lean on
   * rather than clamping; clipped back to the screen so two neighbours do not
   * lay their alpha over each other along the seam. */
  groundWash(g, parts, sx, sy) {
    const tiles = parts.tiles;
    if (!tiles || (globalThis as any).__noWash) return;
    const key = sx + ',' + sy;
    let img = this.washCache.get(key);
    if (!img) {
      const seed = this.world ? this.world.seedNum : 0;
      const ox = sx * COLS, oy = sy * ROWS;
      const w = COLS + 2, h = ROWS + 2;
      const rgba = washImage(seed, ox, oy, tiles);
      img = document.createElement('canvas');
      img.width = w; img.height = h;
      const ig = img.getContext('2d');
      const data = ig.createImageData && ig.createImageData(w, h);
      if (!data || !data.data) return;            // a stub context (the sims): no wash to draw
      data.data.set(rgba);
      ig.putImageData(data, 0, 0);
      this.washCache.set(key, img);
    }
    /* BLENDED BY HAND. The eighteen-by-fourteen wash was scaled up with the
     * canvas's own smoothing, which in practice left each cell its own tint
     * with a ruled edge between two cells that differed. The wash is now
     * interpolated between cell centres into a screen-sized image once per
     * screen, so a tint change is a slope sixteen pixels wide (LS-218). */
    if (!this.washBig) this.washBig = new Map();
    let big = this.washBig.get(key);
    if (big === undefined) {
      big = null;
      if ((globalThis as any).__noField) { this.washBig.set(key, null); }
      else {
      const W = COLS * TS, H = ROWS * TS;
      const src = img.getContext('2d');
      const sd = src.getImageData ? src.getImageData(0, 0, img.width, img.height).data : null;
      const c = document.createElement('canvas'); c.width = W; c.height = H;
      const cg = c.getContext('2d');
      const out = sd && cg.createImageData ? cg.createImageData(W, H) : null;
      if (out && out.data) {
        // the wash noise itself is smooth at any position, so it is read per pixel rather than per cell and
        // blended: a cell's worth of blend between two cells' values still showed the cells as soft blobs
        const o = out.data, seed = this.world ? this.world.seedNum : 0;
        const ox = sx * COLS, oy = sy * ROWS, gk = parts.groundKinds;
        for (let py = 0; py < H; py++) {
          for (let px = 0; px < W; px++) {
            const dv = washAt(seed, ox + (px + 0.5) / TS, oy + (py + 0.5) / TS);
            const k = gk ? gk[py * W + px] : tiles[Math.floor(py / TS)][Math.floor(px / TS)];
            const floor = OPEN.has(k) && !isWet(k);
            const warm = dv >= 0, oi = (py * W + px) * 4;
            o[oi] = warm ? 203 : 79; o[oi + 1] = warm ? 176 : 143; o[oi + 2] = warm ? 106 : 134;
            o[oi + 3] = floor ? Math.round(Math.abs(dv) * WASH_MAX * 255) : 0;
          }
        }
        cg.putImageData(out, 0, 0);
        big = c;
      }
      this.washBig.set(key, big);
      if (this.washBig.size > 64) { const first = this.washBig.keys().next().value; this.washBig.delete(first); }
      }
    }
    g.save();
    g.beginPath();
    g.rect(0, 0, PW, PH);   // inside render()'s HUD translate already
    g.clip();
    if (big) g.drawImage(big, 0, 0);
    else {
      const was = g.imageSmoothingEnabled;
      g.imageSmoothingEnabled = true;
      g.drawImage(img, -TS, -TS, (COLS + 2) * TS, (ROWS + 2) * TS);
      g.imageSmoothingEnabled = was;
    }
    g.restore();
  }

  /* ONE MERGED RUN OF WALL, DRAWN ONCE. See the greedy-meshing note in
   * `drawCliffs`: `sp` is a maximal row of cliff tiles at the same terrace
   * standing the same height, and everything below is drawn across the whole
   * of it rather than sixteen pixels at a time.
   *
   * What this replaces drew a RECTANGLE per tile -- a flat fill, a lit brink
   * ruled along its top, a ruled line along its bottom, and ink corners on both
   * sides. Every edge of it was straight and every edge was on the tile grid,
   * so a tall face came out as a bar lying on the ground rather than a wall
   * standing in it, and a terrace came out as a row of those bars.
   *
   * Three properties make the difference and none of them survives being drawn
   * per tile. The FOOT is broken, column by column, where rock meets ground in
   * a ragged line rather than a ruled one -- and the break has to run the
   * length of the wall, not restart every tile. The STRATA run DOWN the face,
   * because the strata of a wall you are looking at are vertical and horizontal
   * banding is what a floor does; they are scattered over the run, so a long
   * wall gets more of them and a short one does not get the same five. And the
   * CORNERS happen only where the run genuinely ends. */
  drawCliffRun(g, parts, sp, ox, oy, palOf, hgt, taperAt) {
    const INK = this.terraceInk(parts);
    const land = parts.land;
    const pal = palOf(sp.x0, sp.ty);
    const tone = cliffTones(pal);
    const rock = tone.face, litRock = tone.lit;
    /* NO HUD OFFSET HERE. render() translates the whole world pass by HUD_H
     * before a screen is drawn; adding it again put every face three tiles
     * south of its rock (measured with the camera printed: a three-tall
     * face at world rows 81-83 painted at 84-86, over the sand and sea
     * below it), and what read as the wall under the brink was the front
     * tile's own art. The tile layer never added it, which is how the two
     * disagreed for so long. */
    const x = sp.x0 * TS, y = sp.ty * TS;
    const wpx = (sp.x1 - sp.x0 + 1) * TS;
    /* THE RUN'S ENDS FOLLOW THE ROCK FIELD (LS-218). A face was painted as
     * a rectangle of columns, so a run's two ends were vertical cell edges.
     * Within six pixels of either end the painting is clipped to the pixels
     * the rock field calls cliff, so the end of a wall bulges and hollows
     * with the same meander as its tile art and its ledge ink. */
    g.save();
    if (parts.rockKinds) {
      const W = COLS * TS, H = ROWS * TS, rk = parts.rockKinds, ff = parts.frontField;
      const tallPx = Math.max(TS, Math.round(sp.tall * TS)) + 10;
      g.beginPath();
      for (let c = 0; c < wpx; c++) {
        const px = x + c;
        if (c >= 6 && c < wpx - 6) { g.rect(px, y - 8, 1, tallPx + 8); continue; }
        for (let yy = y - 2; yy < y + tallPx; yy++) {
          if (px < 0 || yy < 0 || px >= W || yy >= H) continue;
          // the face ends where the fold field's front ends (a plateau top beside it is rock too, but not face); else the rock field
          if (ff ? ff[yy * W + px] === 1 : rk[yy * W + px] === T.CLIFF) g.rect(px, yy, 1, 1);
        }
      }
      g.clip();
    }
    const hash = (n) => {
      let h2 = ((ox + sp.x0) * 374761393 + (oy + sp.ty) * 668265263
        + n * 2246822519) | 0;
      h2 = (h2 ^ (h2 >>> 13)) * 1274126177 | 0;
      return ((h2 ^ (h2 >>> 16)) >>> 0) / 4294967296;
    };
    // how far the rock reaches down in each column: broken, not ruled, and
    // broken along the whole run instead of restarting at every tile edge
    /* A WALL THAT LANDS ON MORE ROCK HAS NO FOOT.
     *
     * The face is drawn three pixels short of the tile so its foot can break
     * against the ground. Where the terraces step down every single tile -- most
     * of heath, all of ash -- the tile below is not ground, it is the next
     * wall: so every sixteen pixels the face stopped, ruled a foot line, laid a
     * contact shadow, and the next face began three pixels later. Stacked up a
     * hillside that is a dark line every tile, and the cliffs read as courses of
     * brickwork. Two rounds of retuning the stone tones did nothing to it,
     * because the grid was never in the tones.
     *
     * Rock standing on rock is one wall. The face runs the full height of the
     * tile, and the foot -- the broken line, the ink, the shadow -- is drawn
     * only where there is actually ground for it to land on. */
    const onRock = [];
    const foots = new Array(wpx);
    let deepest = 0;
    for (let c = 0; c < wpx; c++) {
      const tx = sp.x0 + Math.floor(c / TS);
      // rock under the whole face, not under its first row: a wall two cells tall had its foot ruled along the cell below it
      const rocky = !!hgt(tx, sp.ty + Math.max(1, Math.round(sp.tall)));
      onRock.push(rocky);
      const full = Math.round(sp.tall * TS * taperAt(tx, sp.ty));
      const body = Math.max(4, Math.round((sp.tall * TS - 3) * taperAt(tx, sp.ty)));
      let foot = rocky ? full
        : body - (hash(100 + sp.x0 * TS + c) < 0.42 ? 1 + Math.floor(hash(1400 + sp.x0 * TS + c) * 2) : 0);
      /* THE FOOT OF THE FACE IS WHERE THE ROCK FIELD ENDS. The hash above
       * takes a pixel or two off a column, but off a baseline ruled per tile,
       * so a long face still met the ground on one line; where the rock field
       * is baked the column's foot is the field's last cliff pixel near that
       * baseline, and the foot wanders with the meander (LS-218). */
      if (!rocky && parts.rockKinds) {
        const W = COLS * TS, H = ROWS * TS, rk = parts.rockKinds, px = x + c;
        if (px >= 0 && px < W) {
          let last = -1;
          for (let yy = Math.max(0, y + body - 9); yy < Math.min(H, y + body + 10); yy++) if (rk[yy * W + px] === T.CLIFF) last = yy;
          if (last >= 0) foot = Math.max(3, last + 1 - y);
        }
      }
      foots[c] = foot;
      deepest = Math.max(deepest, foots[c]);
    }
    /* AND THE BRINK BREAKS TOO.
     *
     * The foot of the face was given a broken line and the top was left ruled,
     * so every wall on the island had one perfectly straight edge running its
     * whole length with a lit line along it. At 300% that is the loudest thing
     * about a cliff and it is unmistakably drawn with a ruler -- rock does not
     * have a straight edge at either end.
     *
     * Small: one or two pixels, on about half the columns, and never where the
     * wall carries on up into more rock. The brink is where the eye reads the
     * height from, so a top that wanders more than that stops reading as a
     * level surface ending. */
    /* A WALL STANDING ON A WALL IS ONE WALL.
     *
     * A ledge one or two tiles wide is its own region, and the pass that keeps
     * the tier maths honest turns regions that small into rock -- "a ledge that
     * small is not a place, it is part of the wall". So a hillside that steps
     * down twice in three tiles loses its middle ledge and becomes one tall
     * drop. Measured over three worlds, 52 of 754 walls have rock directly
     * above them for that reason, and only three are a genuine double step.
     *
     * The draw did not know. Each run laid its own brink and its own ink
     * undercut, so a continuous face of rock came out with a lit line ruled
     * across its middle -- reported from play as cliffs stacked directly on top
     * of each other, which is what it looks like and is not what it is.
     *
     * Where the tile above is rock of any kind, the wall carries on up: no
     * brink, no undercut, and the face starts flush at the tile's top edge. */
    const capped = [];
    const tops = new Array(wpx);
    for (let c = 0; c < wpx; c++) {
      const above = !!hgt(sp.x0 + Math.floor(c / TS), sp.ty - 1);
      capped.push(above);
      /* IN RUNS, NOT PER COLUMN. Choosing an offset for each column
       * independently makes the brink alternate every pixel, and a one-pixel
       * alternation along a lit edge is a row of TEETH -- at 450% the steps
       * came out crenellated, like a battlement. Groups of four columns share
       * an offset, so the top undulates over a few pixels the way a weathered
       * edge does. */
      const grp = Math.floor((sp.x0 * TS + c) / 4);
      tops[c] = above ? 0
        : (hash(600 + (grp & 511)) < 0.55 ? 1 + (hash(640 + (grp & 511)) < 0.3 ? 1 : 0) : 0);
    }
    /* THE WALL IS NOT ONE TONE ALONG ITS LENGTH.
     *
     * With the grain scattered and the edges broken, what was left was a band
     * of a single colour running the whole width of a formation -- and a long
     * flat band is the thing that reads as paint rather than as rock, however
     * good its outline is. Real stone is lighter where it stands proud and
     * darker where it is set back, over stretches of a few tiles.
     *
     * Five steps quantised from a smooth world field, so the change is gradual
     * and does not land on the tile grid, and columns sharing a step are filled
     * in one go -- a run of nine tiles costs five fills rather than 144. */
    /* THE FACE'S LIP FOLLOWS THE FIELD (LS-218). Each column's top wandered
     * by a pixel or two of hash; with the screen's smoothed terrace level it
     * starts where the field's upper terrace ends above this column, so a
     * run's lip meanders with the ledge ink and the shifted tile art rather
     * than ruling a line along the row. Lowered only: the rock above a
     * raised lip is the tile layer's, already shifted the same way. */
    if (parts.frontField) {
      // the face begins where the fold field says, up to six pixels above or below the cell's top
      const W = COLS * TS, H = ROWS * TS, ff = parts.frontField;
      for (let c = 0; c < wpx; c++) {
        if (capped[c]) continue;
        const px = x + c;
        if (px < 0 || px >= W) continue;
        let lip = 99;
        for (let yy = Math.max(0, y - 6); yy < Math.min(H, y + 8); yy++) { if (ff[yy * W + px]) { lip = yy - y; break; } }
        if (lip !== 99) tops[c] = Math.max(-6, Math.min(6, lip));
      }
    } else if (parts.groundLevels) {
      const W = COLS * TS, H = ROWS * TS, lv = parts.groundLevels;
      for (let c = 0; c < wpx; c++) {
        if (capped[c]) continue;
        const px = x + c;
        if (px < 0 || px >= W) continue;
        let lip = -1;
        for (let yy = Math.max(0, y - 6); yy < Math.min(H, y + 8); yy++) { if (lv[yy * W + px] <= sp.here) { lip = yy - y; break; } }
        if (lip > 0) tops[c] = Math.max(tops[c], Math.min(6, lip));
      }
    }
    const STEPS = [0.90, 0.95, 1.0, 1.05, 1.10].map((k) => darkenHex(rock, k));
    const toneAt = (c) => {
      const f = (ox + sp.x0) * TS + c;
      const cell = 40;                       // pixels: about two and a half tiles
      const i0 = Math.floor(f / cell), a = (f / cell) - i0;
      const h0 = hash(3000 + (i0 & 255)), h1 = hash(3000 + ((i0 + 1) & 255));
      const sm = a * a * (3 - 2 * a);
      return Math.max(0, Math.min(4, Math.floor((h0 + (h1 - h0) * sm) * 5)));
    };
    for (let c = 0; c < wpx;) {
      const t2 = toneAt(c);
      let n = 1;
      while (c + n < wpx && toneAt(c + n) === t2) n++;
      g.fillStyle = STEPS[t2];
      for (let k = 0; k < n; k++) {
        g.fillRect(x + c + k, y + tops[c + k], 1,
          Math.max(1, foots[c + k] - tops[c + k]));
      }
      c += n;
    }
    /* STRATA. A face was one tone per column from brink to foot, and in the
     * stony countries a two-tile wall read as a pasted strip. Stone shows its
     * bedding: bands four to six pixels deep, a tone apart, each band's
     * boundary wandering a pixel per column so no line rules across the run.
     * Drawn as thin rows of the neighbouring tone over the column fill, so
     * the run still reads as one mass. */
    {
      const hgtPx = sp.tall * TS;
      for (let c = 0; c < wpx; c++) {
        const t2 = toneAt(c);
        const wob = Math.floor(hash(700 + ((sp.x0 * TS + c) & 1023)) * 2);
        const bottom = foots[c];
        for (let b = 5 + wob; b < bottom - 1; b += 5 + (Math.floor(hash(760 + b) * 2))) {
          const yy = b + tops[c];
          if (yy >= bottom - 1 || yy >= hgtPx) break;
          g.fillStyle = STEPS[Math.max(0, Math.min(4, t2 + ((b >> 3) & 1 ? 2 : -2)))];
          g.fillRect(x + c, y + yy, 1, 1);
        }
      }
    }
    /* THE BRINK IS QUIETER ON A SHORT STEP.
     *
     * Every run drew the same two-pixel lit brink over the same ink undercut,
     * whatever it was the top of. Where the terraces step down every single
     * tile -- which is most of heath and all of ash -- that is a lit line and a
     * dark line every sixteen pixels, and a hillside came out as a brick wall.
     * A one-tile step is a kerb: it gets a hairline. */
    const tall1 = sp.tall <= 1;
    g.fillStyle = INK;
    for (let c = 0; c < wpx; c++) {
      if (!capped[c]) g.fillRect(x + c, y + tops[c], 1, 1);
    }
    /* THE BRINK IS LIT ROCK, NOT WHITE PAINT.
     *
     * A flat white overlay lands on every country at the same strength, and the
     * countries are not the same darkness: over ash's rock, which sits at 55 to
     * 86 luma, fifteen per cent white is a jump of nearly thirty and the brink
     * came out as a bright bar ruled along the top of every wall. At 450% an
     * ash hillside was a stack of dark slabs separated by white lines.
     *
     * Lightening the wall's own tone instead scales with it: the same
     * multiplier is a small step on dark stone and a small step on pale stone,
     * and the brink stays the colour of the rock it is the top of. */
    // 1.46 was WORSE than the white it replaced: ash's rock at 86 luma times
    // 1.46 is 125, where fifteen per cent white gave 111. A brink is the top of
    // a wall catching a little light, not a highlight -- a fifth brighter than
    // the face is the whole of it.
    g.fillStyle = darkenHex(rock, tall1 ? 1.10 : 1.20);
    for (let c = 0; c < wpx; c++) {
      if (!capped[c]) g.fillRect(x + c, y + tops[c] + 1, 1, tall1 ? 1 : 2);
    }
    /* SHORT SEGMENTS, NOT LINES. A full-height stripe down a third of the
     * columns in the paler stone is a PICKET FENCE -- the same barcode five
     * attempts at the ramp tile were rejected for. A stratum is a seam in rock:
     * it starts somewhere down the face and ends somewhere above the foot, it
     * is a shade rather than a colour, and there are a few of them per tile of
     * run rather than a fixed five however long the wall is. */
    /* ...AND NOT SO MANY THAT THEY ARE THE FACE. Four a tile, once the faces
     * stood on their rock (LS-158) and could be seen at all, read as a
     * curtain of vertical dashes in fen and ash, and the bedding underneath
     * them could not be seen. About one and a half a tile, and the strata
     * carry the rest. */
    const seams = Math.max(2, Math.round(wpx / 16 * 1.5));
    for (let i = 0; i < seams; i++) {
      const c = Math.floor(hash(2000 + i) * wpx);
      const top = 3 + Math.floor(hash(2200 + i) * Math.max(1, deepest * 0.4));
      const len = Math.max(2, Math.round(deepest * (0.18 + hash(2400 + i) * 0.34)));
      const end = Math.min(foots[c] - 1, top + len);
      if (end <= top) continue;
      /* THE SEAMS HAVE TO BE VISIBLE TO BE SEAMS. At a tenth of an alpha over
       * a mid-tone face they were, at 250% on a bloom cliff, not there: the
       * wall was a flat olive rectangle with a dark line along its top. A
       * stratum is a break in the rock, not a whisper. */
      const wide = 1 + (hash(2800 + i) < 0.3 ? 1 : 0);
      g.fillStyle = hash(2600 + i) < 0.45
        ? 'rgba(255,246,228,0.17)' : 'rgba(12,9,20,0.28)';
      g.fillRect(x + c, y + top, wide, end - top);
    }
    /* A TALL WALL IS BENCHED.
     *
     * The heights percentile now runs to four tiles, and four tiles is
     * sixty-one pixels of face: on the cliffiest screen in the game 81 of 129
     * rock tiles are INSIDE a wall -- covered by the face hanging down from a
     * foot above them -- and at that size a single unbroken band is the flat
     * grey area that keeps getting asked about. It is not a side and it is not
     * a back, it is the body of one very tall wall.
     *
     * Escarpments that tall do not fall in one clean sweep; they step. A ledge
     * partway down -- lit on top, shadowed under, wandering with the same
     * grouped hash the brink uses -- reads as two drops rather than one slab,
     * which is both what rock does and what makes the height legible. Only from
     * three tiles up: benching a two-tile step would be the courses again. */
    if (sp.tall >= 3) {
      for (let c = 0; c < wpx; c++) {
        const wc = sp.x0 * TS + c;
        const at2 = tops[c] + Math.round((foots[c] - tops[c]) * 0.54 + 1.5 * Math.sin(wc * 0.31) + 1.5 * Math.sin(wc * 0.09 + 1.7))   // a stratum wanders, it is not ruled
          + (hash(3200 + (Math.floor(wc / 5) & 511)) < 0.5 ? 1 : 0);
        g.fillStyle = 'rgba(12,9,20,0.34)';
        g.fillRect(x + c, y + at2, 1, 3);
        g.fillStyle = 'rgba(255,246,228,0.13)';
        g.fillRect(x + c, y + at2 - 1, 1, 1);
      }
    }
    /* AND IT DARKENS TOWARD ITS FOOT. A wall is lit from the sky, so the
     * further down it you look the less of the sky it can see -- and a face
     * that is one flat tone is the thing that made these read as slabs once the
     * tones stopped being black. Three bands rather than one, over the lower
     * half, which at sixteen pixels is as much gradient as there is room for. */
    for (let i = 0; i < 3; i++) {
      g.fillStyle = 'rgba(12,9,20,0.14)';
      for (let c = 0; c < wpx; c++) {
        const wc = sp.x0 * TS + c;
        const from = Math.round(foots[c] * (0.46 + i * 0.18) + 1.5 * Math.sin(wc * 0.27 + i) + 1.5 * Math.sin(wc * 0.08 + 2 * i));   // the shading bands wander too
        g.fillRect(x + c, y + from, 1, Math.max(1, foots[c] - from));
      }
    }
    g.fillStyle = INK;
    for (let c = 0; c < wpx; c++) {
      if (!onRock[c]) g.fillRect(x + c, y + foots[c] - 1, 1, 1);
    }
    /* THE ENDS OF THE RUN, and only the ends. Drawn per tile these were a pair
     * of ink columns every sixteen pixels wherever two tiles of the same wall
     * happened to disagree about their height, which is the seam that made a
     * terrace read as separate blocks. */
    if (!hgt(sp.x0 - 1, sp.ty)) { g.fillStyle = INK; g.fillRect(x, y, 2, deepest); }
    if (!hgt(sp.x1 + 1, sp.ty)) {
      g.fillStyle = INK; g.fillRect(x + wpx - 2, y, 2, deepest);
    }
    /* ...AND THE SHADOW IT THROWS ON THE GROUND IT STANDS ON, following the
     * broken line of its foot so the shadow is the wall's shape and not a
     * rectangle under it.
     *
     * ONLY ONTO GROUND. Where the terraces stack -- ash steps down every one or
     * two tiles -- this threw a long shadow onto the NEXT WALL DOWN, over its
     * brink and its face, and the screen came out as overlapping dark
     * rectangles with light lines through them. A wall standing directly on
     * more rock gets a contact shadow and nothing else: there is no ground
     * there for a shadow to lie on. */
    for (let c = 0; c < wpx; c++) {
      if (onRock[c]) continue;              // no ground here to throw one onto
      g.fillStyle = 'rgba(12,9,20,0.50)';
      g.fillRect(x + c, y + foots[c], 1, 5);
      g.fillStyle = 'rgba(12,9,20,0.28)';
      g.fillRect(x + c, y + foots[c] + 5, 1, 3 + sp.tall * 3);
    }
    /* CORNERS ARE OCCLUDED, which is the other thing a voxel renderer does:
     * each corner of a face is darkened by how many of the voxels touching it
     * are solid. Here the two ends of the run are the corners, and a wall that
     * turns into more rock at its end is in a pocket -- darker than one that
     * ends in open air. A uniform band cannot tell those apart; this can. */
    const occl = (tx, dir) => {
      let n = 0;
      if (hgt(tx + dir, sp.ty)) n++;
      if (hgt(tx + dir, sp.ty - 1)) n++;
      if (hgt(tx, sp.ty - 1)) n++;
      return n;
    };
    for (const [tx, dir, at] of [[sp.x0, -1, 0], [sp.x1, 1, wpx - 1]]) {
      const n = occl(tx, dir);
      if (!n) continue;
      const a = (n / 3) * 0.30;
      for (let i = 0; i < 3; i++) {
        g.fillStyle = 'rgba(8,6,14,' + (a * (1 - i / 3)).toFixed(3) + ')';
        const c = at + (dir > 0 ? -i : i);
        if (c < 0 || c >= wpx) continue;
        g.fillRect(x + c, y, 1, foots[c]);
      }
    }
    g.restore();
  }

  drawCliffs(g, parts) {
    const land = parts.land;
    if (!land) return;
    /* EVERY SIDE OF THE ROCK, DRAWN FROM ONE PLACE.
     *
     * This pass used to walk the BRINKS — the ground at the top of a drop —
     * and could therefore only draw the face below one. South-facing drops got
     * a wall two or three tiles tall while the same plateau's east and west
     * edges were a four-pixel strip drawn by the tile art, so a raised area
     * came out as a stack of planks instead of a solid mass of stone.
     *
     * It walks the ROCK now: every tile the terrain calls a cliff foot, asking
     * which of its neighbours stand higher, and drawing the face each of those
     * calls for. That is the whole of the Wang question for cliffs — which
     * sides are rock, which sides are open, and what happens where two of them
     * meet — and having it in one loop is what lets the corners agree.
     *
     * Height is paint. The collision is the one tile of rock the generator
     * laid; what hangs below it is the picture of a wall that tall.
     */
    /* THE ROCK BELONGS TO THE COUNTRY IT STANDS IN.
     *
     * These came from `parts.pal`, which is the SCREEN's palette — one country
     * for the whole window — while the ground has been per-tile for some time.
     * So a cliff inside the stone country was drawn in the dust country's warm
     * brown: measured on a screenshot, a face of (69,59,39) standing in stone
     * of (98,98,118), which reads as a wooden post planted in grey rock. Asked
     * per tile, a cliff is always made of the ground it interrupts. */
    const INK = this.terraceInk(parts);
    const palOf = (tx, ty) => {
      const b = this.world.biomeAtTile(ox + tx, oy + ty);
      return terracePal(b, land.step(tx, ty));
    };
    const ox = parts.coords ? parts.coords.x * COLS : 0;
    const oy = parts.coords ? parts.coords.y * ROWS : 0;
    const hgt = (tx, ty) => (land.face ? land.face(tx, ty) : 0) || 0;
    /* World-hashed, so a corner is the same size from either side of a screen
     * seam and does not crawl when the camera moves. */
    const hashAt = (tx, ty, n) => {
      let h2 = ((ox + tx) * 374761393 + (oy + ty) * 668265263 + n * 2246822519) | 0;
      h2 = (h2 ^ (h2 >>> 13)) * 1274126177 | 0;
      return ((h2 ^ (h2 >>> 16)) >>> 0) / 4294967296;
    };
    /* A RAMP IS A HOLE IN THE WALL, SO NO WALL IS DRAWN OVER IT.
     *
     * `terrain.face` says where the rock IS, and a ramp is cut through it --
     * the generator changes the tile but the face array still marks the spot,
     * because that is what makes the ramp a way through rather than a gap in
     * the terrain. Reading the face array alone hung a full cliff face over
     * every ramp: the one place in the world that is supposed to read as a way
     * up was drawn as the wall it goes through. The tile is the authority. */
    const isRock = (tx, ty) => {
      if (!hgt(tx, ty)) return false;
      if (parts.tiles && tx >= 0 && ty >= 0 && tx < COLS && ty < ROWS
        && parts.tiles[ty][tx] !== T.CLIFF) return false;
      // a crag is drawn as a boulder by the tile art, and a wall face hung
      // below it, or an ink edge run round it, puts the plank straight back
      if (land.crag && land.crag(tx, ty)) return false;
      // ...and so is a STUB: one, two or three tiles of visible wall, which
      // the tile art now draws as rock (LS-199). Hanging a face under it puts
      // the crate back exactly as a crag would.
      if (land.stub && land.stub(tx, ty)) return false;
      return true;
    };
    /* THE FACE TAPERS INTO A RAMP. A wall that simply STOPS at the gap reads as
     * a doorway -- a hole in a flat surface -- not as a way up. What says "the
     * ground rises here" is the face getting shorter as it approaches the gap,
     * because a shorter face is a smaller drop, and a drop that shrinks to
     * nothing is a slope. */
    const taperAt = (tx, ty) => {
      let taper = 1;
      if (parts.ramps) {
        for (const [rx, ry] of parts.ramps) {
          if (ry !== ty) continue;
          const gap = Math.abs(rx - tx);
          if (gap === 1) taper = Math.min(taper, 0.45);
          else if (gap === 2) taper = Math.min(taper, 0.75);
        }
      }
      return taper;
    };
    /* --- ONE WALL PER RUN, NOT ONE PER TILE. ---
     *
     * Greedy meshing, which is what a voxel engine does to its chunks: adjacent
     * coplanar faces at the same height merge into a single quad. Here the
     * merge is along a row -- a maximal run of cliff tiles at the same terrace
     * standing the same height with the same drop in front of them.
     *
     * This pass used to walk tiles and draw a RECTANGLE on each, with its own
     * lit brink, its own ink undercut, its own corner returns and its own cast
     * shadow. Sixteen pixels wide, over and over along a terrace: that is why a
     * wall read as a row of separate slabs however the tones were tuned, and
     * why three rounds of art review came back with the same word -- planks,
     * fallen logs, dominoes lying flat. The fault was never the colour, it was
     * that the silhouette restarted every sixteen pixels.
     *
     * Merged, a terrace is one wall: one broken foot line running its whole
     * length, one brink, one shadow, and corners only where the run genuinely
     * ends. Measured on the island, the same merge takes 2,180 exposed side
     * faces down to 834 quads.
     */
    const spans = [];
    for (let ty = 0; ty < ROWS; ty++) {
      let run = null;
      for (let tx = 0; tx <= COLS; tx++) {
        const here = tx < COLS ? land.step(tx, ty) : 0;
        const front = tx < COLS && isRock(tx, ty) && land.step(tx, ty - 1) > here;
        if (front) {
          const tall = hgt(tx, ty);
          if (run && run.here === here && run.tall === tall && run.x1 === tx - 1) {
            run.x1 = tx;
            continue;
          }
          if (run) spans.push(run);
          run = { ty, x0: tx, x1: tx, here, tall };
        } else if (run) { spans.push(run); run = null; }
      }
      if (run) spans.push(run);
    }
    for (const sp of spans) this.drawCliffRun(g, parts, sp, ox, oy, palOf, hgt, taperAt);

    for (let ty = 0; ty < ROWS; ty++) {
      for (let tx = 0; tx < COLS; tx++) {
        if (!isRock(tx, ty)) continue;
        /* ONLY THE FRONT IS A WALL. A rock tile whose higher ground is not to
         * the north is the side or the back of a plateau, and from above
         * those are the plateau's own floor running out to its edge -- the
         * tile art paints it so (see T.CLIFF). What marks the edge is one
         * line of ink on each side that drops to lower open ground; a wall
         * you could see from here would have been drawn as a front. Flecks
         * and cracks are for stone, so it takes none. */
        const here0 = land.step(tx, ty);
        if (!(land.step(tx, ty - 1) > here0)) continue;   // a rim: its line is drawn in `drawLedges`
        /* A MASS OF ROCK IS NOT ONE TONE.
         *
         * With the walls no longer near-black and no longer ruled into courses,
         * what a big formation reads as is a flat dark shape -- heath's outcrop
         * came out as a patch of asphalt in a meadow. Rock has light and dark
         * in it at a scale much larger than a tile: a face catches the sky at
         * its shoulders and loses it in its hollows.
         *
         * The same slow world field the ground wash uses, on its own seed, laid
         * over every rock tile including the faces already drawn on them -- so
         * the light and dark cross the whole formation rather than stopping at
         * whichever tile happens to be a foot. Bounded low: this is form, and
         * the moment it is strong enough to read as a pattern it is mottling,
         * not stone. */
        if (parts.rockKinds) {
          // the tint is one image for the screen, blended between tile centres and cut to the rock field (LS-218)
          const tk = (parts.coords ? parts.coords.x + ',' + parts.coords.y : '0,0');
          if (!this.cliffTintFrame) this.cliffTintFrame = new Map();
          if (this.cliffTintFrame.get(tk) !== this.frame) { this.cliffTintFrame.set(tk, this.frame); this.drawCliffTint(g, parts, ox, oy); }
        } else {
          const d2 = washAt((this.world ? this.world.seedNum : 0) + 7717,
            ox + tx, oy + ty);
          if (Math.abs(d2) > 0.06) {
            g.fillStyle = d2 > 0
              ? 'rgba(255,247,232,' + (d2 * 0.17).toFixed(3) + ')'
              : 'rgba(10,8,16,' + (-d2 * 0.26).toFixed(3) + ')';
            g.fillRect(tx * TS, ty * TS, TS, TS);
          }
        }
        /* THE GRAIN, HASHED ON WORLD POSITION.
         *
         * See `stoneMass`: a cliff's variant comes entirely from the terrain,
         * so every interior tile of a formation bakes to the same image and the
         * flecks in it repeat as wallpaper -- rows of identical pale marks at
         * sixteen-pixel intervals across a face. The tile art keeps the body;
         * the marks are scattered here, where the world coordinate is known and
         * no two tiles can agree. */
        {
          const gt = cliffTones(palOf(tx, ty));
          for (let i = 0; i < 7; i++) {
            const gx = Math.floor(hashAt(tx, ty, 300 + i) * (TS - 2));
            const gy = Math.floor(hashAt(tx, ty, 340 + i) * (TS - 1));
            const gw = 1 + Math.floor(hashAt(tx, ty, 380 + i) * 3);
            g.fillStyle = i < 4 ? gt.lit : INK;
            g.fillRect(tx * TS + gx, ty * TS + gy, gw, 1);
          }
          /* AND ROCK IS FRACTURED. Flecks give a surface grain and nothing
           * more; what says "this is stone" rather than "this is a painted
           * area" is that it is BROKEN -- lines running across it at a scale
           * bigger than a fleck and smaller than the formation. About one tile
           * in three carries a short crack, hashed on world position so a
           * crack crosses a tile boundary as readily as it sits inside one. */
          if (hashAt(tx, ty, 420) < 0.34) {
            const cx3 = Math.floor(hashAt(tx, ty, 440) * (TS - 6));
            const cy3 = Math.floor(hashAt(tx, ty, 460) * (TS - 4));
            const len = 4 + Math.floor(hashAt(tx, ty, 480) * 7);
            const down = hashAt(tx, ty, 500) < 0.45;
            g.fillStyle = 'rgba(12,9,20,0.34)';
            for (let k = 0; k < len; k++) {
              const jx = Math.floor(hashAt(tx, ty, 520 + k) * 2);
              if (down) g.fillRect(tx * TS + cx3 + jx, ty * TS + cy3 + k, 1, 1);
              else g.fillRect(tx * TS + cx3 + k, ty * TS + cy3 + jx, 1, 1);
            }
          }
        }
        /* THE SIDES AND THE BACK. The face itself -- the one wall the
         * projection can show -- is drawn by `drawCliffRun` over a whole merged
         * run; what is left per tile is the seam where the rock meets the
         * ground beside it, which is a fact about that tile's four neighbours
         * and cannot be merged along a row. */
        const here = land.step(tx, ty);
        const x = tx * TS, y = ty * TS;   // inside render()'s HUD translate already
        const N = land.step(tx, ty - 1) > here;
        const E = land.step(tx + 1, ty) > here;
        const W = land.step(tx - 1, ty) > here;
        const S = land.step(tx, ty + 1) > here;
        /* A STEP YOU SEE FROM THE SIDE IS A SHADOW ON THE GROUND BESIDE IT.
         *
         * Two things were wrong here once and they compounded. The pass drew
         * two pixels of ink along the edge the HIGHER ground is on, with a lit
         * pixel just inside the rock -- a highlight on the low side of the
         * step, saying the low ground is the one catching the sky -- and it
         * drew nothing at all on the side the rock actually drops away on. So a
         * north-south terrace came out as a dark strip with a white line down
         * it lying in flat ground, with no cue anywhere about which half of the
         * screen was higher: "a lot of this doesn't make physical sense."
         *
         * The model is the rock itself. A cliff tile is a piece of WALL, and
         * from above you see the top of it. Where it meets the upper ground,
         * that seam catches the light. Where it drops to open ground, it shades
         * that ground -- hardest against the rock and fading out within a few
         * pixels. Ambient occlusion needs no light direction to be right, and
         * it is the only depth cue this projection can carry on a wall whose
         * face points away from the camera. */
        const AO = ['rgba(12,9,20,0.30)', 'rgba(12,9,20,0.20)',
          'rgba(12,9,20,0.12)', 'rgba(12,9,20,0.06)'];
        const LIP = 'rgba(255,255,255,0.20)';
        // open ground, not more rock: a wall does not cast a shadow on itself
        const open = (dx, dy) => hgt(tx + dx, ty + dy) === 0 && !(parts.tiles && parts.tiles[ty + dy] && STANDING.has(parts.tiles[ty + dy][tx + dx]));   // no shadow strip over a thing that stands there

        /* A WALL SEEN FROM DIRECTLY ABOVE IS AN EDGE, NOT A BAR.
         *
         * A terrace boundary running north-south is one tile of rock with the
         * higher ground on one side and the lower on the other, and it was
         * painted as sixteen pixels of dark stone. From directly above you
         * would see almost none of that wall: the upper ground runs out to its
         * brink, and what is left is a narrow lip of exposed rock and then the
         * drop. On the biome sheet these came out as dark fence posts standing
         * in the fields -- three in bloom, four in fen, a row down the side of
         * scree -- and they were the last thing in the picture reading as a
         * prop rather than as terrain.
         *
         * THE FIRST ATTEMPT FILLED THE TILE WITH THE NEIGHBOUR'S PALETTE ENTRY
         * AND WAS REJECTED ON SIGHT: heath came back with glowing green pillars
         * in it, because a palette entry is a FLAT FILL -- no turf, no decals,
         * no grain, since every one of those is drawn per tile on tiles that
         * are not rock. A flat bright bar in a textured meadow reads worse than
         * a dark one.
         *
         * What it needed was the neighbour's actual PIXELS, and they are
         * already on the canvas: `drawTurf` runs immediately before this pass,
         * so by now the tile beside this one has its ground, its wash and its
         * baked turf on it. Copying a strip of them across continues the
         * meadow over the wall's top instead of inventing a colour for it, and
         * the strip lines up because it is taken from the same offset it is
         * drawn at. The source rectangle is in canvas space and this context is
         * translated by the screen's origin, so the transform has to be asked
         * for that offset rather than assumed. */
        const ridge = (hiDx, hiDy) => {
          const nx2 = tx + hiDx, ny2 = ty + hiDy;
          if (nx2 < 0 || ny2 < 0 || nx2 >= COLS || ny2 >= ROWS) return;
          const tr = g.getTransform();
          const sx2 = tr.e + nx2 * TS, sy2 = tr.f + ny2 * TS;   // tr.f already carries the HUD translate
          const lip = 5, w2 = TS - lip;
          if (hiDx > 0) g.drawImage(g.canvas, sx2, sy2, w2, TS, x + lip, y, w2, TS);
          else if (hiDx < 0) g.drawImage(g.canvas, sx2 + lip, sy2, w2, TS, x, y, w2, TS);
          else if (hiDy > 0) g.drawImage(g.canvas, sx2, sy2, TS, w2, x, y + lip, TS, w2);
        };
        if (E && !W && !N && open(-1, 0)) ridge(1, 0);
        else if (W && !E && !N && open(1, 0)) ridge(-1, 0);
        else if (S && !N && !E && !W && open(0, -1)) ridge(0, 1);



        /* THE SEAM WHERE THE WALL MEETS THE GROUND ABOVE IT IS DARK.
         *
         * Drawn as ink and not left to the AO, because at map scale this line
         * is the CONTOUR: it is what makes a plateau read as one shape rather
         * than as a scatter of dark posts. A cut of this pass that shaded the
         * low ground and left the high seam to a single lit pixel measured
         * fine up close and flattened the whole island -- every terrace lost
         * its outline and the map went back to looking like flat ground with
         * bars lying on it. Two pixels of ink with the lit pixel on the HIGH
         * side of them, which is the same cue the north brink gives. */
        /* THE STROKES FOLLOW THE ROCK FIELD. Each of these was a bar the
         * length of the cell along the cell's edge -- ink inside, a lit line
         * and a shadow gradient outside -- and a wall three cells long wore
         * three bars end to end on one ruled line. Where the rock field is
         * baked, each row (or column) finds the field's own edge within six
         * pixels of the cell's and lays the same strokes there (LS-218). */
        const rk = parts.rockKinds, FW = COLS * TS, FH = ROWS * TS;
        const edgeE = (py) => { let e = x + TS; if (!rk) return e; for (let xx = Math.max(0, x + TS - 10); xx < Math.min(FW, x + TS + 10); xx++) if (rk[py * FW + xx] === T.CLIFF) e = xx + 1; return e; };
        const edgeW = (py) => { let e = x; if (!rk) return e; for (let xx = Math.min(FW - 1, x + 9); xx >= Math.max(0, x - 10); xx--) if (rk[py * FW + xx] === T.CLIFF) e = xx; return e; };
        const edgeS = (px) => { let e = y + TS; if (!rk) return e; for (let yy = Math.max(0, y + TS - 10); yy < Math.min(FH, y + TS + 10); yy++) if (rk[yy * FW + px] === T.CLIFF) e = yy + 1; return e; };
        const edgeN = (px) => { let e = y; if (!rk) return e; for (let yy = Math.min(FH - 1, y + 9); yy >= Math.max(0, y - 10); yy--) if (rk[yy * FW + px] === T.CLIFF) e = yy; return e; };
        const rowOK = (py) => py >= 0 && py < FH, colOK = (px) => px >= 0 && px < FW;
        if (E) for (let r = 0; r < TS; r++) { const py = y + r; if (!rowOK(py)) continue; const e = edgeE(py); g.fillStyle = INK; g.fillRect(e - 2, py, 2, 1); g.fillStyle = LIP; g.fillRect(e, py, 1, 1); }
        if (W) for (let r = 0; r < TS; r++) { const py = y + r; if (!rowOK(py)) continue; const e = edgeW(py); g.fillStyle = INK; g.fillRect(e, py, 2, 1); g.fillStyle = LIP; g.fillRect(e - 1, py, 1, 1); }
        if (S) for (let c = 0; c < TS; c++) { const px = x + c; if (!colOK(px)) continue; const e = edgeS(px); g.fillStyle = INK; g.fillRect(px, e - 2, 1, 2); g.fillStyle = LIP; g.fillRect(px, e, 1, 1); }
        if (open(1, 0)) for (let r = 0; r < TS; r++) { const py = y + r; if (!rowOK(py)) continue; const e = edgeE(py); for (let i = 0; i < AO.length; i++) { g.fillStyle = AO[i]; g.fillRect(e + i, py, 1, 1); } g.fillStyle = INK; g.fillRect(e - 1, py, 1, 1); }
        if (open(-1, 0)) for (let r = 0; r < TS; r++) { const py = y + r; if (!rowOK(py)) continue; const e = edgeW(py); for (let i = 0; i < AO.length; i++) { g.fillStyle = AO[i]; g.fillRect(e - 1 - i, py, 1, 1); } g.fillStyle = INK; g.fillRect(e, py, 1, 1); }
        if (open(0, 1)) for (let c = 0; c < TS; c++) { const px = x + c; if (!colOK(px)) continue; const e = edgeS(px); for (let i = 0; i < AO.length; i++) { g.fillStyle = AO[i]; g.fillRect(px, e + i, 1, 1); } g.fillStyle = INK; g.fillRect(px, e - 1, 1, 1); }
        if (open(0, -1)) for (let c = 0; c < TS; c++) { const px = x + c; if (!colOK(px)) continue; const e = edgeN(px); g.fillStyle = AO[1]; g.fillRect(px, e - 1, 1, 1); g.fillStyle = AO[3]; g.fillRect(px, e - 2, 1, 1); }
        const notch = (cx2, cy2, sx2, sy2) => {
          const gpal = palOf(tx + sx2, ty + sy2);
          g.fillStyle = gpal.ground || gpal.floor || UI.groundFallback;
          const big = 2 + Math.floor(hashAt(tx, ty, cx2 * 7 + cy2 * 13) * 2);
          for (let i = 0; i < big; i++) {
            const w2 = big - i;
            g.fillRect(cx2 ? x + TS - w2 : x, cy2 ? y + TS - 1 - i : y + i, w2, 1);
          }
        };
        if (open(0, -1) && open(-1, 0)) notch(0, 0, -1, -1);
        if (open(0, -1) && open(1, 0)) notch(1, 0, 1, -1);
        if (open(0, 1) && open(-1, 0)) notch(0, 1, -1, 1);
        if (open(0, 1) && open(1, 0)) notch(1, 1, 1, 1);
      }
    }

    this.layers.push('cliffs');
  }

  /* THE SUNLIT EDGE OF THE GROUND ABOVE A DROP — drawn LAST of the ground.
   *
   * It belongs to the grass rather than to the rock, and it is the one row
   * that tells the eye the top of a cliff is an edge you could fall off: a
   * dark outline is a silhouette line, not a lit lip, and only the lip says
   * which side is up.
   *
   * It used to be drawn at the end of `drawCliffs`, which is before the turf
   * blades, the decals and the critters — all of which paint over it. Measured
   * at 199 brinks, the ground's last row was lighter than its middle at only
   * 41 of them, a median difference of zero. The lip was being drawn and then
   * buried. */
  /* THE LEDGES: one line of ink where a plateau drops, and its shadow on the
   * ground below. Drawn AFTER the blades. They were drawn with the cliffs,
   * under the turf's blades, and once the rim wore turf its blades cut the
   * line to dashes (measured on the composite: the ink pixel gone from the
   * rim's edge). A rock tile whose higher ground is not to the north is the
   * side or back of a plateau; its plateau is its own level or the higher
   * ground beside it, and the line goes on each side that is open and below
   * that plateau. The shadow is what says which side is higher. */
  /* THE OUTLINE IS THE ROCK'S OWN DARK, NOT PRINTER'S INK.
   *
   * Every terrace edge, brink and ledge lip was drawn in the HUD's ink, one
   * near-black for all six countries; on the whole-map render the steps read
   * as a black wireframe laid over the colour. The line is now the screen's
   * cliff face pulled most of the way to black: still an edge, but a warm
   * one on dust, a cold one on scree, and part of the picture. */
  terraceInk(parts) {
    const c = parts && parts.coords;
    const b = c && this.world && this.world.biomeAtTile
      ? this.world.biomeAtTile(c.x * COLS + (COLS >> 1), c.y * ROWS + (ROWS >> 1)) : null;
    const key = b || '-';
    let ink = this._terraceInk && this._terraceInk.get(key);
    if (ink) return ink;
    const pal = b ? terracePal(b, 0) : null;
    ink = pal ? mixHex(UI.ink, cliffTones(pal).face, 0.55) : UI.ink;
    (this._terraceInk || (this._terraceInk = new Map())).set(key, ink);
    return ink;
  }

  drawLedges(g, parts) {
    const land = parts.land;
    if (!land || !land.face) return;
    const INK = this.terraceInk(parts);
    /* A GATE IN A GULLY IS A SLUICE. A tier's water gate sits in a one-tile
     * cut through rock, and the water tile alone -- bright, foam-rimmed --
     * read as a glitch in an ash wall before it read as a flooded cut. Two
     * lips of ink where the cut's walls meet the water, and the water a
     * shade darker for standing in shadow: a sluice, gated, and still
     * plainly water for the charm that opens it. */
    if (parts.tiles) {
      for (let ty = 0; ty < ROWS; ty++) {
        for (let tx = 0; tx < COLS; tx++) {
          const v = parts.tiles[ty][tx];
          if (!isWet(v)) continue;
          const rockW = tx > 0 && parts.tiles[ty][tx - 1] === T.CLIFF;
          const rockE = tx < COLS - 1 && parts.tiles[ty][tx + 1] === T.CLIFF;
          const rockN = ty > 0 && parts.tiles[ty - 1][tx] === T.CLIFF;
          const rockS = ty < ROWS - 1 && parts.tiles[ty + 1][tx] === T.CLIFF;
          if (!((rockW && rockE) || (rockN && rockS))) continue;
          const gk = parts.groundKinds, rk0 = parts.rockKinds;
          if (gk && rk0) {
            // the pool's shade and ink follow the fields: shade where the field is wet, ink where wet meets rock (LS-218)
            const FW = COLS * TS, FH = ROWS * TS;
            const wet = (px, py) => px >= 0 && py >= 0 && px < FW && py < FH && isWet(gk[py * FW + px]);
            const rock = (px, py) => px >= 0 && py >= 0 && px < FW && py < FH && rk0[py * FW + px] === T.CLIFF;
            for (let py = ty * TS; py < ty * TS + TS; py++) for (let px = tx * TS; px < tx * TS + TS; px++) {
              if (!wet(px, py)) continue;
              g.fillStyle = 'rgba(10,8,16,0.28)'; g.fillRect(px, py, 1, 1);
              const near = rock(px - 1, py) || rock(px + 1, py) || rock(px - 2, py) || rock(px + 2, py) || rock(px, py - 1) || rock(px, py + 1) || rock(px, py - 2) || rock(px, py + 2);
              if (near) { g.fillStyle = INK; g.fillRect(px, py, 1, 1); }
            }
          } else {
            g.fillStyle = 'rgba(10,8,16,0.28)';
            g.fillRect(tx * TS, ty * TS, TS, TS);
            g.fillStyle = INK;
            if (rockW && rockE) { g.fillRect(tx * TS, ty * TS, 2, TS); g.fillRect(tx * TS + TS - 2, ty * TS, 2, TS); }
            if (rockN && rockS) { g.fillRect(tx * TS, ty * TS, TS, 2); g.fillRect(tx * TS, ty * TS + TS - 2, TS, 2); }
          }
        }
      }
    }
    const isRock = (tx, ty) => !!land.face(tx, ty)
      && !(parts.tiles && tx >= 0 && ty >= 0 && tx < COLS && ty < ROWS && parts.tiles[ty][tx] !== T.CLIFF)
      && !(parts.propGround && tx >= 0 && ty >= 0 && tx < COLS && ty < ROWS && parts.propGround[ty][tx] !== T.CLIFF)   // a wall facing away is the plateau's ground (LS-394)
      && !(land.crag && land.crag(tx, ty));
    for (let ty = 0; ty < ROWS; ty++) {
      for (let tx = 0; tx < COLS; tx++) {
        if (!isRock(tx, ty)) continue;
        const here = land.step(tx, ty);
        if (land.step(tx, ty - 1) > here) {
          /* A FRONT CASTS THE SAME SHADOW A LEDGE DOES, onto the floor at the
           * bottom of its stack: the wall is drawn hanging from the brink to
           * the last claimed tile, and the ground south of that tile lies
           * under it. Without this the ledges read as raised and the walls
           * as pasted on. */
          let by = ty;
          while (by + 1 < ROWS && isRock(tx, by + 1) && !(land.step(tx, by) > land.step(tx, by + 1))) by++;
          if (by + 1 < ROWS && !isRock(tx, by + 1) && land.step(tx, by + 1) <= here) {
            g.fillStyle = 'rgba(10,8,16,0.30)';
            const rk = parts.rockKinds;
            if (rk) {
              // the shade starts where the rock field's cliff ends in each column, not on the tile row (LS-218)
              const W = COLS * TS, H = ROWS * TS, y0 = (by + 1) * TS;
              for (let c = 0; c < TS; c++) {
                const px = tx * TS + c;
                let last = y0 - 1;
                for (let yy = Math.max(0, y0 - 9); yy < Math.min(H, y0 + 10); yy++) if (rk[yy * W + px] === T.CLIFF) last = yy;
                g.fillRect(px, last + 1, 1, 4);
              }
            } else g.fillRect(tx * TS, (by + 1) * TS, TS, 4);
          }
          continue;
        }
        let top = here;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) top = Math.max(top, land.step(tx + dx, ty + dy));
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = tx + dx, ny = ty + dy;
          if (isRock(nx, ny)) continue;
          if (land.step(nx, ny) >= top) continue;
          if (parts.tiles && nx >= 0 && ny >= 0 && nx < COLS && ny < ROWS && STANDING.has(parts.tiles[ny][nx])) continue;   // a boulder or a stone beside the rock says the edge itself
          /* THE LEDGE'S INK FOLLOWS THE FIELD (LS-218). This inked the cell's
           * edge and shaded four pixels of the neighbour, per tile, and was the
           * ring around every low cliff -- found last, by asking the canvas
           * which call painted the ring's pixel. With the screen's per-pixel
           * kinds the ink sits where the field's cliff begins along each
           * column or row, the shade on the ground side of that, so the ring
           * meanders with the cliff art the overlay shifted to the same line. */
          const levels = parts.groundLevels;
          /* The neighbour may lie beyond the screen (LS-394): the ink still follows the rock field's meander inside
           * this screen, where before it was ruled along the edge because the neighbour was out of bounds. */
          if (levels) {
            // the rock's own level is `top`; the ledge is where the smoothed level field climbs to it
            const W = COLS * TS, H = ROWS * TS;
            // the rock field is the cliff's outline where it exists; the level field only knows where the ground steps, and a face tile carries its top's level
            const rk = parts.rockKinds;
            const cliffAt = rk ? (x, y) => x >= 0 && y >= 0 && x < W && y < H && rk[y * W + x] === T.CLIFF
              : (x, y) => x >= 0 && y >= 0 && x < W && y < H && levels[y * W + x] >= top;
            // the contour is looked for within eight pixels of the cell edge; a column or row where the
            // field's step is not there at all draws nothing (the step ran off to another cell's side)
            const R = 8;
            if (dy) {
              for (let x = 0; x < TS; x++) {
                const px = tx * TS + x;
                const edge = dy < 0 ? ty * TS : ty * TS + TS;
                let by = -1;
                if (dy < 0) { for (let y = edge - R; y < edge + R; y++) if (!cliffAt(px, y) && cliffAt(px, y + 1)) { by = y + 1; break; } }
                else { for (let y = edge + R; y > edge - R; y--) if (!cliffAt(px, y) && cliffAt(px, y - 1)) { by = y; break; } }
                if (by < 0) continue;
                g.fillStyle = INK;
                g.fillRect(px, dy < 0 ? by : by - 1, 1, 1);
                g.fillStyle = 'rgba(10,8,16,0.30)';
                g.fillRect(px, dy < 0 ? by - 4 : by, 1, 4);
              }
            } else {
              for (let y = 0; y < TS; y++) {
                const py = ty * TS + y;
                const edge = dx < 0 ? tx * TS : tx * TS + TS;
                let bx = -1;
                if (dx < 0) { for (let x = edge - R; x < edge + R; x++) if (!cliffAt(x, py) && cliffAt(x + 1, py)) { bx = x + 1; break; } }
                else { for (let x = edge + R; x > edge - R; x--) if (!cliffAt(x, py) && cliffAt(x - 1, py)) { bx = x; break; } }
                if (bx < 0) continue;
                g.fillStyle = INK;
                g.fillRect(dx < 0 ? bx : bx - 1, py, 1, 1);
                g.fillStyle = 'rgba(10,8,16,0.30)';
                g.fillRect(dx < 0 ? bx - 4 : bx, py, 4, 1);
              }
            }
            continue;
          }
          g.fillStyle = INK;
          g.fillRect(tx * TS + (dx > 0 ? TS - 1 : 0), ty * TS + (dy > 0 ? TS - 1 : 0), dx ? 1 : TS, dy ? 1 : TS);
          g.fillStyle = 'rgba(10,8,16,0.30)';
          g.fillRect(nx * TS + (dx < 0 ? TS - 4 : 0), ny * TS + (dy < 0 ? TS - 4 : 0), dx ? 4 : TS, dy ? 4 : TS);
        }
      }
    }
  }

  /* THE CLIFF'S WASH, AS A FIELD. Each rock cell had its own tint, a square
   * of light or dark laid over the tile, and where two cells' tints differed
   * the seam between them was a ruled line across the rock. The tint is read
   * at the four nearest cell centres and blended, and painted only where the
   * rock field says rock, so it fades across a wall and stops at its meander
   * (LS-218). Baked once per screen. */
  drawCliffTint(g, parts, ox, oy) {
    // cached on the game by screen: `parts` here is a per-frame copy, so a field on it would be rebuilt every frame
    if (!this.cliffTints) this.cliffTints = new Map();
    const key = parts.coords ? parts.coords.x + ',' + parts.coords.y : '0,0';
    let tint = this.cliffTints.get(key);
    if (tint === undefined) {
      const W = COLS * TS, H = ROWS * TS, rk = parts.rockKinds;
      const c = document.createElement('canvas'); c.width = W; c.height = H;
      const cg = c.getContext('2d');
      const img = cg.createImageData && cg.createImageData(W, H);
      if (!img || !img.data) { this.cliffTints.set(key, null); return; }
      const seed = (this.world ? this.world.seedNum : 0) + 7717;
      const wcache = new Map();
      const wAt = (tx, ty) => { const k = tx + ',' + ty; let v = wcache.get(k); if (v === undefined) { v = washAt(seed, ox + tx, oy + ty); wcache.set(k, v); } return v; };
      const d = img.data;
      for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
        if (rk[py * W + px] !== T.CLIFF) continue;
        const fx = (px + 0.5) / TS - 0.5, fy = (py + 0.5) / TS - 0.5;
        const tx0 = Math.floor(fx), ty0 = Math.floor(fy), ax = fx - tx0, ay = fy - ty0;
        const d2 = (wAt(tx0, ty0) * (1 - ax) + wAt(tx0 + 1, ty0) * ax) * (1 - ay) + (wAt(tx0, ty0 + 1) * (1 - ax) + wAt(tx0 + 1, ty0 + 1) * ax) * ay;
        if (Math.abs(d2) <= 0.03) continue;
        const i = (py * W + px) * 4;
        if (d2 > 0) { d[i] = 255; d[i + 1] = 247; d[i + 2] = 232; d[i + 3] = Math.min(255, Math.round(d2 * 0.17 * 255)); }
        else { d[i] = 10; d[i + 1] = 8; d[i + 2] = 16; d[i + 3] = Math.min(255, Math.round(-d2 * 0.26 * 255)); }
      }
      cg.putImageData(img, 0, 0);
      tint = c; this.cliffTints.set(key, tint);
      if (this.cliffTints.size > 64) { const first = this.cliffTints.keys().next().value; this.cliffTints.delete(first); }
    }
    if (tint) g.drawImage(tint, 0, 0);
  }

  /* THE TERRACE PAINTERS, BAKED ONCE A SCREEN (performance, Vijay: "performance
   * is starting to lag on mobile on new build when walking around"). The
   * cliffs, the lip and the ledges follow the fields pixel by pixel, which
   * made them scans in every frame for every screen in view; none of them
   * changes with the frame, so each is painted once into a canvas the size
   * of the screen and drawn from there, until the screen's tiles change. */
  /* THE TILE LAYER, REDRAWN A TILE AT A TIME (performance). Nearly every kind
   * animates, slowly and staggered by column, so a screen's picture has no
   * still version to bake; but only the tiles whose frame changed this tick
   * need drawing, a handful a screen, and the rest are already on the
   * screen's canvas. Seven hundred drawImages a frame become forty. */
  cachedTiles(parts, sx, sy, palAt) {
    if (typeof document === 'undefined' || !parts.tiles) return null;
    if (!this.tileLayerCache) this.tileLayerCache = new Map();
    const key = sx + ',' + sy;
    const tiles = parts.propGround || parts.tiles;
    let e = this.tileLayerCache.get(key);
    if (!e || e.tiles !== parts.tiles || e.palKey !== parts.palKey) {
      const c = document.createElement('canvas'); c.width = COLS * TS; c.height = ROWS * TS;
      const cg = c.getContext('2d');
      if (!cg || !cg.getImageData) return null;
      e = { tiles: parts.tiles, palKey: parts.palKey, canvas: c, ctx: cg, last: new Int16Array(COLS * ROWS).fill(-1), kind: new Int16Array(COLS * ROWS).fill(-1) };   // the kind beside the frame: a tile whose kind changed but whose frame index did not was skipped as unchanged (2026-09-11)
      this.tileLayerCache.set(key, e);
      if (this.tileLayerCache.size > 48) { const first = this.tileLayerCache.keys().next().value; this.tileLayerCache.delete(first); }
    }
    const t = this.frame, was = this.g; this.g = e.ctx;
    try {
      for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
        const k = tiles[y][x];
        const f = H.tileFrame(k, x, H.tileHash(sx, sy, x, y, k), t);
        if (e.last[y * COLS + x] === f && e.kind[y * COLS + x] === k) continue;
        e.ctx.clearRect(x * TS, y * TS, TS, TS);
        const pp = typeof palAt === 'function' ? palAt(x, y) : null;
        H.drawOneTile(this, tiles, pp ? pp.pal : palAt, pp ? pp.key : parts.palKey, x, y, 0, 0, { x: sx, y: sy });
        e.last[y * COLS + x] = f; e.kind[y * COLS + x] = k;
      }
    } finally { this.g = was; }
    return e.canvas;
  }

  cachedLayer(g, parts, name, painter) {
    if (!parts.coords || typeof document === 'undefined') { painter(g); return; }
    if (!this.layerCache) this.layerCache = new Map();
    const key = parts.coords.x + ',' + parts.coords.y;
    let e = this.layerCache.get(key);
    if (!e || e.tiles !== parts.tiles) { e = { tiles: parts.tiles, canvases: {} }; this.layerCache.set(key, e); if (this.layerCache.size > 48) { const first = this.layerCache.keys().next().value; this.layerCache.delete(first); } }
    let c = e.canvases[name];
    if (!c) {
      c = document.createElement('canvas'); c.width = COLS * TS; c.height = ROWS * TS;
      const cg = c.getContext('2d');
      if (!cg || !cg.getImageData) { painter(g); return; }
      const was = this.g; this.g = cg;
      try { painter(cg); } finally { this.g = was; }
      e.canvases[name] = c;
    }
    g.drawImage(c, 0, 0);
  }

  drawCliffLip(g, parts) {
    const land = parts.land;
    if (!land || !land.face) return;
    const ox = parts.coords ? parts.coords.x * COLS : 0;
    const oy = parts.coords ? parts.coords.y * ROWS : 0;
    for (let ty = 0; ty < ROWS; ty++) {
      for (let tx = 0; tx < COLS; tx++) {
        if (land.step(tx, ty) <= land.step(tx, ty + 1)) continue;
        if (!land.face(tx, ty + 1)) continue;
        const b = this.world.biomeAtTile(ox + tx, oy + ty);
        g.globalAlpha = 0.55;
        g.fillStyle = terracePal(b, land.step(tx, ty)).stoneFace || UI.stoneFaceFallback;
        const rk = parts.frontField || parts.rockKinds, want = parts.frontField ? 1 : T.CLIFF;
        if (rk) {
          // the lip sits where the fold field (else the rock field) begins in each column, not on the tile's last two rows (LS-218)
          const W = COLS * TS, H = ROWS * TS, y0 = ty * TS + TS;
          for (let c = 0; c < TS; c++) {
            const px = tx * TS + c;
            let edge = y0;
            for (let yy = Math.max(0, y0 - 10); yy < Math.min(H, y0 + 11); yy++) { if (rk[yy * W + px] === want) { edge = yy; break; } }
            g.fillRect(px, edge - 2, 1, 2);
          }
        } else g.fillRect(tx * TS, ty * TS + TS - 2, TS, 2);
        g.globalAlpha = 1;
      }
    }
    this.layers.push('cliff-lip');
  }

  /** One ledge, from tile x0 to x1 on row ty, dropping `drop` terraces. */
  drawLedge(g, pal, x0, x1, ty, drop) {
    const tall = 6 * drop;
    const x = x0 * TS, w = (x1 - x0 + 1) * TS, y = ty * TS;   // inside render()'s HUD translate already
    g.fillStyle = pal.rock2 || pal.wall2;
    g.fillRect(x, y + TS - tall, w, tall + 2);          // the face, overhanging
    g.fillStyle = pal.shade || pal.ground;
    g.fillRect(x, y + TS - tall, w, 2);                 // the lip, catching light
    g.fillStyle = pal.stoneEdge || pal.rock2;
    g.fillRect(x, y + TS + 1, w, 1);                    // where face meets ground
    g.fillStyle = 'rgba(10,8,16,0.34)';
    g.fillRect(x, y + TS + 2, w, 2 + drop);             // and its shadow
  }


  /* `opts.backOnly` draws the ground up to the bodies and stops; `opts.frontOnly`
   * draws only what goes OVER the bodies (the front blades, the ledges, the
   * canopy), with `opts.feet` the player's feet in this screen's own pixels.
   * The camera path (LS-164) draws every visible screen's back, then the
   * bodies once, then every visible screen's front -- so a crown that reaches
   * over a seam still covers you, and the grass still stands in front of your
   * legs. Drawn whole per screen with the bodies last, as it was, the forager
   * stood over every tree and every blade (Vijay: "i'm able to stand over
   * trees", "grass covering player stopped working"). */
  /** The current screen's turf picture, painted again now (a cut, a regrowth). */
  turfRepaintAt = -1e9;
  turfWantsRepaint = false;
  refreshTurfArt(now = true) {
    const p = this.partsCache.get(this.area.x + ',' + this.area.y);
    if (!p || !p.rebakeTurf || p.turf !== this.turf) return;
    // a cut repaints at once (and not twice in five frames); a tuft standing again waits for the half-second beat, or a
    // field regrowing cell by cell painted a full-screen canvas every frame (1,782 in thirty seconds, measured)
    if (!now) { this.turfWantsRepaint = true; return; }
    if (this.frame - this.turfRepaintAt < 15) { this.turfWantsRepaint = true; return; }   // a swing cuts on several frames: one picture for the swing
    /* A FEW FRAMES, NOT ONE (LS-188, 2026-09-11). The whole turf picture was
     * repainted in the frame of the cut -- 35-50 ms on a 4x phone, every cut
     * and every regrowth beat. The painter already yields seven times a
     * screen; the repaint is a job now, two steps a frame, and the old
     * picture is drawn until the new one is whole. */
    if (p.rebakeTurfSteps) { this.turfJob = { p, it: p.rebakeTurfSteps() }; this.turfRepaintAt = this.frame; this.turfWantsRepaint = false; this.stepTurfJob(); return; }
    this.turfArt = p.rebakeTurf(); this.turfRepaintAt = this.frame; this.turfWantsRepaint = false;
  }
  /** Two steps of a turf repaint in flight; the picture swaps in when the job is done. A screen change drops it. */
  stepTurfJob() {
    const job = this.turfJob; if (!job) return false;
    if (job.p.turf !== this.turf) { this.turfJob = null; return false; }
    let r = job.it.next();
    if (!r.done) r = job.it.next();
    if (r.done) { job.p.turfArt = r.value; this.turfArt = r.value; this.turfJob = null; }
    return true;
  }

  /* THE CANOPY, PAINTED EVERY THIRD FRAME (LS-227): the crowns sway with the
   * wind and were drawn one picture each every frame -- 286 drawImages a
   * frame at a wooded lair, the largest count left on the glass. Each
   * screen's canopy goes into a canvas keyed by the sway's frame in threes
   * and the bake it came from, so a still frame is one drawImage. */
  canopyLayers: Map<string, { canvas: any; sig: string }> = new Map();
  drawCanopyCached(g, parts) {
    if (typeof document === 'undefined' || !parts.coords || !parts.canopy) { drawCanopy(g, parts.canopy, this.frame); return; }
    const key = parts.coords.x + ',' + parts.coords.y;
    // ...and the same for the crowns: a neighbour's wood moves a third as often (see `drawBladesCached`)
    const ownC = !parts.coords || (parts.coords.x === this.area.x && parts.coords.y === this.area.y);
  /* THE FOUR SCREENS DO NOT ALL REBAKE ON THE SAME FRAME (2026-09-07; Vijay:
   * "i want it to be as smooth as butter"). The signature was
   * `floor(frame / cadence)`, which is the SAME NUMBER for every screen, so all
   * four on the glass changed signature together: one frame paid for four
   * rebakes and the next nine paid for none. Measured on a throttled phone at
   * rung 1, that is exactly the shape of the frame times -- a median of 16.7 ms
   * and a p75 of 33.3, with 34% of frames over budget, and render costing 16.3
   * ms on a slow frame against 9.0 on a fast one.
   *
   * A phase per screen spreads the same work across the cadence window. No
   * screen rebakes more often than it did; they simply stop queueing. */
    const cadC = foliageCadence(this.quality) * (ownC ? 1 : 3);
    const phaseC = ((parts.coords.x * 5 + parts.coords.y * 3) % cadC + cadC) % cadC;
    const sig = Math.floor((this.frame + phaseC) / cadC) + '|' + (parts.canopy.bakeId || (parts.canopy.bakeId = ++this.bakeIds));
    let e = this.canopyLayers.get(key);
    if (!e) { const canvas = document.createElement('canvas'); canvas.width = PW; canvas.height = PH; e = { canvas, sig: '' }; this.canopyLayers.set(key, e); if (this.canopyLayers.size > 12) { const first = this.canopyLayers.keys().next().value; this.canopyLayers.delete(first); } }
    if (e.sig !== sig) { const cg = e.canvas.getContext('2d'); cg.clearRect(0, 0, PW, PH); drawCanopy(cg, parts.canopy, this.frame); e.sig = sig; }
    g.drawImage(e.canvas, 0, 0);
  }
  bakeIds = 0;

  /* THE BLADES, PAINTED EVERY THIRD FRAME (Vijay's phone at q0): the standing
   * grass swayed live at thirty-eight hundred fillRects a frame, the single
   * biggest cost on the glass. Each screen's blades -- the half behind the
   * feet and the half in front -- go into a canvas keyed by the sway's frame
   * in threes, the feet's row in fours, and a hash of the turf's cut states,
   * so a cut or a regrowth repaints at once and a still frame costs one
   * drawImage. On the desk the sway at twenty frames a second reads the same. */
  bladeLayers: Map<string, { canvas: any; sig: string; lip: number }> = new Map();
  drawBladesCached(g, parts, pal, feet, front) {
    if (typeof document === 'undefined' || !parts.coords) {
      drawBlades(g, parts.turf, COLS, ROWS, this.frame, pal, parts.flora, this.seedNum, parts.turfOx || 0, parts.turfOy || 0, { at: feet, front });
      return;
    }
    // the cut in coarse stages, or a regrowing field changed the hash every frame and the cache never hit
    let h = 7; for (const row of parts.turf) for (const c of row) h = (h * 31 + (c ? ((c.cut | 0) > 0 ? 2 : 1) : 0)) | 0;   // cut or standing: the blades know no stages
    const key = parts.coords.x + ',' + parts.coords.y + (front ? 'f' : 'b');
    // Keep precise depth where foliage can overlap the posed body, including
    // a neighbouring screen at a horizontal seam. Distant columns retain the
    // cheaper cadence/buckets; out-of-screen foot movement changes no sorting.
    const screenX = parts.coords.x * PW;
    const bodyLeft = Math.round(this.player.x) + RIG_OX;
    const nearPlayer = bodyLeft + RIG_W > screenX && bodyLeft < screenX + PW;
    const footStep = nearPlayer ? 1 : this.quality >= 3 ? 4 : this.quality >= 2 ? 8 : 16;
    const depthRow = Math.max(-1, Math.min(PH, Math.floor(feet / footStep) * footStep));
    /* A NEIGHBOUR'S GRASS SWAYS IN THE CORNER OF THE EYE (2026-09-06; Vijay: "mobile is still lagging
     * significantly"). The camera scrolls, so FOUR screens are always on the glass, and measured at 4x CPU
     * throttle the three that are not the player's cost 36.7 ms of a 66.7 ms frame -- 55% of it. Drawing only the
     * player's screen more than doubles the frame rate, which is not something the game can do, but slowing what
     * those three REBAKE is. A blade layer costs 0.01 ms to blit and 2.37 ms to rebake, and 38% of calls were
     * rebaking; a neighbour's now does so a third as often. The sway you are looking at is untouched. */
    const own = !parts.coords || (parts.coords.x === this.area.x && parts.coords.y === this.area.y);
    const cadence = foliageCadence(this.quality) * (own ? 1 : 3);
    /* ...and the blades take a phase of their own, for the same reason and off
     * the same screen coordinates (see the note in `drawCanopyCached`). */
    const phase = parts.coords ? ((parts.coords.x * 5 + parts.coords.y * 3) % cadence + cadence) % cadence : 0;
    const sig = Math.floor((this.frame + phase) / cadence) + '|' + depthRow + '|' + h + '|' + (parts.turf === this.turf ? 'l' : 'p');
    let e = this.bladeLayers.get(key);
    if (!e) { const lip = (globalThis as any).__noLip ? 0 : BLADE_LIP; const canvas = document.createElement('canvas'); canvas.width = PW; canvas.height = PH + lip; e = { canvas, sig: '', lip }; this.bladeLayers.set(key, e); if (this.bladeLayers.size > 24) { const first = this.bladeLayers.keys().next().value; this.bladeLayers.delete(first); } }
    if (e.sig !== sig) {
      // through a pixel sink (LS-227): the blades are a thousand one-pixel columns a screen, and a byte a pixel is cheaper than a rect a pixel
      const sink = pixelSink(e.canvas, e.lip);
      drawBlades(sink, parts.turf, COLS, ROWS, this.frame, pal, parts.flora, this.seedNum, parts.turfOx || 0, parts.turfOy || 0, { at: feet, front });
      sink.flush();
      e.sig = sig;
    }
    g.drawImage(e.canvas, 0, -e.lip);
  }

  drawWorld(parts, bodies, opts: any = {}) {
    const g = this.g;
    const pal = parts.pal || this.pal;
    const feet = opts.feet !== undefined ? opts.feet : (bodies ? this.player.y + this.player.h : -1e9);
    if (!opts.frontOnly) {
    // `drawTurf` IS the roots blit — the two paths used to call it two
    // different ways, which is how they drifted in the first place.
    if (parts.soilArt) g.drawImage(parts.soilArt, 0, 0);
    this.drawBiomeEdges(g, parts);
    drawTurf(g, parts.turf, parts.turfArt, COLS, ROWS);
    this.drawFrost(g, parts);   // rime where the ground stays cold (LS-240) -- of THIS screen, not the player's
    this.cachedLayer(g, parts, 'cliffs', (cg) => this.drawCliffs(cg, parts));
    this.drawCastShadows(g, parts);   // what stands up throws its own shape on what lies flat -- after the cliffs, whose painted mass is the stencil
    this.layers.push('turf');
    /* NOTHING OF THE CANOPY IS DRAWN UNDER THE BODIES. The mass of the leaves
     * used to go here, under everything that walks, with only the crowns
     * redrawn on top: a body on a treeline sat over the wood. "I should
     * never render over tree tops." The whole canopy is drawn after the
     * bodies now (see 'canopy-front'); this layer is the ruins' turn only. */
    this.layers.push('canopy');
    /* RUINS ARE NEVER OBSCURED BY TREES.
     *
     * Reported from play in round 16 and fixed there for the TILE layer: the
     * clearing around a standing stone is cut after the treeline, so no tree
     * GROWS over one. The canopy is a second layer and was never told — a
     * crown is a disc centred on a neighbouring tree and it spills, and
     * `drawCanopy` then lays a live lit cap on top of that.
     *
     * It stayed invisible until round 36, because the crown lattice was
     * per-screen; fixing it moved every crown in the game and one of them
     * landed on a stone's foot. Measured on rendered pixels: 22 dark px of
     * inscription, then 0. A stone you cannot read is worse than the seam the
     * lattice fix was for.
     *
     * Clipping the crowns away was tried first and did not hold, because more
     * than one pass paints foliage. Drawing the made things again, after all
     * of it, is the rule stated directly — and it is cheap: a handful of tiles
     * on the screens that have any. */
    if (parts.canopy) this.drawMade(parts);
    // the blades BEHIND the player: their base is higher up the screen than
    // his feet, so they are further away
    if (parts.turf) this.drawBladesCached(g, parts, pal, feet, false);
    this.layers.push('blades-back');
    /* THE THINGS THAT STAND, STANDING ON THE TURF. Every tile is drawn once
     * under the soil and the turf, which is right for the ground and wrong
     * for a boulder, a cairn or a standing stone: the field now grows grass
     * under them, and the grass buried their feet -- "these decals are
     * getting covered up". They are drawn again here, over the turf and the
     * back blades, so the meadow surrounds them instead (LS-218). */
    if (parts.tiles && parts.palAt) this.drawTiles(parts.tiles, parts.palAt, parts.palKey, 0, 0, parts.coords, BARE_IN_WILD);   // only the kinds with a bare base: a tree tile's square of ground over the turf was the grid again
    this.layers.push('props');
    this.cachedLayer(g, parts, 'decals', (cg) => { for (const d of parts.decals || []) drawDecal(cg, d, pal); });   // static, so painted once a screen, not a thousand rects a frame
    this.layers.push('decals');
    for (const c of parts.critters || []) drawCritter(g, c, this.frame);
    this.layers.push('critters');
    // ...and now the ground is finished, the lit edge of every drop on it
    this.cachedLayer(g, parts, 'lip', (cg) => this.drawCliffLip(cg, parts));
    if (bodies) {
      const order = this.enemies.slice().sort((a, b) => a.y - b.y);
      for (const e of order) {
        if (e.hiding) { g.globalAlpha = 0.45; e.draw(g, this); g.globalAlpha = 1; }   // in the thicket (LS-176 step 6)
        else e.draw(g, this);   // the body is drawn FOR a game: see `drawTrim`, which used to reach for the page's global instead
      }
      for (const b of this.worldHulls()) if (this.hullNear(b)) this.drawBoat(g, b);   // ...and nothing off the window is drawn (2026-09-20)
      this.player.draw(g, this);   // ...INCLUDING WHILE DYING (2026-09-10): this read `if (this.mode !== 'dying')`, so the death was a vanishing
      this.drawBoatOver(g);   // ...and the gunwale over the legs
      for (const sh of this.shots) sh.draw(g, this);
      for (const f of this.fx) if (f.kind !== 'wind') f.draw(g);   // the wind is drawn last of all, see render (LS-209)
    }
    this.layers.push('bodies');
    }
    if (opts.backOnly) return;
    // THE BLADES GO OVER THE BODIES. Standing in tall grass should bury your
    // legs — it is what makes a field something you are IN rather than
    // something you are on top of.
    if (parts.turf) this.drawBladesCached(g, parts, pal, feet, true);
    // the layer trace names each pass once; the per-screen front passes of the
    // camera path repeat the same three and stay out of it
    if (!opts.frontOnly) this.layers.push('blades-front');
    this.cachedLayer(g, parts, 'ledges', (cg) => this.drawLedges(cg, parts));
    if (!opts.frontOnly) this.layers.push('ledges');
    // AND THE LEAVES GO OVER THE BODIES, for the same reason. A crown reaches
    // past the trunks it grows from, so walking a treeline puts you UNDER the
    // edge of the wood. Only the crowns are redrawn, never the mass fill.
    this.drawCanopyCached(g, parts);          // the whole canopy, over the bodies, from a picture painted every third frame
    this.drawBossBars(g);                     // and the beasts' bars over the canopy (the art lens, round 47)
    if (parts.canopy) this.drawMade(parts);
    if (!opts.frontOnly) this.layers.push('canopy-front');
  }

  /** A thrown blade wears the stack it was thrown from, the same way a swing does. */
  wearThrown(kind, slot) {
    if (!kind) return false;
    return wearWeapon(this.state, kind, slot);
  }

  /** Is a crown overhead? The tiles around the player, and one row further up, because a crown is painted above the
   *  trunk it grows from and hangs down over whoever stands below it. */
  underCrown() {
    const tx = Math.floor(this.player.cx / TS), ty = Math.floor(this.player.cy / TS);   // world tiles: a crown over a seam shades both sides
    for (let dy = -2; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const tile = this.tileAt(tx + dx, ty + dy);
      if (tile === T.TREE) return true;
    }
    return false;
  }

  /** Only ever clears THIS seed's slot — on winning it, never on starting
   *  another world. Other runs are untouchable from here. */
  clearSave() { return S.clearSave(this); }

  // --------------------------------------------------------------- render
  /** THE ONE PICTURE NEEDS A SCENE (2026-09-14). The game is in 3D and there is no flat view to fall back to, so a
   *  device that cannot give us a WebGL2 context is told so in the world's own rows rather than shown a second game.
   *  The readout is still drawn over it, so the frame is never blank. */
  drawNeedsScene(g: any) {
    g.save();
    g.fillStyle = UI.ink; g.fillRect(0, 0, VW, VH - HUD_H);   // the caller has already translated to the playfield and clipped to it (2026-09-17): this was offset by the HUD's height twice
    g.fillStyle = 'rgba(160,150,130,0.9)';
    const lines = ['this world is drawn in 3d', 'and needs webgl 2', '', 'try another browser'];
    let y = 60;
    for (const line of lines) { if (line) drawText(g, line, Math.round((VW - line.length * 6) / 2), y, UI.linen); y += 12; }
    g.restore();
  }

  renderCount = 0; prepAt = -1;
  render() {
    this.renderCount++;
    const g = this.g;
    this.onWorldEffects?.((this.mode === 'play' || this.mode === 'dying') && !this.paused && !this.bagOpen);
    // reset the layer trace each frame; `test/soak.ts` reads it to compare a
    // transition against ordinary play
    this.layers = [];
    /* WHAT IS ON THE GLASS DECIDES WHETHER THERE IS WATER ON IT, AND ONLY THE
     * PATH THAT IS GOING TO ANSWER IT MAY ASK (2026-09-06; Vijay: "mobile is
     * still lagging significantly").
     *
     * These two were called every frame, and then a few lines down the camera
     * block called `cameraWater` and `cameraLight`, which compose the window's
     * own buffers and hand those over instead -- so the first answer was built
     * and uploaded and then thrown away. Worse than wasted: both paths reach
     * the presenter through the same guard (`_wetKey`, `_litKey`), so the two
     * keys alternated A, B, A, B and NEITHER ever matched. The guard whose
     * whole job is "do not re-upload a texture that has not moved" could never
     * fire once.
     *
     * Measured on a throttled phone in steady play: 404 draws, 808 water
     * hand-offs, 809 light hand-offs -- two of each a frame, every one
     * uploading a 256x264 texture, 81 uploads a second and 5.5 million pixels.
     *
     * The title and the win screen return before the camera block, so they
     * still ask here; everything else is answered once, by the camera. */
    if (this.mode === 'title' || this.mode === 'win') { this.pushWater(); this.pushLight(); }
    /* THE WORLD'S ROWS ARE LEFT EMPTY WHEN THE DISPLAY IS COMPOSITING THEM (2026-09-14). The scene's picture is
     * uploaded into the presenter's own context now rather than copied into this canvas, so this canvas must carry
     * the readout and the flat overlays ONLY -- ink under the readout, and nothing at all over the world, whose alpha
     * is what the composite blends by. A frame where the scene fails still fills everything, since worldToDisplay is
     * false and the copy happens as before. */
    /* THE DECISION IS MADE BEFORE THE CLEAR, NOT AFTER IT (2026-09-14). Reading last frame's outcome meant the FIRST
     * frame of a scene filled the world's rows with ink and the composite then blended the world against opaque ink --
     * a black world, which is what view3d.ts read the moment it turned the view on. Whether the presenter will take the
     * world is knowable now: it is a question about the display, not about the frame. */
    let noticeFrame = false;   // a frame with no scene and no art picture is a NOTICE: nothing else paints the world's rows
    const handing = !!this.view3d && !this.noWorld && (() => { const d: any = (globalThis as any).display; return !!(d && typeof d.setWorldSource === 'function' && d.ok && d.gl && !d.gl.isContextLost()); })();
    (this as any).worldToDisplay = handing;
    g.fillStyle = UI.ink;
    if (handing) { g.clearRect(0, 0, VW, VH); g.fillRect(0, 0, VW, HUD_H); }
    else g.fillRect(0, 0, VW, VH);

    /* THE INKED SCREENS (2026-09-28, LS-479): the title, the bag and the map drawn in ink at the glass's own size and laid
       over the frame by the display; the game's own pixel screens are still drawn underneath them */
    const inkUi = !(typeof location !== 'undefined' && /[?&]pixelfloor=1/.test(location.search)) && !(globalThis as any).__noInkUi;
    const disp: any = (globalThis as any).display; const uiK = disp && typeof disp.worldScale === 'function' ? Math.max(1, disp.worldScale() | 0) : 1;
    const setUi = (c: any) => { if (disp && typeof disp.setUiSource === 'function') disp.setUiSource(c); };
    (this as any).inkUiOn = inkUi && !!disp && !!(this as any).worldToDisplay; (this as any).inkPlay = null;   // the painters leave what the ink layer should letter this frame
    if (this.mode === 'title') { this.drawTitle(); setUi(inkUi ? IU.present('title', () => IU.drawTitleInk(this, uiK), uiK) : null); return; }
    if (this.mode === 'win') { this.drawWin(); setUi(inkUi ? IU.present('win', () => IU.drawEndInk(this, uiK, 'win'), uiK) : null); return; }

    /* THE COMBAT CAMERA SHAKES, IT DOES NOT ZOOM (LS-203). A zoom was built
     * first -- four and a half percent about the playfield, eased in while a
     * swing was out -- and Vijay, having seen it: "do camera shake instead of
     * zoom for combat". So the presenter's zoom stays at one, and the kick is
     * the shake: every swing jolts the frame a pixel for a few frames, every
     * landed blow jolts it by its weight (see `hurt`), and a kill more. The
     * shake is the game's own, a translate of the canvas under the world. */
    if (this.zoom !== 1) { this.zoom = 1; if (this.onZoom) this.onZoom(1, 0.5, 0.5); }
    g.save();
    g.translate(0, HUD_H);
    g.beginPath(); g.rect(0, 0, PW, PH); g.clip();
    if (this.shake) {
      const m = this.shake.mag * (this.shake.t / 20);
      g.translate(Math.round((Math.random() - 0.5) * m), Math.round((Math.random() - 0.5) * m));
    }

    {
      /* EVERY SCREEN THE WINDOW STANDS ON, each at its own offset.
       *
       * `drawWorld` has taken a parts object and drawn it under an arbitrary
       * canvas translate for several rounds — the scroll transition called it
       * twice a frame to do exactly this for two screens. All that changes
       * here is that the offsets come from a camera instead of from an easing
       * curve, and there is no longer an end to the slide. */
      const cam = this.camera();
      // the presenter's buffers follow the window, not the screen under the
      // player — see `cameraWater`
      if (this.view3d) { if (this.onWater) this.onWater(null, HUD_H / (ROWS * TS + HUD_H), 0, 0, -1); } else this.cameraWater(cam);
      // bodies, items and flames live in world coordinates, so the only
      // offset they need is the window's own
      const here = -cam.x, hereY = -cam.y;
      const seen = this.visibleScreens(cam);
      /* THE WORLD IN TRUE 3D (LS-398, 2026-09-13): with the view on, scene3d paints the world's rows from real geometry --
       * the same camera centre, the same bodies, the same screens -- and the 2D painters below are skipped; the display's
       * relief and water masks are withheld, since the picture is lit and shaped already. Off, nothing here changed. */
      let drew3d = false;
      /* A CHECK THAT HOLDS A RULE NEEDS NO WORLD AT ALL (2026-09-14, on the way to deleting the flat branch). The rule
       * and driven checks were opening the flat painters with `3d=0` because a scene on every frame cost them minutes
       * -- but what they actually want is the game RUNNING, not a picture of it, and the flat presentation is the thing
       * being retired. With `?world=0` (game.noWorld) the world's rows are left empty and the readout is drawn over
       * them: cheaper than either renderer, and it keeps no second presentation alive. Anything measuring pixels of the
       * world says so instead -- the art checks read the painters' BAKE, and view3d.ts reads the presenter. */
      if (this.noWorld) { drew3d = true; }
      /* AND A STALE WORLD IS NEVER LEFT IN THE PRESENTER (2026-09-14): the display holds whatever canvas it was last
       * handed, so a frame that does NOT come from the scene -- the flat escape, a scene that threw -- must take the
       * hand-over back or the picture would be a live readout over a frozen world. */
      if (!this.view3d || this.noWorld) { const d: any = (globalThis as any).display; if (d && typeof d.setWorldSource === 'function' && d.worldSrc) d.setWorldSource(null); (this as any).worldToDisplay = false; }
      if (this.view3d && !this.noWorld) { if (!this.scene3d) this.scene3d = new Scene3D(); try { /* THE 3D VIEW SCROLLS BY THE CAMERA'S OWN POSITION, NOT ITS ROUNDING (2026-09-29, Vijay: "make everything feel buttery smooth"): the world is drawn at four glass pixels to one of the world's, so a camera rounded to whole world pixels scrolled in four-pixel jumps; the rounding is kept for the painters, which lay whole texels */ const cam3 = this.cam ? { ...cam, x: this.cam.x, y: this.cam.y } : cam; drew3d = !!this.scene3d.draw(this, cam3, seen); } catch (err) { console.warn('scene3d failed, back to the painters', err, (err && (err as any).stack || '').split('\n').slice(0, 5).join(' | ')); this.view3d = false; drew3d = false; } }
      /* THE FLAT PICTURE IS AN ART INSTRUMENT NOW, NOT A VIEW (2026-09-14; Vijay: "get rid of all 2d rendering codes dn
       * focus fully on 3d"). What follows composes the PAINTERS' BAKE into a flat picture. The 3D scene lays its floor
       * and its sprites from that same bake, so this composition is how the art itself is measured -- eight check files
       * read it (art, hud, seams, weather, flames, round53, pixel-grid, rig-light) and so do the flat instruments. It is
       * reachable ONLY with `?art=1`, which no menu offers and nothing remembers. A PLAYER whose device cannot give us a
       * scene gets the notice below instead, because a game with two pictures is the thing being retired.
       *
       * Deleting these lines means giving all eight of those files a way to compose the bake themselves; until that is
       * done they are the reason the code is here, and `artPicture` is the name that says so. */
      if (!drew3d && !this.paintBake) { this.drawNeedsScene(g); noticeFrame = true; }
      /* AND THE FLAT PICTURE IS NOT IN THIS FILE ANY MORE (2026-09-14). The seventy lines that composed the painters'
       * bake live in src/bake-frame.ts, which nothing in the game imports: main.ts fetches it only when the address
       * carries `?art=1`, so a player's bundle does not contain a flat renderer at all. When it is absent -- which is
       * every shipped frame -- a scene that could not draw gets the notice instead. */
      else if (!drew3d && this.paintBake) this.paintBake(this, g, seen, here, hereY);
      // The light must read this frame's posed bodies, including the canopy pass.
      /* WHAT THE 3D SCENE DOES NOT PAINT was painted TWICE (2026-09-13, then 2026-09-17). The wind's ribbons and the rain
       * are the 2D painters' and stay so -- and this block drew both at the window's offset, then the shared tail of the
       * same frame drew them again at the same offset ("THE WIND OVER EVERYTHING" and "THE RAIN FALLS IN FRONT OF THE
       * WORLD" below, neither guarded by the view). Every streak and every ribbon was laid down twice, so the downpour
       * was twice as thick in 3D as the number that asks for it. The tail is the one that runs. */
      if (drew3d) { if (this.onLight) this.onLight(null, [], null); } else this.cameraLight(cam);
      /* THE RAIN FALLS IN FRONT OF THE WORLD (LS-223, moved and thickened 2026-09-06, the art lens).
       *
       * It was drawn inside `drawWorld` BEFORE the critters, the bodies, the front blades, the ledges and the
       * canopy -- so the heaviest downpour this game can make fell BEHIND the grass. Measured, rendering the same
       * frame dry and at full rain: the picture dimmed by 3.2 LUMA and the count of cool pixels went DOWN by 2,201,
       * because the veil darkened the blues past the threshold that was counting them. A third of a run happens in
       * weather nobody can see.
       *
       * Rain is between the eye and the world, so it belongs here, after everything -- and once, over the whole
       * window, not per screen: it is the one thing in this frame that is honestly screen-space, because it falls
       * toward the camera rather than sitting on the ground. (Which is the opposite of the sun's wash and the cast
       * shadows, both of which had to be nailed to the WORLD earlier today; the test is whether a thing would still
       * be there if you turned round, and rain would not.) Three times the streaks, longer and brighter, and a veil
       * that cools instead of only darkening. */
      if (this.systems) {
        const rain = this.systems.rain(this);
        if (rain > 0.05) {
          const n = Math.round(rain * 320), fr = this.frame;
          g.fillStyle = 'rgba(200,224,238,0.70)';
          if ((this as any).inkUiOn) ((this as any).inkPlay ||= {}).rain = rain;   // the ink look draws the rain as pen strokes (inkui.drawPlayInk)
          else for (let i = 0; i < n; i++) {
            const h = mix(i, 977, 3), speed = 5 + (h & 3);
            const x = (h >>> 4) % (COLS * TS), y = (((h >>> 12) % (ROWS * TS)) + fr * speed) % (ROWS * TS + 8) - 8;
            g.fillRect(x - (fr % 2), y, 1, 5);
          }
          g.fillStyle = 'rgba(34,52,86,' + (0.22 * rain).toFixed(3) + ')';
          g.fillRect(0, 0, COLS * TS, ROWS * TS);
        }
      }
    }

    // A DARK ROOM. Not a filter over the whole screen — a ring of shadow that
    // the player, the braziers and anything on fire push back. Built out of
    // eight rectangles rather than a radial gradient so the edge is a hard
    // pixel step like everything else here.
    // ...and at night, everywhere. The gloom is the one effect that already
    // reads as "you cannot see far", and a night world is that everywhere
    // rather than in one room in twelve.
    if (this.night && !noticeFrame) this.drawGloom(g);

    /* THE SUN ACROSS THE WHOLE WINDOW (2026-09-05; placed properly 2026-09-06). One warm diagonal wash where the
     * sun comes from, cooling into violet at the far corner, so the surfaces do not read flat -- the judge's first
     * note against the reference renders was "no clear warm light source".
     *
     * IT WAS NAILED TO THE WINDOW, AND THAT IS WHAT VIJAY SAW. This baked the wash for `this.area` -- the ONE screen
     * the player stands on -- and blitted it at `drawImage(wash, 0, 0)`, the viewport's own origin, while every
     * other layer of the world is drawn inside `g.translate(ox, oy)` at the offset its screen actually sits at.
     * The camera scrolls freely and never aligns to a screen, so FOUR SCREENS ARE IN THE WINDOW AT ALL TIMES:
     * measured at four player positions, the window stood on 3,5 / 4,5 / 3,6 / 4,6 every time, with the player's own
     * screen at an offset of 81,-7 rather than 0,0. The wash is VW by PH and so is the playfield, so it COVERED the
     * window -- what was wrong was never coverage but CONTENT. Every pixel showed the field sampled for the PLAYER'S
     * screen box, so three quarters of the picture wore a neighbouring screen's light and the quarter that was right
     * was still slid eighty pixels off the ground it was computed for. Walk, and the light stayed stuck to the glass
     * while the ground moved under it; cross a seam, and `area` changed and a different stamp snapped in, jumping the
     * whole window's light in one frame -- a median of 11.2 luma and up to 37.2, measured on the field, with nothing
     * in between. Vijay, playing: "the sun lighting looks different across different chunks there's no continuity".
     * It did, and this was why.
     *
     * The field itself was never the problem -- `sunWashAt` is world-continuous arithmetic and its edges agree with
     * its neighbours' by construction. The bug was purely one of PLACEMENT, which is why the earlier fix to the
     * band's wavelength helped and did not cure it. Now every screen in the window gets its own bake, drawn where
     * that screen is, so the light is one sky over the whole picture and nothing moves when you do.
     *
     * The sun's DIRECTION is not this layer's job and never was -- the rim lights, the cast shadows and the lit
     * facets on every solid say where the light comes from. This is the weather over the ground. */
    const styleB2 = this.view3d && !(typeof location !== 'undefined' && /[?&]pixelfloor=1/.test(location.search));   /* B (2026-09-27): the scene lights the ground itself; the wash is a 256-wide dither laid over a full-resolution world */
    if (!noticeFrame && !styleB2) {
      const scam = this.camera();
      for (const [sx, sy, ox, oy] of this.visibleScreens(scam)) g.drawImage(this.sunWashFor(sx, sy), ox, oy);
      this.layers.push('sun');
    }

    /* THE WIND OVER EVERYTHING (LS-209). Vijay: "the wind needs to render
     * over everything else". A ribbon is air, and air is in front of the
     * canopy, the bodies, the cliffs and the gloom; drawn here, after every
     * world layer and before the HUD, in the window's own offset. */
    {
      const off = this.camera();
      g.save(); g.translate(-off.x, -off.y);
      if (!noticeFrame && !((this as any).inkHudOn && this.view3d)) for (const f of this.fx) if (f.kind === 'wind') f.draw(g);   // in the ink look the scene draws the wind as ink strokes (scene3d)
      // ...and the heat ribbons are `wind` fx, so they are already in the loop above (2026-09-10)
      if (!noticeFrame) { this.drawWetness(g);   // the rain's wet on the ground and on what stands on it (2026-09-11; Vijay: "we need to show wetness for objects visually")
      /* THE RIME IS DRAWN IN THE VIEW THE GAME SHIPS (2026-09-18). `drawFrost` had one caller -- `drawWorld`, which
       * only the `?art=1` escape reaches -- so the frost the docs promise under the temperature's own line ("under
       * 0.22 the ground wears frost, the bar reads COLD, and standing there chills you") was painted by nothing a
       * player sees: a screen at 0.02 and the same screen at 0.9 differed in 0 of 49,152 pixels in 3D. It belongs
       * here, with the other overlays the live world lays over the baked floor, drawn per visible screen at that
       * screen's own offset -- not in the bake, which is kept for the hour and the weather it was baked under. */
      const cam2 = this.camera();
        if (!this.view3d) for (const [sx2, sy2] of this.visibleScreens(cam2)) { g.save(); g.translate(sx2 * PW, sy2 * PH); this.drawFrost(g, { coords: { x: sx2, y: sy2 } }); g.restore(); }   /* in 3D the rime is on the ground, in the scene (2026-09-27): drawn here it lay over trees and bodies like snow */
      this.drawKindling(g);   // and the torch shows what wants to burn (LS-379)
      this.drawStoneLikeness(g); }   // a beast you have felled before, over the stone that names it (LS-294)
      g.restore();
      this.layers.push('wind');
    }
    g.restore();

    // THE SKY FLASHES (LS-381): two frames a beat before the strike, five on it, over the world and under the HUD
    if (this.stormFlash > 0) {
      g.fillStyle = 'rgba(236,240,255,' + (0.12 + 0.1 * this.stormFlash).toFixed(2) + ')'; g.fillRect(0, 0, g.canvas.width, g.canvas.height);
      /* ...AND THE BOLT ITSELF, on the strike's frames: a jagged white line from the top of the sky to the tile it
       * hit, three frames wide then one, so the eye is told where the strike went and not only that one came. */
      const ls = this.systems && this.systems.lastStrike;
      if (ls && this.systems.ticks - ls.tick < 6) {
        const cam = this.camera(), off = g.canvas.height - 192;
        const x1 = ls.x * TS + 8 - cam.x, y1 = ls.y * TS + 8 - cam.y + off;   // the strike is a world tile (LS-511)
        const rng = new RNG(mix(this.seedNum, ls.tick, 77));
        g.save(); g.strokeStyle = this.stormFlash >= 4 ? '#ffffff' : '#dfe8ff'; g.lineWidth = this.stormFlash >= 4 ? 2 : 1; g.beginPath();
        let x = x1 + (rng.int(41) - 20), y = off; g.moveTo(x, y);
        while (y < y1 - 6) { y += 6 + rng.int(6); x += rng.int(9) - 4 + (x1 - x) * 0.25; g.lineTo(Math.round(x), Math.round(Math.min(y, y1))); }
        g.lineTo(x1, y1); g.stroke(); g.restore();
      }
      this.stormFlash--;
    }
    if (!this.paused && !this.bagOpen && this.mode === 'play') H.drawLandBearings(this);   // where the land lies, from a hull (LS-461)
    if (!this.paused && !this.bagOpen && this.mode === 'play') H.drawAnswerBearings(this);   // where a beast's answer lies, on its island (LS-464)
    if (!this.paused && !this.bagOpen && this.mode === 'play' && H.drawQuarryBearings) H.drawQuarryBearings(this);   // ...and, answer in hand, where its beast is
    this.drawHud();
    if (!(this as any).inkHudOn) H.drawHudFrame(this);   // the carved frame goes on last: the readouts sit IN it (not under the inked HUD, whose paper is its own frame)
    /* THE INKED HUD (2026-09-28, LS-479): drawn at the world's own scale and handed to the display over the strip's rows
       while the world is handed over too; the bag, the map and the end screens keep the strip's own picture */
    { const d: any = (globalThis as any).display; if (d && typeof d.setHudSource === 'function') {
        const ink = !(typeof location !== 'undefined' && /[?&]pixelfloor=1/.test(location.search)) && (this as any).worldToDisplay && (this.mode === 'play' || this.mode === 'dying' || this.mode === 'gameover');   // the bag and the chart keep the inked HUD over them (2026-09-28); and the fall and the death screen keep it too, not the old pixel strip (2026-09-29)
        const K = ink ? Math.max(1, (this.scene3d && this.scene3d.ss) || 1) : 1;
        (this as any).inkHudOn = !!ink; d.setHudSource(ink && H.drawHudInk ? H.drawHudInk(this, K) : null);
        if (ink) this.g.clearRect(0, HUD_H, VW, 4); } }   // the strip's plates cast a shadow into the world's first rows; the inked HUD has none

    /* UNDER THE INK, NOTHING (2026-09-29): the pixel bag and chart were still painted beneath the inked ones -- unseen while
       the ink covered them, but the ink now washes in from the middle, and the dark pixel screen showed round the bloom.
       They still run (whatever they settle for a frame is settled) but onto a sheet nobody sees. */
    const aside = (fn: () => void) => { if (!(this as any).inkUiOn) { fn(); return; } const g0 = this.g, c0 = g0.canvas as HTMLCanvasElement;
      let sc = (this as any)._asideG as CanvasRenderingContext2D | undefined;
      if (!sc || sc.canvas.width !== c0.width || sc.canvas.height !== c0.height) { const c = document.createElement('canvas'); c.width = c0.width; c.height = c0.height; sc = (this as any)._asideG = c.getContext('2d')!; }
      sc.setTransform(g0.getTransform()); this.g = sc; try { fn(); } finally { this.g = g0; } };
    if (this.bagOpen) aside(() => H.drawBag(this));   // the bag screen over the world (LS-225)
    if (this.paused && !this.menuHold) aside(() => this.drawMap());
    { const kind = this.mode === 'gameover' ? 'gameover' : this.mode !== 'play' ? this.mode : this.paused && !this.menuHold ? 'map' : this.bagOpen ? 'bag' : 'play';   // every inked screen through one door, so each washes in and out (inkui.present)
      if (inkUi) (this as any).inkWash = IU.washState;   // a check reads it
      setUi(!inkUi ? null : IU.present(kind, () => kind === 'gameover' ? IU.drawEndInk(this, uiK, 'gameover') : this.mode !== 'play' ? null : kind === 'map' ? IU.drawMapInk(this, uiK, H.mapGeom(this)) : kind === 'bag' ? IU.drawBagInk(this, uiK) : IU.drawPlayInk(this, uiK), uiK)); }
    if (this.mode === 'gameover') { if ((this as any).inkUiOn) { this.g.fillStyle = 'rgba(14,12,18,0.85)'; this.g.fillRect(0, HUD_H, VW, PH); } aside(() => this.drawGameOver()); }   // the fall's darkness stays under the inked sheet as it blooms (only the pixel words go aside)
    /* THE LAST THIRD OF THE FALL DIMS INTO THE SCREEN (2026-09-10). The
     * death screen is an 85% wash over the picture, and arriving at it from a
     * bright meadow in one frame is the hard cut Vijay called jarring. The
     * same wash comes up over the closing frames of the fall instead, so the
     * body lies still in a darkening world and the screen it turns into is
     * already there. */
    if (this.mode === 'dying') {
      const into = 1 - Math.min(1, this.deathTimer / (DEATH_FRAMES / 3));
      if (into > 0) {
        this.g.fillStyle = 'rgba(14,12,18,' + (0.85 * into).toFixed(3) + ')';
        this.g.fillRect(0, HUD_H, VW, PH);
      }
    }
  }

  /** The shadow in a dark room, in tiles rather than pixels: a tile is lit if
   *  it is close enough to the player or to something burning. Tile-sized,
   *  because a smooth falloff in a game of hard pixels reads as a bug. */
  wakeFrom() {
    {
      // wake where you fell, not seventeen screens away at world spawn
      const back = this.respawnAt || this.world.start;
      this.area = { kind: 'wild', x: back.x, y: back.y, level: 0 };
      this.loadArea();   // what lies on the ground is the world's, and stays where it lies (LS-286, LS-511)
      // TILE-ALIGNED. `PW/2 - 7` is 121 for a 13px body, which spans 121-134
      // and 89-102 and therefore straddles the boundaries at 128 and 96: the
      // wake point ALWAYS covered four tiles, and if any of them was solid the
      // player could not move at all.
      const [bx, by] = this.bodyAtTile(back.x * COLS + COLS / 2, back.y * ROWS + ROWS / 2);
      this.player.x = bx; this.player.y = by;
    }
    /* A RESPAWN IS A CUT, NOT A TRAVEL. The window eases toward the player,
     * which is right for walking and wrong for waking up somewhere else — it
     * would sail across the map showing every screen between. Dropping the
     * camera makes the next frame place it. */
    this.cam = null;
    this.fall = null;
    this.player.dir = 'down';
    this.player.kb = null;
    this.player.invuln = 60;
    this.player.recentre();
    this.freeThePlayer();
    // ...and a place you can LEAVE. `freeThePlayer` finds somewhere the body
    // fits, which is not the same thing: a pocket ringed by rubble fits and
    // is a dead run, and its BFS will not cross the barrier to get you out
    // (correctly — that is what stops a rescue handing you a gate). A respawn
    // is the one moment the game chooses where you stand, so it has to choose
    // somewhere with a way out.
    this.wakeSomewhereOpen();
  }

  /** Move the player to the nearest tile that both fits their body and has a
   *  way out of the area. Called only on respawn. */
  wakeSomewhereOpen() {
    const p = this.player;
    if (this.bodyFits(p.x, p.y) && this.canLeaveFrom(p.cx, p.cy)) return;
    /* THE CANDIDATES ARE THIS SCREEN'S TILES, IN WORLD COORDINATES.
     *
     * `p.cx / TS` is a world tile now, and the loop below walks one screen's
     * grid — so the distances were being measured between a world tile and a
     * screen-local one, and every candidate looked equally far away. Offsetting
     * the grid into the world makes "nearest" mean nearest again. */
    const cx = Math.floor(p.cx / TS), cy = Math.floor(p.cy / TS);
    const cands = [];
    for (let y = cy - 8; y <= cy + 8; y++) for (let x = cx - 10; x <= cx + 10; x++) cands.push([x, y, Math.abs(x - cx) + Math.abs(y - cy)]);   // the ground round the body (LS-511: the screen's own tiles)
    cands.sort((a, b) => a[2] - b[2]);
    let fallback = null;
    for (const [x, y] of cands) {
      const [bx, by] = this.bodyAtTile(x, y);
      if (!this.bodyFits(bx, by)) continue;
      if (!fallback) fallback = [bx, by];
      if (!this.canLeaveFrom(x * TS + TS / 2, y * TS + TS / 2)) continue;
      p.x = bx; p.y = by; p.recentre();
      return;
    }
    if (fallback) { p.x = fallback[0]; p.y = fallback[1]; p.recentre(); }
  }


  /* ------------------------------------------------------------- audio
   *
   * Lifted into `src/audio.ts`; see the note at the top of that file. These
   * delegators exist so that nothing else in the project had to learn about
   * the move — every `this.sfx(...)` and `this.music()` in this file, in
   * `entities.js` and in the suite still reads the same. */
  ac() { return A.ac(this); }
  noise(dur, from, to, v, q = 1) { return A.noise(this, dur, from, to, v, q); }
  blip(f, to, d, type, v, at = 0, detune = 0) { return A.blip(this, f, to, d, type, v, at, detune); }
  phrase(notes, type = 'triangle', v = 0.05, step = 0.075) { return A.phrase(this, notes, type, v, step); }
  /** `x` is the emitter's position in screen pixels; omit it for a cue with
   *  no place in the world, which stays centred. */
  sfx(name, x?: number) { if (typeof x === 'number' && this.cam) { const c = this.camera(); if (x < c.x - PW / 2 || x > c.x + PW * 1.5) return undefined; } return A.cue(this, name, x); }   // a creature half a screen past the glass is not heard (LS-511: the neighbours' tick was silent)   // through the mix rules (LS-197)
  music() { return A.music(this); }
  trackFor() { return A.trackFor(this); }
  ambience() { return A.ambience(this); }
  rampTo(param, value) { return A.rampTo(this, param, value); }
  hushAmbience() { return A.hushAmbience(this); }

  /* ------------------------------------------------------- presentation
   *
   * Lifted into `src/hud.ts`; see the note at the top of that file. As with
   * the audio delegators, these exist so no call site had to learn about the
   * move — `this.drawHud()` reads the same here, in `main.js` and in the
   * suite. Nothing in `hud.js` mutates game state. */
  drawGloom(g) { return H.drawGloom(this, g); }
  /* The made things, drawn again over the wood. See `drawWorld`.
   *
   * It takes the WHOLE `parts` and reads the grid out of it, with no fallback
   * to `this.tiles`. The fallback is what produced the bug: during a scroll
   * `drawWorld` is called twice, and the outgoing call's parts had no tiles,
   * so the screen being left was drawn with the stones of the screen being
   * entered. A missing grid is now nothing drawn, which is a visible absence
   * rather than a confident lie. */
  drawMade(parts) {
    const tiles = parts && parts.tiles;
    if (!tiles) return;
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        if (!MADE_TILES.has(tiles[y][x])) continue;
        H.drawOneTile(this, tiles, parts.pal || this.pal, parts.palKey || this.palKey,
          x, y, 0, 0, parts.coords);
      }
    }
  }

  /* `coords` names the SCREEN being drawn, and only the scroll path has one
   * to give — ordinary play is drawing the screen the game is on. Optional,
   * as it always was in fact. */
  drawTiles(tiles, pal, palKey, ox, oy, coords?, only = null) { return H.drawTiles(this, tiles, pal, palKey, ox, oy, coords, only); }
  /** A fresh uniform tile of `kind` in the palette a field label names (biome blend and level), for the ground field's
   *  texels where this screen has no tile of that kind at that level (2026-09-12; the terrace tone seam). */
  synthTexels(parts, kind, label) {
    const i = label >> 3, lv = label & 7;
    let key = null;
    if (this.palIds) for (const [k, id] of this.palIds) if (id === i) { key = k; break; }
    if (key === null) return null;
    const bits = key.split('|');
    const base = bits.length >= 3 ? terracePal(bits[0], lv, bits[1], +bits[2]) : terracePal(key, lv);
    const wq = parts.wetQ || 0;
    const pal = wetPalette(base, wq / 2), palKey = key + '|' + lv + (wq ? '|w' + wq : '') + '|synth';
    const c = tile(kind, pal, palKey, 0, 0);
    if (!c || !c.getContext) return null;
    return c.getContext('2d').getImageData(0, 0, TS, TS);
  }
  /** The per-tile palette of a wild screen: the biome blend and the terrace level, tile by tile. */
  wildPalAt(parts, sx, sy) {
    const step = parts.land ? parts.land.step : null;
    const blend = this.world ? this.world.biomeBlendAt : null;
    const wq = parts.wetQ || 0, wsuf = wq ? '|w' + wq : '';   // a wet screen's tiles are painted in the wet palette (2026-09-11)
    const tiles = parts.tiles || null;   // the parts' own tiles: a stub game in the unit tests has no screens to ask
    return parts.biomeTiles
      ? (x, y) => {
        const lv = step ? step(x, y) : 0;
        if (tiles && tiles[y] && tiles[y][x] === T.SEA) {   // open sea is one water in every country (LS-383)
          const b = parts.biomeTiles[y][x];
          return { pal: { ...terracePal(b, lv), ...SEA_WATER }, key: b + lv + '|sea' };
        }
        if (blend) {
          const m = blend(sx * COLS + x, sy * ROWS + y);
          return { pal: wetPalette(terracePal(m.a, lv, m.b, m.t), wq / 2), key: m.a + '|' + m.b + '|' + m.t + '|' + lv + wsuf };
        }
        const b = parts.biomeTiles[y][x];
        return { pal: wetPalette(terracePal(b, lv), wq / 2), key: b + lv + wsuf };
      }
      : parts.pal;
  }
  /** The ground-field overlay of a wild screen (LS-218), baked once from the screen's own tile layer. */
  groundFieldFor(parts, sx, sy, palAt) {
    const steps = this.groundFieldSteps(parts, sx, sy, palAt);
    let result = steps.next();
    while (!result.done) result = steps.next();
    return result.value;
  }
  *groundFieldSteps(parts, sx, sy, palAt) {
    if (parts.groundField) return parts.groundField;
    if (parts.groundField === null) return null;   // baked once and found nothing to repaint
    if ((globalThis as any).__noField || typeof document === 'undefined' || !parts.tiles) return undefined;
    try {
      // the kinds are made with the screen's paint; a parts object baked for the
      // roster (no paint) gets them here, the first time it is drawn
      if (!parts.groundKinds) parts.groundKinds = fieldKinds(this.world, parts.groundTiles || parts.tiles, COLS, ROWS, sx, sy);
      const layer = document.createElement('canvas');
      layer.width = COLS * TS; layer.height = ROWS * TS;
      const lg = layer.getContext('2d');
      if (!lg || !lg.getImageData) return undefined;
      const was = this.g;
      this.g = lg;
      try { this.drawTiles(parts.propGround || parts.tiles, palAt, parts.palKey, 0, 0, { x: sx, y: sy }); } finally { this.g = was; }
      parts.groundField = yield* bakeGroundFieldSteps(layer, parts.propGround || parts.tiles, COLS, ROWS, parts.groundKinds, this.world ? this.world.seedNum : 0, sx * COLS, sy * ROWS, parts.rockKinds || null, parts.groundPals || parts.groundLevels || null, parts.groundPals ? parts.palOf : (parts.levelOf || null), typeof palAt === 'function' ? (tx, ty) => palAt(tx, ty).pal : null,
        parts.groundPals ? (k, lb) => this.synthTexels(parts, k, lb) : null);
      if (!parts.groundField) parts.groundField = null;
    } catch (e) { parts.groundFieldError = String(e && (e as any).stack || e).slice(0, 300); return undefined; }   // not cached: tried again next frame
    return parts.groundField;
  }
  text(str, x, y, col = UI.paperDim, size = 8) { return H.text(this, str, x, y, col, size); }
  textWidth(str, size = 8) { return H.textWidth(this, str, size); }
  textMid(str, y, col, size = 8) { return H.textMid(this, str, y, col, size); }
  /* THE FOUND FORGES (2026-09-12; Vijay: "do all that"). Nothing invited a player to set a rock beside a brazier, and
   * the recipes lived only on the docs page. So the world lays two forges out. Near the glade, on the home island: a
   * lit brazier, a rock, and a raft HALF LAID on it -- two logs and one cutting -- the rock sparking; the hedge the
   * player cuts on the way in gives the last cutting, and the first forge teaches by being finished. On the sow's
   * island: a boat half laid, the raft and the rivet, so the way home is taught the same way once the hide is in hand.
   * The tiles go through the player's own set-down overlay (they are saved with it) and the laid things are seeded
   * once, remembered in `state.forgeSeeded`. */
  _forges: any[] | null = null;
  foundForges() {
    if (this._forges) return this._forges;
    if (!this.world || !this.world.start) return [];
    const W = WORLD_W, H = WORLD_H, start = this.world.start;
    const isleOf = (x, y) => this.world.island ? this.world.island[y * W + x] : 0;
    const home = isleOf(start.x, start.y);
    const raw = (x, y) => generateScreen(this.world, x, y).tiles;   // the bare tiles: this runs from screenAt itself
    /* `soft`: the ground a forge can be built on. Plain ground and moss everywhere by preference; on a cramped
     * island, where the ring and then the whole island have both failed, tuft and sand will take a brazier and a
     * rock as well (2026-09-18 -- one world in forty had a sow whose island offered no patch at all outside her own
     * lair screen, and lost the boat's teaching and its free raft and rivet with it). */
    const patchOn = (x, y, soft = false, least = false) => {
      const t = raw(x, y);
      const ok = (v: number) => v === T.GROUND || v === T.MOSS || (soft && (v === T.TUFT || v === T.SAND));
      for (let ty = 3; ty < ROWS - 3; ty++) for (let tx = 3; tx < COLS - 4; tx++) {
        if (least) {
          /* THE LAST RESORT (2026-09-18): the two tiles the forge stands on, and one tile beside the rock to stand on
           * while you lay things. A cramped island is still owed its forge -- without it the boat is never taught,
           * and its free raft and rivet never laid. */
          if (!ok(t[ty][tx]) || !ok(t[ty][tx + 1])) continue;
          const beside = [[tx + 2, ty], [tx - 1, ty], [tx + 1, ty - 1], [tx + 1, ty + 1]].some(([bx, by]) => t[by] && ok(t[by][bx]));
          if (beside) return [tx, ty];
          continue;
        }
        let open = true;
        for (let dy = -1; dy <= 1 && open; dy++) for (let dx = -1; dx <= 2; dx++) if (!ok(t[ty + dy][tx + dx])) { open = false; break; }
        if (open) return [tx, ty];   // the brazier at tx, the rock at tx + 1, open ground round both
      }
      return null;
    };
    const thornOn = (x, y) => raw(x, y).some((row) => row.includes(T.THORN));
    const out: any[] = [];
    const pick = (cands: [number, number][], laid: string[], name: string, soft = false, least = false) => {
      for (const [x, y] of cands) { const at = patchOn(x, y, soft, least); if (at) { out.push({ name, x, y, tx: at[0] + 1, ty: at[1], laid }); return; } }
    };
    // the home forge: a screen of the home island within three of the glade, not the glade's own, with thorn on it
    const near: [number, number][] = [];
    for (let r = 1; r <= 3; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
      const x = start.x + dx, y = start.y + dy;
      if (x < 0 || y < 0 || x >= W || y >= H || isleOf(x, y) !== home || !this.world.dist.has(x + ',' + y)) continue;
      near.push([x, y]);
    }
    /* the piece left out is the piece the island can give: a cutting where thorn grows within a walk of the glade, a log
     * where it does not (raft-0's home island has trees and no thorn) -- trees stand on every island */
    const thornNear = near.some(([x, y]) => thornOn(x, y));
    const homeLaid = thornNear ? ['log', 'log', 'cuttings'] : ['log', 'cuttings', 'cuttings'];
    const opening = this.state.forgeLayout >= 1
      ? openingWorkshop(raw(start.x, start.y), spawnTile(this.world.seedNum)) : null;
    if (opening) {
      const resource = this.state.forgeLayout === 2
        ? openingResource(raw(start.x, start.y), spawnTile(this.world.seedNum), opening) : null;
      out.push({name: 'home', x: start.x, y: start.y, tx: opening[0] + 1, ty: opening[1],
        laid: resource ? ['log','log','cuttings'] : homeLaid, ...(resource ? {resource} : {})});
    }
    else pick(near.filter(([x, y]) => thornOn(x, y)).concat(near), homeLaid, 'home');
    // the sow's forge: the ring of her island, nearest her lair first
    const sow = this.beastLairs().find((l) => l.kind === 'sow');
    if (sow) {
      /* HER ISLAND, WHICHEVER ISLAND THAT IS (2026-09-18). The ring also refused the home island -- which is right
       * when her lair is elsewhere and wrong when it is not: `beastLairs` falls back to the home island when no
       * other will take her, and then this filter emptied the ring and the world laid no second forge at all. Her
       * own screen is kept clear by the `(dx || dy)` beside it, which is what the clause was for. */
      const ring: [number, number][] = [];
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) { const x = sow.x + dx, y = sow.y + dy; if ((dx || dy) && x >= 0 && y >= 0 && x < W && y < H && isleOf(x, y) === isleOf(sow.x, sow.y)) ring.push([x, y]); }
      ring.sort((a, b) => Math.hypot(a[0] - sow.x, a[1] - sow.y) - Math.hypot(b[0] - sow.x, b[1] - sow.y));
      pick(ring, ['raft', 'rivet'], 'sow');
      /* ...AND IF THE RING IS ALL CLIFF AND WATER, THE REST OF HER ISLAND (2026-09-18). A forge needs a four-by-three
       * patch of plain ground, and on a cramped island no screen of the five-by-five ring had one: the boat was
       * never taught and the free raft and rivet never laid. The whole island, nearest her lair first. */
      if (!out.some((f) => f.name === 'sow')) {
        const isle: [number, number][] = [];
        for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if ((x !== sow.x || y !== sow.y) && isleOf(x, y) === isleOf(sow.x, sow.y)) isle.push([x, y]);
        isle.sort((a, b) => Math.hypot(a[0] - sow.x, a[1] - sow.y) - Math.hypot(b[0] - sow.x, b[1] - sow.y));
        pick(isle, ['raft', 'rivet'], 'sow');
        if (!out.some((f) => f.name === 'sow')) pick(isle, ['raft', 'rivet'], 'sow', true);   // ...then ground that is merely soft: tuft and sand take a brazier and a rock too
        if (!out.some((f) => f.name === 'sow')) pick(isle, ['raft', 'rivet'], 'sow', true, true);   // ...and last, the two tiles and somewhere to stand beside them
      }
    }
    return this._forges = out;
  }
  _forgesSeeded = false;
  _shelterSeeded = false;
  _raftSeeded = false;
  seedFoundForges() {
    if (this._forgesSeeded || !this.world || !this.world.start) return;
    this._forgesSeeded = true;   // before the walk: foundForges generates screens, which come back through screenAt
    this.placed = this.placed || new Map();
    for (const f of this.foundForges()) {
      const key = 'W|' + f.x + ',' + f.y;
      if (!this.placed.has(key)) this.placed.set(key, new Map());
      const m = this.placed.get(key);
      if (!m.has((f.tx - 1) + ',' + f.ty)) m.set((f.tx - 1) + ',' + f.ty, T.BRAZIER_LIT);
      if (!m.has(f.tx + ',' + f.ty)) m.set(f.tx + ',' + f.ty, T.ROCK);
      if (f.resource) {
        const at = f.resource.join(',');
        if (!m.has(at) && !this.broken.get(key)?.has(at)) m.set(at, T.THORN);
      }
      const cached = this.screens.get(f.x + ',' + f.y);   // a screen the search itself cached, before the overlay had the forge: dress it now
      if (cached) this.applyPlacedTo(cached.tiles, key);
    }
  }
  /** The laid things of a found forge on this screen, once: the items are the fire's after that (see systems.forge). */
  /** TWO SITUATIONS ON EVERY APPROACH, BY CONSTRUCTION (LS-395, 2026-09-12). The six situations were placed by other
   *  rules (pearls by basin, coal by heat, the tusk by its spit, stones by distance, sleepers wherever a thicket
   *  stood) and an approach carried two of them by luck: route-1's oldshell, on a rocky island, carried one. Count
   *  what each beast's approach will hold -- the caches on it, the unread stones, a forge with loot beside it -- and
   *  where that is under two, plant a thicket of four on an approach screen, which beds a sleeper (fillArea, the
   *  fifth situation) the way any thicket does. The thicket rides the placed overlay like the found forges, so it is
   *  saved with the world and the player can cut it. */
  seedApproachShelter() {
    if (this._shelterSeeded || !this.world || !this.world.start || !this.world.island) return;
    this._shelterSeeded = true;
    this.placed = this.placed || new Map();
    const loot = this.worldLoot() as any[], lairs = this.beastLairs();
    const forges = this.foundForges ? this.foundForges() : [];
    const lairKeys = new Set(lairs.map((l) => l.x + ',' + l.y));
    const dry = (v) => v === T.GROUND || v === T.SAND || v === T.MOSS;
    for (const lair of lairs) {
      const ring = this.approachOf(lair);
      let have = 0;
      for (const k of ring) {
        const [x, y] = k.split(',').map(Number);
        for (const c of loot) if (c.x === x && c.y === y && (c.kind === 'pearl' || c.kind === 'coal' || c.kind === 'tusk')) have++;
        /* A STONE ON THE WAY IS A STONE (2026-09-18). This asked for `mark === 'unknown'`, and a mark is the CHARM a
         * stone names -- 'unknown' belongs to the single rako stone in the world, whose first word names nothing. So
         * what the rule counted was not "an unread stone waits on the approach" but "the one withheld-word stone is
         * on this screen": over five seeds, twelve of fifteen approaches carried a stone and the rule counted one
         * none of those times, and thirteen of fifteen approaches were given a thicket they did not need. A stone
         * counts once for its screen, whatever it says -- a standing stone with a bearing on it IS the situation the
         * docs promise. */
        if (this.world.stones && [...this.world.stones.keys()].some((sk) => String(sk).startsWith(k + '|'))) have++;
        if (forges.some((f) => f.x === x && f.y === y) && loot.some((c) => Math.abs(c.x - x) <= 1 && Math.abs(c.y - y) <= 1)) have++;
      }
      if (have >= 2) continue;
      // nearest the glade first, never the lair's own screen nor a forge's
      const cands = ring.filter((k) => !lairKeys.has(k) && !forges.some((f) => f.x + ',' + f.y === k))
        .sort((a, b) => (this.world.dist.get(a) ?? 99) - (this.world.dist.get(b) ?? 99));
      for (const k of cands) {
        if (have >= 2) break;
        const [x, y] = k.split(',').map(Number);
        const tiles = this.screenAt(x, y).tiles;
        const pkey = 'W|' + k;
        const already = this.placed.get(pkey);
        let spot: [number, number] | null = null;
        const rng = new RNG(mix(this.seedNum, 4747, x * 31 + y));
        const order: [number, number][] = [];
        for (let ty = 3; ty < ROWS - 4; ty++) for (let tx = 3; tx < COLS - 4; tx++) order.push([tx, ty]);
        rng.shuffle(order);
        for (const [tx, ty] of order) {
          let ok = true;
          for (let dy = -1; dy <= 2 && ok; dy++) for (let dx = -1; dx <= 2; dx++) {
            const v = tiles[ty + dy][tx + dx];
            const inner = dx >= 0 && dx <= 1 && dy >= 0 && dy <= 1;
            if (inner ? !dry(v) : !(dry(v) || v === T.TUFT)) { ok = false; break; }
            if (already && already.has((tx + dx) + ',' + (ty + dy))) { ok = false; break; }
          }
          if (ok) { spot = [tx, ty]; break; }
        }
        if (!spot) continue;
        if (!this.placed.has(pkey)) this.placed.set(pkey, new Map());
        const m = this.placed.get(pkey);
        for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]]) m.set((spot[0] + dx) + ',' + (spot[1] + dy), T.THORN);
        const cached = this.screens.get(k);
        if (cached) this.applyPlacedTo(cached.tiles, pkey);
        this.partsCache.delete(k);
        have++;
      }
    }
  }
  /** A RAFT ON THE FIRST SHORE (LS-397, 2026-09-12; the deeper pass: the half-laid raft at the forge was the only teaching
   *  of forge, recipe and raft at once). The home island's launching place that faces the nearest other island holds a
   *  raft, once a world, laid a little offshore: the first crossing is learned by riding before it is learned by
   *  making, and the forge's half-laid raft then reads as the way to make another. */
  /** The island the wind would carry this raft to right now, as a bearing -- or null, because the wind is foul.
   *
   *  THE STREAKS SAY WHICH WAY THE WIND BLOWS; NOTHING SAID WHEN TO GO (LS-397 h, 2026-09-15). A raft is the first
   *  crossing and the wind is half its engine: it veers a full circle over a day, and paddling into it makes a fifth
   *  of the way that paddling with it does (0.253 px a frame against 1.393, measured by test/_probe_raftwind.ts). So
   *  waiting for the wind to come round IS the move, and a player had no way to know that -- the sea's foam streaks
   *  (LS-389) show the bearing, but reading them against where the islands lie is a piece of navigation nobody was
   *  taught. This is the other half of the tell, and it costs no words: when the wind would actually carry the hull
   *  to land, gold motes lift off it that way. Wait, and the gold arrives. That is the lesson.
   *
   *  A quarter-turn cone (the dot over 0.9) and land at least two screens off, so the beach you are standing on
   *  does not count as somewhere to go. */
  fairAcross() {
    const b = this.state.boat; if (!b || b.kind !== 'raft' || !this.world || !this.world.island) return null;
    const w = this.systems && this.systems.wind; if (!w) return null;
    const W = WORLD_W, H = WORLD_H, isle = (sx: number, sy: number) => this.world.island[sy * W + sx];
    const hx = Math.floor(b.x / (COLS * TS)), hy = Math.floor(b.y / (ROWS * TS));
    /* AND "FAIR" MEANS SOMEWHERE WORTH GOING, or the cue is on almost always and means nothing. Five islands in a
     * sixteen-by-thirteen world put land down most bearings: the first cut of this lit the hull for a wind blowing
     * the OPPOSITE way too, which its own check caught and called decoration. So the only islands that count are
     * the ones still holding a beast that lives -- which is the run's whole errand -- and never the island the hull
     * is already on. Gold on the water therefore means "this wind carries you toward a beast", and that teaches the
     * errand at the same time as the weather. */
    const want = new Set<number>();
    for (const r of this.state.beasts || []) { if (!r || r.alive === false || !r.home) continue; const id = isle(r.home[0], r.home[1]); if (id >= 0) want.add(id); }
    if (!want.size) return null;
    const hereIsle = hx >= 0 && hy >= 0 && hx < W && hy < H ? isle(hx, hy) : -1;
    let best: any = null;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const id = isle(x, y);
      if (id < 0 || !want.has(id) || (hereIsle >= 0 && id === hereIsle)) continue;
      if (Math.abs(x - hx) <= 1 && Math.abs(y - hy) <= 1) continue;   // not the shore you are leaving
      const gx = (x * COLS + COLS / 2) * TS, gy = (y * ROWS + ROWS / 2) * TS;
      const dx = gx - b.x, dy = gy - b.y, len = Math.hypot(dx, dy) || 1;
      const dot = (dx / len) * w[0] + (dy / len) * w[1];
      if (dot > 0.9 && (!best || len < best.len)) best = { dx: dx / len, dy: dy / len, len, dot, at: [x, y] };
    }
    return best;
  }

  /** The beast the dial's needle points at: the one heard last at a stone -- or, with the crow charm worn, the nearest
   *  one still living, heard or not (LS-335, 2026-09-16). The HUD and the map both ask this, so they cannot disagree. */
  needleBeast(): { kind: string } | null {
    const recs = (this.state.beasts || []).filter((r: any) => r && r.alive !== false);
    if (wearing(this.state, 'crow') && this.world && this.world.lairs && this.player) {
      let best: any = null;
      for (const r of recs) { const l = this.world.lairs.find((q: any) => q.kind === r.kind); if (!l) continue; const d = Math.hypot(l.px - this.player.cx, l.py - this.player.cy); if (!best || d < best.d) best = { kind: r.kind, d }; }
      return best ? { kind: best.kind } : null;
    }
    const heard = recs.filter((r: any) => r.heardAt).sort((a: any, b: any) => (b.heardAt || 0) - (a.heardAt || 0))[0];
    return heard ? { kind: heard.kind } : null;
  }
  seedFoundRaft() {
    if (this._raftSeeded || !this.world || !this.world.start || !this.world.island || (this.world.islandCount || 1) < 2) return;
    this._raftSeeded = true;
    const st = this.state; if (!st || st.boat || st.raftSeeded) return;
    const W = WORLD_W, H = WORLD_H, isle = (sx, sy) => this.world.island[sy * W + sx];
    const home = isle(this.world.start.x, this.world.start.y);
    let target = null;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) { const id = isle(x, y); if (id < 0 || id === home) continue; const d = Math.hypot(x - this.world.start.x, y - this.world.start.y); if (!target || d < target.d) target = { x, y, d }; }
    if (!target) return;
    const gx = (target.x * COLS + COLS / 2) * TS, gy = (target.y * ROWS + ROWS / 2) * TS;
    const wet = (v) => v === T.WATER || v === T.SEA || v === T.SHALLOW;
    let pick = null;
    for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
      if (isle(sx, sy) !== home) continue; const t = this.screenAt(sx, sy).tiles;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) for (let ty = 2; ty < ROWS - 2; ty++) for (let tx = 2; tx < COLS - 2; tx++) {
        const v = t[ty][tx]; if (v === undefined || wet(v) || !walkableTile(v)) continue;
        let deep = true; for (let k = 1; k <= 3; k++) for (let side = -1; side <= 1; side++) { const r = t[ty + dy * k + (dx ? side : 0)]; if (!r || !wet(r[tx + dx * k + (dy ? side : 0)])) deep = false; }
        { const r3 = t[ty + dy * 3]; if (!r3 || r3[tx + dx * 3] !== T.SEA) deep = false; }
        if (!deep) continue; const wx = sx * COLS + tx, wy = sy * ROWS + ty;
        if ((gx - wx * TS) * dx + (gy - wy * TS) * dy <= 0) continue;
        const d = Math.hypot(wx * TS - gx, wy * TS - gy); if (!pick || d < pick.d) pick = { sx, sy, tx, ty, dx, dy, d };
      }
    }
    if (!pick) return;
    const x = (pick.sx * COLS + pick.tx) * TS + TS / 2 + pick.dx * 20, y = (pick.sy * ROWS + pick.ty) * TS + TS / 2 + pick.dy * 18;
    if (!this.hullFits(x, y)) return;
    st.boat = { kind: 'raft', x, y, vx: 0, vy: 0, heading: 0 }; st.raftSeeded = true;
  }
  seedForgeOfferings(a = this.area) {
    for (const f of this.foundForges()) {
      if (f.x !== a.x || f.y !== a.y) continue;
      const key = f.x + ',' + f.y + ':' + f.name;
      const seeded = (this.state.forgeSeeded ||= []);
      if (seeded.includes(key)) continue;
      seeded.push(key);
      for (let i = 0; i < f.laid.length; i++) {
        /* SAVED, LIKE ANYTHING ELSE LYING IN THE WORLD (2026-09-17). These went onto `items` unmarked, and the save
         * writes only what is marked -- while the fact that the pile had been laid WAS saved, in `forgeSeeded`. So a
         * reload deleted the world's half-laid forges for good: the raft and the rivet waiting on the sow's island
         * were gone and would never be laid again. */
        const rx = f.x * COLS + f.tx, ry = f.y * ROWS + f.ty;   // the forge's rock as a world tile
        const it = this.dropAt(f.laid[i], rx * TS + 1 + (i % 2) * 5, ry * TS - 2 + Math.floor(i / 2) * 4, { exact: true, offered: true, dropped: true });
        it.offered = true; it.forgeTx = rx; it.forgeTy = ry; it.seeded = true;   // the world's own pile: a lesson, not a pickup (LS-456)
      }
    }
  }
  /* THE FORGE (LS-384). A lit brazier with a rock beside it. Facing the rock with a material picked, the tool
   * button lays the material on the rock instead of setting it down as a tile; `systems.forge` makes the product
   * when a whole recipe lies there. */
  isForgeRock(tx, ty) {
    if (this.tileAt(tx, ty) !== T.ROCK) return false;   // (tx, ty): a world tile (LS-511)
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (this.tileAt(tx + dx, ty + dy) === T.BRAZIER_LIT) return true;
    return false;
  }
  /** The same four-pixel reach as aheadTile, across the body's facing edge. */
  forgeAhead() {
    if (!this.area || this.aboard) return null;
    const center = this.aheadTile();
    if (center && this.isForgeRock(center.tx, center.ty)) return center;
    const p = this.player;
    const vertical = p.dir === 'up' || p.dir === 'down';
    const front = p.dir === 'up' ? p.y - 4 : p.dir === 'down' ? p.y + p.h + 4
      : p.dir === 'left' ? p.x - 4 : p.x + p.w + 4;
    let found: {tx: number; ty: number; kind: number} | null = null;
    for (let end = 0; end < 2; end++) {
      const side = vertical ? p.x + (end ? p.w - 1 : 1) : p.y + (end ? p.h - 1 : 1);
      const x = vertical ? side : front, y = vertical ? front : side;
      const tx = Math.floor(x / TS), ty = Math.floor(y / TS);
      if (!this.isForgeRock(tx, ty)) continue;
      if (found && (found.tx !== tx || found.ty !== ty)) return null;
      found = {tx, ty, kind: T.ROCK};
    }
    return found;
  }
  offerToForge(sel) {
    if (!sel || !forgeTakes(sel.kind)) return false;
    const a = this.forgeAhead();
    if (!a) return false;
    const bag = bagOf(this.state);
    if (bag.slots[bag.selected] !== sel || sel.n <= 0) return false;
    if (--sel.n === 0) bag.slots[bag.selected] = null;
    syncFlags(this.state);
    const it = this.dropAt(sel.kind, a.tx * TS + 2, a.ty * TS - 2, { exact: true, offered: true, dropped: true });   // and it is written down: a material laid on the rock and left there was deleted by a reload (2026-09-17)
    if (sel.dur !== undefined) it.dur = sel.dur;
    it.offered = true; it.forgeTx = a.tx; it.forgeTy = a.ty; it.laidAt = this.frame;   // when it was laid, so the fire can let a pile settle before it reads it (2026-09-18)
    this.puff(a.tx * TS + 8, a.ty * TS + 4, UI.flame, 4, 'ember');
    this.sfx('drop', a.tx * TS + 8);
    return true;
  }
  /** WHAT EACH FORGE ON THE SCREEN IS SHORT OF, FOR THE WORLD TO SHOW (LS-457, 2026-09-26). The preview lived in the HUD
   *  and only while the body faced the rock, so a half-built raft read as three pieces of loot on a stone: the newcomer
   *  probe carried the missing cutting from five seconds in and never laid it. Every pile on this screen that no recipe
   *  takes yet names the one piece it wants; the scene hangs that piece over the rock. `held` is whether the player
   *  carries it, which brightens the ghost. */
  _fw: { frame: number; out: any[] } | null = null;
  forgeWants(): { tx: number; ty: number; kind: string; held: boolean; out?: string }[] {
    if (this.mode !== 'play' || !this.area || (globalThis as any).__noForgeGhost) return [];
    if (this._fw && this._fw.frame === this.frame && !this.items.some((it: any) => it.laidAt === this.frame)) return this._fw.out;   // asked by the scene, the key's pulse and the HUD every frame: once is enough
    const out0 = this.forgeWantsNow(); this._fw = { frame: this.frame, out: out0 }; return out0;
  }
  forgeWantsNow(): { tx: number; ty: number; kind: string; held: boolean; out?: string }[] {
    const piles = new Map<string, Record<string, number>>();
    for (const it of this.items) {
      if (it.dead || !it.offered || it.forgeTx === undefined) continue;
      if (!this.offeredAtForge(it, it.forgeTx, it.forgeTy)) continue;
      const k = it.forgeTx + ',' + it.forgeTy; const have = piles.get(k) || {}; have[it.kind] = (have[it.kind] || 0) + (it.bulk || 1); piles.set(k, have);
    }
    const out: { tx: number; ty: number; kind: string; held: boolean; out?: string }[] = [];
    for (const [k, have] of piles) {
      if (matchingForgeRecipe(have)) continue;
      const miss = missingForgeIngredient(have); if (!miss) continue;
      const [tx, ty] = k.split(',').map(Number);
      out.push({ tx, ty, kind: miss.kind, held: bagCount(this.state, miss.kind) >= (miss.n || 1), out: miss.out });   // and what the pile will make: the workshop shows its raft
    }
    return out;
  }
  forgePreview() {
    if (this.mode !== 'play' || !this.area || this.aboard) return null;
    const a = this.forgeAhead();
    if (!a) return null;
    const have: Record<string, number> = {};
    let offered = 0;
    for (const it of this.items) if (this.offeredAtForge(it, a.tx, a.ty)) {
      have[it.kind] = (have[it.kind] || 0) + 1; offered++;
    }
    if (!offered) return null;
    const recipe = matchingForgeRecipe(have);
    return { recipe, missing: recipe ? null : missingForgeIngredient(have) };
  }
  /** WHAT THE KEY MAY TAKE BACK FROM A FORGE (LS-456, 2026-09-26): only what the player laid there. The home forge is
   *  laid with three of the raft's four pieces so the player adds the fourth, and with a blade in hand the key's pulse
   *  there meant TAKE -- the newcomer probe's first press, one second in, lifted the half-built raft into its bag and
   *  it never made anything. The world's pile stays on the rock; it burns into the raft with the rest. */
  takeableAtForge(it, tx: number, ty: number) { return this.offeredAtForge(it, tx, ty) && !it.seeded; }
  offeredAtForge(it, tx: number, ty: number) {
    // (tx, ty) and the offering's own rock are world tiles (LS-511)
    return !it.dead && it.offered && it.forgeTx === tx && it.forgeTy === ty
      && Math.abs(it.x - tx * TS) < TS
      && Math.abs(it.y - ty * TS) < TS;
  }
  takeForgeOfferings(tx: number, ty: number) {
    const a = this.forgeAhead();
    if (!a || a.tx !== tx || a.ty !== ty) return false;
    const slots = bagOf(this.state).slots, counts = slots.map(s => s?.n || 0);
    let taken = 0;
    for (const it of this.items) {
      if (!this.takeableAtForge(it, tx, ty)) continue;
      const n = it.bulk || 1, left = addItem(this.state, it.kind, n, it.dur);
      taken += n - left;
      if (!left) it.dead = true;
      else it.bulk = left;
    }
    if (!taken) return false;
    slots.forEach((s, i) => { if (s && s.n > counts[i]) this.pickupUntil.set(s, this.runFrames + 120); });
    this.sfx('pickup');
    // Save only after the bag and all surviving offerings agree.
    this.save();
    return true;
  }
  // state.boat is the current hull for legacy saves/rider physics; otherBoats retain all the rest.
  *worldHulls() {
    if (this.state.boat) yield this.state.boat;
    yield* this.state.otherBoats || [];
  }
  canPlaceHull(x, y) {
    if (this.aboard || !this.hullFits(x, y)) return false;
    let count = 0;
    for (const b of this.worldHulls()) {
      if (++count >= MAX_HULLS || Math.hypot(b.x - x, b.y - y) < 24) return false;
    }
    return this.hullGetsOut(x, y);
  }
  /** A HULL GOES DOWN WHERE IT CAN GET OUT (2026-09-26). On route-2 the newcomer probe set its raft in a sliver of
   *  shallows under a cliff, a hull's width of water walled on three sides, and paddled into rock for four minutes: the
   *  press took it and the pulse said yes. Now the water must open out -- a flood of tiles a hull fits through
   *  reaches forty of them, the sea or a lake worth crossing -- or the press refuses at the shore, where a player can try along the beach. */
  hullGetsOut(x: number, y: number): boolean {
    const tx0 = Math.floor(x / TS), ty0 = Math.floor(y / TS), key = tx0 + ',' + ty0 + ',' + Math.floor((this.runFrames || 0) / 120);
    const memo = (this._outMemo ||= new Map<string, boolean>()); if (memo.has(key)) return memo.get(key)!;
    if (memo.size > 256) memo.clear();
    const seen = new Set<number>([ty0 * 100000 + tx0]); const q: number[][] = [[tx0, ty0]]; let sea = 0, ok = false;
    for (let head = 0; head < q.length && q.length < 900; head++) {
      const [tx, ty] = q[head]; const cx = tx * TS + TS / 2, cy = ty * TS + TS / 2;
      if (++sea >= 40) { ok = true; break; } void cx; void cy;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = tx + dx, ny = ty + dy, k = ny * 100000 + nx; if (seen.has(k)) continue; seen.add(k); if (!this.boatWet(nx * TS + TS / 2, ny * TS + TS / 2) || !this.hullFits(nx * TS + TS / 2, ny * TS + TS / 2)) continue; q.push([nx, ny]); }
    }
    memo.set(key, ok); return ok;
  }
  _outMemo: Map<string, boolean> | null = null;
  /** WHERE A HULL CAN PUT YOU ASHORE, per island (LS-461, 2026-09-26): every tile of standable ground with open water
   *  beside it that a hull fits in -- the sea or its shallows, not an inland pool. The sow's island is walled with cliff
   *  on most sides, and a raft steered at its nearest ground pressed against rock for minutes; the markers at the edge
   *  of the view point here instead. Read once a world, from the world's own tiles. */
  _landings: Map<number, [number, number][]> | null = null;
  landings(): Map<number, [number, number][]> {
    if (this._landings) return this._landings;
    const out = new Map<number, [number, number][]>();
    const w = this.world; if (!w || !w.island) return (this._landings = out);
    const W = w.biome[0].length, H = w.biome.length;
    for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
      const id = w.island[sy * W + sx]; if (id < 0) continue;
      for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
        const wx = sx * COLS + tx, wy = sy * ROWS + ty, px = wx * TS + 8, py = wy * TS + 8;
        const k = this.tileKindAt(px, py); if (k === undefined || this.boatWet(px, py) || !walkableTile(k, {})) continue;
        let ok = false;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const qx = px + dx * TS, qy = py + dy * TS, q = this.tileKindAt(qx, qy); if ((q === T.SEA || q === T.SHALLOW) && this.hullFits(qx, qy)) { ok = true; break; } }
        if (!ok) continue;
        let list = out.get(id); if (!list) out.set(id, list = []); list.push([wx, wy]);
      }
    }
    /* ...AND ONLY THE ONES THAT LEAD SOMEWHERE (2026-09-26). The newcomer probe landed where a marker sent it, on a sand
     * spit under a cliff with no way up, and died there. Each island's standable ground is flooded into the stretches a
     * body can walk between (the game's own collision, tile to tile), and only the landings on its largest stretch --
     * the island proper -- are kept. */
    const stand = (wx: number, wy: number) => { const px = wx * TS + 8, py = wy * TS + 8, k = this.tileKindAt(px, py); return k !== undefined && !this.boatWet(px, py) && walkableTile(k, {}) && !this.solid(px, py, false, this.player); };
    for (const [id, list] of out) {
      const comp = new Map<number, number>(); const key = (x: number, y: number) => y * 4096 + x; let best = -1, bestN = 0, nextC = 0;
      for (let sy = 0; sy < H; sy++) for (let sx = 0; sx < W; sx++) {
        if (w.island[sy * W + sx] !== id) continue;
        for (let ty = 0; ty < ROWS; ty++) for (let tx = 0; tx < COLS; tx++) {
          const x0 = sx * COLS + tx, y0 = sy * ROWS + ty; if (comp.has(key(x0, y0)) || !stand(x0, y0)) continue;
          const c = nextC++; let n = 0; const q = [[x0, y0]]; comp.set(key(x0, y0), c);
          while (q.length) { const [x, y] = q.pop()!; n++; for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = x + dx, ny = y + dy; if (comp.has(key(nx, ny))) continue; const nsx = Math.floor(nx / COLS), nsy = Math.floor(ny / ROWS); if (nsx < 0 || nsy < 0 || nsx >= W || nsy >= H || w.island[nsy * W + nsx] !== id) continue; if (!stand(nx, ny)) continue; comp.set(key(nx, ny), c); q.push([nx, ny]); } }
          if (n > bestN) { bestN = n; best = c; }
        }
      }
      const kept = list.filter(([x, y]) => comp.get(key(x, y)) === best);
      if (kept.length) out.set(id, kept);
    }
    return (this._landings = out);
  }
  boatWet(x, y) { const t = this.tileKindAt(x, y); return t === T.WATER || t === T.SEA || t === T.SHALLOW || t === T.ICE; }
  /** Ground the body could step onto within two tiles of the hull (2026-09-19): what makes a mooring a mooring. */
  hullBesideLand(b) { for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) { const t = this.tileKindAt(b.x + dx * TS, b.y + dy * TS); if (t !== undefined && !this.boatWet(b.x + dx * TS, b.y + dy * TS) && walkableTile(t)) return true; } return false; }
  hullFits(x, y) { return this.boatWet(x - 7, y - 4) && this.boatWet(x + 7, y - 4) && this.boatWet(x - 7, y + 4) && this.boatWet(x + 7, y + 4); }
  /** ...AND THE SEAT HAS TO BE ON THE MAP (2026-09-18). A hull sailed at the world's north rim stopped where the water
   *  ran out, at y=4, and the rider's seat is 8.5 px above the hull's middle -- off the map. `checkEdges` then clamped
   *  the BODY back onto it every frame while `steerBoat` pinned it to the hull every frame, so the body sat 8.5 px
   *  astern of its own boat, for ever. The hull is what gets bounded; the seat is what it is bounded by. */
  hullSeatOnMap(b, x, y) {
    const p = this.player, up = b && b.kind === 'boat' ? 6 : 3;
    const sx = x - p.w / 2, sy = y - p.h / 2 - up;
    return sx >= 0 && sy >= 0 && sx <= WORLD_W * COLS * TS - p.w && sy <= WORLD_H * ROWS * TS - p.h;
  }
  boardIfBeside(dx, dy) {
    if (this.aboard) return;
    const p = this.player, ax = p.cx + dx * 12, ay = p.cy + dy * 12;
    let b = null, distance = 16;
    for (const hull of this.worldHulls()) {
      const d = Math.hypot(hull.x - ax, hull.y - ay);
      if (d <= distance) { b = hull; distance = d; }
    }
    if (!b) return;
    if (b !== this.state.boat) {
      const others = this.state.otherBoats;
      others.splice(others.indexOf(b), 1);
      if (this.state.boat) others.push(this.state.boat);
      this.state.boat = b;
    }
    this.aboard = true; b.moored = false;   // ...and it is loose again the moment you are in it (2026-09-18)
    this.sfx('splash', b.x);
    p.x = b.x - p.w / 2; p.y = b.y - p.h / 2 - 2; p.recentre ? p.recentre() : null;
  }
  /* WAY ON THE WATER (Vijay: "movement with momentum"). The stick is thrust, not position: the hull gathers way,
   * the water drags it, and it keeps going when the stick is let go. Its heading is one of eight, from its travel. */
  moveHull(b, thrustX, thrustY) {
    b.vx = (b.vx || 0) + thrustX; b.vy = (b.vy || 0) + thrustY;
    b.vx *= HULL_DRAG; b.vy *= HULL_DRAG;
    const sp = Math.hypot(b.vx, b.vy);
    /* AND THE KEEL LIFTS THE CEILING, NOT ONLY THE PUSH (2026-09-17). The charm says "a hull under your hand makes
     * a fifth more way" and multiplied the per-frame thrust alone -- but a steered boat settles at thrust/(1-drag),
     * which is 2.6, and this clamp already held it to 2.2. So the charm was worth 0.7% in a boat (it did work on a
     * raft, whose wind thrust settles under the ceiling). A fifth more way means a fifth more of both. */
    const top = HULL_TOP * (this.state && wearing(this.state, 'keel') ? 1.2 : 1);
    if (sp > top) { b.vx *= top / sp; b.vy *= top / sp; }
    /* THE DEAD ZONE ONLY WHEN NOTHING PUSHES (2026-09-12; boat.ts drift-3): a raft's wind thrust is 0.03 a frame, so the
     * wind's smaller component (under a third of it) never survived the 0.01 floor and a raft could only drift along the
     * axis the wind mostly blew -- straight past an island a screen off that axis. */
    if (!thrustX && Math.abs(b.vx) < 0.01) b.vx = 0; if (!thrustY && Math.abs(b.vy) < 0.01) b.vy = 0;
    if (b.vx) { if (this.hullFits(b.x + b.vx, b.y) && this.hullSeatOnMap(b, b.x + b.vx, b.y)) b.x += b.vx; else b.vx = -b.vx * 0.3; }   // the shore turns a hull back, and so does the world's rim
    if (b.vy) { if (this.hullFits(b.x, b.y + b.vy) && this.hullSeatOnMap(b, b.x, b.y + b.vy)) b.y += b.vy; else b.vy = -b.vy * 0.3; }
    if (b.kind === 'boat' && Math.hypot(b.vx, b.vy) > 0.25) {
      const heading = ((Math.round(Math.atan2(b.vy, b.vx) / (Math.PI / 4)) % 8) + 8) % 8;
      if (heading !== b.heading && this.aboard && b === this.state.boat) this.sfx('creak', b.x);
      b.heading = heading;
    }
    /* SOUND UNDER WAY (LS-390): water laps the planks while the hull moves, and the bow hisses at speed */
    if (this.aboard && b === this.state.boat) {
      const way = Math.hypot(b.vx, b.vy);
      b.lapAt = b.lapAt || 0;
      if (way > 0.3 && this.frame - b.lapAt >= 42) { b.lapAt = this.frame; this.sfx(way > 1.6 && b.kind === 'boat' ? 'wake' : 'lap', b.x); }
    }
  }
  /** NOT INTO A POCKET (2026-09-29, the playthrough: the first landing on the sow's island put the body on three tiles of
   *  sand walled by trunks, the sea behind, the only way on a diagonal no body passes). The dry ground a landing opens
   *  onto is flooded a tile at a time, side to side as a body walks; fewer than twelve tiles reached is a closet, and the
   *  hull keeps along the coast instead. Bounded work: it stops at twelve. */
  landingPocket(x, y) {
    const tx0 = Math.floor(x / TS), ty0 = Math.floor(y / TS), seen = new Set([tx0 + ',' + ty0]), q = [[tx0, ty0]];
    const open = (tx, ty) => { const v = this.tileKindAt(tx * TS + 8, ty * TS + 8); return v !== undefined && walkableTile(v, {}) && !this.boatWet(tx * TS + 8, ty * TS + 8); };
    while (q.length) { const [tx, ty] = q.shift(); for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const k = (tx + dx) + ',' + (ty + dy); if (seen.has(k) || !open(tx + dx, ty + dy)) continue; seen.add(k); if (seen.size >= 12) return false; q.push([tx + dx, ty + dy]); } }
    return true;
  }
  canStepOffHull(b, x, y) {
    const p = this.player, distance = Math.hypot(x - b.x, y - b.y);
    // A 24px step remains inside the existing 12px reach + 16px boarding radius,
    // including the nearest keyboard diagonal. Never move the hull to make a landing fit.
    if (distance > 24 || this.boatWet(x, y) || !walkableTile(this.tileKindAt(x, y), {})) return false;
    if (this.landingPocket(x, y)) return false;
    for (const [px, py] of bodyPoints(x - p.w / 2, y - p.h / 2, p.w, p.h)) {
      // While aboard, solid() deliberately permits water; ashore, every foot must be dry.
      if (this.boatWet(px, py) || this.solid(px, py, false, p)) return false;
    }
    const steps = Math.ceil(distance / 2);
    for (let i = 1; i < steps; i++) {
      const cx = b.x + (x - b.x) * i / steps, cy = b.y + (y - b.y) * i / steps;
      for (const [px, py] of bodyPoints(cx - p.w / 2, cy - p.h / 2, p.w, p.h)) {
        if (!this.boatWet(px, py) && this.solid(px, py, false, p)) return false;
      }
    }
    return true;
  }
  steerBoat(dx, dy) {
    const b = this.state.boat, p = this.player; if (!b) { this.aboard = false; return; }
    // a push toward standable ground is a step ashore
    if (dx || dy) {
      /* A HULL YOU STEP OFF IS MOORED (2026-09-18). "The hull waits" is what the docs promise, and it did not: a raft
       * rides the wind wherever it is, so a body that stepped ashore on a beast's island had its raft out of reach
       * within half a second and a hundred tiles away within a minute -- and two of the three lairs sit on islands
       * with no forge and no brazier, so there is nothing to build another with. The run was over and nothing said
       * so. A hull the body left waits where it was left; boarding it again sets it loose. A hull that was never
       * ridden -- the one the world lays off the home shore, or one you set down out on the water -- still rides the wind;
       * one set down at the shore is moored there (placeHull, 2026-09-29). */
      const ashore = (x, y) => { this.aboard = false; b.vx = 0; b.vy = 0; b.moored = true; p.x = x - p.w / 2; p.y = y - p.h / 2; p.recentre ? p.recentre() : null; this.sfx('splash', x); };
      const tryAshore = (ddx, ddy) => {
        const gx = b.x + ddx * 16, gy = b.y + ddy * 14;
        const t = this.tileKindAt(gx, gy);
        if (this.canStepOffHull(b, gx, gy)) { ashore(gx, gy); return true; }
        // An adjacent beach may fit, but a distant opening beyond a cliff is not a step ashore.
        if (t !== undefined && !this.boatWet(gx, gy)) {
          const px = -ddy, py = ddx;
          for (const k of [1, -1]) {
            const x = gx + px * k * TS, y = gy + py * k * TS;
            if (!this.canStepOffHull(b, x, y)) continue;
            ashore(x, y); return true;
          }
        }
        return false;
      };
      /* A DIAGONAL PUSH TRIES EACH AXIS TOO (2026-09-12; boat.ts drift-0): held toward an island to the south-east, the
       * stick asked only the diagonal tile, which was sea, while the beach lay due east. */
      if (tryAshore(dx, dy)) return;
      if (dx && dy && (tryAshore(dx, 0) || tryAshore(0, dy))) return;
    }
    const len = Math.hypot(dx, dy) || 1;
    const steered = b.kind === 'boat';
    const push = (steered ? HULL_THRUST : RAFT_PADDLE) * (wearing(this.state, 'keel') ? 1.2 : 1) * (!steered && wearing(this.state, 'fincharm') ? 1.5 : 1);   // the fin charm (LS-335): the paddle's way and a half, a raft's alone   // a raft is paddled at a third of a boat's way (LS-389); the keel charm adds a fifth to either (LS-335)
    let tx = (dx || dy) ? dx / len * push : 0, ty = (dx || dy) ? dy / len * push : 0;
    if (b.kind === 'raft' && this.systems && this.systems.wind && !wearing(this.state, 'anchor')) { tx += this.systems.wind[0] * RAFT_WIND; ty += this.systems.wind[1] * RAFT_WIND; }   // the anchor charm (LS-335): a ridden raft takes no wind, for better and worse
    this.moveHull(b, tx, ty);
    if (steered && Math.hypot(b.vx, b.vy) > 0.25) p.dir = HEADING_DIR[b.heading];
    p.x = b.x - p.w / 2; p.y = b.y - p.h / 2 - (b.kind === 'boat' ? 6 : 3);   // deeper in a boat's belly: the seated feet showed under the hull (Vijay, 2026-09-12) if (p.recentre) p.recentre();
  }
  /** A hull near enough to the window to be worth a frame's work: its own screen or one beside it (2026-09-20). */
  hullNear(b) { return Math.abs(Math.floor(b.x / PW) - this.area.x) <= 1 && Math.abs(Math.floor(b.y / PH) - this.area.y) <= 1; }
  driftRaft() {   // a raft drifts whether or not you are on it; a boat keeps whatever way it had
    for (const b of this.worldHulls()) {
      if (this.aboard && b === this.state.boat) continue;
      /* ...AND ONLY WHERE THE PICTURE IS (2026-09-20). Every placed hull was stepped every frame, and `moveHull` asks
       * `tileKindAt` four times -- which GENERATES a screen that was never visited. A raft adrift over open country
       * the player has not walked made the generator run inside the frame loop, off their path. Out there the wind
       * moves nothing until the window comes near; a hull is where you left it when you return. */
      if (!this.hullNear(b)) continue;
      if (b.moored) { b.vx = 0; b.vy = 0; continue; }
      if (b.kind === 'raft' && this.systems && this.systems.wind) this.moveHull(b, this.systems.wind[0] * RAFT_WIND, this.systems.wind[1] * RAFT_WIND);
      else if (b.vx || b.vy) this.moveHull(b, 0, 0);
    }
  }
  /** The hull's outline in world pixels: a raft is a square, a boat a pointed hull turned to its heading. */
  hullPoly(b, inset = 0) {
    if (b.kind === 'raft') { const r = 8 - inset; return [[-r, -r * 0.75], [r, -r * 0.75], [r, r * 0.75], [-r, r * 0.75]].map(([x, y]) => [b.x + x, b.y + y]); }
    const a = (b.heading || 0) * Math.PI / 4, c = Math.cos(a), s = Math.sin(a), L = 11 - inset, W = 5.5 - inset;
    const local = [[L, 0], [L * 0.45, -W], [-L * 0.7, -W], [-L, -W * 0.45], [-L, W * 0.45], [-L * 0.7, W], [L * 0.45, W]];
    return local.map(([x, y]) => [b.x + x * c - y * s, b.y + x * s + y * c]);
  }
  fillPoly(g, pts, fill, stroke = null) {
    g.beginPath(); pts.forEach(([x, y], i) => i ? g.lineTo(Math.round(x), Math.round(y)) : g.moveTo(Math.round(x), Math.round(y))); g.closePath();
    g.fillStyle = fill; g.fill(); if (stroke) { g.strokeStyle = stroke; g.lineWidth = 1; g.stroke(); }
  }
  drawBoat(g, b = this.state.boat) {
    if (!b) return;
    const bob = Math.round(Math.sin(this.frame / 14) * 1), bb = { ...b, y: b.y + bob };
    const sp = Math.hypot(b.vx || 0, b.vy || 0);
    // the wake: two short foam lines astern when the hull has way on
    if (sp > 0.4 && b.kind === 'boat') { const a = b.heading * Math.PI / 4, c = Math.cos(a), s = Math.sin(a); g.fillStyle = 'rgba(220,240,244,0.55)'; for (const side of [-1, 1]) for (let k = 0; k < 3; k++) { const d = 12 + k * 4 + (this.frame % 4), w = 4 + k * 2; g.fillRect(Math.round(b.x - c * d - s * side * w), Math.round(b.y - s * d + c * side * w), 2, 1); } }
    this.fillPoly(g, this.hullPoly({ ...b, y: b.y + 4 }), 'rgba(0,0,0,0.22)');   // the shadow on the water
    const img = this.hullImage(b.kind, b.kind === 'raft' ? 0 : (b.heading || 0));
    if (img) g.drawImage(img, Math.round(b.x - HULL_CX), Math.round(b.y + bob - HULL_CY));
  }
  hullImages = new Map<string, any>();
  /** The hull's sprite for a heading, blitted once from art's sheet onto its own canvas and kept. */
  hullImage(kind, heading) {
    const k = kind + '|' + heading; let c = this.hullImages.get(k);
    if (c) return c;
    if (typeof document === 'undefined') return null;
    const sheet = hullSheet(kind, heading);
    c = document.createElement('canvas'); c.width = sheet.w; c.height = sheet.h;
    const cg = c.getContext('2d'); if (!cg) return null;
    for (let y = 0; y < sheet.h; y++) for (let x = 0; x < sheet.w; x++) { const p = sheet.px[y * sheet.w + x]; if (p) { cg.fillStyle = p; cg.fillRect(x, y, 1, 1); } }
    this.hullImages.set(k, c); return c;
  }
  /** The near gunwale over the body's legs, so it sits IN the hull rather than standing on it. */
  drawBoatOver(g) {
    const b = this.state.boat; if (!b || !this.aboard) return;
    if (b.kind === 'raft') return;   // a raft is a floor: the body stands on it, nothing of it is in front of the legs (2026-09-12)
    const bob = Math.round(Math.sin(this.frame / 14) * 1), p = this.player;
    g.save(); g.beginPath(); g.rect(Math.round(b.x) - 14, Math.round(p.cy) + 1, 28, 14); g.clip();
    const img = this.hullImage(b.kind, b.kind === 'raft' ? 0 : (b.heading || 0));
    if (img) g.drawImage(img, Math.round(b.x - HULL_CX), Math.round(b.y + bob - HULL_CY));
    g.restore();
  }
  /* WETNESS, SHOWN (2026-09-11; Vijay: "we need to show wetness for objects visually"). The rain has always left a
   * wetness on the ground -- the field the fire reads, the pace reads, the chill reads -- and nothing drew it. Now a
   * wet tile is darker and cooler by how wet it is, with a glint or two that shifts, and the darkening lies over
   * whatever stands on the tile (a rock, a tree's foot, a body) so the objects read as wet with the ground. Bodies
   * that are Soaked drip. Only the current screen carries a rain field; the neighbours take its mean, so the seam
   * does not show. Drawn in the world's frame, before the wind and the kindling, under the HUD. */
  /* WHAT IS WET SAYS SO ITSELF NOW (2026-09-11): the tiles, the turf, the creatures and the forager paint their
   * own wet state (art.ts wetTone), so the tint that used to lie over the world is gone. What is left here is the
   * drip off a soaked body, which is a particle and not a picture. */
  drawWetness(g) {
    if (this.frame % 12 !== 0) return;
    for (const b of [this.player, ...(this.enemies || [])]) {
      if (!b || b.dead || !afflicted(b, 'Soaked')) continue;
      this.puff(b.cx + ((this.frame >> 2) % 5) - 2, b.y + b.h - 1, UI.foamLit, 1, 'splash');
    }
  }
  drawHud() { return H.drawHud(this); }
  drawMap() { return H.drawMap(this); }
  drawTitle() { return H.drawTitle(this); }
  drawWin() { return H.drawWin(this); }
  drawGameOver() { return H.drawGameOver(this); }
}
